import { UnjoinedAccountDeletion } from "./UnjoinedAccountDeletion";
import { TaskIcon } from "./TaskIcon";
import { useTaskIcons } from "./useTaskIcons";
import { NativeConsent } from "./NativeConsent";
import { DailySummarySettings } from "./DailySummarySettings";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { configureChildSession, ensureChildSession } from "./child-session";
import QRCode from "qrcode";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type SyntheticEvent,
} from "react";
import {
  openTaskCommandOutbox,
  type TaskCommandActorBinding,
  type TaskCommandMutation,
  type TaskChecklistOperation,
  type TaskHelpReason,
  type TaskLifecycleOperation,
} from "./outbox";
import {
  createWebMcpSearchHistoryTools,
  registerWebMcpTools,
  requireWebMcpDate,
  requireWebMcpIdempotencyKey,
  requireWebMcpInteger,
  requireWebMcpRoutineSchedule,
  requireWebMcpString,
  selectParentWebMcpTools,
  type WebMcpTool,
} from "./webmcp";
import {
  addLocalDays,
  addLocalMonths,
  localDateInTimezone,
  movePlanTaskToDrop,
  planDragAutoScrollDelta,
  planPointerDragActivated,
  planningMonth,
  planningMonthLabel,
  planningWeek,
  planningWeekLabel,
  replaceTaskCollectionItem,
  startOfPlanningWeek,
  type PlanTaskDropPlacement,
} from "./parent-planning";
import { ParentSearchHistory } from "./ParentSearchHistory";
import { childIdsForContextualForm } from "./child-context";
import {
  reorderTodayTaskIdsWithinLaneForDrop,
  todayLaneForState,
  todayLifecycleCommandForDrop,
  type TodayLaneId,
  type TodayTaskDropPlacement,
} from "./today-board";

type FormSubmitEvent = SyntheticEvent<HTMLFormElement, SubmitEvent>;
type TaskCollectionRefreshMode = "replace" | "background";

interface ApiErrorEnvelope {
  error?: { code?: string; message?: string; details?: Record<string, string> };
}

class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, string>;

  constructor(status: number, envelope: ApiErrorEnvelope) {
    super(envelope.error?.message ?? `通信に失敗しました (${String(status)})`);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = envelope.error?.code ?? "API_REQUEST_FAILED";
    this.details = envelope.error?.details ?? {};
  }
}

interface SessionActor {
  id: string;
  type: "adult" | "child";
  workspace_id: string;
  membership_role: "owner" | "guardian" | "supporter" | null;
  bound_child_id: string | null;
  device_id: string;
  session_id: string;
  capabilities: (
    "today:read" | "tasks:start" | "tasks:complete" | "tasks:help" | "tasks:reorder"
  )[];
  membership_scopes: {
    child_id: string | null;
    area_id: string | null;
    capability:
      | "tasks:read"
      | "tasks:write"
      | "tasks:proxy"
      | "routines:read"
      | "routines:write"
      | "evidence:read"
      | "evidence:verify"
      | "insights:read"
      | "reviews:read";
  }[];
}

interface WorkspaceSummary {
  id: string;
  name: string;
  timezone: string;
  role: "owner" | "guardian" | "supporter" | null;
}

interface BrowserSession {
  actor: SessionActor;
  workspace: WorkspaceSummary;
  csrf_token: string;
  recovery_codes?: string[];
}

interface WebPushSubscriptionState {
  id: string;
  workspace_id: string;
  device_id: string;
  status: "active" | "revoked" | "permanent_failure";
  expiration_time: number | null;
  last_success_at: number | null;
  last_failure_at: number | null;
  failure_code: string | null;
  version: number;
  created_at: number;
  updated_at: number;
}

interface WebPushSubscriptionResponse {
  subscription: WebPushSubscriptionState | null;
  idempotency_replayed: boolean;
  request_id: string;
}

interface ChildProfile {
  id: string;
  display_name: string;
  theme_color: string;
  reading_level: "emergent" | "beginner" | "independent";
  display_density: "comfortable" | "compact";
  version: number;
  archived_at: string | null;
}

interface WorkspaceSettings {
  id: string;
  name: string;
  timezone: string;
  week_starts_on: number;
  default_cycle_days: number;
  quiet_hours: { enabled: boolean; start: string; end: string };
  locale: string;
  version: number;
  updated_at: string;
}

interface WorkspaceExport {
  id: string;
  workspace_id: string;
  scope: "workspace" | "child";
  child_id: string | null;
  include_media: boolean;
  status: "queued" | "running" | "completed" | "failed" | "deleting" | "deleted" | "expired";
  archive_bytes: number | null;
  table_counts: Record<string, number>;
  media_entry_count: number;
  media_bytes: number;
  error_code: string | null;
  version: number;
  requested_at: string;
  started_at: string | null;
  completed_at: string | null;
  expires_at: string;
  deleted_at: string | null;
}

function workspaceExportStatusLabel(status: WorkspaceExport["status"]): string {
  const labels: Record<WorkspaceExport["status"], string> = {
    queued: "準備待ち",
    running: "書き出し中",
    completed: "ダウンロードできます",
    failed: "書き出せませんでした",
    deleting: "削除中",
    deleted: "削除済み",
    expired: "期限切れ・削除済み",
  };
  return labels[status];
}

function workspaceExportErrorLabel(code: string | null): string | null {
  if (code === null) return null;
  const labels: Record<string, string> = {
    EXPORT_MEDIA_MISSING:
      "保存済み写真の一部を確認できませんでした。写真を確認して再実行してください。",
    EXPORT_SOURCE_CHANGED: "作成中に家族のデータが変わりました。もう一度書き出してください。",
    ZIP64_REQUIRED: "データ量が1つの書き出し上限を超えました。子供ごとに分けてください。",
  };
  return labels[code] ?? "書き出し処理を完了できませんでした。もう一度お試しください。";
}

function workspaceExportRequestError(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError && error.code === "RECENT_AUTH_REQUIRED") {
    return "安全のため、もう一度パスキーでログインしてから操作してください。";
  }
  if (error instanceof ApiRequestError && error.code === "EXPORT_LIMIT_REACHED") {
    return "保存中の書き出しが上限です。不要な書き出しを削除してから再実行してください。";
  }
  return error instanceof Error ? error.message : fallback;
}

function formatByteSize(bytes: number): string {
  if (bytes < 1_024) return `${String(bytes)} B`;
  if (bytes < 1_048_576) return `${(bytes / 1_024).toFixed(1)} KB`;
  if (bytes < 1_073_741_824) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  return `${(bytes / 1_073_741_824).toFixed(1)} GB`;
}

interface WorkspaceMembership {
  id: string;
  account_id: string;
  display_name: string;
  role: "owner" | "guardian" | "supporter";
  status: "invited" | "active" | "suspended" | "left";
  version: number;
  accepted_at: string | null;
  scopes: { child_id: string | null; area_id: string | null; capability: string }[];
}

type SupporterCapability =
  | "tasks:read"
  | "tasks:write"
  | "tasks:proxy"
  | "routines:read"
  | "routines:write"
  | "evidence:read"
  | "evidence:verify"
  | "insights:read"
  | "reviews:read";

interface MembershipInvitation {
  id: string;
  role: "guardian" | "supporter";
  status: "active" | "expired" | "revoked" | "accepted";
  scopes: {
    child_id: string | null;
    area_id: string | null;
    capability: SupporterCapability;
  }[];
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
}

interface PublicMembershipInvitation {
  workspace_name: string;
  role: "guardian" | "supporter";
  status: "active" | "expired" | "revoked" | "accepted";
  scopes: {
    child_name: string | null;
    area_name: string | null;
    capability: SupporterCapability;
  }[];
  expires_at: string;
}

const supporterCapabilityCopy: Record<SupporterCapability, string> = {
  "tasks:read": "タスクを見る",
  "tasks:write": "タスクを作成・編集する",
  "tasks:proxy": "子供の代わりにタスクを操作する",
  "routines:read": "ルーティンを見る",
  "routines:write": "ルーティンを作成・編集する",
  "evidence:read": "写真・メモを見る",
  "evidence:verify": "写真・メモを確認する",
  "insights:read": "集計を見る",
  "reviews:read": "ふりかえりを見る",
};

interface PasskeyCredential {
  id: string;
  device_label: string;
  transports: string[];
  backed_up: boolean;
  backup_eligible: boolean;
  last_used_at: string | null;
  created_at: string;
}

type McpScope =
  | "workspace:read"
  | "organization:write"
  | "tasks:read"
  | "tasks:write"
  | "routines:read"
  | "routines:write"
  | "reviews:read"
  | "reviews:write"
  | "notifications:read"
  | "notifications:write"
  | "evidence:metadata:read"
  | "insights:read";

interface AgentGrant {
  id: string;
  client_id: string;
  client_name: string;
  scopes: McpScope[];
  child_scope: "selected" | "all";
  child_ids: string[];
  expires_at: string;
  revoked_at: string | null;
  created_at: string;
  version: number;
}

interface InboxItem {
  id: string;
  workspace_id: string;
  title: string;
  notes: string;
  source_type: "manual" | "mcp" | "api";
  source_label: string | null;
  created_by_client: "web" | "mcp" | "api";
  status: "untriaged" | "planned" | "routine_created" | "cancelled";
  resolved_task_id: string | null;
  version: number;
  created_at: number;
  updated_at: number;
  resolved_at: number | null;
}

interface McpAuthorizationRequest {
  id: string;
  client_id: string;
  client_name: string;
  requested_scopes: McpScope[];
  expires_at: string;
}

const mcpScopeLabels: Record<McpScope, { title: string; description: string }> = {
  "workspace:read": {
    title: "家族とプロジェクトを見る",
    description: "選んだ子供のプロフィールとプロジェクト名を読みます。",
  },
  "organization:write": {
    title: "領域と期間を変更する",
    description: "家族全体の領域・学期・長期休みの区切りを追加、編集、整理します。",
  },
  "tasks:read": {
    title: "タスクを見る",
    description: "選んだ子供の予定日、期限、進み具合を読みます。",
  },
  "tasks:write": {
    title: "タスクを作る・変更する",
    description: "親が依頼したタスクの登録や再計画を行います。完了を推測して記録できません。",
  },
  "routines:read": {
    title: "ルーティンを見る",
    description: "選んだ子供の繰り返し予定を読みます。",
  },
  "routines:write": {
    title: "ルーティンを変更する",
    description: "影響件数を事前に確認した変更だけを適用します。",
  },
  "reviews:read": {
    title: "振り返りを見る",
    description: "選んだ子供の家族レビューと集計を読みます。",
  },
  "reviews:write": {
    title: "振り返りを保存する",
    description: "親が依頼した会話メモと、まだ適用しない次のaction候補を保存します。",
  },
  "notifications:read": {
    title: "通知を見る",
    description: "保護者本人に届いた家族の通知と配信状態を読みます。",
  },
  "notifications:write": {
    title: "通知を整理する",
    description: "保護者本人の通知を既読、未読、あとで確認へ変更します。",
  },
  "evidence:metadata:read": {
    title: "証跡の情報を見る",
    description: "写真そのものではなく、添付の有無や種類だけを読みます。",
  },
  "insights:read": {
    title: "集計を見る",
    description: "許可した子供の期間集計、負荷、振り返り用ハイライトを読みます。",
  },
};

type ChildDialogStep = "closed" | "create" | "created" | "enroll" | "enrollment-ready";

interface EnrollmentResult {
  url: string;
  expiresAt: string;
}

const childDeviceCapabilities: {
  value: SessionActor["capabilities"][number];
  label: string;
}[] = [
  { value: "today:read", label: "きょうのタスクを見る" },
  { value: "tasks:start", label: "タスクをはじめる" },
  { value: "tasks:complete", label: "できたにする" },
  { value: "tasks:help", label: "たすけてを伝える" },
  { value: "tasks:reorder", label: "今日の順番を変える" },
];

interface Project {
  id: string;
  name: string;
  child_copy: string;
  description: string;
  area_id: string | null;
  term_id: string | null;
  status: "planned" | "active" | "paused" | "completed" | "cancelled";
  start_local_date: string | null;
  target_local_date: string | null;
  color: string;
  participants: { child_id: string; display_name: string; theme_color: string }[];
  milestones: Milestone[];
  version: number;
  archived_at: number | null;
}

interface Area {
  id: string;
  workspace_id: string;
  name: string;
  child_copy: string;
  icon: string | null;
  color: string;
  sort_key: string;
  version: number;
  created_at: number;
  updated_at: number;
  archived_at: number | null;
}

interface Term {
  id: string;
  workspace_id: string;
  name: string;
  school_year_label: string | null;
  start_local_date: string;
  end_local_date: string;
  version: number;
  created_at: number;
  updated_at: number;
  archived_at: number | null;
}

interface WorkspaceCalendar {
  id: string;
  workspace_id: string;
  name: string;
  kind: "school_holiday" | "public_holiday" | "travel" | "sick" | "custom";
  periods: { name: string; start_local_date: string; end_local_date: string }[];
  child_ids: string[];
  version: number;
  created_at: number;
  updated_at: number;
  archived_at: number | null;
}

interface WorkspaceCalendarPeriodDraft {
  key: string;
  name: string;
  start_local_date: string;
  end_local_date: string;
}

interface FamilyCycle {
  id: string;
  workspace_id: string;
  start_local_date: string;
  end_local_date: string;
  state: "draft" | "planned" | "active" | "reviewing" | "closed";
  plan_summary: string;
  review_summary: string;
  participants: {
    id: string;
    participant_type: "adult" | "child";
    participant_id: string;
    created_at: number;
  }[];
  commitments: {
    id: string;
    kind: "task" | "routine" | "event" | "custom";
    child_id: string | null;
    task_id: string | null;
    routine_id: string | null;
    title: string;
    child_copy: string;
    status: "proposed" | "agreed" | "changed";
    position: number;
  }[];
  review_notes: {
    id: string;
    category: "good" | "helped" | "difficult" | "next_change";
    child_id: string | null;
    note: string;
  }[];
  review_actions: {
    id: string;
    kind: "create_task" | "reschedule_task" | "update_routine" | "other";
    child_id: string | null;
    summary: string;
    target_type: "task" | "routine" | null;
    target_id: string | null;
    status: "proposed" | "applied" | "dismissed";
    command_id: string | null;
  }[];
  version: number;
  created_at: number;
  updated_at: number;
  closed_at: number | null;
}

interface ChildCurrentFamilyCycle {
  id: string;
  start_local_date: string;
  end_local_date: string;
  state: "planned" | "active" | "reviewing";
  commitments: {
    id: string;
    child_copy: string;
    status: "proposed" | "agreed" | "changed";
  }[];
}

interface CycleCommitmentDraft {
  key: string;
  kind: "task" | "routine" | "event" | "custom";
  child_id: string | null;
  task_id: string | null;
  routine_id: string | null;
  title: string;
  child_copy: string;
  status: "proposed" | "agreed" | "changed";
}

type ReviewActionKind = FamilyCycle["review_actions"][number]["kind"];

interface CycleReviewActionDraft {
  key: string;
  kind: ReviewActionKind;
  summary: string;
  target_id: string | null;
}

interface CycleActionIntent {
  cycle_id: string;
  action_id: string;
  expected_version: number;
  kind: Exclude<ReviewActionKind, "other">;
  link_idempotency_key: string;
  downstream_request_id: string | null;
  target_type: "task" | "routine" | null;
  target_id: string | null;
}

interface CycleActionDismissal {
  cycle_id: string;
  action_id: string;
  expected_version: number;
  summary: string;
  idempotency_key: string;
}

type RoutineSchedule =
  | { kind: "daily"; interval_days: number }
  | { kind: "weekly"; interval_weeks: number; weekdays: number[] }
  | { kind: "monthly_days"; days: (number | "last")[] }
  | { kind: "monthly_nth_weekday"; ordinal: -1 | 1 | 2 | 3 | 4 | 5; weekday: number };

interface RoutineVersion {
  id: string;
  version_number: number;
  schedule: RoutineSchedule;
  timezone: string;
  start_local_date: string;
  end_local_date: string | null;
  availability: {
    period: "anytime" | "morning" | "after_school" | "evening" | "custom";
    from_local_time: string | null;
    until_local_time: string | null;
  };
  due_offset_days: number;
  default_duration_minutes: number | null;
  verification_policy: "none" | "guardian_optional" | "guardian_required" | "evidence_required";
  evidence_policy: { required: "none" | "photo" | "note" | "photo_or_note" };
  notification_policy: { child_reminder: boolean; guardian_digest: boolean };
  exception_dates: string[];
  pause_ranges: { start_local_date: string; end_local_date: string }[];
  calendar_ids: string[];
  effective_at: number;
  created_at: number;
}

interface Routine {
  id: string;
  name: string;
  child_copy: string;
  notes: string;
  area_id: string | null;
  project_id: string | null;
  term_id: string | null;
  status: "active" | "paused";
  color: string;
  current_version: RoutineVersion;
  participants: { child_id: string; display_name: string; theme_color: string }[];
  upcoming: {
    id: string;
    child_id: string;
    planned_local_date: string;
    task_id: string;
  }[];
  version: number;
  updated_at: number;
}

interface RoutineTemplate {
  id: string;
  workspace_id: string;
  name: string;
  child_copy: string;
  notes: string;
  color: string;
  schedule: RoutineSchedule;
  availability: RoutineVersion["availability"];
  due_offset_days: number;
  default_duration_minutes: number | null;
  verification_policy: "none";
  evidence_policy: { required: "none" };
  notification_policy: { child_reminder: false; guardian_digest: false };
  version: number;
  created_at: number;
  updated_at: number;
  archived_at: number | null;
}

type RoutineComparableField =
  | "schedule"
  | "timezone"
  | "start_local_date"
  | "end_local_date"
  | "availability"
  | "due_offset_days"
  | "default_duration_minutes"
  | "verification_policy"
  | "evidence_policy"
  | "notification_policy"
  | "exception_dates"
  | "pause_ranges"
  | "calendar_ids";

interface RoutineVersionComparison {
  routine_id: string;
  from_version: RoutineVersion;
  to_version: RoutineVersion;
  changed_fields: RoutineComparableField[];
  request_id: string;
}

interface RoutineReconciliationRun {
  id: string;
  source: "web" | "mcp" | "cron";
  from_local_date: string;
  until_local_date: string;
  status: "queued" | "running" | "completed" | "failed";
  missing_count: number;
  repaired_count: number;
  error_code: string | null;
  version: number;
  created_at: number;
  started_at: number | null;
  completed_at: number | null;
}

interface ParentNotification {
  id: string;
  event_type:
    | "routine_reconciliation_repaired"
    | "routine_reconciliation_failed"
    | "task_help_requested"
    | "task_verification_waiting"
    | "task_start_reminder"
    | "task_stalled"
    | "timer_finished"
    | "notification_delivery_failed";
  related_child_id: string | null;
  task_id: string | null;
  routine_id: string | null;
  reconciliation_run_id: string | null;
  deep_link: {
    route: "parent_today" | "parent_routines" | "parent_review" | "child_today";
    target_id: string | null;
  };
  payload: {
    missing_count: number | null;
    repaired_count: number | null;
    task_count: number | null;
    child_count: number | null;
    error_code: string | null;
  };
  read_at: number | null;
  snoozed_until: number | null;
  delivered_at: number | null;
  version: number;
  created_at: number;
  updated_at: number;
}

type NotificationPreferenceEventType = ParentNotification["event_type"];

interface NotificationPreference {
  id: string;
  workspace_id: string;
  recipient_type: "membership" | "child";
  child_id: string | null;
  related_child_id: string | null;
  routine_id: string | null;
  channel: "in_app" | "web_push" | "apns";
  event_types: NotificationPreferenceEventType[];
  quiet_hours_behavior: "defer" | "digest" | "allow";
  batching_mode: "immediate" | "digest";
  enabled: boolean;
  active_weekdays: number[];
  active_from_local_time: string | null;
  active_until_local_time: string | null;
  digest_local_time: string | null;
  version: number;
  created_at: number;
  updated_at: number;
}

interface NotificationPreferenceDraft {
  current: NotificationPreference | null;
  recipient_type: "membership" | "child";
  child_id: string | null;
  related_child_id: string | null;
  routine_id: string | null;
  channel: "in_app" | "web_push" | "apns";
  event_types: NotificationPreferenceEventType[];
  quiet_hours_behavior: "defer" | "digest" | "allow";
  batching_mode: "immediate" | "digest";
  enabled: boolean;
  active_weekdays: number[];
  active_from_local_time: string;
  active_until_local_time: string;
  digest_local_time: string;
}

interface NotificationPreferenceInput {
  expected_version: number | null;
  recipient: { type: "membership" } | { type: "child"; child_id: string };
  related_child_id: string | null;
  routine_id: string | null;
  channel: "in_app" | "web_push" | "apns";
  event_types: NotificationPreferenceEventType[];
  quiet_hours_behavior: "defer" | "digest" | "allow";
  batching_mode: "immediate" | "digest";
  enabled: boolean;
  active_weekdays: number[];
  active_from_local_time: string | null;
  active_until_local_time: string | null;
  digest_local_time: string | null;
}

const guardianNotificationEvents: NotificationPreferenceEventType[] = [
  "routine_reconciliation_repaired",
  "routine_reconciliation_failed",
  "task_help_requested",
  "task_verification_waiting",
  "task_stalled",
  "notification_delivery_failed",
];
const childNotificationEvents: NotificationPreferenceEventType[] = [
  "task_start_reminder",
  "timer_finished",
];

function notificationEventLabel(eventType: NotificationPreferenceEventType): string {
  return {
    routine_reconciliation_repaired: "ルーティン点検の自動修復",
    routine_reconciliation_failed: "ルーティン点検の失敗",
    task_help_requested: "子供からの『たすけて』",
    task_verification_waiting: "確認待ち",
    task_start_reminder: "開始時刻のリマインド",
    task_stalled: "予定時刻を過ぎたタスク",
    timer_finished: "タイマー終了",
    notification_delivery_failed: "通知配送の失敗",
  }[eventType];
}

function parseWebMcpNotificationPreference(value: unknown): NotificationPreferenceInput {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error("preference must be an object.");
  }
  const input = value as Record<string, unknown>;
  const allowedKeys = new Set([
    "expected_version",
    "recipient",
    "related_child_id",
    "routine_id",
    "channel",
    "event_types",
    "quiet_hours_behavior",
    "batching_mode",
    "enabled",
    "active_weekdays",
    "active_from_local_time",
    "active_until_local_time",
    "digest_local_time",
  ]);
  if (Object.keys(input).some((key) => !allowedKeys.has(key))) {
    throw new Error("preference contains an unknown field.");
  }
  const uuid = (candidate: unknown): candidate is string =>
    typeof candidate === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(candidate);
  const nullableUuid = (candidate: unknown): candidate is string | null =>
    candidate === null || uuid(candidate);
  const time = (candidate: unknown): candidate is string | null =>
    candidate === null ||
    (typeof candidate === "string" && /^(?:[01]\d|2[0-3]):[0-5]\d$/u.test(candidate));
  const recipient = input.recipient;
  if (typeof recipient !== "object" || recipient === null || Array.isArray(recipient)) {
    throw new Error("recipient must be an object.");
  }
  const recipientRecord = recipient as Record<string, unknown>;
  const parsedRecipient =
    recipientRecord.type === "membership" && Object.keys(recipientRecord).length === 1
      ? ({ type: "membership" } as const)
      : recipientRecord.type === "child" &&
          Object.keys(recipientRecord).length === 2 &&
          uuid(recipientRecord.child_id)
        ? ({ type: "child", child_id: recipientRecord.child_id } as const)
        : null;
  if (parsedRecipient === null) throw new Error("recipient is invalid.");
  const allowedEvents =
    parsedRecipient.type === "child" ? childNotificationEvents : guardianNotificationEvents;
  if (
    !Array.isArray(input.event_types) ||
    input.event_types.length === 0 ||
    new Set(input.event_types).size !== input.event_types.length ||
    input.event_types.some((eventType) => !allowedEvents.includes(eventType as never))
  ) {
    throw new Error("event_types are invalid for this recipient.");
  }
  if (
    !Array.isArray(input.active_weekdays) ||
    input.active_weekdays.length === 0 ||
    new Set(input.active_weekdays).size !== input.active_weekdays.length ||
    input.active_weekdays.some(
      (weekday) =>
        typeof weekday !== "number" || !Number.isInteger(weekday) || weekday < 0 || weekday > 6,
    )
  ) {
    throw new Error("active_weekdays must contain unique integers from 0 through 6.");
  }
  if (
    (input.expected_version !== null &&
      (typeof input.expected_version !== "number" ||
        !Number.isInteger(input.expected_version) ||
        input.expected_version < 1)) ||
    !nullableUuid(input.related_child_id) ||
    !nullableUuid(input.routine_id) ||
    (input.channel !== "in_app" && input.channel !== "web_push") ||
    !["defer", "digest", "allow"].includes(String(input.quiet_hours_behavior)) ||
    !["immediate", "digest"].includes(String(input.batching_mode)) ||
    typeof input.enabled !== "boolean" ||
    !time(input.active_from_local_time) ||
    !time(input.active_until_local_time) ||
    !time(input.digest_local_time) ||
    (input.active_from_local_time === null) !== (input.active_until_local_time === null) ||
    (input.batching_mode === "digest" && input.digest_local_time === null) ||
    (parsedRecipient.type === "child" &&
      (input.related_child_id !== null || input.batching_mode !== "immediate"))
  ) {
    throw new Error("preference schedule or delivery fields are invalid.");
  }
  return {
    expected_version: input.expected_version,
    recipient: parsedRecipient,
    related_child_id: input.related_child_id,
    routine_id: input.routine_id,
    channel: input.channel,
    event_types: input.event_types as NotificationPreferenceEventType[],
    quiet_hours_behavior: input.quiet_hours_behavior as "defer" | "digest" | "allow",
    batching_mode: input.batching_mode as "immediate" | "digest",
    enabled: input.enabled,
    active_weekdays: input.active_weekdays as number[],
    active_from_local_time: input.active_from_local_time,
    active_until_local_time: input.active_until_local_time,
    digest_local_time: input.digest_local_time,
  };
}

function parentNotificationCopy(notification: ParentNotification): {
  title: string;
  description: string;
} {
  switch (notification.event_type) {
    case "routine_reconciliation_repaired":
      return {
        title: "ルーティンの生成漏れを修復しました",
        description: `${String(notification.payload.missing_count ?? 0)}件を検出し、${String(notification.payload.repaired_count ?? 0)}件を計画へ戻しました。`,
      };
    case "routine_reconciliation_failed":
      return {
        title: "ルーティンの点検を完了できませんでした",
        description: "ルーティン画面から再試行できます。既存のタスクは変更していません。",
      };
    case "task_help_requested":
      return {
        title: "子供から『たすけて』が届きました",
        description: "今日の画面で内容を確認できます。",
      };
    case "task_verification_waiting":
      return {
        title: "確認を待っているタスクがあります",
        description: "親子で内容を見て、必要な声かけをしてください。",
      };
    case "task_start_reminder":
      return { title: "開始予定のタスクがあります", description: "今日の計画を確認できます。" };
    case "task_stalled":
      return {
        title:
          notification.payload.task_count === null
            ? "進め方を相談したいタスクがあります"
            : `${String(notification.payload.task_count)}件の予定を一緒に見直しましょう`,
        description:
          notification.payload.child_count !== null && notification.payload.child_count > 1
            ? `${String(notification.payload.child_count)}人分をまとめています。完了を急がせず、困っている点を確認してください。`
            : "完了を急がせず、困っている点を確認してください。",
      };
    case "timer_finished":
      return { title: "タイマーが終わりました", description: "子供の今日の画面を確認できます。" };
    case "notification_delivery_failed":
      return {
        title: "一部の通知を届けられませんでした",
        description: "通知設定と登録端末を確認してください。",
      };
  }
}

interface RoutineCreatePreview {
  id: string;
  affected_count: number;
  expires_at: string;
  occurrences: { child_id: string; planned_local_date: string }[];
}

interface RoutineUpdatePreview {
  id: string;
  routine_id: string;
  next_version_number: number;
  affected_count: number;
  protected_count: number;
  expires_at: string;
  items: {
    child_id: string;
    planned_local_date: string;
    task_id: string | null;
    action: "create" | "update" | "cancel" | "protected";
    protected_reason: "started_or_finished" | "individually_changed" | "already_cancelled" | null;
  }[];
}

type RoutinePreview = RoutineCreatePreview | RoutineUpdatePreview;

interface RoutineCreateDraft {
  name: string;
  child_copy: string;
  notes: string;
  area_id: string | null;
  project_id: string | null;
  term_id: string | null;
  child_ids: string[];
  color: string;
  schedule: RoutineSchedule;
  timezone: string;
  start_local_date: string;
  end_local_date: string | null;
  availability: {
    period: "anytime" | "morning" | "after_school" | "evening" | "custom";
    from_local_time: string | null;
    until_local_time: string | null;
  };
  due_offset_days: number;
  default_duration_minutes: number | null;
  verification_policy: "none" | "guardian_optional" | "guardian_required" | "evidence_required";
  evidence_policy: { required: "none" | "photo" | "note" | "photo_or_note" };
  notification_policy: { child_reminder: boolean; guardian_digest: boolean };
  exception_dates: string[];
  pause_ranges: { start_local_date: string; end_local_date: string }[];
  calendar_ids: string[];
  materialize_from_local_date: string;
  materialize_until_local_date: string;
}

interface Milestone {
  id: string;
  project_id: string;
  title: string;
  description: string;
  due_local_date: string | null;
  status: "planned" | "in_progress" | "completed" | "cancelled";
  completed_at: number | null;
  version: number;
}

interface Device {
  id: string;
  actor_type: "adult" | "child";
  child_id: string | null;
  label: string;
  last_seen_at: string | null;
  revoked_at: string | null;
}

interface PasskeyOptions {
  ceremony_id: string;
  public_key: Record<string, unknown>;
}

type TaskState =
  | "planned"
  | "ready"
  | "in_progress"
  | "blocked"
  | "done"
  | "verified"
  | "needs_changes"
  | "deferred"
  | "skipped"
  | "cancelled";

interface TodayTask {
  task: {
    id: string;
    project_id: string | null;
    title: string;
    child_copy: string;
    planned_local_date: string | null;
    due_local_date: string | null;
    timezone: string | null;
    available_from_local_time: string | null;
    available_until_local_time: string | null;
    today_pinned: boolean;
    estimated_minutes: number | null;
    status: TaskState;
    verification_policy: "none" | "guardian_optional" | "guardian_required" | "evidence_required";
    evidence_policy: { required: "none" | "photo" | "note" | "photo_or_note" };
    version: number;
  };
  project: { id: string; name: string; child_copy: string; color: string } | null;
  assignment: {
    id: string;
    child_id: string;
    state: TaskState;
    version: number;
  };
  help: { id: string; reason: TaskHelpReason; note: string; requested_at: number } | null;
}

interface CarryoverTask extends TodayTask {
  missed_planned_local_date: string;
}

function CarryoverSection({
  items,
  loading = false,
  childFacing = false,
  busyTaskId = null,
  canWrite = false,
  onMoveToday,
  onChooseDate,
  onReview,
}: {
  items: CarryoverTask[];
  loading?: boolean;
  childFacing?: boolean;
  busyTaskId?: string | null;
  canWrite?: boolean;
  onMoveToday?: (item: CarryoverTask) => void;
  onChooseDate?: (item: CarryoverTask) => void;
  onReview?: (item: CarryoverTask) => void;
}) {
  if (!loading && items.length === 0) return null;
  return (
    <section
      className={`carryover-section${childFacing ? " carryover-section-child" : ""}`}
      aria-labelledby={childFacing ? "child-carryover-title" : "parent-carryover-title"}
    >
      <header>
        <div>
          <p className="eyebrow">{childFacing ? "まだ きめていないよ" : "予定を見直す"}</p>
          <h2 id={childFacing ? "child-carryover-title" : "parent-carryover-title"}>
            {childFacing ? "おうちのひとと きめること" : "昨日までに終わらなかったタスク"}
          </h2>
          <p>
            {childFacing
              ? "きょう やるか、べつの ひに するかを おうちのひとと きめよう。"
              : "進行中かどうかに関係なく、過ぎた「やる日」の未完了タスクです。今日か別の日へ決め直します。"}
          </p>
        </div>
        {loading ? null : <span className="count-badge">{items.length}</span>}
      </header>
      {loading ? (
        <p className="status" role="status">
          持ち越しを読み込んでいます…
        </p>
      ) : (
        <ul>
          {items.map((item) => (
            <li key={item.task.id}>
              <div className="carryover-task-copy">
                <strong>{childFacing ? item.task.child_copy : item.task.title}</strong>
                {childFacing || item.task.child_copy === item.task.title ? null : (
                  <span>{item.task.child_copy}</span>
                )}
                <small>
                  {childFacing ? "やる よていだったひ" : "元のやる日"}{" "}
                  {item.missed_planned_local_date}
                  {childFacing || item.task.due_local_date === null
                    ? ""
                    : `・期限 ${item.task.due_local_date}`}
                </small>
              </div>
              {childFacing || !canWrite ? null : (
                <div className="carryover-actions">
                  <button
                    type="button"
                    className="primary"
                    disabled={busyTaskId !== null}
                    onClick={() => onMoveToday?.(item)}
                  >
                    {busyTaskId === item.task.id ? "変更中…" : "今日やる"}
                  </button>
                  <button
                    type="button"
                    disabled={busyTaskId !== null}
                    onClick={() => onChooseDate?.(item)}
                  >
                    別の日
                  </button>
                  {taskPlanCommandsFor(item.assignment.state).length === 0 ? null : (
                    <button
                      type="button"
                      disabled={busyTaskId !== null}
                      onClick={() => onReview?.(item)}
                    >
                      予定を見直す
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

interface TaskEvidence {
  id: string;
  task_id: string;
  assignment_id: string;
  child_id: string;
  type: "photo" | "note" | "audio";
  status: "reserved" | "uploading" | "processing" | "ready" | "failed" | "deleting" | "deleted";
  note_text: string | null;
  mime_type: "image/jpeg" | "image/png" | null;
  byte_size: number | null;
  width: number | null;
  height: number | null;
  processing_error_code: string | null;
  created_by_actor_type: "adult" | "child";
  created_by_actor_id: string;
  ready_at: number | null;
  deleted_at: number | null;
  version: number;
  created_at: number;
  updated_at: number;
}

interface TaskEvidenceUpload {
  id: string;
  evidence_id: string;
  full_uploaded: boolean;
  thumbnail_uploaded: boolean;
  version: number;
}

interface DashboardRate {
  numerator: number;
  denominator: number;
  percent: number | null;
  unavailable_reason: string | null;
}

interface DashboardResponse {
  range: {
    from_local_date: string;
    to_local_date: string;
    timezone: string;
    child_id: string | null;
  };
  task_breakdown: Record<TaskState, number> & {
    total: number;
    verification_waiting: number;
  };
  self_start_rate: DashboardRate;
  no_start_reminder_rate: DashboardRate;
  help: {
    requested_count: number;
    resolved_count: number;
    open_count: number;
    average_resolution_minutes: number | null;
  };
  daily_load: {
    local_date: string;
    planned_count: number;
    completed_count: number;
    estimated_minutes: number;
    missing_estimate_count: number;
  }[];
  routines: {
    routine_id: string;
    name: string;
    child_copy: string;
    planned_count: number;
    completed_count: number;
    deferred_count: number;
    skipped_count: number;
    completion_rate: DashboardRate;
  }[];
  projects: {
    project_id: string;
    name: string;
    status: Project["status"];
    task_total: number;
    task_completed: number;
    milestone_total: number;
    milestone_completed: number;
  }[];
  terms: {
    term_id: string;
    name: string;
    task_total: number;
    task_completed: number;
  }[];
  highlights: {
    evidence_id: string;
    task_id: string;
    assignment_id: string;
    child_id: string;
    evidence_type: "photo" | "note";
    task_title: string;
    child_copy: string;
    note_text: string | null;
    created_at: number;
  }[];
  data_quality: {
    assignments_without_estimate: number;
    adult_proxy_completion_count: number;
    agent_completion_count: number;
    latest_change_sequence: number;
    caveats: string[];
  };
  request_id: string;
}

interface ScheduledTask extends TodayTask {
  schedule_local_date: string;
}

interface TaskBulkReschedulePreview {
  id: string;
  affected_count: number;
  expires_at: string;
  changes: {
    child_id: string;
    task_id: string;
    assignment_id: string;
    expected_version: number;
    planned_local_date: string;
    due_local_date: string | null;
    task_title: string;
    previous_planned_local_date: string | null;
    previous_due_local_date: string | null;
  }[];
}

interface TaskChecklistItem {
  id: string;
  task_id: string;
  text: string;
  child_copy: string;
  is_done: boolean;
  done_actor_type: "adult" | "child" | "system" | "agent" | null;
  done_at: number | null;
  version: number;
}

interface TaskDependency {
  id: string;
  task_id: string;
  depends_on_task: {
    id: string;
    title: string;
    child_copy: string;
    status: TaskState;
    version: number;
  };
  is_satisfied: boolean;
  override: {
    reason: string;
    overridden_by: { id: string; display_name: string };
    overridden_at: number;
  } | null;
  version: number;
}

interface TaskStructure {
  item: TodayTask;
  checklist: TaskChecklistItem[];
  dependencies: TaskDependency[];
  can_start: boolean;
}

interface TaskCommandResponse {
  item: TodayTask;
  command: TaskLifecycleOperation;
}

interface TodayTaskReorderResponse {
  child_id: string;
  local_date: string;
  items: TodayTask[];
  idempotency_replayed: boolean;
  sync_cursor: string;
  request_id: string;
}

type TaskPlanCommand = Extract<TaskLifecycleOperation, "defer" | "skip" | "cancel" | "restore">;

interface TaskPlanDecision {
  item: TodayTask;
  command: TaskPlanCommand;
}

const taskPlanCommandCopy: Record<
  TaskPlanCommand,
  { label: string; description: string; submit: string }
> = {
  defer: {
    label: "あとに回す",
    description: "この予定をいったん止めます。日付やこれまでの記録は残ります。",
    submit: "あとに回す",
  },
  skip: {
    label: "今回はやらない",
    description: "この回は取り組まなかった記録として残します。",
    submit: "今回はやらない",
  },
  cancel: {
    label: "予定を取り消す",
    description: "タスクは削除せず、取り消した記録を残します。あとから戻せます。",
    submit: "予定を取り消す",
  },
  restore: {
    label: "予定に戻す",
    description: "日付や手順を保ったまま、やる予定へ戻します。",
    submit: "予定に戻す",
  },
};

function taskPlanCommandsFor(state: TaskState): TaskPlanCommand[] {
  if (state === "deferred") return ["restore", "skip", "cancel"];
  if (state === "skipped" || state === "cancelled") return ["restore"];
  if (state === "planned" || state === "ready" || state === "needs_changes") {
    return ["defer", "skip", "cancel"];
  }
  if (state === "in_progress" || state === "blocked") return ["defer", "cancel"];
  return [];
}

function moveTodayTaskWithinLane(
  items: TodayTask[],
  taskId: string,
  direction: "earlier" | "later",
): TodayTask[] | null {
  const source = items.find((item) => item.task.id === taskId);
  if (source === undefined) return null;
  const sourceLane = todayLaneForState(source.assignment.state);
  const laneItems = items.filter((item) => todayLaneForState(item.assignment.state) === sourceLane);
  const sourceIndex = laneItems.findIndex((item) => item.task.id === taskId);
  const target = laneItems[direction === "earlier" ? sourceIndex - 1 : sourceIndex + 1];
  if (sourceIndex < 0 || target === undefined) return null;
  return moveTodayTaskToDrop(
    items,
    taskId,
    target.task.id,
    direction === "earlier" ? "before" : "after",
  );
}

function requireTodayOrderItems(input: Record<string, unknown>, current: TodayTask[]): TodayTask[] {
  if (!Array.isArray(input.items) || input.items.length !== current.length || current.length < 2) {
    throw new Error("表示中のToday全件をitemsへ指定してください。");
  }
  const currentByTask = new Map(current.map((item) => [item.task.id, item] as const));
  const ordered = input.items.map((raw) => {
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
      throw new Error("itemsの形式が正しくありません。");
    }
    const itemInput = raw as Record<string, unknown>;
    const taskId = itemInput.task_id;
    const expectedVersion = itemInput.expected_version;
    if (typeof taskId !== "string" || !Number.isInteger(expectedVersion)) {
      throw new Error("各itemにはtask_idとexpected_versionが必要です。");
    }
    const item = currentByTask.get(taskId);
    if (item === undefined || item.assignment.version !== expectedVersion) {
      throw new Error("Todayのタスクが見つからないか、別の端末で変更されています。");
    }
    return item;
  });
  if (new Set(ordered.map((item) => item.task.id)).size !== current.length) {
    throw new Error("Today全件を重複なく指定してください。");
  }
  return ordered;
}

interface TaskChecklistItemMutationResponse {
  item: TaskChecklistItem;
  removed: boolean;
}

interface StagingRuntimeProofResponse {
  proof_id: string;
  d1_persisted: true;
  r2_roundtrip: { value_hash: string; deleted: true };
  queue_job_id: string;
  workflow_job_id: string;
  workflow_instance_id: string;
  request_id: string;
}

interface BackgroundJob {
  id: string;
  source: "queue" | "workflow";
  status: "accepted" | "running" | "completed" | "failed";
  attempts: number;
}

interface RealtimeProofResult {
  connections_seen: number;
  resume_cursor: number;
  pong_cursor: number;
}

type ParentView =
  | "today"
  | "dashboard"
  | "inbox"
  | "plan"
  | "routines"
  | "projects"
  | "organization"
  | "search"
  | "review"
  | "settings";
type TaskCreateContext = "today" | "plan" | "project";
type PlanDisplayMode = "week" | "month";
type TaskPlacement = "scheduled" | "backlog";

const WORKSPACE_KEY = "oyako.workspace-id";
let taskOutboxPromise: ReturnType<typeof openTaskCommandOutbox> | null = null;

function readParentView(): ParentView {
  if (location.hash === "#parent/family") return "settings";
  if (location.hash === "#parent/create") return "plan";
  const match =
    /^#parent\/(today|dashboard|inbox|plan|routines|projects|organization|search|review|settings)$/.exec(
      location.hash,
    );
  return (match?.[1] as ParentView | undefined) ?? "today";
}

function readChildPreviewId(): string | null {
  const match =
    /^#child-preview\/([0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/iu.exec(
      location.hash,
    );
  return match?.[1] ?? null;
}

function childPreviewHref(childId: string): string {
  return `${location.origin}${location.pathname}#child-preview/${encodeURIComponent(childId)}`;
}

function selectedChildStorageKey(workspaceId: string): string {
  return `oyako.selected-child:${workspaceId}`;
}

function formString(fields: FormData, name: string, fallback = ""): string {
  const value = fields.get(name);
  return typeof value === "string" ? value : fallback;
}

function routineScheduleLabel(schedule: RoutineSchedule): string {
  if (schedule.kind === "daily") {
    return schedule.interval_days === 1 ? "毎日" : `${String(schedule.interval_days)}日おき`;
  }
  if (schedule.kind === "weekly") {
    const weekdayLabels = ["月", "火", "水", "木", "金", "土", "日"];
    const days = schedule.weekdays.map((weekday) => weekdayLabels[weekday - 1]).join("・");
    return `${schedule.interval_weeks === 1 ? "毎週" : `${String(schedule.interval_weeks)}週おき`} ${days}`;
  }
  if (schedule.kind === "monthly_days") {
    return `毎月 ${schedule.days.map((day) => (day === "last" ? "末日" : `${String(day)}日`)).join("・")}`;
  }
  const ordinal = schedule.ordinal === -1 ? "最終" : `第${String(schedule.ordinal)}`;
  const weekday = ["月", "火", "水", "木", "金", "土", "日"][schedule.weekday - 1] ?? "?";
  return `毎月 ${ordinal}${weekday}曜`;
}

function routineScheduleFromForm(fields: FormData, kind: RoutineSchedule["kind"]): RoutineSchedule {
  if (kind === "daily") {
    return {
      kind: "daily",
      interval_days: Number(formString(fields, "interval_days", "1")),
    };
  }
  if (kind === "weekly") {
    const weekdays = fields
      .getAll("weekdays")
      .filter((value): value is string => typeof value === "string")
      .map(Number);
    if (weekdays.length === 0) throw new Error("曜日を1つ以上選んでください。");
    return {
      kind: "weekly",
      interval_weeks: Number(formString(fields, "interval_weeks", "1")),
      weekdays,
    };
  }
  if (kind === "monthly_days") {
    const days = formString(fields, "month_days")
      .split(/[,、\s]+/u)
      .map((value) => value.trim())
      .filter((value) => value.length > 0)
      .map((value): number | "last" =>
        value === "末日" || value === "last" ? "last" : Number(value),
      );
    if (
      days.length === 0 ||
      days.some((day) => day !== "last" && (!Number.isInteger(day) || day < 1 || day > 31))
    ) {
      throw new Error("毎月の日は1〜31または「末日」で入力してください。");
    }
    return { kind: "monthly_days", days: [...new Set(days)] };
  }
  return {
    kind: "monthly_nth_weekday",
    ordinal: Number(formString(fields, "month_ordinal", "1")) as -1 | 1 | 2 | 3 | 4 | 5,
    weekday: Number(formString(fields, "month_weekday", "1")),
  };
}

const routineComparisonLabels: Record<RoutineComparableField, string> = {
  schedule: "繰り返し",
  timezone: "タイムゾーン",
  start_local_date: "開始日",
  end_local_date: "終了日",
  availability: "時間帯",
  due_offset_days: "期限",
  default_duration_minutes: "目安時間",
  verification_policy: "完了確認",
  evidence_policy: "記録",
  notification_policy: "通知",
  exception_dates: "お休み日",
  pause_ranges: "一時停止期間",
  calendar_ids: "お休みカレンダー",
};

function routineComparisonValue(field: RoutineComparableField, version: RoutineVersion): string {
  const value = version[field];
  if (field === "schedule") return routineScheduleLabel(version.schedule);
  if (field === "availability") {
    const labels: Record<RoutineVersion["availability"]["period"], string> = {
      anytime: "いつでも",
      morning: "朝",
      after_school: "放課後",
      evening: "夜",
      custom: `${version.availability.from_local_time ?? "?"}〜${version.availability.until_local_time ?? "?"}`,
    };
    return labels[version.availability.period];
  }
  if (field === "default_duration_minutes")
    return version.default_duration_minutes === null
      ? "未設定"
      : `${String(version.default_duration_minutes)}分`;
  if (field === "due_offset_days")
    return version.due_offset_days === 0
      ? "やる日まで"
      : `${String(version.due_offset_days)}日後まで`;
  if (Array.isArray(value)) return value.length === 0 ? "なし" : JSON.stringify(value);
  if (typeof value === "object" && value !== null) return JSON.stringify(value);
  return value === null ? "なし" : String(value);
}

function readCookie(name: string): string | null {
  for (const part of document.cookie.split(";")) {
    const [key, ...valueParts] = part.trim().split("=");
    if (key === name) return decodeURIComponent(valueParts.join("="));
  }
  return null;
}

async function api<T>(path: string, init?: RequestInit, workspaceId?: string): Promise<T> {
  await ensureChildSession(workspaceId);
  const headers = new Headers(init?.headers);
  if (workspaceId !== undefined) headers.set("X-Workspace-Id", workspaceId);
  if (init?.body !== undefined) headers.set("Content-Type", "application/json");
  const csrf = readCookie("oyako_csrf");
  if (csrf !== null && init?.method !== undefined && init.method !== "GET") {
    headers.set("X-CSRF-Token", csrf);
  }
  const response = await fetch(path, { ...init, headers, credentials: "same-origin" });
  const body = (await response.json().catch(() => ({}))) as T & ApiErrorEnvelope;
  if (!response.ok) {
    throw new ApiRequestError(response.status, body);
  }
  return body;
}

async function apiBinary<T>(
  path: string,
  bytes: Uint8Array<ArrayBuffer>,
  contentType: string,
  idempotencyKey: string,
  workspaceId: string,
): Promise<T> {
  await ensureChildSession(workspaceId);
  const headers = new Headers({
    "Content-Type": contentType,
    "Idempotency-Key": idempotencyKey,
    "X-Workspace-Id": workspaceId,
  });
  const csrf = readCookie("oyako_csrf");
  if (csrf !== null) headers.set("X-CSRF-Token", csrf);
  const response = await fetch(path, {
    method: "PUT",
    headers,
    body: bytes,
    credentials: "same-origin",
  });
  const body = (await response.json().catch(() => ({}))) as T & ApiErrorEnvelope;
  if (!response.ok) throw new ApiRequestError(response.status, body);
  return body;
}

function canvasBlob(canvas: HTMLCanvasElement, type: "image/jpeg", quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob === null ? reject(new Error("画像を変換できませんでした。")) : resolve(blob)),
      type,
      quality,
    );
  });
}

async function prepareEvidencePhoto(file: File) {
  if (!new Set(["image/jpeg", "image/png"]).has(file.type)) {
    throw new Error("JPEGまたはPNGの写真を選んでください。");
  }
  if (file.size > 20 * 1024 * 1024) throw new Error("元の写真は20MB以下にしてください。");
  const bitmap = await createImageBitmap(file);
  try {
    const pixelScale = Math.sqrt(12_000_000 / (bitmap.width * bitmap.height));
    const fullScale = Math.min(1, 2400 / Math.max(bitmap.width, bitmap.height), pixelScale);
    const fullWidth = Math.max(1, Math.round(bitmap.width * fullScale));
    const fullHeight = Math.max(1, Math.round(bitmap.height * fullScale));
    const fullCanvas = document.createElement("canvas");
    fullCanvas.width = fullWidth;
    fullCanvas.height = fullHeight;
    const fullContext = fullCanvas.getContext("2d", { alpha: false });
    if (fullContext === null) throw new Error("写真を処理できないブラウザです。");
    fullContext.fillStyle = "#ffffff";
    fullContext.fillRect(0, 0, fullWidth, fullHeight);
    fullContext.drawImage(bitmap, 0, 0, fullWidth, fullHeight);

    const thumbnailScale = Math.min(1, 512 / Math.max(bitmap.width, bitmap.height));
    const thumbnailWidth = Math.max(1, Math.round(bitmap.width * thumbnailScale));
    const thumbnailHeight = Math.max(1, Math.round(bitmap.height * thumbnailScale));
    const thumbnailCanvas = document.createElement("canvas");
    thumbnailCanvas.width = thumbnailWidth;
    thumbnailCanvas.height = thumbnailHeight;
    const thumbnailContext = thumbnailCanvas.getContext("2d", { alpha: false });
    if (thumbnailContext === null) throw new Error("写真を処理できないブラウザです。");
    thumbnailContext.fillStyle = "#ffffff";
    thumbnailContext.fillRect(0, 0, thumbnailWidth, thumbnailHeight);
    thumbnailContext.drawImage(bitmap, 0, 0, thumbnailWidth, thumbnailHeight);

    const [fullBlob, thumbnailBlob] = await Promise.all([
      canvasBlob(fullCanvas, "image/jpeg", 0.88),
      canvasBlob(thumbnailCanvas, "image/jpeg", 0.78),
    ]);
    if (fullBlob.size > 5 * 1024 * 1024 || thumbnailBlob.size > 512 * 1024) {
      throw new Error("変換後の写真が大きすぎます。別の写真を選んでください。");
    }
    return {
      mimeType: "image/jpeg" as const,
      full: new Uint8Array(await fullBlob.arrayBuffer()),
      fullWidth,
      fullHeight,
      thumbnail: new Uint8Array(await thumbnailBlob.arrayBuffer()),
      thumbnailWidth,
      thumbnailHeight,
    };
  } finally {
    bitmap.close();
  }
}

function TaskEvidencePanel({
  session,
  item,
  childFacing,
  readOnly = false,
  allowDelete = true,
  onReadyChange,
}: {
  session: BrowserSession;
  item: TodayTask;
  childFacing: boolean;
  readOnly?: boolean;
  allowDelete?: boolean;
  onReadyChange?: (taskId: string, ready: boolean) => void;
}) {
  const [items, setItems] = useState<TaskEvidence[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<"note" | "photo" | "view" | "delete" | null>(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [deleteTarget, setDeleteTarget] = useState<TaskEvidence | null>(null);
  const [deleteReason, setDeleteReason] = useState("");
  const objectUrls = useRef<string[]>([]);
  const workspaceId = session.workspace.id;
  const required = item.task.evidence_policy.required;

  const reloadEvidence = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api<{ items: TaskEvidence[] }>(
        `/api/v1/workspaces/${workspaceId}/tasks/${item.task.id}/evidence?assignment_id=${encodeURIComponent(item.assignment.id)}`,
        undefined,
        workspaceId,
      );
      setItems(response.items);
      setError(null);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : childFacing
            ? "きろくを よめませんでした。"
            : "記録を読み込めませんでした。",
      );
    } finally {
      setLoading(false);
    }
  }, [childFacing, item.assignment.id, item.task.id, workspaceId]);

  useEffect(() => {
    void Promise.resolve().then(reloadEvidence);
  }, [reloadEvidence]);

  useEffect(
    () => () => {
      for (const url of objectUrls.current) URL.revokeObjectURL(url);
    },
    [],
  );

  const requirementReady =
    required === "none" ||
    items.some(
      (evidence) =>
        evidence.status === "ready" && (required === "photo_or_note" || evidence.type === required),
    );
  useEffect(() => {
    if (!loading) onReadyChange?.(item.task.id, requirementReady);
  }, [item.task.id, loading, onReadyChange, requirementReady]);

  async function saveNote() {
    if (busy !== null || note.trim().length === 0) return;
    setBusy("note");
    setError(null);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/tasks/${item.task.id}/evidence`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({ assignment_id: item.assignment.id, note: note.trim() }),
        },
        workspaceId,
      );
      setNote("");
      await reloadEvidence();
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : childFacing
            ? "ことばを のこせませんでした。"
            : "メモを保存できませんでした。",
      );
    } finally {
      setBusy(null);
    }
  }

  async function uploadPhoto(file: File) {
    if (busy !== null) return;
    setBusy("photo");
    setError(null);
    try {
      const photo = await prepareEvidencePhoto(file);
      const reservation = await api<{
        evidence: TaskEvidence;
        upload: TaskEvidenceUpload;
        upload_targets: { full: string; thumbnail: string };
      }>(
        `/api/v1/workspaces/${workspaceId}/tasks/${item.task.id}/evidence/uploads`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            assignment_id: item.assignment.id,
            mime_type: photo.mimeType,
            byte_size: photo.full.byteLength,
            width: photo.fullWidth,
            height: photo.fullHeight,
            thumbnail_mime_type: photo.mimeType,
            thumbnail_byte_size: photo.thumbnail.byteLength,
            thumbnail_width: photo.thumbnailWidth,
            thumbnail_height: photo.thumbnailHeight,
          }),
        },
        workspaceId,
      );
      await apiBinary<{ upload: TaskEvidenceUpload }>(
        reservation.upload_targets.full,
        photo.full,
        photo.mimeType,
        crypto.randomUUID(),
        workspaceId,
      );
      const thumbnailUpload = await apiBinary<{ upload: TaskEvidenceUpload }>(
        reservation.upload_targets.thumbnail,
        photo.thumbnail,
        photo.mimeType,
        crypto.randomUUID(),
        workspaceId,
      );
      await api(
        `/api/v1/workspaces/${workspaceId}/tasks/${item.task.id}/evidence/uploads/${reservation.upload.id}/finalize`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({ expected_version: thumbnailUpload.upload.version }),
        },
        workspaceId,
      );
      await reloadEvidence();
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : childFacing
            ? "しゃしんを のこせませんでした。"
            : "写真を保存できませんでした。",
      );
    } finally {
      setBusy(null);
    }
  }

  async function showPhoto(evidence: TaskEvidence) {
    if (busy !== null) return;
    const existing = previewUrls[evidence.id];
    if (existing !== undefined) {
      URL.revokeObjectURL(existing);
      objectUrls.current = objectUrls.current.filter((url) => url !== existing);
      setPreviewUrls((current) =>
        Object.fromEntries(Object.entries(current).filter(([id]) => id !== evidence.id)),
      );
      return;
    }
    setBusy("view");
    setError(null);
    try {
      const access = await api<{ token: string; endpoint: string }>(
        `/api/v1/workspaces/${workspaceId}/evidence/${evidence.id}/access`,
        { method: "POST", body: JSON.stringify({ variant: "thumbnail" }) },
        workspaceId,
      );
      const response = await fetch(access.endpoint, {
        headers: { Authorization: `Evidence ${access.token}` },
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error("写真を表示できませんでした。");
      const url = URL.createObjectURL(await response.blob());
      objectUrls.current.push(url);
      setPreviewUrls((current) => ({ ...current, [evidence.id]: url }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "写真を表示できませんでした。");
    } finally {
      setBusy(null);
    }
  }

  async function deleteEvidence() {
    if (deleteTarget === null || deleteReason.trim().length === 0 || busy !== null) return;
    setBusy("delete");
    setError(null);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/evidence/${deleteTarget.id}`,
        {
          method: "DELETE",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            expected_version: deleteTarget.version,
            reason: deleteReason.trim(),
          }),
        },
        workspaceId,
      );
      setDeleteTarget(null);
      setDeleteReason("");
      await reloadEvidence();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "記録を削除できませんでした。");
    } finally {
      setBusy(null);
    }
  }

  const readyItems = items.filter((evidence) => evidence.status === "ready");
  const requiredCopy =
    required === "photo"
      ? childFacing
        ? "できたら しゃしんを のこしてね"
        : "完了には写真が必要です"
      : required === "note"
        ? childFacing
          ? "できたら ことばを のこしてね"
          : "完了にはメモが必要です"
        : required === "photo_or_note"
          ? childFacing
            ? "しゃしんか ことばを のこしてね"
            : "完了には写真またはメモが必要です"
          : childFacing
            ? "できた きろくを のこせるよ"
            : "写真やメモを任意で残せます";

  return (
    <section className={`task-evidence${requirementReady ? " is-ready" : " is-required"}`}>
      <button
        type="button"
        className="task-evidence-toggle"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span>
          <strong>{childFacing ? "できた きろく" : "完了の記録"}</strong>
          <small>{requiredCopy}</small>
        </span>
        <span className="evidence-count">
          {loading ? "…" : `${String(readyItems.length)}${childFacing ? "こ" : "件"}`}
        </span>
      </button>
      {!open ? null : (
        <div className="task-evidence-body">
          {error === null ? null : (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          {loading ? (
            <p className="status" role="status">
              {childFacing ? "きろくを よんでいるよ…" : "記録を読み込んでいます…"}
            </p>
          ) : items.length === 0 ? (
            <p className="evidence-empty">
              {childFacing ? "まだ きろくは ないよ。" : "記録はまだありません。"}
            </p>
          ) : (
            <ul className="evidence-list">
              {items.map((evidence) => (
                <li key={evidence.id} className={`evidence-${evidence.status}`}>
                  <div>
                    <strong>
                      {evidence.type === "photo"
                        ? childFacing
                          ? "しゃしん"
                          : "写真"
                        : childFacing
                          ? "ことば"
                          : "メモ"}
                    </strong>
                    <small>
                      {evidence.status === "ready"
                        ? new Intl.DateTimeFormat("ja-JP", {
                            month: "numeric",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          }).format(new Date(evidence.ready_at ?? evidence.created_at))
                        : evidence.status === "failed"
                          ? childFacing
                            ? "うまく のこせませんでした"
                            : "処理に失敗しました"
                          : childFacing
                            ? "じゅんびちゅう"
                            : "処理中"}
                    </small>
                  </div>
                  {evidence.note_text === null ? null : <p>{evidence.note_text}</p>}
                  {evidence.type !== "photo" || evidence.status !== "ready" ? null : (
                    <>
                      <button
                        type="button"
                        disabled={busy !== null}
                        onClick={() => void showPhoto(evidence)}
                      >
                        {previewUrls[evidence.id] === undefined
                          ? childFacing
                            ? "しゃしんを みる"
                            : "写真を見る"
                          : childFacing
                            ? "しゃしんを とじる"
                            : "写真を閉じる"}
                      </button>
                      {previewUrls[evidence.id] === undefined ? null : (
                        <img
                          src={previewUrls[evidence.id]}
                          alt={childFacing ? "のこした しゃしん" : "保存された証跡写真"}
                        />
                      )}
                    </>
                  )}
                  {!readOnly && allowDelete && session.actor.type === "adult" ? (
                    <button
                      type="button"
                      className="danger-link"
                      disabled={busy !== null}
                      onClick={() => {
                        setDeleteTarget(evidence);
                        setDeleteReason("");
                      }}
                    >
                      削除
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {deleteTarget === null ? null : (
            <div className="evidence-delete-confirm" role="group" aria-label="記録の削除確認">
              <strong>この記録を完全に削除しますか？</strong>
              <label>
                理由
                <input
                  value={deleteReason}
                  maxLength={500}
                  placeholder="不要になったため"
                  onChange={(event) => setDeleteReason(event.currentTarget.value)}
                />
              </label>
              <div>
                <button type="button" onClick={() => setDeleteTarget(null)}>
                  やめる
                </button>
                <button
                  type="button"
                  className="danger"
                  disabled={busy !== null || deleteReason.trim().length === 0}
                  onClick={() => void deleteEvidence()}
                >
                  完全に削除
                </button>
              </div>
            </div>
          )}
          {readOnly ? null : (
            <div className="evidence-compose">
              <label>
                <span>{childFacing ? "ことばで のこす" : "メモを残す"}</span>
                <textarea
                  value={note}
                  maxLength={2000}
                  rows={2}
                  placeholder={childFacing ? "できたことを かいてね" : "取り組みの様子や会話を記録"}
                  onChange={(event) => setNote(event.currentTarget.value)}
                />
              </label>
              <div>
                <button
                  type="button"
                  disabled={busy !== null || note.trim().length === 0}
                  onClick={() => void saveNote()}
                >
                  {busy === "note"
                    ? childFacing
                      ? "のこしているよ…"
                      : "保存中…"
                    : childFacing
                      ? "ことばを のこす"
                      : "メモを保存"}
                </button>
                <label className={`button-file${busy !== null ? " is-disabled" : ""}`}>
                  <input
                    type="file"
                    accept="image/jpeg,image/png"
                    disabled={busy !== null}
                    onChange={(event) => {
                      const file = event.currentTarget.files?.[0];
                      event.currentTarget.value = "";
                      if (file !== undefined) void uploadPhoto(file);
                    }}
                  />
                  {busy === "photo"
                    ? childFacing
                      ? "しゃしんを のこしているよ…"
                      : "写真を処理中…"
                    : childFacing
                      ? "しゃしんを えらぶ"
                      : "写真を選ぶ"}
                </label>
              </div>
              <small>
                {childFacing
                  ? "しゃしんの ばしょの じょうほうは のこらないよ"
                  : "写真は端末内で縮小し、位置情報などのメタデータを除いて非公開保存します。"}
              </small>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function base64UrlBytes(value: string): Uint8Array<ArrayBuffer> {
  const normalized = value.replaceAll("-", "+").replaceAll("_", "/");
  const binary = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "="));
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function WebPushControl({
  session,
  childFacing = false,
}: {
  session: BrowserSession;
  childFacing?: boolean;
}) {
  const [supported] = useState(
    () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window,
  );
  const [available, setAvailable] = useState<boolean | null>(null);
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [serverSubscription, setServerSubscription] = useState<WebPushSubscriptionState | null>(
    null,
  );
  const [browserSubscription, setBrowserSubscription] = useState<PushSubscription | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const workspaceId = session.workspace.id;

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    void Promise.all([
      api<{ available: boolean; public_key: string | null }>(
        "/api/v1/notifications/web-push/config",
      ),
      api<WebPushSubscriptionResponse>(
        `/api/v1/workspaces/${workspaceId}/web-push-subscription`,
        undefined,
        workspaceId,
      ),
      navigator.serviceWorker.ready.then((registration) =>
        registration.pushManager.getSubscription(),
      ),
    ])
      .then(([config, persisted, browser]) => {
        if (cancelled) return;
        setAvailable(config.available);
        setPublicKey(config.public_key);
        setServerSubscription(persisted.subscription);
        setBrowserSubscription(browser);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : "通知の状態を確認できませんでした。");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [supported, workspaceId]);

  async function enablePush() {
    if (!supported || !available || publicKey === null || busy) return;
    setBusy(true);
    setError(null);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new Error(
          childFacing
            ? "つうちが ゆるされていません。おうちのひとと せっていを みてください。"
            : "ブラウザで通知が許可されませんでした。サイト設定を確認してください。",
        );
      }
      const registration = await navigator.serviceWorker.ready;
      const subscription =
        (await registration.pushManager.getSubscription()) ??
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: base64UrlBytes(publicKey),
        }));
      const json = subscription.toJSON();
      if (
        typeof json.endpoint !== "string" ||
        typeof json.keys?.p256dh !== "string" ||
        typeof json.keys.auth !== "string"
      ) {
        throw new Error("ブラウザから有効な通知登録情報を取得できませんでした。");
      }
      const response = await api<WebPushSubscriptionResponse>(
        `/api/v1/workspaces/${workspaceId}/web-push-subscription`,
        {
          method: "PUT",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            expected_version: serverSubscription?.version ?? null,
            endpoint: json.endpoint,
            expiration_time: subscription.expirationTime,
            keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
          }),
        },
        workspaceId,
      );
      setBrowserSubscription(subscription);
      setServerSubscription(response.subscription);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "通知を有効にできませんでした。");
    } finally {
      setBusy(false);
    }
  }

  async function disablePush() {
    if (serverSubscription === null || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api<WebPushSubscriptionResponse>(
        `/api/v1/workspaces/${workspaceId}/web-push-subscription`,
        {
          method: "DELETE",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({ expected_version: serverSubscription.version }),
        },
        workspaceId,
      );
      await browserSubscription?.unsubscribe();
      setServerSubscription(null);
      setBrowserSubscription(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "通知を停止できませんでした。");
    } finally {
      setBusy(false);
    }
  }

  if (!supported) {
    return (
      <div className="web-push-control web-push-unavailable">
        <strong>{childFacing ? "このたんまつでは つうちを つかえません" : "通知非対応"}</strong>
        <small>アプリ内の通知は引き続き表示されます。</small>
      </div>
    );
  }
  if (available === false) {
    return (
      <div className="web-push-control web-push-unavailable">
        <strong>{childFacing ? "つうちは じゅんびちゅうです" : "端末通知は準備中です"}</strong>
        <small>アプリ内の通知は引き続き表示されます。</small>
      </div>
    );
  }
  const enabled = serverSubscription !== null && browserSubscription !== null;
  return (
    <div className="web-push-control">
      <div>
        <strong>{childFacing ? "このたんまつの つうち" : "この端末の通知"}</strong>
        <small>
          {enabled
            ? childFacing
              ? "はじめる じかんを おしらせします"
              : "ブラウザを閉じていても安全な短い通知を受け取れます。"
            : childFacing
              ? "おうちのひとと いっしょに おんにできます"
              : "許可はこの端末だけに保存され、いつでも停止できます。"}
        </small>
      </div>
      <button
        type="button"
        disabled={busy || available === null}
        onClick={() => void (enabled ? disablePush() : enablePush())}
      >
        {busy
          ? childFacing
            ? "かくにんちゅう…"
            : "確認中…"
          : enabled
            ? childFacing
              ? "つうちを とめる"
              : "この端末の通知を停止"
            : childFacing
              ? "つうちを うけとる"
              : "この端末で通知を受け取る"}
      </button>
      {error === null ? null : (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function actorBinding(session: BrowserSession): TaskCommandActorBinding {
  return {
    actorId: session.actor.id,
    actorType: session.actor.type,
    sessionId: session.actor.session_id,
    deviceId: session.actor.device_id,
  };
}

async function taskOutbox() {
  taskOutboxPromise ??= openTaskCommandOutbox();
  return taskOutboxPromise;
}

function wait(delayMs: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, delayMs));
}

async function proveRealtime(workspaceId: string, cursor: number): Promise<RealtimeProofResult> {
  return new Promise((resolve, reject) => {
    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(
      `${protocol}//${location.host}/api/v1/workspaces/${workspaceId}/realtime?cursor=${String(cursor)}`,
    );
    const timeout = window.setTimeout(() => {
      socket.close();
      reject(new Error("Realtimeの応答が時間内に届きませんでした。"));
    }, 10_000);
    let connected: { connections_seen: number; resume_cursor: number } | null = null;
    socket.addEventListener("message", (event) => {
      const message = JSON.parse(String(event.data)) as Record<string, unknown>;
      if (message.type === "connected") {
        connected = {
          connections_seen: Number(message.connections_seen),
          resume_cursor: Number(message.resume_cursor),
        };
        socket.send(JSON.stringify({ type: "ping", cursor }));
        return;
      }
      if (message.type === "pong" && connected !== null) {
        window.clearTimeout(timeout);
        socket.close();
        resolve({ ...connected, pong_cursor: Number(message.cursor) });
      }
    });
    socket.addEventListener("error", () => {
      window.clearTimeout(timeout);
      reject(new Error("Realtime接続に失敗しました。"));
    });
  });
}

type SentTaskMutation =
  | { kind: "task"; response: TaskCommandResponse }
  | { kind: "checklist"; response: TaskChecklistItemMutationResponse }
  | { kind: "reorder"; response: TodayTaskReorderResponse };

async function sendClaimedMutation(
  mutation: TaskCommandMutation,
): Promise<SentTaskMutation | null> {
  const outbox = await taskOutbox();
  if (mutation.claimId === null) throw new Error("Outbox claim is missing.");
  try {
    const isReorder = mutation.operation === "reorder";
    const isChecklist =
      mutation.operation === "checklist_complete" || mutation.operation === "checklist_reopen";
    const response = isReorder
      ? await api<TodayTaskReorderResponse>(
          `/api/v1/workspaces/${mutation.workspaceId}/today/reorder`,
          {
            method: "POST",
            headers: { "Idempotency-Key": mutation.idempotencyKey },
            body: JSON.stringify({
              child_id: mutation.entityId,
              local_date: mutation.payload.local_date,
              items: mutation.payload.ordered_items,
            }),
          },
          mutation.workspaceId,
        )
      : isChecklist
        ? await api<TaskChecklistItemMutationResponse>(
            `/api/v1/workspaces/${mutation.workspaceId}/tasks/${mutation.entityId}/checklist/${mutation.payload.checklist_item_id ?? "missing"}/commands/${mutation.operation === "checklist_complete" ? "complete" : "reopen"}`,
            {
              method: "POST",
              headers: { "Idempotency-Key": mutation.idempotencyKey },
              body: JSON.stringify({
                assignment_id: mutation.payload.assignment_id,
                expected_version: mutation.payload.expected_version,
              }),
            },
            mutation.workspaceId,
          )
        : await api<TaskCommandResponse>(
            `/api/v1/workspaces/${mutation.workspaceId}/tasks/${mutation.entityId}/commands/${mutation.operation.replaceAll("_", "-")}`,
            {
              method: "POST",
              headers: { "Idempotency-Key": mutation.idempotencyKey },
              body: JSON.stringify(mutation.payload),
            },
            mutation.workspaceId,
          );
    await outbox.acknowledgeSuccess(mutation.mutationId, mutation.claimId);
    return isReorder
      ? { kind: "reorder", response: response as TodayTaskReorderResponse }
      : isChecklist
        ? { kind: "checklist", response: response as TaskChecklistItemMutationResponse }
        : { kind: "task", response: response as TaskCommandResponse };
  } catch (caught) {
    if (caught instanceof ApiRequestError && caught.status === 409) {
      const currentVersion = Number(caught.details.current_version);
      await outbox.markConflict(mutation.mutationId, mutation.claimId, {
        errorCode: caught.code,
        serverVersion: Number.isInteger(currentVersion) ? currentVersion : null,
      });
      throw caught;
    }
    if (caught instanceof ApiRequestError && caught.status >= 400 && caught.status < 500) {
      await outbox.markFailed(mutation.mutationId, mutation.claimId, {
        errorCode: caught.code,
        errorMessage: caught.message,
      });
      throw caught;
    }
    await outbox.requeueClaim(mutation.mutationId, mutation.claimId, {
      retryAt: Date.now() + Math.min(60_000, 2 ** mutation.retryAttemptCount * 1_000),
      errorCode: caught instanceof ApiRequestError ? caught.code : "NETWORK_UNAVAILABLE",
      errorMessage: caught instanceof Error ? caught.message : "ネットワークに接続できません。",
    });
    return null;
  }
}

async function flushTaskCommands(
  session: BrowserSession,
  targetMutationId?: string,
): Promise<SentTaskMutation | null> {
  const outbox = await taskOutbox();
  let targetResponse: SentTaskMutation | null = null;
  for (let index = 0; index < 20; index += 1) {
    const claimed = await outbox.claimNext(Date.now(), {
      workspaceId: session.workspace.id,
      actorBinding: actorBinding(session),
    });
    if (claimed === null) break;
    const response = await sendClaimedMutation(claimed);
    if (claimed.mutationId === targetMutationId) targetResponse = response;
    if (response === null) break;
  }
  return targetResponse;
}

async function enqueueTaskCommand(
  session: BrowserSession,
  item: TodayTask,
  operation: TaskLifecycleOperation,
  options: { reason?: TaskHelpReason; note?: string; mutationId?: string } = {},
): Promise<TaskCommandResponse | null> {
  const outbox = await taskOutbox();
  const mutation = await outbox.enqueue({
    actorBinding: actorBinding(session),
    workspaceId: session.workspace.id,
    entityId: item.task.id,
    operation,
    ...(options.mutationId === undefined ? {} : { mutationId: options.mutationId }),
    payload: {
      assignment_id: item.assignment.id,
      expected_version: item.assignment.version,
      ...(options.reason === undefined ? {} : { reason: options.reason }),
      ...(options.note === undefined ? {} : { note: options.note }),
    },
  });
  const result = await flushTaskCommands(session, mutation.mutationId);
  if (result === null) return null;
  if (result.kind !== "task") throw new Error("Unexpected checklist response for a task command.");
  return result.response;
}

async function enqueueChecklistCommand(
  session: BrowserSession,
  item: TodayTask,
  checklistItem: TaskChecklistItem,
  operation: TaskChecklistOperation,
  mutationId?: string,
): Promise<TaskChecklistItemMutationResponse | null> {
  const outbox = await taskOutbox();
  const mutation = await outbox.enqueue({
    actorBinding: actorBinding(session),
    workspaceId: session.workspace.id,
    entityId: item.task.id,
    operation,
    ...(mutationId === undefined ? {} : { mutationId }),
    payload: {
      assignment_id: item.assignment.id,
      expected_version: checklistItem.version,
      checklist_item_id: checklistItem.id,
    },
  });
  const result = await flushTaskCommands(session, mutation.mutationId);
  if (result === null) return null;
  if (result.kind !== "checklist")
    throw new Error("Unexpected task response for a checklist command.");
  return result.response;
}

async function enqueueTodayTaskReorder(
  session: BrowserSession,
  childId: string,
  localDate: string,
  items: TodayTask[],
  mutationId?: string,
): Promise<TodayTaskReorderResponse | null> {
  if (items.length < 2) throw new Error("並べ替えるタスクが2件以上必要です。");
  const outbox = await taskOutbox();
  const mutation = await outbox.enqueue({
    actorBinding: actorBinding(session),
    workspaceId: session.workspace.id,
    entityId: childId,
    operation: "reorder",
    ...(mutationId === undefined ? {} : { mutationId }),
    payload: {
      assignment_id: childId,
      expected_version: Math.max(...items.map((item) => item.assignment.version)),
      local_date: localDate,
      ordered_items: items.map((item) => ({
        task_id: item.task.id,
        assignment_id: item.assignment.id,
        expected_version: item.assignment.version,
      })),
    },
  });
  const result = await flushTaskCommands(session, mutation.mutationId);
  if (result === null) return null;
  if (result.kind !== "reorder") throw new Error("Unexpected response for a Today reorder.");
  return result.response;
}

function MembershipInvitationPanel({
  token,
  onSession,
}: {
  token: string;
  onSession: (session: BrowserSession) => void;
}) {
  const [invitation, setInvitation] = useState<PublicMembershipInvitation | null>(null);
  const [mode, setMode] = useState<"existing" | "new">("existing");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api<PublicMembershipInvitation>("/api/v1/auth/membership-invitations/inspect", {
      method: "POST",
      body: JSON.stringify({ token }),
    })
      .then((result) => {
        if (!cancelled) setInvitation(result);
      })
      .catch((caught: unknown) => {
        if (!cancelled)
          setError(caught instanceof Error ? caught.message : "招待を確認できませんでした。");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function joinWithExistingPasskey(event: FormSubmitEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const fields = new FormData(event.currentTarget);
    const deviceLabel = formString(fields, "device_label", "保護者のブラウザ");
    try {
      const options = await api<PasskeyOptions>("/api/v1/auth/passkeys/login/options", {
        method: "POST",
        body: JSON.stringify({ device_label: deviceLabel, invitation_token: token }),
      });
      const credential = await startAuthentication({
        optionsJSON: options.public_key as unknown as Parameters<
          typeof startAuthentication
        >[0]["optionsJSON"],
      });
      const session = await api<BrowserSession>("/api/v1/auth/passkeys/login/verify", {
        method: "POST",
        body: JSON.stringify({
          ceremony_id: options.ceremony_id,
          credential,
          device_label: deviceLabel,
        }),
      });
      onSession(session);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "招待に参加できませんでした。");
      setBusy(false);
    }
  }

  async function joinWithNewPasskey(event: FormSubmitEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const fields = new FormData(event.currentTarget);
    try {
      const options = await api<PasskeyOptions>(
        "/api/v1/auth/membership-invitations/register/options",
        {
          method: "POST",
          body: JSON.stringify({
            token,
            display_name: fields.get("display_name"),
            device_label: fields.get("device_label"),
          }),
        },
      );
      const credential = await startRegistration({
        optionsJSON: options.public_key as unknown as Parameters<
          typeof startRegistration
        >[0]["optionsJSON"],
      });
      const session = await api<BrowserSession>(
        "/api/v1/auth/membership-invitations/register/verify",
        {
          method: "POST",
          body: JSON.stringify({ ceremony_id: options.ceremony_id, credential }),
        },
      );
      onSession(session);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "パスキーを作成できませんでした。");
      setBusy(false);
    }
  }

  const active = invitation?.status === "active";
  return (
    <main className="auth-shell">
      <section className="auth-card invitation-card" aria-labelledby="invitation-title">
        <p className="eyebrow">家族からの招待</p>
        <h1 id="invitation-title">
          {invitation === null ? "招待を確認しています" : `${invitation.workspace_name}に参加`}
        </h1>
        {loading ? (
          <p className="status" role="status">
            一度限りのリンクを確認しています…
          </p>
        ) : null}
        {invitation === null ? null : (
          <>
            <p className="lede">
              {invitation.role === "guardian"
                ? "保護者として、家族の計画と確認に参加します。"
                : "サポーターとして、下に表示された子供と操作だけを担当します。"}
            </p>
            {invitation.scopes.length === 0 ? null : (
              <ul className="invitation-scope-list" aria-label="許可される範囲">
                {invitation.scopes.map((scope, index) => (
                  <li
                    key={`${scope.child_name ?? ""}:${scope.area_name ?? ""}:${scope.capability}:${String(index)}`}
                  >
                    <strong>{scope.child_name ?? scope.area_name ?? "対象"}</strong>
                    <span>{supporterCapabilityCopy[scope.capability]}</span>
                    {scope.child_name !== null && scope.area_name !== null ? (
                      <small>{scope.area_name}</small>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            <p className="security-note">
              有効期限 {new Date(invitation.expires_at).toLocaleString("ja-JP")}
              。参加すると、このリンクは使えなくなります。
            </p>
          </>
        )}
        {!active ? null : (
          <>
            <div className="segmented" aria-label="参加方法">
              <button aria-pressed={mode === "existing"} onClick={() => setMode("existing")}>
                既存のパスキー
              </button>
              <button aria-pressed={mode === "new"} onClick={() => setMode("new")}>
                初めて使う
              </button>
            </div>
            {mode === "existing" ? (
              <form onSubmit={(event) => void joinWithExistingPasskey(event)}>
                <label>
                  この端末の名前
                  <input
                    name="device_label"
                    required
                    maxLength={120}
                    defaultValue="保護者のブラウザ"
                  />
                </label>
                <button className="primary" disabled={busy}>
                  {busy ? "確認しています…" : "パスキーで参加"}
                </button>
              </form>
            ) : (
              <form onSubmit={(event) => void joinWithNewPasskey(event)}>
                <label>
                  あなたの表示名
                  <input name="display_name" autoComplete="name" required maxLength={120} />
                </label>
                <label>
                  この端末の名前
                  <input
                    name="device_label"
                    required
                    maxLength={120}
                    defaultValue="保護者のブラウザ"
                  />
                </label>
                <button className="primary" disabled={busy}>
                  {busy ? "パスキーを確認しています…" : "パスキーを作って参加"}
                </button>
              </form>
            )}
          </>
        )}
        {invitation !== null && !active ? (
          <p className="error" role="alert">
            {invitation.status === "accepted"
              ? "この招待は既に使われています。"
              : invitation.status === "revoked"
                ? "この招待は取り消されました。"
                : "この招待は期限切れです。"}
          </p>
        ) : null}
        {error === null ? null : (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <a className="button-link" href="/">
          通常のログインへ
        </a>
      </section>
    </main>
  );
}

function AuthPanel({
  onSession,
}: {
  onSession: (session: BrowserSession, recovered?: boolean) => void;
}) {
  const [mode, setMode] = useState<"register" | "login" | "recovery">(
    new URLSearchParams(location.search).get("auth_mode") === "recovery"
      ? "recovery"
      : new URLSearchParams(location.search).get("auth_mode") === "login"
        ? "login"
        : "register",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function register(event: FormSubmitEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const fields = new FormData(event.currentTarget);
    try {
      const options = await api<PasskeyOptions>("/api/v1/auth/passkeys/register/options", {
        method: "POST",
        body: JSON.stringify({
          display_name: fields.get("display_name"),
          workspace_name: fields.get("workspace_name"),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          device_label: fields.get("device_label"),
        }),
      });
      const credential = await startRegistration({
        optionsJSON: options.public_key as unknown as Parameters<
          typeof startRegistration
        >[0]["optionsJSON"],
      });
      const session = await api<BrowserSession>("/api/v1/auth/passkeys/register/verify", {
        method: "POST",
        body: JSON.stringify({ ceremony_id: options.ceremony_id, credential }),
      });
      onSession(session);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "パスキーを作成できませんでした。");
    } finally {
      setBusy(false);
    }
  }

  async function login(event: FormSubmitEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const fields = new FormData(event.currentTarget);
    const deviceLabel = formString(fields, "device_label", "ブラウザ");
    try {
      const options = await api<PasskeyOptions>("/api/v1/auth/passkeys/login/options", {
        method: "POST",
        body: JSON.stringify({ device_label: deviceLabel }),
      });
      const credential = await startAuthentication({
        optionsJSON: options.public_key as unknown as Parameters<
          typeof startAuthentication
        >[0]["optionsJSON"],
      });
      const session = await api<BrowserSession>("/api/v1/auth/passkeys/login/verify", {
        method: "POST",
        body: JSON.stringify({
          ceremony_id: options.ceremony_id,
          credential,
          device_label: deviceLabel,
        }),
      });
      onSession(session);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "ログインできませんでした。");
    } finally {
      setBusy(false);
    }
  }

  async function recover(event: FormSubmitEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const fields = new FormData(event.currentTarget);
    try {
      const session = await api<BrowserSession>("/api/v1/auth/recovery", {
        method: "POST",
        body: JSON.stringify({
          recovery_code: fields.get("recovery_code"),
          device_label: fields.get("device_label"),
        }),
      });
      onSession(session, true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "回復コードを使えませんでした。");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-shell">
      <section className="auth-card" aria-labelledby="auth-title">
        <p className="eyebrow">Oyako Flow</p>
        <h1 id="auth-title">親子の「きょう」を、いっしょにつくる。</h1>
        <p className="lede">
          親はパスキーで安全に入り、子供は一度だけの登録リンクから自分の画面を使います。
        </p>
        <div className="segmented" aria-label="認証方法">
          <button aria-pressed={mode === "register"} onClick={() => setMode("register")}>
            はじめる
          </button>
          <button aria-pressed={mode === "login"} onClick={() => setMode("login")}>
            パスキーで入る
          </button>
          <button aria-pressed={mode === "recovery"} onClick={() => setMode("recovery")}>
            回復コード
          </button>
        </div>
        {mode === "register" ? (
          <form onSubmit={(event) => void register(event)}>
            <label>
              保護者の表示名
              <input name="display_name" autoComplete="name" required maxLength={120} />
            </label>
            <label>
              家族の名前
              <input name="workspace_name" required maxLength={120} placeholder="おぎはら家" />
            </label>
            <label>
              この端末の名前
              <input name="device_label" required maxLength={120} defaultValue="保護者のブラウザ" />
            </label>
            <button className="primary" disabled={busy}>
              {busy ? "パスキーを確認しています…" : "パスキーで家族をつくる"}
            </button>
          </form>
        ) : null}
        {mode === "login" ? (
          <form onSubmit={(event) => void login(event)}>
            <label>
              この端末の名前
              <input name="device_label" required maxLength={120} defaultValue="保護者のブラウザ" />
            </label>
            <button className="primary" disabled={busy}>
              {busy ? "確認しています…" : "パスキーで入る"}
            </button>
          </form>
        ) : null}
        {mode === "recovery" ? (
          <form onSubmit={(event) => void recover(event)}>
            <label>
              回復コード
              <input name="recovery_code" required autoComplete="one-time-code" />
            </label>
            <label>
              この端末の名前
              <input name="device_label" required maxLength={120} defaultValue="回復したブラウザ" />
            </label>
            <button className="primary" disabled={busy}>
              {busy ? "確認しています…" : "回復コードで入る"}
            </button>
          </form>
        ) : null}
        {error === null ? null : (
          <p className="error" role="alert">
            {error}
          </p>
        )}
      </section>
    </main>
  );
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  return (
    <main className="auth-shell">
      <section className="auth-card">
        <p className="eyebrow">一度だけ表示</p>
        <h1>回復コードを保存してください</h1>
        <p className="lede">パスキーを失ったときに使います。各コードは一度だけ有効です。</p>
        <ol className="recovery-codes">
          {codes.map((code) => (
            <li key={code}>
              <code>{code}</code>
            </li>
          ))}
        </ol>
        <button className="primary" onClick={onDone}>
          安全な場所に保存しました
        </button>
      </section>
    </main>
  );
}

function EnrollmentPanel({
  token,
  onSession,
}: {
  token: string;
  onSession: (session: BrowserSession) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function redeem() {
    setBusy(true);
    setError(null);
    try {
      const session = await api<BrowserSession>("/api/v1/auth/child-enrollments/redeem", {
        method: "POST",
        body: JSON.stringify({ token }),
      });
      history.replaceState({}, "", "/");
      onSession(session);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "このリンクを使えませんでした。");
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="auth-shell child-mode">
      <section className="auth-card">
        <p className="eyebrow">こどもの たんまつ</p>
        <h1>この たんまつを つかう？</h1>
        <p className="lede">おうちのひとが つくった いちどだけの リンクです。</p>
        <button className="primary child-action" disabled={busy} onClick={() => void redeem()}>
          {busy ? "つないでいるよ…" : "つかいはじめる"}
        </button>
        {error === null ? null : (
          <p className="error" role="alert">
            {error}
          </p>
        )}
      </section>
    </main>
  );
}

interface ChildPointerDrag {
  pointerId: number;
  item: TodayTask;
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
  active: boolean;
  handle: HTMLElement;
  sourceCard: HTMLElement;
  preview: HTMLElement | null;
}

function createChildTaskDragPreview(sourceCard: HTMLElement, drag: ChildPointerDrag): HTMLElement {
  const sourceBounds = sourceCard.getBoundingClientRect();
  const preview = document.createElement("ul");
  const clonedCard = sourceCard.cloneNode(true) as HTMLElement;
  preview.className = "task-list child-task-drag-preview";
  preview.setAttribute("aria-hidden", "true");
  preview.style.width = `${String(Math.min(sourceBounds.width, window.innerWidth - 24))}px`;
  clonedCard.classList.remove(
    "child-task-drag-source",
    "child-task-drop-before",
    "child-task-drop-after",
  );
  clonedCard
    .querySelectorAll<HTMLElement>("button, input, select, textarea, a")
    .forEach((control) => control.setAttribute("tabindex", "-1"));
  preview.appendChild(clonedCard);
  document.body.appendChild(preview);
  drag.preview = preview;
  return preview;
}

function positionChildTaskDragPreview(drag: ChildPointerDrag, clientX: number, clientY: number) {
  if (drag.preview === null) return;
  const bounds = drag.preview.getBoundingClientRect();
  const left = Math.max(
    12,
    Math.min(clientX - drag.offsetX, window.innerWidth - bounds.width - 12),
  );
  const top = Math.max(
    12,
    Math.min(clientY - drag.offsetY, window.innerHeight - bounds.height - 12),
  );
  drag.preview.style.left = `${String(left)}px`;
  drag.preview.style.top = `${String(top)}px`;
}

function removeChildTaskDragPreview(drag: ChildPointerDrag) {
  drag.preview?.remove();
  drag.sourceCard.classList.remove("child-task-drag-source");
  document.body.classList.remove("child-task-dragging");
}

function FloatingNotice({
  message,
  childFacing = false,
  onDismiss,
}: {
  message: string;
  childFacing?: boolean;
  onDismiss: () => void;
}) {
  useEffect(() => {
    const timeout = window.setTimeout(onDismiss, 6500);
    return () => window.clearTimeout(timeout);
  }, [message, onDismiss]);

  return (
    <div className="notice floating-notice" role="status" aria-live="polite" aria-atomic="true">
      <span>{message}</span>
      <button
        type="button"
        className="floating-notice-dismiss"
        aria-label={childFacing ? "おしらせを とじる" : "通知を閉じる"}
        onClick={onDismiss}
      >
        <span aria-hidden="true">×</span>
      </button>
    </div>
  );
}

function moveTodayTaskToDrop(
  items: TodayTask[],
  sourceTaskId: string,
  targetTaskId: string,
  placement: TodayTaskDropPlacement,
): TodayTask[] | null {
  const nextIds = reorderTodayTaskIdsWithinLaneForDrop(
    items.map((item) => ({ taskId: item.task.id, state: item.assignment.state })),
    sourceTaskId,
    targetTaskId,
    placement,
  );
  if (nextIds === null) return null;
  const itemsById = new Map(items.map((item) => [item.task.id, item] as const));
  const next: TodayTask[] = [];
  for (const [index, taskId] of nextIds.entries()) {
    const item = itemsById.get(taskId) ?? items[index];
    if (item === undefined) return null;
    next.push(item);
  }
  return next;
}

function ChildHome({
  session,
  previewChild,
  onLogout,
}: {
  session: BrowserSession;
  previewChild?: ChildProfile;
  onLogout?: () => void;
}) {
  const [items, setItems] = useState<TodayTask[]>([]);
  const taskIcon = useTaskIcons(session.workspace.id, session.actor.session_id, items);
  const [carryoverItems, setCarryoverItems] = useState<CarryoverTask[]>([]);
  const [currentCycle, setCurrentCycle] = useState<ChildCurrentFamilyCycle | null>(null);
  const [childReminders, setChildReminders] = useState<ParentNotification[]>([]);
  const [busyTaskId, setBusyTaskId] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [evidenceReadyByTask, setEvidenceReadyByTask] = useState<Record<string, boolean>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dismissNotice = useCallback(() => setNotice(null), []);
  const [childDropTarget, setChildDropTarget] = useState<{
    lane: TodayLaneId;
    taskId: string | null;
    placement: TodayTaskDropPlacement | null;
  } | null>(null);
  const childTaskDialogRef = useRef<HTMLDialogElement>(null);
  const childPointerDragRef = useRef<ChildPointerDrag | null>(null);
  const suppressChildDragClickRef = useRef(false);
  const childDragClickResetTimerRef = useRef<number | null>(null);
  const isParentPreview = previewChild !== undefined;
  const isGuardianPreview =
    isParentPreview &&
    session.actor.type === "adult" &&
    (session.actor.membership_role === "owner" || session.actor.membership_role === "guardian");
  const hasPreviewMembershipCapability = useCallback(
    (capability: SupporterCapability, childId: string) => {
      if (!isParentPreview || session.actor.type !== "adult") return false;
      if (
        session.actor.membership_role === "owner" ||
        session.actor.membership_role === "guardian"
      ) {
        return true;
      }
      return session.actor.membership_scopes.some(
        (scope) =>
          scope.capability === capability &&
          (scope.child_id === null || scope.child_id === childId),
      );
    },
    [isParentPreview, session.actor],
  );
  const canProxyTask = useCallback(
    (item: TodayTask) =>
      isParentPreview
        ? hasPreviewMembershipCapability("tasks:proxy", item.assignment.child_id)
        : session.actor.capabilities.includes("tasks:start") ||
          session.actor.capabilities.includes("tasks:complete") ||
          session.actor.capabilities.includes("tasks:help"),
    [hasPreviewMembershipCapability, isParentPreview, session.actor.capabilities],
  );
  const canStartTask = useCallback(
    (item: TodayTask) =>
      isParentPreview
        ? hasPreviewMembershipCapability("tasks:proxy", item.assignment.child_id)
        : session.actor.capabilities.includes("tasks:start"),
    [hasPreviewMembershipCapability, isParentPreview, session.actor.capabilities],
  );
  const canCompleteTask = useCallback(
    (item: TodayTask) =>
      isParentPreview
        ? hasPreviewMembershipCapability("tasks:proxy", item.assignment.child_id)
        : session.actor.capabilities.includes("tasks:complete"),
    [hasPreviewMembershipCapability, isParentPreview, session.actor.capabilities],
  );
  const canReorderTask = useCallback(
    (item: TodayTask) =>
      isParentPreview
        ? hasPreviewMembershipCapability("tasks:write", item.assignment.child_id)
        : session.actor.capabilities.includes("tasks:reorder"),
    [hasPreviewMembershipCapability, isParentPreview, session.actor.capabilities],
  );
  const canReadEvidence = useCallback(
    (item: TodayTask) =>
      !isParentPreview || hasPreviewMembershipCapability("evidence:read", item.assignment.child_id),
    [hasPreviewMembershipCapability, isParentPreview],
  );
  const handleEvidenceReady = useCallback((taskId: string, ready: boolean) => {
    setEvidenceReadyByTask((current) =>
      current[taskId] === ready ? current : { ...current, [taskId]: ready },
    );
  }, []);

  const reload = useCallback(async () => {
    try {
      const childId = previewChild?.id ?? session.actor.bound_child_id;
      const localDate = localDateInTimezone(session.workspace.timezone);
      const [today, carryovers, notificationPage, cyclePage] = await Promise.all([
        api<{ items: TodayTask[] }>(
          `/api/v1/workspaces/${session.workspace.id}/today${
            previewChild === undefined ? "" : `?child_id=${encodeURIComponent(previewChild.id)}`
          }`,
          undefined,
          session.workspace.id,
        ),
        childId === null
          ? Promise.resolve({ items: [] as CarryoverTask[] })
          : api<{ items: CarryoverTask[] }>(
              `/api/v1/workspaces/${session.workspace.id}/tasks/carryovers?${new URLSearchParams({ child_id: childId, local_date: localDate, limit: "100" }).toString()}`,
              undefined,
              session.workspace.id,
            ),
        isParentPreview
          ? Promise.resolve({ items: [] as ParentNotification[] })
          : api<{ items: ParentNotification[] }>(
              `/api/v1/workspaces/${session.workspace.id}/notifications?unread_only=true&limit=20`,
              undefined,
              session.workspace.id,
            ),
        childId === null
          ? Promise.resolve({ cycle: null as ChildCurrentFamilyCycle | null })
          : api<{ cycle: ChildCurrentFamilyCycle | null }>(
              `/api/v1/workspaces/${session.workspace.id}/cycles/current?${new URLSearchParams({ child_id: childId, local_date: localDate }).toString()}`,
              undefined,
              session.workspace.id,
            ),
      ]);
      setItems(today.items);
      setCarryoverItems(carryovers.items);
      setCurrentCycle(cyclePage.cycle);
      setChildReminders(
        notificationPage.items.filter(
          (notification) => notification.event_type === "task_start_reminder",
        ),
      );
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "きょうの やることを よめませんでした。");
    }
  }, [
    isParentPreview,
    previewChild,
    session.actor.bound_child_id,
    session.workspace.id,
    session.workspace.timezone,
  ]);

  useEffect(() => {
    void Promise.resolve().then(reload);
    const notificationRefresh = isParentPreview
      ? null
      : window.setInterval(() => void reload(), 60_000);
    const handleOnline = () => {
      void flushTaskCommands(session)
        .then(reload)
        .catch(() => undefined);
    };
    window.addEventListener("online", handleOnline);
    return () => {
      if (notificationRefresh !== null) window.clearInterval(notificationRefresh);
      window.removeEventListener("online", handleOnline);
    };
  }, [isParentPreview, reload, session]);

  useEffect(
    () => () => {
      const drag = childPointerDragRef.current;
      if (drag !== null) {
        if (drag.handle.hasPointerCapture(drag.pointerId)) {
          drag.handle.releasePointerCapture(drag.pointerId);
        }
        removeChildTaskDragPreview(drag);
        childPointerDragRef.current = null;
      }
      if (childDragClickResetTimerRef.current !== null) {
        window.clearTimeout(childDragClickResetTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    const dialog = childTaskDialogRef.current;
    if (dialog === null) return;
    if (selectedTaskId !== null && !dialog.open) dialog.showModal();
    if (selectedTaskId === null && dialog.open) dialog.close();
  }, [selectedTaskId]);

  async function command(
    item: TodayTask,
    operation: TaskLifecycleOperation,
    options: { reason?: TaskHelpReason; note?: string } = {},
  ) {
    if (isParentPreview && !canProxyTask(item)) return;
    setBusyTaskId(item.task.id);
    setError(null);
    setNotice(null);
    try {
      const response = await enqueueTaskCommand(session, item, operation, options);
      if (response === null) {
        setNotice("でんぱが もどったら おくります。この たんまつに のこっています。");
      } else {
        setItems((current) =>
          current.map((candidate) =>
            candidate.task.id === response.item.task.id ? response.item : candidate,
          ),
        );
        if (operation === "start" || operation === "complete") {
          setChildReminders((current) =>
            current.filter((notification) => notification.task_id !== item.task.id),
          );
        }
        setNotice(
          isParentPreview
            ? operation === "complete"
              ? "おうちのひとの操作として「できた」にしました。"
              : operation === "reopen"
                ? "おうちのひとの操作として「やってる」に戻しました。"
                : operation === "restore"
                  ? "おうちのひとの操作として「やる」に戻しました。"
                  : "おうちのひとの操作として記録しました。"
            : operation === "complete"
              ? "できたね。"
              : operation === "reopen"
                ? "やってるに もどしたよ。"
                : operation === "restore"
                  ? "やるに もどしたよ。"
                  : "うけつけました。",
        );
      }
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.code === "TASK_DEPENDENCY_BLOCKED") {
        setError("さきに おわらせることが あります。おうちのひとと いっしょに みてね。");
      } else if (caught instanceof ApiRequestError && caught.code === "TASK_CHECKLIST_INCOMPLETE") {
        setError("このタスクは おうちのひとに かくにんしてもらってね。");
      } else if (caught instanceof ApiRequestError && caught.code === "TASK_EVIDENCE_REQUIRED") {
        setError("できたに するまえに、ひつような きろくを つけてね。");
      } else {
        setError(caught instanceof Error ? caught.message : "そうさを おくれませんでした。");
      }
    } finally {
      setBusyTaskId(null);
    }
  }

  async function persistChildTaskOrder(
    previous: TodayTask[],
    next: TodayTask[],
    busyItem: TodayTask,
  ) {
    setBusyTaskId(busyItem.task.id);
    setError(null);
    setNotice(null);
    setItems(next);
    try {
      const response = await enqueueTodayTaskReorder(
        session,
        busyItem.assignment.child_id,
        localDateInTimezone(session.workspace.timezone),
        next,
      );
      if (response === null) {
        setNotice("でんぱが もどったら じゅんばんを おくるよ。この たんまつに のこっています。");
      } else {
        setItems(response.items);
        setNotice("じゅんばんを かえたよ。");
      }
    } catch (caught) {
      setItems(previous);
      if (caught instanceof ApiRequestError && caught.code === "TASK_ORDER_DEPENDENCY_CONFLICT") {
        setError("さきに やることが あるので、この じゅんばんには できませんでした。");
      } else if (
        caught instanceof ApiRequestError &&
        caught.code === "TASK_ORDER_PINNED_CONFLICT"
      ) {
        setError("📌の ついたものは、おうちのひとが さきに すると きめています。");
      } else if (
        caught instanceof ApiRequestError &&
        caught.code === "TASK_ORDER_TIME_WINDOW_CONFLICT"
      ) {
        setError("はじめられる じかんの じゅんばんを まもってね。");
      } else if (
        caught instanceof ApiRequestError &&
        (caught.code === "TASK_ORDER_VERSION_CONFLICT" || caught.status === 409)
      ) {
        setError("ほかの たんまつで かわりました。もういちど よみこみます。");
        await reload();
      } else {
        setError("じゅんばんを かえられませんでした。");
      }
    } finally {
      setBusyTaskId(null);
    }
  }

  async function reorderChildTask(item: TodayTask, direction: "earlier" | "later") {
    if (busyTaskId !== null || !canReorderTask(item)) return;
    const previous = items;
    const next = moveTodayTaskWithinLane(items, item.task.id, direction);
    if (next === null) return;
    await persistChildTaskOrder(previous, next, item);
  }

  async function reorderChildTaskByDrop(
    item: TodayTask,
    targetTaskId: string,
    placement: TodayTaskDropPlacement,
  ) {
    if (busyTaskId !== null || !canReorderTask(item)) return;
    const previous = items;
    const next = moveTodayTaskToDrop(items, item.task.id, targetTaskId, placement);
    if (next === null) return;
    await persistChildTaskOrder(previous, next, item);
  }

  function childDropTargetAtPoint(
    clientX: number,
    clientY: number,
    sourceItem: TodayTask,
  ): {
    lane: TodayLaneId;
    taskId: string | null;
    placement: TodayTaskDropPlacement | null;
  } | null {
    const pointed = document.elementFromPoint(clientX, clientY);
    const laneElement = pointed?.closest<HTMLElement>("[data-today-drop-lane]");
    const lane = laneElement?.dataset.todayDropLane as TodayLaneId | undefined;
    if (laneElement === null || laneElement === undefined || lane === undefined) return null;
    const sourceLane = todayLaneForState(sourceItem.assignment.state);
    if (sourceLane !== lane) {
      const command = todayLifecycleCommandForDrop(sourceItem.assignment.state, lane);
      if (
        (command === "start" && canStartTask(sourceItem)) ||
        (command === "complete" && canCompleteTask(sourceItem)) ||
        (command === "reopen" && canCompleteTask(sourceItem)) ||
        (command === "restore" && canStartTask(sourceItem))
      ) {
        return { lane, taskId: null, placement: null };
      }
      return null;
    }
    if (!canReorderTask(sourceItem)) return null;
    const cards = Array.from(
      laneElement.querySelectorAll<HTMLElement>("[data-today-drag-task-id]"),
    ).filter((card) => card.dataset.todayDragTaskId !== sourceItem.task.id);
    if (cards.length === 0) return null;
    const directCard = pointed?.closest<HTMLElement>("[data-today-drag-task-id]");
    const targetCard =
      directCard?.dataset.todayDragTaskId === sourceItem.task.id
        ? cards.reduce((closest, candidate) => {
            const closestBounds = closest.getBoundingClientRect();
            const candidateBounds = candidate.getBoundingClientRect();
            return Math.abs(clientY - (candidateBounds.top + candidateBounds.height / 2)) <
              Math.abs(clientY - (closestBounds.top + closestBounds.height / 2))
              ? candidate
              : closest;
          })
        : (directCard ??
          cards.reduce((closest, candidate) => {
            const closestBounds = closest.getBoundingClientRect();
            const candidateBounds = candidate.getBoundingClientRect();
            return Math.abs(clientY - (candidateBounds.top + candidateBounds.height / 2)) <
              Math.abs(clientY - (closestBounds.top + closestBounds.height / 2))
              ? candidate
              : closest;
          }));
    const taskId = targetCard.dataset.todayDragTaskId;
    if (taskId === undefined) return null;
    const bounds = targetCard.getBoundingClientRect();
    return {
      lane,
      taskId,
      placement: clientY >= bounds.top + bounds.height / 2 ? "after" : "before",
    };
  }

  function autoScrollChildAtPointer(clientX: number, clientY: number) {
    const edgeSize = 72;
    const maximumDelta = 14;
    if (clientY < edgeSize) {
      window.scrollBy(0, -Math.ceil(maximumDelta * (1 - clientY / edgeSize)));
    } else if (clientY > window.innerHeight - edgeSize) {
      window.scrollBy(0, Math.ceil(maximumDelta * (1 - (window.innerHeight - clientY) / edgeSize)));
    }
    const board = document.querySelector<HTMLElement>(".child-mode .kanban");
    if (board === null || board.scrollWidth <= board.clientWidth) return;
    const bounds = board.getBoundingClientRect();
    if (clientY < bounds.top || clientY > bounds.bottom) return;
    const delta = planDragAutoScrollDelta(clientX, bounds.left, bounds.right);
    if (delta !== 0) board.scrollLeft += delta;
  }

  function canDirectDragChildTask(item: TodayTask): boolean {
    if (busyTaskId !== null) return false;
    const lane = todayLaneForState(item.assignment.state);
    const canReorderWithinLane =
      canReorderTask(item) &&
      items.filter((candidate) => todayLaneForState(candidate.assignment.state) === lane).length >
        1;
    if (lane === "todo") return canReorderWithinLane || canStartTask(item);
    if (lane === "doing") {
      return (
        canReorderWithinLane ||
        canCompleteTask(item) ||
        (item.assignment.state === "in_progress" && canStartTask(item))
      );
    }
    return canReorderWithinLane || (item.assignment.state === "done" && canCompleteTask(item));
  }

  function beginChildPointerDrag(event: ReactPointerEvent<HTMLLIElement>, item: TodayTask) {
    if (
      !event.isPrimary ||
      (event.pointerType === "mouse" && event.button !== 0) ||
      !canDirectDragChildTask(item)
    ) {
      return;
    }
    const sourceCard = event.currentTarget;
    const bounds = sourceCard.getBoundingClientRect();
    event.currentTarget.setPointerCapture(event.pointerId);
    childPointerDragRef.current = {
      pointerId: event.pointerId,
      item,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - bounds.left,
      offsetY: event.clientY - bounds.top,
      active: false,
      handle: event.currentTarget,
      sourceCard,
      preview: null,
    };
  }

  function moveChildPointerDrag(event: ReactPointerEvent<HTMLLIElement>) {
    const drag = childPointerDragRef.current;
    if (drag?.pointerId !== event.pointerId) return;
    if (
      !drag.active &&
      !planPointerDragActivated(drag.startX, drag.startY, event.clientX, event.clientY)
    ) {
      return;
    }
    event.preventDefault();
    if (!drag.active) {
      drag.active = true;
      createChildTaskDragPreview(drag.sourceCard, drag);
      drag.sourceCard.classList.add("child-task-drag-source");
      document.body.classList.add("child-task-dragging");
      setError(null);
      setNotice(null);
      window.getSelection()?.removeAllRanges();
    }
    autoScrollChildAtPointer(event.clientX, event.clientY);
    positionChildTaskDragPreview(drag, event.clientX, event.clientY);
    const nextTarget = childDropTargetAtPoint(event.clientX, event.clientY, drag.item);
    setChildDropTarget((current) =>
      current !== null &&
      nextTarget !== null &&
      current.lane === nextTarget.lane &&
      current.taskId === nextTarget.taskId &&
      current.placement === nextTarget.placement
        ? current
        : nextTarget,
    );
  }

  function suppressNextChildDragClick() {
    suppressChildDragClickRef.current = true;
    if (childDragClickResetTimerRef.current !== null) {
      window.clearTimeout(childDragClickResetTimerRef.current);
    }
    childDragClickResetTimerRef.current = window.setTimeout(() => {
      suppressChildDragClickRef.current = false;
      childDragClickResetTimerRef.current = null;
    }, 750);
  }

  function finishChildPointerDrag(event: ReactPointerEvent<HTMLLIElement>, cancelled = false) {
    const drag = childPointerDragRef.current;
    if (drag?.pointerId !== event.pointerId) return;
    childPointerDragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (!drag.active) {
      if (!cancelled) setSelectedTaskId(drag.item.task.id);
      return;
    }
    event.preventDefault();
    const target = cancelled
      ? null
      : childDropTargetAtPoint(event.clientX, event.clientY, drag.item);
    removeChildTaskDragPreview(drag);
    setChildDropTarget(null);
    suppressNextChildDragClick();
    if (target === null) return;
    const operation = todayLifecycleCommandForDrop(drag.item.assignment.state, target.lane);
    if (operation !== null) {
      void command(drag.item, operation);
    } else if (target.taskId !== null && target.placement !== null) {
      void reorderChildTaskByDrop(drag.item, target.taskId, target.placement);
    }
  }

  function openChildTaskFromCard(item: TodayTask) {
    if (suppressChildDragClickRef.current) {
      suppressChildDragClickRef.current = false;
      if (childDragClickResetTimerRef.current !== null) {
        window.clearTimeout(childDragClickResetTimerRef.current);
        childDragClickResetTimerRef.current = null;
      }
      return;
    }
    setSelectedTaskId(item.task.id);
  }

  useEffect(() => {
    if (isParentPreview) return;
    const workspaceId = session.workspace.id;
    const readToday = async (signal: AbortSignal) =>
      api<{ items: TodayTask[] }>(
        `/api/v1/workspaces/${workspaceId}/today`,
        { signal },
        workspaceId,
      );
    const readStructure = async (item: TodayTask, signal: AbortSignal) =>
      api<{ structure: TaskStructure }>(
        `/api/v1/workspaces/${workspaceId}/tasks/${item.task.id}/structure?assignment_id=${encodeURIComponent(item.assignment.id)}`,
        { signal },
        workspaceId,
      );
    const commandSchema: Record<string, unknown> = {
      type: "object",
      additionalProperties: false,
      required: ["task_id", "idempotency_key"],
      properties: {
        task_id: {
          type: "string",
          format: "uuid",
          description: "今日の一覧に含まれるタスクID",
        },
        idempotency_key: {
          type: "string",
          minLength: 16,
          maxLength: 128,
          description: "再試行時にも同じ値を使う一意なキー",
        },
      },
    };
    const executeCommand = async (
      input: Record<string, unknown>,
      signal: AbortSignal,
      operation: "start" | "complete" | "reopen" | "restore" | "request_help",
    ) => {
      const taskId = requireWebMcpString(input, "task_id", 128);
      const idempotencyKey = requireWebMcpIdempotencyKey(input);
      const today = await readToday(signal);
      const item = today.items.find((candidate) => candidate.task.id === taskId);
      if (item === undefined) throw new Error("この端末の今日の一覧にないタスクです。");
      if (signal.aborted) throw signal.reason;
      const response = await enqueueTaskCommand(session, item, operation, {
        ...(operation === "request_help"
          ? { reason: "stuck" as const, note: "いっしょに みてほしい" }
          : {}),
        mutationId: `webmcp:${idempotencyKey}`,
      });
      if (response !== null) {
        setItems((current) =>
          current.map((candidate) =>
            candidate.task.id === response.item.task.id ? response.item : candidate,
          ),
        );
      }
      return {
        schema_version: 1,
        outcome: response === null ? "queued_offline" : "applied",
        task_id: taskId,
        operation,
        state: response?.item.assignment.state ?? item.assignment.state,
      };
    };
    const readTool: WebMcpTool = {
      name: "oyako_list_today_tasks_v1",
      title: "きょうのやることを見る",
      description:
        "この子供端末に認可された、きょうのタスクだけを読みます。ほかの子供や家族の情報は返しません。",
      inputSchema: { type: "object", additionalProperties: false },
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      execute: async (_input, { signal }) => {
        const today = await readToday(signal);
        return {
          schema_version: 1,
          items: today.items.map((item) => ({
            task_id: item.task.id,
            child_copy: item.task.child_copy,
            state: item.assignment.state,
            estimated_minutes: item.task.estimated_minutes,
            version: item.assignment.version,
            needs_help: item.help !== null,
          })),
        };
      },
    };
    const carryoverReadTool: WebMcpTool = {
      name: "oyako_list_carryover_tasks_v1",
      title: "おうちのひとと きめることを見る",
      description:
        "この子供端末に認可された、前のやる日に終わらなかったタスクだけを読みます。今日やるとは決めず、見るだけです。",
      inputSchema: { type: "object", additionalProperties: false },
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      execute: async (_input, { signal }) => {
        const response = await api<{ items: CarryoverTask[] }>(
          `/api/v1/workspaces/${workspaceId}/tasks/carryovers?${new URLSearchParams({
            child_id: session.actor.id,
            local_date: localDateInTimezone(session.workspace.timezone),
            limit: "100",
          }).toString()}`,
          { signal },
          workspaceId,
        );
        return {
          schema_version: 1,
          items: response.items.map((item) => ({
            task_id: item.task.id,
            child_copy: item.task.child_copy,
            state: item.assignment.state,
            missed_planned_local_date: item.missed_planned_local_date,
          })),
        };
      },
    };
    const structureTool: WebMcpTool = {
      name: "oyako_get_my_task_structure_v1",
      title: "タスクのてじゅんを見る",
      description:
        "この子供端末の今日の一覧にある1件について、チェック項目と先に終えるタスクを読みます。",
      inputSchema: {
        type: "object",
        additionalProperties: false,
        required: ["task_id"],
        properties: { task_id: { type: "string", format: "uuid" } },
      },
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      execute: async (input, { signal }) => {
        const taskId = requireWebMcpString(input, "task_id", 128);
        const today = await readToday(signal);
        const item = today.items.find((candidate) => candidate.task.id === taskId);
        if (item === undefined) throw new Error("この端末の今日の一覧にないタスクです。");
        const response = await readStructure(item, signal);
        return {
          schema_version: 1,
          task_id: taskId,
          child_copy: response.structure.item.task.child_copy,
          can_start: response.structure.can_start,
          checklist: response.structure.checklist.map((entry) => ({
            checklist_item_id: entry.id,
            child_copy: entry.child_copy,
            is_done: entry.is_done,
            version: entry.version,
          })),
          prerequisites: response.structure.dependencies.map((entry) => ({
            task_id: entry.depends_on_task.id,
            child_copy: entry.depends_on_task.child_copy,
            is_satisfied: entry.is_satisfied,
            was_overridden: entry.override !== null,
          })),
        };
      },
    };
    const checklistCommandTool: WebMcpTool = {
      name: "oyako_set_my_checklist_item_state_v1",
      title: "てじゅんのチェックを変える",
      description:
        "この子供端末の今日のタスクにあるチェック項目だけを完了または未完了にします。オフライン時は端末内outboxへ保存します。",
      inputSchema: {
        type: "object",
        additionalProperties: false,
        required: ["task_id", "checklist_item_id", "expected_version", "state", "idempotency_key"],
        properties: {
          task_id: { type: "string", format: "uuid" },
          checklist_item_id: { type: "string", format: "uuid" },
          expected_version: { type: "integer", minimum: 1 },
          state: { type: "string", enum: ["done", "open"] },
          idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
        },
      },
      annotations: { readOnlyHint: false, untrustedContentHint: false },
      execute: async (input, { signal }) => {
        const taskId = requireWebMcpString(input, "task_id", 128);
        const checklistItemId = requireWebMcpString(input, "checklist_item_id", 128);
        const expectedVersion = requireWebMcpInteger(input, "expected_version", 1, 1_000_000);
        const state = requireWebMcpString(input, "state", 16);
        if (state !== "done" && state !== "open") throw new Error("state must be done or open.");
        const idempotencyKey = requireWebMcpIdempotencyKey(input);
        const today = await readToday(signal);
        const item = today.items.find((candidate) => candidate.task.id === taskId);
        if (item === undefined) throw new Error("この端末の今日の一覧にないタスクです。");
        const structure = await readStructure(item, signal);
        const checklistItem = structure.structure.checklist.find(
          (entry) => entry.id === checklistItemId,
        );
        if (checklistItem?.version !== expectedVersion)
          throw new Error("チェック項目が見つからないか、別の端末で変更されています。");
        const response = await enqueueChecklistCommand(
          session,
          item,
          checklistItem,
          state === "done" ? "checklist_complete" : "checklist_reopen",
          `webmcp:${idempotencyKey}`,
        );
        return {
          schema_version: 1,
          outcome: response === null ? "queued_offline" : "applied",
          task_id: taskId,
          checklist_item_id: checklistItemId,
          state,
          version: response?.item.version ?? checklistItem.version,
        };
      },
    };
    const reorderTool: WebMcpTool = {
      name: "oyako_reorder_my_today_tasks_v1",
      title: "きょうの じゅんばんを かえる",
      description:
        "この子供端末のToday全件を、いまのversion付きで希望順に並べ替えます。おうちのひとの📌、じかん、さきにやることは、わけを伝えて守ります。ほかの子供のタスクは変更しません。",
      inputSchema: {
        type: "object",
        additionalProperties: false,
        required: ["items", "idempotency_key"],
        properties: {
          items: {
            type: "array",
            minItems: 2,
            maxItems: 50,
            items: {
              type: "object",
              additionalProperties: false,
              required: ["task_id", "expected_version"],
              properties: {
                task_id: { type: "string", format: "uuid" },
                expected_version: { type: "integer", minimum: 1 },
              },
            },
          },
          idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
        },
      },
      annotations: { readOnlyHint: false, untrustedContentHint: false },
      execute: async (input, { signal }) => {
        const today = await readToday(signal);
        const ordered = requireTodayOrderItems(input, today.items);
        const idempotencyKey = requireWebMcpIdempotencyKey(input);
        if (signal.aborted) throw signal.reason;
        const response = await enqueueTodayTaskReorder(
          session,
          session.actor.id,
          localDateInTimezone(session.workspace.timezone),
          ordered,
          `webmcp:${idempotencyKey}`,
        );
        if (response !== null) setItems(response.items);
        return {
          schema_version: 1,
          outcome: response === null ? "queued_offline" : "reordered",
          ordered_task_ids: ordered.map((item) => item.task.id),
          versions:
            response?.items.map((item) => ({
              task_id: item.task.id,
              version: item.assignment.version,
            })) ??
            ordered.map((item) => ({ task_id: item.task.id, version: item.assignment.version })),
        };
      },
    };
    const tools: WebMcpTool[] = [
      ...(session.actor.capabilities.includes("today:read") ? [readTool] : []),
      ...(session.actor.capabilities.includes("today:read") ? [carryoverReadTool] : []),
      ...(session.actor.capabilities.includes("today:read") ? [structureTool] : []),
      ...(session.actor.capabilities.includes("tasks:complete") ? [checklistCommandTool] : []),
      ...(session.actor.capabilities.includes("tasks:reorder") ? [reorderTool] : []),
      ...(
        [
          ["oyako_start_my_task_v1", "タスクをはじめる", "start", "tasks:start"],
          ["oyako_restore_my_task_v1", "やってるをやるに戻す", "restore", "tasks:start"],
          ["oyako_complete_my_task_v1", "タスクをできたにする", "complete", "tasks:complete"],
          ["oyako_reopen_my_task_v1", "できたをやってるに戻す", "reopen", "tasks:complete"],
          ["oyako_request_help_v1", "たすけてを伝える", "request_help", "tasks:help"],
        ] as const
      )
        .filter(([, , , capability]) => session.actor.capabilities.includes(capability))
        .map(([name, title, operation]) => ({
          name,
          title,
          description: `この子供端末の今日の一覧にあるタスクへ「${title}」操作を送ります。完了を推測せず、明示されたタスクだけを変更します。`,
          inputSchema: commandSchema,
          annotations: { readOnlyHint: false, untrustedContentHint: false },
          execute: async (input: Record<string, unknown>, { signal }: { signal: AbortSignal }) =>
            executeCommand(input, signal, operation),
        })),
    ];
    const registration = registerWebMcpTools(tools);
    return registration.dispose;
  }, [isParentPreview, session]);

  const columns: { lane: TodayLaneId; title: string; states: TaskState[]; empty: string }[] = [
    {
      lane: "todo",
      title: "やる",
      states: ["planned", "ready", "deferred", "needs_changes"],
      empty: "きょうの やることは まだ ありません。",
    },
    {
      lane: "doing",
      title: "やってる",
      states: ["in_progress", "blocked"],
      empty: "いま やっていることは ありません。",
    },
    {
      lane: "done",
      title: "できた",
      states: ["done", "verified"],
      empty: "できたことは ここに のこります。",
    },
  ];
  const selectedTask = items.find((item) => item.task.id === selectedTaskId) ?? null;
  const selectedColumn =
    selectedTask === null
      ? null
      : (columns.find((column) => column.states.includes(selectedTask.assignment.state)) ?? null);
  const selectedColumnItems =
    selectedColumn === null
      ? []
      : items.filter((item) => selectedColumn.states.includes(item.assignment.state));
  const agreedCycleCommitments =
    currentCycle?.commitments.filter((commitment) => commitment.status !== "proposed") ?? [];
  const proposedCycleCommitments =
    currentCycle?.commitments.filter((commitment) => commitment.status === "proposed") ?? [];
  return (
    <main className="product-shell child-mode">
      {isParentPreview ? (
        <section className="parent-preview-banner" aria-label="親のプレビュー">
          <div>
            <strong>{previewChild.display_name}の子供画面</strong>
            <span>親のプレビュー・親として操作中</span>
          </div>
          <a className="button-link" href="#parent/today">
            親画面へ
          </a>
        </section>
      ) : null}
      <div
        className={`child-today-heading${currentCycle === null || currentCycle.commitments.length === 0 ? " is-cycle-empty" : ""}`}
      >
        <header className="masthead">
          <div>
            <p className="eyebrow">Oyako Flow</p>
            <h1>きょう</h1>
          </div>
          <span className="truth-badge">
            {isParentPreview ? "おやが いっしょに そうさ" : "この たんまつ"}
          </span>
        </header>
        {currentCycle === null || currentCycle.commitments.length === 0 ? null : (
          <section
            className="child-cycle-commitments"
            aria-labelledby={`child-cycle-title-${currentCycle.id}`}
          >
            <header>
              <div>
                <p className="eyebrow">こんしゅう</p>
                <h2 id={`child-cycle-title-${currentCycle.id}`}>
                  {agreedCycleCommitments.length === 0
                    ? "いっしょに そうだんすること"
                    : "いっしょに きめたこと"}
                </h2>
              </div>
              <span className="child-cycle-dates">
                {currentCycle.start_local_date.split("-").slice(1).map(Number).join("/")}〜
                {currentCycle.end_local_date.split("-").slice(1).map(Number).join("/")}
              </span>
            </header>
            <ul>
              {(agreedCycleCommitments.length === 0
                ? proposedCycleCommitments
                : agreedCycleCommitments
              ).map((commitment) => (
                <li key={commitment.id}>{commitment.child_copy}</li>
              ))}
            </ul>
            {agreedCycleCommitments.length === 0 || proposedCycleCommitments.length === 0 ? null : (
              <div className="child-cycle-proposals">
                <h3>つぎに そうだんすること</h3>
                <ul>
                  {proposedCycleCommitments.map((commitment) => (
                    <li key={commitment.id}>{commitment.child_copy}</li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}
      </div>
      {notice === null || selectedTaskId !== null ? null : (
        <FloatingNotice message={notice} childFacing onDismiss={dismissNotice} />
      )}
      {error === null || selectedTaskId !== null ? null : (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {isParentPreview ? null : <WebPushControl session={session} childFacing />}
      {childReminders.length === 0 ? null : (
        <section className="child-reminder-list" aria-labelledby="child-reminder-title">
          <div>
            <p className="eyebrow">そろそろ</p>
            <h2 id="child-reminder-title">はじめる じかんだよ</h2>
          </div>
          <ul>
            {childReminders.map((notification) => {
              const task = items.find((item) => item.task.id === notification.task_id);
              return (
                <li key={notification.id}>
                  <span aria-hidden="true">🕒</span>
                  <div>
                    <strong>{task?.task.child_copy ?? "きょうの やること"}</strong>
                    <small>じゅんびが できたら、じぶんの ペースで はじめよう</small>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}
      <CarryoverSection items={carryoverItems} childFacing />
      <section className="kanban" aria-label="きょうのタスク">
        {columns.map((column) => {
          const columnItems = items.filter((item) => column.states.includes(item.assignment.state));
          return (
            <article
              className={`column today-drop-lane${childDropTarget?.lane === column.lane && childDropTarget.taskId === null ? " today-drop-lane-active" : ""}`}
              key={column.title}
              data-today-drop-lane={column.lane}
            >
              <h2>{column.title}</h2>
              {columnItems.length === 0 ? (
                <p className="empty-state">{column.empty}</p>
              ) : (
                <ul className="task-list">
                  {columnItems.map((item) => {
                    const canDrag = canDirectDragChildTask(item);
                    const dropClass =
                      childDropTarget?.taskId !== item.task.id
                        ? ""
                        : childDropTarget.placement === "before"
                          ? "child-task-drop-before"
                          : "child-task-drop-after";
                    return (
                      <li
                        className={`task-card child-task-card${canDrag ? " child-task-card-reorderable" : ""}${dropClass === "" ? "" : ` ${dropClass}`}`}
                        key={item.task.id}
                        data-today-drag-task-id={item.task.id}
                        onPointerDown={
                          canDrag ? (event) => beginChildPointerDrag(event, item) : undefined
                        }
                        onPointerMove={canDrag ? moveChildPointerDrag : undefined}
                        onPointerUp={canDrag ? (event) => finishChildPointerDrag(event) : undefined}
                        onPointerCancel={
                          canDrag ? (event) => finishChildPointerDrag(event, true) : undefined
                        }
                        onLostPointerCapture={
                          canDrag ? (event) => finishChildPointerDrag(event, true) : undefined
                        }
                      >
                        <button
                          type="button"
                          className="child-task-card-open"
                          aria-label={`${item.task.child_copy}を ひらく${canDrag ? "。カードをドラッグして じゅんばんや じょうたいを かえる" : ""}`}
                          aria-haspopup="dialog"
                          draggable={false}
                          onClick={() => openChildTaskFromCard(item)}
                        />
                        <div className="task-card-heading">
                          <TaskIcon id={taskIcon(item)} />
                          <h3>{item.task.child_copy}</h3>
                        </div>
                        {item.task.estimated_minutes === null ? null : (
                          <p>{item.task.estimated_minutes} ふん くらい</p>
                        )}
                        {item.task.today_pinned || item.task.available_from_local_time !== null ? (
                          <div
                            className="child-task-constraints"
                            aria-label="おうちのひとときめたじゅんばん"
                          >
                            {item.task.today_pinned ? (
                              <span>📌 おうちのひとが さきに するように きめたよ</span>
                            ) : null}
                            {item.task.available_from_local_time === null ||
                            item.task.available_until_local_time === null ? null : (
                              <span>
                                🕒 {item.task.available_from_local_time} から{" "}
                                {item.task.available_until_local_time} の あいだ
                              </span>
                            )}
                          </div>
                        ) : null}
                        {item.help === null ? null : (
                          <p className="help-state">
                            たすけて: {item.help.note || "いっしょに みてほしい"}
                          </p>
                        )}
                        <span className="child-task-card-hint" aria-hidden="true">
                          ひらく →
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </article>
          );
        })}
      </section>
      <dialog
        ref={childTaskDialogRef}
        className="app-dialog child-task-detail-dialog"
        aria-labelledby="child-task-detail-title"
        onClose={() => setSelectedTaskId(null)}
        onCancel={(event) => {
          if (busyTaskId === selectedTaskId) event.preventDefault();
        }}
      >
        {selectedTask === null || selectedColumn === null ? null : (
          <>
            <header className="app-dialog-header">
              <div>
                <p className="eyebrow">{selectedColumn.title}</p>
                <h2 id="child-task-detail-title">{selectedTask.task.child_copy}</h2>
              </div>
              <button
                type="button"
                className="dialog-close"
                aria-label="とじる"
                disabled={busyTaskId === selectedTask.task.id}
                onClick={() => setSelectedTaskId(null)}
              >
                ×
              </button>
            </header>
            <div className="app-dialog-body child-task-detail-body">
              {selectedTask.task.estimated_minutes === null ? null : (
                <p className="child-task-detail-time">
                  {selectedTask.task.estimated_minutes} ふん くらい
                </p>
              )}
              {selectedTask.task.today_pinned ||
              selectedTask.task.available_from_local_time !== null ? (
                <div className="child-task-constraints" aria-label="おうちのひとときめたじゅんばん">
                  {selectedTask.task.today_pinned ? (
                    <span>📌 おうちのひとが さきに するように きめたよ</span>
                  ) : null}
                  {selectedTask.task.available_from_local_time === null ||
                  selectedTask.task.available_until_local_time === null ? null : (
                    <span>
                      🕒 {selectedTask.task.available_from_local_time} から{" "}
                      {selectedTask.task.available_until_local_time} の あいだ
                    </span>
                  )}
                </div>
              ) : null}
              {selectedTask.help === null ? null : (
                <p className="help-state">
                  たすけて: {selectedTask.help.note || "いっしょに みてほしい"}
                </p>
              )}
              {notice === null ? null : (
                <FloatingNotice message={notice} childFacing onDismiss={dismissNotice} />
              )}
              {error === null ? null : (
                <p className="error" role="alert">
                  {error}
                </p>
              )}
              {canReadEvidence(selectedTask) ? (
                <TaskEvidencePanel
                  session={session}
                  item={selectedTask}
                  childFacing
                  readOnly={isParentPreview && !isGuardianPreview}
                  allowDelete={!isParentPreview}
                  onReadyChange={handleEvidenceReady}
                />
              ) : null}
              <div className="task-actions child-task-detail-actions">
                {selectedColumnItems.length > 1 && canReorderTask(selectedTask) ? (
                  <div className="task-order-actions" aria-label="じゅんばんを かえる">
                    <button
                      type="button"
                      disabled={
                        busyTaskId !== null ||
                        selectedColumnItems[0]?.task.id === selectedTask.task.id
                      }
                      aria-label={`${selectedTask.task.child_copy}を まえへ`}
                      onClick={() => void reorderChildTask(selectedTask, "earlier")}
                    >
                      まえへ
                    </button>
                    <button
                      type="button"
                      disabled={
                        busyTaskId !== null ||
                        selectedColumnItems.at(-1)?.task.id === selectedTask.task.id
                      }
                      aria-label={`${selectedTask.task.child_copy}を あとへ`}
                      onClick={() => void reorderChildTask(selectedTask, "later")}
                    >
                      あとへ
                    </button>
                  </div>
                ) : null}
                {selectedColumn.title === "やる" &&
                (isParentPreview
                  ? canProxyTask(selectedTask)
                  : session.actor.capabilities.includes("tasks:start")) ? (
                  <button
                    className="primary"
                    disabled={busyTaskId === selectedTask.task.id}
                    onClick={() => void command(selectedTask, "start")}
                  >
                    はじめる
                  </button>
                ) : null}
                {selectedColumn.title !== "できた" &&
                (isParentPreview
                  ? canProxyTask(selectedTask)
                  : session.actor.capabilities.includes("tasks:help")) ? (
                  <button
                    disabled={busyTaskId === selectedTask.task.id}
                    onClick={() =>
                      void command(
                        selectedTask,
                        selectedTask.assignment.state === "blocked" ? "clear_help" : "request_help",
                        selectedTask.assignment.state === "blocked"
                          ? {}
                          : { reason: "stuck", note: "いっしょに みてほしい" },
                      )
                    }
                  >
                    {selectedTask.assignment.state === "blocked" ? "だいじょうぶ" : "たすけて"}
                  </button>
                ) : null}
                {selectedColumn.title === "やってる" &&
                selectedTask.assignment.state === "in_progress" &&
                (isParentPreview
                  ? canProxyTask(selectedTask)
                  : session.actor.capabilities.includes("tasks:start")) ? (
                  <button
                    disabled={busyTaskId === selectedTask.task.id}
                    onClick={() => void command(selectedTask, "restore")}
                  >
                    やるに もどす
                  </button>
                ) : null}
                {selectedColumn.title === "やってる" &&
                (isParentPreview
                  ? canProxyTask(selectedTask)
                  : session.actor.capabilities.includes("tasks:complete")) ? (
                  <button
                    className="primary"
                    disabled={
                      busyTaskId === selectedTask.task.id ||
                      (selectedTask.task.evidence_policy.required !== "none" &&
                        evidenceReadyByTask[selectedTask.task.id] !== true)
                    }
                    onClick={() => void command(selectedTask, "complete")}
                  >
                    できた
                  </button>
                ) : null}
                {selectedTask.assignment.state === "done" &&
                (isParentPreview
                  ? canProxyTask(selectedTask)
                  : session.actor.capabilities.includes("tasks:complete")) ? (
                  <button
                    disabled={busyTaskId === selectedTask.task.id}
                    onClick={() => void command(selectedTask, "reopen")}
                  >
                    やってるに もどす
                  </button>
                ) : null}
              </div>
            </div>
          </>
        )}
      </dialog>
      {isParentPreview ? (
        <p className="preview-footnote">
          この画面の操作は、おうちのひとの代理操作として記録されます。子供本人の操作としては集計されません。
        </p>
      ) : null}
      <footer className="child-session-footer">
        <p className="footnote">家族: {session.workspace.name}</p>
        {isParentPreview || onLogout === undefined ? null : (
          <button
            className="child-session-switch"
            onClick={() => {
              const confirmed = window.confirm(
                "この端末の子供ログインを終了します。もう一度子供として使うには、保護者による端末登録が必要です。おとなのログイン画面へ進みますか？",
              );
              if (confirmed) onLogout();
            }}
          >
            おとなのログインへ
          </button>
        )}
      </footer>
    </main>
  );
}

function ParentChildPreview({ session, childId }: { session: BrowserSession; childId: string }) {
  const [child, setChild] = useState<ChildProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api<{ items: ChildProfile[] }>(
      `/api/v1/workspaces/${session.workspace.id}/children?limit=100`,
      undefined,
      session.workspace.id,
    )
      .then((page) => {
        if (cancelled) return;
        const selected = page.items.find((item) => item.id === childId) ?? null;
        setChild(selected);
        setError(selected === null ? "この子供プロフィールを表示できませんでした。" : null);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(caught instanceof Error ? caught.message : "子供画面を表示できませんでした。");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [childId, session.workspace.id]);

  if (loading) {
    return (
      <main className="auth-shell child-mode">
        <section className="auth-card" aria-busy="true">
          <p className="status" role="status">
            子供画面を開いています…
          </p>
        </section>
      </main>
    );
  }
  if (child === null) {
    return (
      <main className="auth-shell child-mode">
        <section className="auth-card">
          <p className="error" role="alert">
            {error ?? "子供画面を表示できませんでした。"}
          </p>
          <a className="button-link" href="#parent/settings">
            家族・端末へ戻る
          </a>
        </section>
      </main>
    );
  }
  return <ChildHome session={session} previewChild={child} />;
}

function DashboardPhoto({
  session,
  evidenceId,
  taskTitle,
}: {
  session: BrowserSession;
  evidenceId: string;
  taskTitle: string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(
    () => () => {
      if (url !== null) URL.revokeObjectURL(url);
    },
    [url],
  );

  async function toggle() {
    if (url !== null) {
      URL.revokeObjectURL(url);
      setUrl(null);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const access = await api<{ token: string; endpoint: string }>(
        `/api/v1/workspaces/${session.workspace.id}/evidence/${evidenceId}/access`,
        { method: "POST", body: JSON.stringify({ variant: "thumbnail" }) },
        session.workspace.id,
      );
      const response = await fetch(access.endpoint, {
        headers: { Authorization: `Evidence ${access.token}` },
        credentials: "same-origin",
      });
      if (!response.ok) throw new Error("写真を表示できませんでした。");
      setUrl(URL.createObjectURL(await response.blob()));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "写真を表示できませんでした。");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dashboard-photo">
      <button type="button" disabled={busy} onClick={() => void toggle()}>
        {busy ? "読み込み中…" : url === null ? "写真を見る" : "写真を閉じる"}
      </button>
      {url === null ? null : <img src={url} alt={`${taskTitle}の記録写真`} />}
      {error === null ? null : (
        <small className="error" role="alert">
          {error}
        </small>
      )}
    </div>
  );
}

function ParentDashboard({
  session,
  selectedChild,
}: {
  session: BrowserSession;
  selectedChild: ChildProfile | null;
}) {
  const today = localDateInTimezone(session.workspace.timezone);
  const [days, setDays] = useState(28);
  const [familyWide, setFamilyWide] = useState(false);
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!familyWide && selectedChild === null) {
      setDashboard(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams({
        from: addLocalDays(today, -(days - 1)),
        to: today,
      });
      if (!familyWide && selectedChild !== null) query.set("child_id", selectedChild.id);
      const response = await api<DashboardResponse>(
        `/api/v1/workspaces/${session.workspace.id}/dashboard?${query.toString()}`,
        undefined,
        session.workspace.id,
      );
      setDashboard(response);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "集計を読み込めませんでした。");
    } finally {
      setLoading(false);
    }
  }, [days, familyWide, selectedChild, session.workspace.id, today]);

  useEffect(() => {
    void Promise.resolve().then(reload);
  }, [reload]);

  const metric = (label: string, value: number, detail: string) => (
    <article className="dashboard-metric">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
  const rateMetric = (label: string, value: DashboardRate) => (
    <article className="dashboard-metric">
      <span>{label}</span>
      <strong>{value.percent === null ? "—" : `${String(value.percent)}%`}</strong>
      <small>
        {value.unavailable_reason ?? `${String(value.numerator)} / ${String(value.denominator)}件`}
      </small>
    </article>
  );

  return (
    <div className="parent-view dashboard-view">
      <section className="panel dashboard-controls" aria-label="集計範囲">
        <label>
          期間
          <select value={days} onChange={(event) => setDays(Number(event.currentTarget.value))}>
            <option value={7}>直近7日</option>
            <option value={28}>直近28日</option>
            <option value={90}>直近90日</option>
          </select>
        </label>
        <fieldset className="segmented dashboard-scope">
          <legend>対象</legend>
          <button
            type="button"
            aria-pressed={!familyWide}
            disabled={selectedChild === null}
            onClick={() => setFamilyWide(false)}
          >
            {selectedChild?.display_name ?? "子供を選択"}
          </button>
          <button type="button" aria-pressed={familyWide} onClick={() => setFamilyWide(true)}>
            家族全体
          </button>
        </fieldset>
        <button type="button" disabled={loading} onClick={() => void reload()}>
          更新
        </button>
      </section>
      {error === null ? null : (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {loading ? (
        <section className="panel loading-panel" aria-busy="true">
          <p className="status" role="status">
            家族の記録を集計しています…
          </p>
        </section>
      ) : dashboard === null ? (
        <section className="panel empty-panel">
          <h2>集計する子供を選んでください</h2>
        </section>
      ) : (
        <>
          <section className="dashboard-metrics" aria-label="期間の概要">
            {metric(
              "予定",
              dashboard.task_breakdown.total,
              `${dashboard.range.from_local_date}〜${dashboard.range.to_local_date}`,
            )}
            {metric(
              "完了",
              dashboard.task_breakdown.done + dashboard.task_breakdown.verified,
              `確認待ち ${String(dashboard.task_breakdown.verification_waiting)}件`,
            )}
            {metric(
              "延期・スキップ",
              dashboard.task_breakdown.deferred + dashboard.task_breakdown.skipped,
              `延期 ${String(dashboard.task_breakdown.deferred)}・スキップ ${String(dashboard.task_breakdown.skipped)}`,
            )}
            {rateMetric("自分で開始", dashboard.self_start_rate)}
            {rateMetric("開始案内なしで完了", dashboard.no_start_reminder_rate)}
            {metric(
              "たすけて",
              dashboard.help.requested_count,
              dashboard.help.average_resolution_minutes === null
                ? `未解決 ${String(dashboard.help.open_count)}件`
                : `平均 ${String(Math.round(dashboard.help.average_resolution_minutes))}分で解決`,
            )}
          </section>

          <section className="panel dashboard-load" aria-labelledby="dashboard-load-title">
            <header>
              <div>
                <p className="eyebrow">予定の負荷</p>
                <h2 id="dashboard-load-title">日ごとのタスクと目安時間</h2>
              </div>
            </header>
            {dashboard.daily_load.length === 0 ? (
              <p className="column-empty">この期間の予定はありません。</p>
            ) : (
              <div className="dashboard-load-chart">
                {dashboard.daily_load.map((day) => {
                  const maxMinutes = Math.max(
                    1,
                    ...dashboard.daily_load.map((candidate) => candidate.estimated_minutes),
                  );
                  return (
                    <div key={day.local_date}>
                      <time dateTime={day.local_date}>
                        {day.local_date.slice(5).replace("-", "/")}
                      </time>
                      <span>
                        <i
                          style={{
                            width: `${String((day.estimated_minutes / maxMinutes) * 100)}%`,
                          }}
                        />
                      </span>
                      <strong>{day.estimated_minutes}分</strong>
                      <small>
                        {day.completed_count}/{day.planned_count}件 完了
                        {day.missing_estimate_count > 0
                          ? `・目安なし ${String(day.missing_estimate_count)}件`
                          : ""}
                      </small>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <div className="dashboard-columns">
            <section className="panel" aria-labelledby="dashboard-routine-title">
              <p className="eyebrow">繰り返し</p>
              <h2 id="dashboard-routine-title">ルーティンの傾向</h2>
              {dashboard.routines.length === 0 ? (
                <p className="column-empty">期間内のルーティンタスクはありません。</p>
              ) : (
                <ul className="dashboard-progress-list">
                  {dashboard.routines.map((routine) => (
                    <li key={routine.routine_id}>
                      <div>
                        <strong>{routine.name}</strong>
                        <small>{routine.child_copy}</small>
                      </div>
                      <span>
                        {routine.completion_rate.percent === null
                          ? "—"
                          : `${String(routine.completion_rate.percent)}%`}
                      </span>
                      <small>
                        完了 {routine.completed_count}/{routine.planned_count}・延期{" "}
                        {routine.deferred_count}・スキップ {routine.skipped_count}
                      </small>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section className="panel" aria-labelledby="dashboard-project-title">
              <p className="eyebrow">目標</p>
              <h2 id="dashboard-project-title">プロジェクトと期間</h2>
              {dashboard.projects.length === 0 && dashboard.terms.length === 0 ? (
                <p className="column-empty">この期間に関連する項目はありません。</p>
              ) : (
                <ul className="dashboard-progress-list">
                  {dashboard.projects.map((project) => (
                    <li key={project.project_id}>
                      <strong>{project.name}</strong>
                      <span>
                        {project.task_completed}/{project.task_total}件
                      </span>
                      <small>
                        マイルストーン {project.milestone_completed}/{project.milestone_total}
                      </small>
                    </li>
                  ))}
                  {dashboard.terms.map((term) => (
                    <li key={term.term_id}>
                      <strong>{term.name}</strong>
                      <span>
                        {term.task_completed}/{term.task_total}件
                      </span>
                      <small>期間に関連するタスク</small>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          <section
            className="panel dashboard-highlights"
            aria-labelledby="dashboard-highlight-title"
          >
            <p className="eyebrow">会話のきっかけ</p>
            <h2 id="dashboard-highlight-title">今期のハイライト</h2>
            {dashboard.highlights.length === 0 ? (
              <p className="column-empty">写真やメモはまだありません。</p>
            ) : (
              <ul>
                {dashboard.highlights.map((highlight) => (
                  <li key={highlight.evidence_id}>
                    <div>
                      <span>{highlight.evidence_type === "photo" ? "写真" : "メモ"}</span>
                      <strong>{highlight.task_title}</strong>
                      <small>{new Date(highlight.created_at).toLocaleString("ja-JP")}</small>
                    </div>
                    {highlight.note_text === null ? null : <p>{highlight.note_text}</p>}
                    {highlight.evidence_type === "photo" ? (
                      <DashboardPhoto
                        session={session}
                        evidenceId={highlight.evidence_id}
                        taskTitle={highlight.task_title}
                      />
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <details className="panel dashboard-quality">
            <summary>集計の前提と不足データ</summary>
            <dl>
              <div>
                <dt>目安時間なし</dt>
                <dd>{dashboard.data_quality.assignments_without_estimate}件</dd>
              </div>
              <div>
                <dt>保護者の代理完了</dt>
                <dd>{dashboard.data_quality.adult_proxy_completion_count}件</dd>
              </div>
              <div>
                <dt>Agentによる完了</dt>
                <dd>{dashboard.data_quality.agent_completion_count}件</dd>
              </div>
            </dl>
            <ul>
              {dashboard.data_quality.caveats.map((caveat) => (
                <li key={caveat}>{caveat}</li>
              ))}
            </ul>
          </details>
        </>
      )}
    </div>
  );
}

interface ParentTodayPointerDrag {
  pointerId: number;
  item: TodayTask;
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
  active: boolean;
  handle: HTMLElement;
  sourceCard: HTMLElement;
  preview: HTMLElement | null;
}

function createParentTodayTaskDragPreview(
  sourceCard: HTMLElement,
  drag: ParentTodayPointerDrag,
): HTMLElement {
  const sourceBounds = sourceCard.getBoundingClientRect();
  const preview = document.createElement("ul");
  const clonedCard = sourceCard.cloneNode(true) as HTMLElement;
  preview.className = "task-list parent-today-task-drag-preview";
  preview.setAttribute("aria-hidden", "true");
  preview.style.width = `${String(Math.min(sourceBounds.width, window.innerWidth - 24))}px`;
  clonedCard.classList.remove(
    "parent-today-task-drag-source",
    "parent-today-task-drop-before",
    "parent-today-task-drop-after",
  );
  clonedCard
    .querySelectorAll<HTMLElement>("button, input, select, textarea, a")
    .forEach((control) => control.setAttribute("tabindex", "-1"));
  preview.appendChild(clonedCard);
  document.body.appendChild(preview);
  drag.preview = preview;
  return preview;
}

function positionParentTodayTaskDragPreview(
  drag: ParentTodayPointerDrag,
  clientX: number,
  clientY: number,
) {
  if (drag.preview === null) return;
  const bounds = drag.preview.getBoundingClientRect();
  const left = Math.max(
    12,
    Math.min(clientX - drag.offsetX, window.innerWidth - bounds.width - 12),
  );
  const top = Math.max(
    12,
    Math.min(clientY - drag.offsetY, window.innerHeight - bounds.height - 12),
  );
  drag.preview.style.left = `${String(left)}px`;
  drag.preview.style.top = `${String(top)}px`;
}

function removeParentTodayTaskDragPreview(drag: ParentTodayPointerDrag) {
  drag.preview?.remove();
  drag.sourceCard.classList.remove("parent-today-task-drag-source");
  document.body.classList.remove("parent-today-task-dragging");
}

interface PlanPointerDrag {
  pointerId: number;
  item: TodayTask;
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
  active: boolean;
  handle: HTMLElement;
  sourceCard: HTMLElement;
  preview: HTMLElement | null;
}

interface PlanTaskDropTarget {
  localDate: string;
  taskId: string | null;
  placement: PlanTaskDropPlacement | null;
}

function createPlanTaskDragPreview(sourceCard: HTMLElement, drag: PlanPointerDrag): HTMLElement {
  const sourceBounds = sourceCard.getBoundingClientRect();
  const preview = document.createElement("div");
  const list = document.createElement("ul");
  const clonedCard = sourceCard.cloneNode(true) as HTMLElement;
  preview.className = "plan-day plan-task-drag-preview";
  preview.setAttribute("aria-hidden", "true");
  preview.style.width = `${String(Math.min(sourceBounds.width, window.innerWidth - 24))}px`;
  clonedCard
    .querySelectorAll<HTMLElement>("button, input, select, textarea, a")
    .forEach((control) => {
      control.setAttribute("tabindex", "-1");
    });
  list.appendChild(clonedCard);
  preview.appendChild(list);
  document.body.appendChild(preview);
  drag.preview = preview;
  return preview;
}

function positionPlanTaskDragPreview(drag: PlanPointerDrag, clientX: number, clientY: number) {
  if (drag.preview === null) return;
  const bounds = drag.preview.getBoundingClientRect();
  const left = Math.max(
    12,
    Math.min(clientX - drag.offsetX, window.innerWidth - bounds.width - 12),
  );
  const top = Math.max(
    12,
    Math.min(clientY - drag.offsetY, window.innerHeight - bounds.height - 12),
  );
  drag.preview.style.left = `${String(left)}px`;
  drag.preview.style.top = `${String(top)}px`;
}

function removePlanTaskDragPreview(drag: PlanPointerDrag) {
  drag.preview?.remove();
  drag.sourceCard.classList.remove("plan-task-drag-source");
  document.body.classList.remove("plan-task-dragging");
}

function ParentHome({
  session,
  onLogout,
  onWorkspaceUpdated,
}: {
  session: BrowserSession;
  onLogout: () => void;
  onWorkspaceUpdated: (workspace: WorkspaceSummary) => void;
}) {
  const workspaceLocalDate = localDateInTimezone(session.workspace.timezone);
  const [children, setChildren] = useState<ChildProfile[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [workspaceSettings, setWorkspaceSettings] = useState<WorkspaceSettings | null>(null);
  const [memberships, setMemberships] = useState<WorkspaceMembership[]>([]);
  const [membershipInvitations, setMembershipInvitations] = useState<MembershipInvitation[]>([]);
  const [passkeys, setPasskeys] = useState<PasskeyCredential[]>([]);
  const [agentGrants, setAgentGrants] = useState<AgentGrant[]>([]);
  const [workspaceExports, setWorkspaceExports] = useState<WorkspaceExport[]>([]);
  const [familyViewNow, setFamilyViewNow] = useState(0);
  const [projects, setProjects] = useState<Project[]>([]);
  const [routines, setRoutines] = useState<Routine[]>([]);
  const [routineTemplates, setRoutineTemplates] = useState<RoutineTemplate[]>([]);
  const [routineReconciliations, setRoutineReconciliations] = useState<RoutineReconciliationRun[]>(
    [],
  );
  const [notifications, setNotifications] = useState<ParentNotification[]>([]);
  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreference[]>(
    [],
  );
  const [notificationPreferenceDraft, setNotificationPreferenceDraft] =
    useState<NotificationPreferenceDraft | null>(null);
  const [notificationPreferenceBusy, setNotificationPreferenceBusy] = useState(false);
  const [notificationPreferenceError, setNotificationPreferenceError] = useState<string | null>(
    null,
  );
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [notificationCenterOpen, setNotificationCenterOpen] = useState(false);
  const [notificationBusyId, setNotificationBusyId] = useState<string | null>(null);
  const [areas, setAreas] = useState<Area[]>([]);
  const [terms, setTerms] = useState<Term[]>([]);
  const [workspaceCalendars, setWorkspaceCalendars] = useState<WorkspaceCalendar[]>([]);
  const [cycles, setCycles] = useState<FamilyCycle[]>([]);
  const [tasks, setTasks] = useState<TodayTask[]>([]);
  const [carryoverTasks, setCarryoverTasks] = useState<CarryoverTask[]>([]);
  const [planTasks, setPlanTasks] = useState<ScheduledTask[]>([]);
  const [backlogTasks, setBacklogTasks] = useState<TodayTask[]>([]);
  const [inboxItems, setInboxItems] = useState<InboxItem[]>([]);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [draftProjectId, setDraftProjectId] = useState("");
  const [planAnchorDate, setPlanAnchorDate] = useState(workspaceLocalDate);
  const [planDisplayMode, setPlanDisplayMode] = useState<PlanDisplayMode>("week");
  const [backlogOpen, setBacklogOpen] = useState(false);
  const [planningTogether, setPlanningTogether] = useState(false);
  const [bulkPlanningMode, setBulkPlanningMode] = useState(false);
  const [bulkSelectedTaskIds, setBulkSelectedTaskIds] = useState<string[]>([]);
  const [bulkPlanningDialogOpen, setBulkPlanningDialogOpen] = useState(false);
  const [bulkPreview, setBulkPreview] = useState<TaskBulkReschedulePreview | null>(null);
  const [bulkPlanningBusy, setBulkPlanningBusy] = useState<"preview" | "apply" | null>(null);
  const [bulkPlanningError, setBulkPlanningError] = useState<string | null>(null);
  const [bulkPreviewIdempotencyKey, setBulkPreviewIdempotencyKey] = useState(crypto.randomUUID());
  const [bulkApplyIdempotencyKey, setBulkApplyIdempotencyKey] = useState(crypto.randomUUID());
  const [taskCreateDate, setTaskCreateDate] = useState(workspaceLocalDate);
  const [taskCreatePlacement, setTaskCreatePlacement] = useState<TaskPlacement>("scheduled");
  const [taskCreateContext, setTaskCreateContext] = useState<TaskCreateContext>("plan");
  const [activeView, setActiveView] = useState<ParentView>(readParentView);
  const taskIcon = useTaskIcons(
    session.workspace.id,
    session.actor.session_id,
    activeView === "today" ? tasks : [],
  );
  const [familyLoading, setFamilyLoading] = useState(true);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [carryoverTasksLoading, setCarryoverTasksLoading] = useState(true);
  const [planTasksLoading, setPlanTasksLoading] = useState(true);
  const [backlogTasksLoading, setBacklogTasksLoading] = useState(true);
  const [inboxLoading, setInboxLoading] = useState(true);
  const [routinesLoading, setRoutinesLoading] = useState(true);
  const [cyclesLoading, setCyclesLoading] = useState(true);
  const [creatingChild, setCreatingChild] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [taskCreateOpen, setTaskCreateOpen] = useState(false);
  const [taskDialogError, setTaskDialogError] = useState<string | null>(null);
  const [editingScheduleTask, setEditingScheduleTask] = useState<TodayTask | null>(null);
  const [editingTask, setEditingTask] = useState<TodayTask | null>(null);
  const [taskEditError, setTaskEditError] = useState<string | null>(null);
  const [updatingTask, setUpdatingTask] = useState(false);
  const [planDropTarget, setPlanDropTarget] = useState<PlanTaskDropTarget | null>(null);
  const [dragMoveBusyTaskId, setDragMoveBusyTaskId] = useState<string | null>(null);
  const [planInteractionError, setPlanInteractionError] = useState<string | null>(null);
  const [returningTask, setReturningTask] = useState<TodayTask | null>(null);
  const [taskReturnError, setTaskReturnError] = useState<string | null>(null);
  const [taskPlanDecision, setTaskPlanDecision] = useState<TaskPlanDecision | null>(null);
  const [taskPlanDecisionError, setTaskPlanDecisionError] = useState<string | null>(null);
  const [schedulePlacement, setSchedulePlacement] = useState<TaskPlacement>("scheduled");
  const [scheduleDialogError, setScheduleDialogError] = useState<string | null>(null);
  const [reschedulingTask, setReschedulingTask] = useState(false);
  const [inboxCreateOpen, setInboxCreateOpen] = useState(false);
  const [creatingInboxItem, setCreatingInboxItem] = useState(false);
  const [inboxDialogError, setInboxDialogError] = useState<string | null>(null);
  const [planningInboxItem, setPlanningInboxItem] = useState<InboxItem | null>(null);
  const [planningInboxBusy, setPlanningInboxBusy] = useState(false);
  const [inboxPlanError, setInboxPlanError] = useState<string | null>(null);
  const [cancellingInboxItem, setCancellingInboxItem] = useState<InboxItem | null>(null);
  const [cancellingInboxBusy, setCancellingInboxBusy] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  const [projectCreateOpen, setProjectCreateOpen] = useState(false);
  const [routineCreateOpen, setRoutineCreateOpen] = useState(false);
  const [routineScheduleKind, setRoutineScheduleKind] = useState<RoutineSchedule["kind"]>("daily");
  const [routineAvailability, setRoutineAvailability] =
    useState<RoutineCreateDraft["availability"]["period"]>("after_school");
  const [routinePreview, setRoutinePreview] = useState<RoutinePreview | null>(null);
  const [routineDraft, setRoutineDraft] = useState<RoutineCreateDraft | null>(null);
  const [routineEditing, setRoutineEditing] = useState<Routine | null>(null);
  const [routineTemplatePreset, setRoutineTemplatePreset] = useState<RoutineTemplate | null>(null);
  const [editingRoutineTemplate, setEditingRoutineTemplate] = useState<
    RoutineTemplate | "create" | null
  >(null);
  const [routineTemplateSource, setRoutineTemplateSource] = useState<Routine | null>(null);
  const [routineTemplateScheduleKind, setRoutineTemplateScheduleKind] =
    useState<RoutineSchedule["kind"]>("daily");
  const [routineTemplateAvailability, setRoutineTemplateAvailability] =
    useState<RoutineVersion["availability"]["period"]>("after_school");
  const [routineTemplateBusy, setRoutineTemplateBusy] = useState(false);
  const [routineTemplateError, setRoutineTemplateError] = useState<string | null>(null);
  const [routineBusy, setRoutineBusy] = useState<"preview" | "apply" | null>(null);
  const [routineDialogError, setRoutineDialogError] = useState<string | null>(null);
  const [routineStatusBusyId, setRoutineStatusBusyId] = useState<string | null>(null);
  const [routineHistory, setRoutineHistory] = useState<Routine | null>(null);
  const [routineHistoryItems, setRoutineHistoryItems] = useState<RoutineVersion[]>([]);
  const [routineHistoryLoading, setRoutineHistoryLoading] = useState(false);
  const [routineHistoryLoadingMore, setRoutineHistoryLoadingMore] = useState(false);
  const [routineHistoryNextCursor, setRoutineHistoryNextCursor] = useState<string | null>(null);
  const [routineHistoryComparison, setRoutineHistoryComparison] =
    useState<RoutineVersionComparison | null>(null);
  const [routineHistoryComparisonLoading, setRoutineHistoryComparisonLoading] = useState(false);
  const [routineHistoryError, setRoutineHistoryError] = useState<string | null>(null);
  const [routineReconciliationBusy, setRoutineReconciliationBusy] = useState(false);
  const [editingArea, setEditingArea] = useState<Area | "create" | null>(null);
  const [editingTerm, setEditingTerm] = useState<Term | "create" | null>(null);
  const [editingCalendar, setEditingCalendar] = useState<WorkspaceCalendar | "create" | null>(null);
  const [calendarPeriodDrafts, setCalendarPeriodDrafts] = useState<WorkspaceCalendarPeriodDraft[]>(
    [],
  );
  const [calendarBusyId, setCalendarBusyId] = useState<string | null>(null);
  const [calendarDialogError, setCalendarDialogError] = useState<string | null>(null);
  const [organizationBusyId, setOrganizationBusyId] = useState<string | null>(null);
  const [organizationDialogError, setOrganizationDialogError] = useState<string | null>(null);
  const [cyclePlanEditor, setCyclePlanEditor] = useState<FamilyCycle | "create" | null>(null);
  const [cyclePlanCommitments, setCyclePlanCommitments] = useState<CycleCommitmentDraft[]>([]);
  const [cycleReviewEditor, setCycleReviewEditor] = useState<FamilyCycle | null>(null);
  const [cycleReviewActions, setCycleReviewActions] = useState<CycleReviewActionDraft[]>([]);
  const [cycleActionIntent, setCycleActionIntent] = useState<CycleActionIntent | null>(null);
  const [cycleActionDismissal, setCycleActionDismissal] = useState<CycleActionDismissal | null>(
    null,
  );
  const [cycleActionBusyId, setCycleActionBusyId] = useState<string | null>(null);
  const [cycleActionError, setCycleActionError] = useState<string | null>(null);
  const [cycleBusyId, setCycleBusyId] = useState<string | null>(null);
  const [cycleDialogError, setCycleDialogError] = useState<string | null>(null);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [milestoneProject, setMilestoneProject] = useState<Project | null>(null);
  const [editingChild, setEditingChild] = useState<ChildProfile | null>(null);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [editingPasskey, setEditingPasskey] = useState<PasskeyCredential | null>(null);
  const [membershipInviteOpen, setMembershipInviteOpen] = useState(false);
  const [membershipInviteRole, setMembershipInviteRole] = useState<"guardian" | "supporter">(
    "guardian",
  );
  const [membershipInviteChildIds, setMembershipInviteChildIds] = useState<string[]>([]);
  const [membershipInviteAreaId, setMembershipInviteAreaId] = useState("");
  const [membershipInviteCapabilities, setMembershipInviteCapabilities] = useState<
    SupporterCapability[]
  >(["tasks:read"]);
  const [membershipInviteUrl, setMembershipInviteUrl] = useState<string | null>(null);
  const [membershipInviteError, setMembershipInviteError] = useState<string | null>(null);
  const [editingMembership, setEditingMembership] = useState<WorkspaceMembership | null>(null);
  const [membershipEditRole, setMembershipEditRole] = useState<"guardian" | "supporter">(
    "guardian",
  );
  const [membershipEditStatus, setMembershipEditStatus] = useState<"active" | "suspended">(
    "active",
  );
  const [membershipScopeDrafts, setMembershipScopeDrafts] = useState<
    MembershipInvitation["scopes"]
  >([]);
  const [membershipEditError, setMembershipEditError] = useState<string | null>(null);
  const [leaveWorkspaceOpen, setLeaveWorkspaceOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportDeleteTarget, setExportDeleteTarget] = useState<WorkspaceExport | null>(null);
  const [exportScope, setExportScope] = useState<"workspace" | "child">("workspace");
  const [exportIncludeMedia, setExportIncludeMedia] = useState(true);
  const [exportBusy, setExportBusy] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [childDialogStep, setChildDialogStep] = useState<ChildDialogStep>("closed");
  const [childDialogChild, setChildDialogChild] = useState<ChildProfile | null>(null);
  const [enrollmentResult, setEnrollmentResult] = useState<EnrollmentResult | null>(null);
  const [enrollmentQrCode, setEnrollmentQrCode] = useState<string | null>(null);
  const [copyNotice, setCopyNotice] = useState<string | null>(null);
  const [updatingProjectId, setUpdatingProjectId] = useState<string | null>(null);
  const [milestoneBusyId, setMilestoneBusyId] = useState<string | null>(null);
  const [taskCommandBusyId, setTaskCommandBusyId] = useState<string | null>(null);
  const [selectedParentTodayTaskId, setSelectedParentTodayTaskId] = useState<string | null>(null);
  const [parentTodayDropTarget, setParentTodayDropTarget] = useState<{
    lane: TodayLaneId;
    taskId: string | null;
    placement: TodayTaskDropPlacement | null;
  } | null>(null);
  const [evidenceReadyByTask, setEvidenceReadyByTask] = useState<Record<string, boolean>>({});
  const [enrollmentBusyChildId, setEnrollmentBusyChildId] = useState<string | null>(null);
  const [revokingDeviceId, setRevokingDeviceId] = useState<string | null>(null);
  const [familyMutationBusy, setFamilyMutationBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const dismissNotice = useCallback(() => setNotice(null), []);
  const [stagingProof, setStagingProof] = useState<string | null>(null);
  const [stagingProofBusy, setStagingProofBusy] = useState(false);
  const [taskCreateIdempotencyKey, setTaskCreateIdempotencyKey] = useState(crypto.randomUUID());
  const [taskScheduleIdempotencyKey, setTaskScheduleIdempotencyKey] = useState(crypto.randomUUID());
  const [routineApplyIdempotencyKey, setRoutineApplyIdempotencyKey] = useState(crypto.randomUUID());
  const handleEvidenceReady = useCallback((taskId: string, ready: boolean) => {
    setEvidenceReadyByTask((current) =>
      current[taskId] === ready ? current : { ...current, [taskId]: ready },
    );
  }, []);
  const workspaceId = session.workspace.id;
  const isGuardian =
    session.actor.membership_role === "owner" || session.actor.membership_role === "guardian";
  const hasMembershipCapability = (
    capability: SessionActor["membership_scopes"][number]["capability"],
  ) =>
    isGuardian || session.actor.membership_scopes.some((scope) => scope.capability === capability);
  const canReadTasks = hasMembershipCapability("tasks:read");
  const canWriteTasks = hasMembershipCapability("tasks:write");
  const canProxyTasks = hasMembershipCapability("tasks:proxy");
  const canReadRoutines = hasMembershipCapability("routines:read");
  const canWriteRoutines = hasMembershipCapability("routines:write");
  const canReadEvidence = hasMembershipCapability("evidence:read");
  const canVerifyEvidence = hasMembershipCapability("evidence:verify");
  const canReadInsights = hasMembershipCapability("insights:read");
  const canReadReviews = hasMembershipCapability("reviews:read");
  const searchHistoryRequest = useCallback(
    (path: string): Promise<unknown> =>
      api(`/api/v1/workspaces/${workspaceId}${path}`, undefined, workspaceId),
    [workspaceId],
  );
  const taskRequestSequence = useRef(0);
  const carryoverTaskRequestSequence = useRef(0);
  const planTaskRequestSequence = useRef(0);
  const backlogTaskRequestSequence = useRef(0);
  const projectDialogRef = useRef<HTMLDialogElement>(null);
  const routineDialogRef = useRef<HTMLDialogElement>(null);
  const routineTemplateDialogRef = useRef<HTMLDialogElement>(null);
  const routineHistoryDialogRef = useRef<HTMLDialogElement>(null);
  const notificationDialogRef = useRef<HTMLDialogElement>(null);
  const notificationPreferenceDialogRef = useRef<HTMLDialogElement>(null);
  const areaDialogRef = useRef<HTMLDialogElement>(null);
  const termDialogRef = useRef<HTMLDialogElement>(null);
  const calendarDialogRef = useRef<HTMLDialogElement>(null);
  const cyclePlanDialogRef = useRef<HTMLDialogElement>(null);
  const cycleReviewDialogRef = useRef<HTMLDialogElement>(null);
  const cycleActionDismissDialogRef = useRef<HTMLDialogElement>(null);
  const projectEditDialogRef = useRef<HTMLDialogElement>(null);
  const milestoneDialogRef = useRef<HTMLDialogElement>(null);
  const taskCreateDialogRef = useRef<HTMLDialogElement>(null);
  const taskScheduleDialogRef = useRef<HTMLDialogElement>(null);
  const taskEditDialogRef = useRef<HTMLDialogElement>(null);
  const taskReturnDialogRef = useRef<HTMLDialogElement>(null);
  const taskPlanDecisionDialogRef = useRef<HTMLDialogElement>(null);
  const taskBulkScheduleDialogRef = useRef<HTMLDialogElement>(null);
  const inboxCreateDialogRef = useRef<HTMLDialogElement>(null);
  const inboxPlanDialogRef = useRef<HTMLDialogElement>(null);
  const inboxCancelDialogRef = useRef<HTMLDialogElement>(null);
  const taskCreateFormRef = useRef<HTMLFormElement>(null);
  const planPointerDragRef = useRef<PlanPointerDrag | null>(null);
  const suppressPlanDragClickRef = useRef(false);
  const planDragClickResetTimerRef = useRef<number | null>(null);
  const parentTodayTaskDialogRef = useRef<HTMLDialogElement>(null);
  const parentTodayPointerDragRef = useRef<ParentTodayPointerDrag | null>(null);
  const suppressParentTodayDragClickRef = useRef(false);
  const parentTodayDragClickResetTimerRef = useRef<number | null>(null);
  const childDialogRef = useRef<HTMLDialogElement>(null);
  const childEditDialogRef = useRef<HTMLDialogElement>(null);
  const deviceEditDialogRef = useRef<HTMLDialogElement>(null);
  const passkeyEditDialogRef = useRef<HTMLDialogElement>(null);
  const membershipInviteDialogRef = useRef<HTMLDialogElement>(null);
  const membershipEditDialogRef = useRef<HTMLDialogElement>(null);
  const leaveWorkspaceDialogRef = useRef<HTMLDialogElement>(null);
  const exportDialogRef = useRef<HTMLDialogElement>(null);
  const exportDeleteDialogRef = useRef<HTMLDialogElement>(null);
  const childCreateFormRef = useRef<HTMLFormElement>(null);

  useEffect(
    () => () => {
      const drag = planPointerDragRef.current;
      if (drag !== null) {
        if (drag.handle.hasPointerCapture(drag.pointerId)) {
          drag.handle.releasePointerCapture(drag.pointerId);
        }
        removePlanTaskDragPreview(drag);
        planPointerDragRef.current = null;
      }
      if (planDragClickResetTimerRef.current !== null) {
        window.clearTimeout(planDragClickResetTimerRef.current);
      }
      const todayDrag = parentTodayPointerDragRef.current;
      if (todayDrag !== null) {
        if (todayDrag.handle.hasPointerCapture(todayDrag.pointerId)) {
          todayDrag.handle.releasePointerCapture(todayDrag.pointerId);
        }
        removeParentTodayTaskDragPreview(todayDrag);
        parentTodayPointerDragRef.current = null;
      }
      if (parentTodayDragClickResetTimerRef.current !== null) {
        window.clearTimeout(parentTodayDragClickResetTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    const dialog = projectDialogRef.current;
    if (dialog === null) return;
    if (projectCreateOpen && !dialog.open) dialog.showModal();
    if (!projectCreateOpen && dialog.open) dialog.close();
  }, [projectCreateOpen]);

  useEffect(() => {
    const dialog = routineDialogRef.current;
    if (dialog === null) return;
    if (routineCreateOpen && !dialog.open) dialog.showModal();
    if (!routineCreateOpen && dialog.open) dialog.close();
  }, [routineCreateOpen]);

  useEffect(() => {
    const dialog = routineTemplateDialogRef.current;
    if (dialog === null) return;
    if (editingRoutineTemplate !== null && !dialog.open) dialog.showModal();
    if (editingRoutineTemplate === null && dialog.open) dialog.close();
  }, [editingRoutineTemplate]);

  useEffect(() => {
    const dialog = routineHistoryDialogRef.current;
    if (dialog === null) return;
    if (routineHistory !== null && !dialog.open) dialog.showModal();
    if (routineHistory === null && dialog.open) dialog.close();
  }, [routineHistory]);

  useEffect(() => {
    const dialog = notificationDialogRef.current;
    if (dialog === null) return;
    if (notificationCenterOpen && !dialog.open) dialog.showModal();
    if (!notificationCenterOpen && dialog.open) dialog.close();
  }, [notificationCenterOpen]);

  useEffect(() => {
    const dialog = notificationPreferenceDialogRef.current;
    if (dialog === null) return;
    if (notificationPreferenceDraft !== null && !dialog.open) dialog.showModal();
    if (notificationPreferenceDraft === null && dialog.open) dialog.close();
  }, [notificationPreferenceDraft]);

  useEffect(() => {
    const dialog = areaDialogRef.current;
    if (dialog === null) return;
    if (editingArea !== null && !dialog.open) dialog.showModal();
    if (editingArea === null && dialog.open) dialog.close();
  }, [editingArea]);

  useEffect(() => {
    const dialog = termDialogRef.current;
    if (dialog === null) return;
    if (editingTerm !== null && !dialog.open) dialog.showModal();
    if (editingTerm === null && dialog.open) dialog.close();
  }, [editingTerm]);

  useEffect(() => {
    const dialog = calendarDialogRef.current;
    if (dialog === null) return;
    if (editingCalendar !== null && !dialog.open) dialog.showModal();
    if (editingCalendar === null && dialog.open) dialog.close();
  }, [editingCalendar]);

  useEffect(() => {
    const dialog = cyclePlanDialogRef.current;
    if (dialog === null) return;
    if (cyclePlanEditor !== null && !dialog.open) dialog.showModal();
    if (cyclePlanEditor === null && dialog.open) dialog.close();
  }, [cyclePlanEditor]);

  useEffect(() => {
    const dialog = cycleReviewDialogRef.current;
    if (dialog === null) return;
    if (cycleReviewEditor !== null && !dialog.open) dialog.showModal();
    if (cycleReviewEditor === null && dialog.open) dialog.close();
  }, [cycleReviewEditor]);

  useEffect(() => {
    const dialog = cycleActionDismissDialogRef.current;
    if (dialog === null) return;
    if (cycleActionDismissal !== null && !dialog.open) dialog.showModal();
    if (cycleActionDismissal === null && dialog.open) dialog.close();
  }, [cycleActionDismissal]);

  useEffect(() => {
    const dialog = taskCreateDialogRef.current;
    if (dialog === null) return;
    if (taskCreateOpen && !dialog.open) dialog.showModal();
    if (!taskCreateOpen && dialog.open) dialog.close();
  }, [taskCreateOpen]);

  useEffect(() => {
    const dialog = taskScheduleDialogRef.current;
    if (dialog === null) return;
    if (editingScheduleTask !== null && !dialog.open) dialog.showModal();
    if (editingScheduleTask === null && dialog.open) dialog.close();
  }, [editingScheduleTask]);

  useEffect(() => {
    const dialog = taskEditDialogRef.current;
    if (dialog === null) return;
    if (editingTask !== null && !dialog.open) dialog.showModal();
    if (editingTask === null && dialog.open) dialog.close();
  }, [editingTask]);

  useEffect(() => {
    const dialog = parentTodayTaskDialogRef.current;
    if (dialog === null) return;
    if (selectedParentTodayTaskId !== null && !dialog.open) dialog.showModal();
    if (selectedParentTodayTaskId === null && dialog.open) dialog.close();
  }, [selectedParentTodayTaskId]);

  useEffect(() => {
    const dialog = taskReturnDialogRef.current;
    if (dialog === null) return;
    if (returningTask !== null && !dialog.open) dialog.showModal();
    if (returningTask === null && dialog.open) dialog.close();
  }, [returningTask]);

  useEffect(() => {
    const dialog = taskPlanDecisionDialogRef.current;
    if (dialog === null) return;
    if (taskPlanDecision !== null && !dialog.open) dialog.showModal();
    if (taskPlanDecision === null && dialog.open) dialog.close();
  }, [taskPlanDecision]);

  useEffect(() => {
    const dialog = taskBulkScheduleDialogRef.current;
    if (dialog === null) return;
    if (bulkPlanningDialogOpen && !dialog.open) dialog.showModal();
    if (!bulkPlanningDialogOpen && dialog.open) dialog.close();
  }, [bulkPlanningDialogOpen]);

  useEffect(() => {
    const dialog = inboxCreateDialogRef.current;
    if (dialog === null) return;
    if (inboxCreateOpen && !dialog.open) dialog.showModal();
    if (!inboxCreateOpen && dialog.open) dialog.close();
  }, [inboxCreateOpen]);

  useEffect(() => {
    const dialog = inboxPlanDialogRef.current;
    if (dialog === null) return;
    if (planningInboxItem !== null && !dialog.open) dialog.showModal();
    if (planningInboxItem === null && dialog.open) dialog.close();
  }, [planningInboxItem]);

  useEffect(() => {
    const dialog = inboxCancelDialogRef.current;
    if (dialog === null) return;
    if (cancellingInboxItem !== null && !dialog.open) dialog.showModal();
    if (cancellingInboxItem === null && dialog.open) dialog.close();
  }, [cancellingInboxItem]);

  useEffect(() => {
    const dialog = projectEditDialogRef.current;
    if (dialog === null) return;
    if (editingProject !== null && !dialog.open) dialog.showModal();
    if (editingProject === null && dialog.open) dialog.close();
  }, [editingProject]);

  useEffect(() => {
    const dialog = milestoneDialogRef.current;
    if (dialog === null) return;
    if (milestoneProject !== null && !dialog.open) dialog.showModal();
    if (milestoneProject === null && dialog.open) dialog.close();
  }, [milestoneProject]);

  useEffect(() => {
    const dialog = childDialogRef.current;
    if (dialog === null) return;
    if (childDialogStep !== "closed" && !dialog.open) dialog.showModal();
    if (childDialogStep === "closed" && dialog.open) dialog.close();
  }, [childDialogStep]);

  useEffect(() => {
    const dialog = childEditDialogRef.current;
    if (dialog === null) return;
    if (editingChild !== null && !dialog.open) dialog.showModal();
    if (editingChild === null && dialog.open) dialog.close();
  }, [editingChild]);

  useEffect(() => {
    const dialog = deviceEditDialogRef.current;
    if (dialog === null) return;
    if (editingDevice !== null && !dialog.open) dialog.showModal();
    if (editingDevice === null && dialog.open) dialog.close();
  }, [editingDevice]);

  useEffect(() => {
    const dialog = passkeyEditDialogRef.current;
    if (dialog === null) return;
    if (editingPasskey !== null && !dialog.open) dialog.showModal();
    if (editingPasskey === null && dialog.open) dialog.close();
  }, [editingPasskey]);

  useEffect(() => {
    const dialog = membershipInviteDialogRef.current;
    if (dialog === null) return;
    if (membershipInviteOpen && !dialog.open) dialog.showModal();
    if (!membershipInviteOpen && dialog.open) dialog.close();
  }, [membershipInviteOpen]);

  useEffect(() => {
    const dialog = membershipEditDialogRef.current;
    if (dialog === null) return;
    if (editingMembership !== null && !dialog.open) dialog.showModal();
    if (editingMembership === null && dialog.open) dialog.close();
  }, [editingMembership]);

  useEffect(() => {
    const dialog = leaveWorkspaceDialogRef.current;
    if (dialog === null) return;
    if (leaveWorkspaceOpen && !dialog.open) dialog.showModal();
    if (!leaveWorkspaceOpen && dialog.open) dialog.close();
  }, [leaveWorkspaceOpen]);

  useEffect(() => {
    const dialog = exportDialogRef.current;
    if (dialog === null) return;
    if (exportDialogOpen && !dialog.open) dialog.showModal();
    if (!exportDialogOpen && dialog.open) dialog.close();
  }, [exportDialogOpen]);

  useEffect(() => {
    const dialog = exportDeleteDialogRef.current;
    if (dialog === null) return;
    if (exportDeleteTarget !== null && !dialog.open) dialog.showModal();
    if (exportDeleteTarget === null && dialog.open) dialog.close();
  }, [exportDeleteTarget]);

  useEffect(() => {
    if (enrollmentResult === null) return;
    let cancelled = false;
    void QRCode.toDataURL(enrollmentResult.url, {
      width: 260,
      margin: 2,
      errorCorrectionLevel: "M",
      color: { dark: "#19302a", light: "#ffffff" },
    })
      .then((dataUrl) => {
        if (!cancelled) setEnrollmentQrCode(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setEnrollmentQrCode(null);
      });
    return () => {
      cancelled = true;
    };
  }, [enrollmentResult]);

  function openProjectCreator() {
    setError(null);
    setNotice(null);
    setProjectCreateOpen(true);
  }

  function closeProjectCreator() {
    if (creatingProject) return;
    setError(null);
    setProjectCreateOpen(false);
  }

  function openRoutineCreator() {
    if (selectedChildId === null) return;
    setNotice(null);
    setRoutineDialogError(null);
    setRoutinePreview(null);
    setRoutineDraft(null);
    setRoutineEditing(null);
    setRoutineTemplatePreset(null);
    setRoutineScheduleKind("daily");
    setRoutineAvailability("after_school");
    setRoutineApplyIdempotencyKey(crypto.randomUUID());
    setCycleActionIntent(null);
    setRoutineCreateOpen(true);
  }

  function openRoutineCreatorFromTemplate(template: RoutineTemplate) {
    if (selectedChildId === null || template.archived_at !== null) return;
    setNotice(null);
    setRoutineDialogError(null);
    setRoutinePreview(null);
    setRoutineDraft(null);
    setRoutineEditing(null);
    setRoutineTemplatePreset(template);
    setRoutineScheduleKind(template.schedule.kind);
    setRoutineAvailability(template.availability.period);
    setRoutineApplyIdempotencyKey(crypto.randomUUID());
    setCycleActionIntent(null);
    setRoutineCreateOpen(true);
  }

  function openRoutineEditor(routine: Routine, actionIntent?: CycleActionIntent) {
    setNotice(null);
    setRoutineDialogError(null);
    setRoutinePreview(null);
    setRoutineDraft(null);
    setRoutineEditing(routine);
    setRoutineTemplatePreset(null);
    setRoutineScheduleKind(routine.current_version.schedule.kind);
    setRoutineAvailability(routine.current_version.availability.period);
    setRoutineApplyIdempotencyKey(crypto.randomUUID());
    setCycleActionIntent(actionIntent ?? null);
    setRoutineCreateOpen(true);
  }

  function closeRoutineCreator() {
    if (routineBusy !== null) return;
    setRoutineDialogError(null);
    setRoutinePreview(null);
    setRoutineDraft(null);
    setRoutineEditing(null);
    setRoutineTemplatePreset(null);
    setCycleActionIntent(null);
    setRoutineCreateOpen(false);
  }

  async function openRoutineHistory(routine: Routine) {
    setRoutineHistory(routine);
    setRoutineHistoryItems([]);
    setRoutineHistoryError(null);
    setRoutineHistoryNextCursor(null);
    setRoutineHistoryComparison(null);
    setRoutineHistoryLoading(true);
    try {
      const response = await api<{ items: RoutineVersion[]; next_cursor: string | null }>(
        `/api/v1/workspaces/${workspaceId}/routines/${routine.id}/versions?limit=20`,
        undefined,
        workspaceId,
      );
      setRoutineHistoryItems(response.items);
      setRoutineHistoryNextCursor(response.next_cursor);
    } catch (caught) {
      setRoutineHistoryError(
        caught instanceof Error ? caught.message : "ルーティンの履歴を読み込めませんでした。",
      );
    } finally {
      setRoutineHistoryLoading(false);
    }
  }

  async function loadMoreRoutineHistory() {
    if (routineHistory === null || routineHistoryNextCursor === null || routineHistoryLoadingMore)
      return;
    setRoutineHistoryLoadingMore(true);
    setRoutineHistoryError(null);
    try {
      const response = await api<{ items: RoutineVersion[]; next_cursor: string | null }>(
        `/api/v1/workspaces/${workspaceId}/routines/${routineHistory.id}/versions?${new URLSearchParams(
          { limit: "20", cursor: routineHistoryNextCursor },
        ).toString()}`,
        undefined,
        workspaceId,
      );
      setRoutineHistoryItems((current) => [...current, ...response.items]);
      setRoutineHistoryNextCursor(response.next_cursor);
    } catch (caught) {
      setRoutineHistoryError(
        caught instanceof Error ? caught.message : "続きの履歴を読み込めませんでした。",
      );
    } finally {
      setRoutineHistoryLoadingMore(false);
    }
  }

  async function compareRoutineVersion(version: RoutineVersion) {
    if (routineHistory === null || routineHistoryComparisonLoading) return;
    setRoutineHistoryComparisonLoading(true);
    setRoutineHistoryError(null);
    try {
      const query = new URLSearchParams({
        from_version: String(version.version_number),
        to_version: String(routineHistory.current_version.version_number),
      });
      const comparison = await api<RoutineVersionComparison>(
        `/api/v1/workspaces/${workspaceId}/routines/${routineHistory.id}/versions/compare?${query.toString()}`,
        undefined,
        workspaceId,
      );
      setRoutineHistoryComparison(comparison);
    } catch (caught) {
      setRoutineHistoryError(
        caught instanceof Error ? caught.message : "バージョンを比較できませんでした。",
      );
    } finally {
      setRoutineHistoryComparisonLoading(false);
    }
  }

  function closeRoutineHistory() {
    if (routineHistoryLoading) return;
    setRoutineHistory(null);
    setRoutineHistoryItems([]);
    setRoutineHistoryNextCursor(null);
    setRoutineHistoryComparison(null);
    setRoutineHistoryError(null);
  }

  function openRoutineTemplateEditor(
    template: RoutineTemplate | "create",
    source: Routine | null = null,
  ) {
    const sourceSchedule =
      template === "create" ? source?.current_version.schedule : template.schedule;
    const sourceAvailability =
      template === "create" ? source?.current_version.availability : template.availability;
    setNotice(null);
    setRoutineTemplateError(null);
    setRoutineTemplateSource(source);
    setRoutineTemplateScheduleKind(sourceSchedule?.kind ?? "daily");
    setRoutineTemplateAvailability(sourceAvailability?.period ?? "after_school");
    setEditingRoutineTemplate(template);
  }

  function closeRoutineTemplateEditor() {
    if (routineTemplateBusy) return;
    setRoutineTemplateError(null);
    setRoutineTemplateSource(null);
    setEditingRoutineTemplate(null);
  }

  async function saveRoutineTemplate(event: FormSubmitEvent) {
    event.preventDefault();
    if (editingRoutineTemplate === null || routineTemplateBusy) return;
    setRoutineTemplateBusy(true);
    setRoutineTemplateError(null);
    const fields = new FormData(event.currentTarget);
    try {
      const body = {
        name: formString(fields, "name"),
        child_copy: formString(fields, "child_copy"),
        notes: formString(fields, "notes"),
        color: formString(fields, "color", "#4f8f72"),
        schedule: routineScheduleFromForm(fields, routineTemplateScheduleKind),
        availability: {
          period: routineTemplateAvailability,
          from_local_time:
            routineTemplateAvailability === "custom"
              ? formString(fields, "available_from_local_time")
              : null,
          until_local_time:
            routineTemplateAvailability === "custom"
              ? formString(fields, "available_until_local_time")
              : null,
        },
        due_offset_days: Number(formString(fields, "due_offset_days", "0")),
        default_duration_minutes:
          formString(fields, "default_duration_minutes").length === 0
            ? null
            : Number(formString(fields, "default_duration_minutes")),
        verification_policy: "none" as const,
        evidence_policy: { required: "none" as const },
        notification_policy: {
          child_reminder: fields.has("child_reminder"),
          guardian_digest: fields.has("guardian_digest"),
        },
      };
      const updating = editingRoutineTemplate !== "create";
      await api(
        updating
          ? `/api/v1/workspaces/${workspaceId}/routine-templates/${editingRoutineTemplate.id}`
          : `/api/v1/workspaces/${workspaceId}/routine-templates`,
        {
          method: updating ? "PATCH" : "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify(
            updating ? { ...body, expected_version: editingRoutineTemplate.version } : body,
          ),
        },
        workspaceId,
      );
      setNotice(updating ? "テンプレートを更新しました。" : "テンプレートを保存しました。");
      setEditingRoutineTemplate(null);
      setRoutineTemplateSource(null);
      await reload();
    } catch (caught) {
      setRoutineTemplateError(
        caught instanceof Error ? caught.message : "テンプレートを保存できませんでした。",
      );
    } finally {
      setRoutineTemplateBusy(false);
    }
  }

  async function changeRoutineTemplateArchive(template: RoutineTemplate) {
    if (routineTemplateBusy) return;
    const command = template.archived_at === null ? "archive" : "restore";
    if (
      command === "archive" &&
      !window.confirm("このテンプレートをアーカイブしますか？ 作成済みのルーティンは変わりません。")
    )
      return;
    setRoutineTemplateBusy(true);
    setError(null);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/routine-templates/${template.id}/commands/${command}`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({ expected_version: template.version }),
        },
        workspaceId,
      );
      setNotice(
        command === "archive"
          ? "テンプレートをアーカイブしました。"
          : "テンプレートを復元しました。",
      );
      await reload();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "テンプレートを変更できませんでした。");
    } finally {
      setRoutineTemplateBusy(false);
    }
  }

  function openAreaEditor(area: Area | "create") {
    setNotice(null);
    setOrganizationDialogError(null);
    setEditingArea(area);
  }

  function closeAreaEditor() {
    if (organizationBusyId !== null) return;
    setOrganizationDialogError(null);
    setEditingArea(null);
  }

  function openTermEditor(term: Term | "create") {
    setNotice(null);
    setOrganizationDialogError(null);
    setEditingTerm(term);
  }

  function closeTermEditor() {
    if (organizationBusyId !== null) return;
    setOrganizationDialogError(null);
    setEditingTerm(null);
  }

  function openCalendarEditor(calendar: WorkspaceCalendar | "create") {
    setNotice(null);
    setCalendarDialogError(null);
    setCalendarPeriodDrafts(
      calendar === "create"
        ? [
            {
              key: crypto.randomUUID(),
              name: "",
              start_local_date: workspaceLocalDate,
              end_local_date: workspaceLocalDate,
            },
          ]
        : calendar.periods.map((period) => ({ key: crypto.randomUUID(), ...period })),
    );
    setEditingCalendar(calendar);
  }

  function closeCalendarEditor() {
    if (calendarBusyId !== null) return;
    setCalendarDialogError(null);
    setCalendarPeriodDrafts([]);
    setEditingCalendar(null);
  }

  function updateCalendarPeriod(key: string, patch: Partial<WorkspaceCalendarPeriodDraft>) {
    setCalendarPeriodDrafts((current) =>
      current.map((period) => (period.key === key ? { ...period, ...patch } : period)),
    );
  }

  function addCalendarPeriod() {
    setCalendarPeriodDrafts((current) => [
      ...current,
      {
        key: crypto.randomUUID(),
        name: "",
        start_local_date: workspaceLocalDate,
        end_local_date: workspaceLocalDate,
      },
    ]);
  }

  function removeCalendarPeriod(key: string) {
    setCalendarPeriodDrafts((current) => current.filter((period) => period.key !== key));
  }

  function openCyclePlanEditor(cycle: FamilyCycle | "create") {
    setNotice(null);
    setCycleDialogError(null);
    setCyclePlanCommitments(
      cycle === "create"
        ? []
        : cycle.commitments.map((commitment) => ({
            key: commitment.id,
            kind: commitment.kind,
            child_id: commitment.child_id,
            task_id: commitment.task_id,
            routine_id: commitment.routine_id,
            title: commitment.title,
            child_copy: commitment.child_copy,
            status: commitment.status,
          })),
    );
    setCyclePlanEditor(cycle);
  }

  function closeCyclePlanEditor() {
    if (cycleBusyId !== null) return;
    setCycleDialogError(null);
    setCyclePlanEditor(null);
    setCyclePlanCommitments([]);
  }

  function addCycleCommitment() {
    setCyclePlanCommitments((current) => [
      ...current,
      {
        key: crypto.randomUUID(),
        kind: "custom",
        child_id: selectedChildId,
        task_id: null,
        routine_id: null,
        title: "",
        child_copy: "",
        status: "proposed",
      },
    ]);
  }

  function updateCycleCommitment(
    key: string,
    patch: Partial<Pick<CycleCommitmentDraft, "child_id" | "title" | "child_copy" | "status">>,
  ) {
    setCyclePlanCommitments((current) =>
      current.map((commitment) =>
        commitment.key === key ? { ...commitment, ...patch } : commitment,
      ),
    );
  }

  async function saveCyclePlan(event: FormSubmitEvent) {
    event.preventDefault();
    if (cyclePlanEditor === null || cycleBusyId !== null) return;
    const form = event.currentTarget;
    const fields = new FormData(form);
    const explicitParticipantChildIds = children
      .filter(
        (child) => child.archived_at === null && fields.get(`cycle-child-${child.id}`) === "on",
      )
      .map((child) => child.id);
    const participantChildIds = childIdsForContextualForm({
      mode: cyclePlanEditor === "create" ? "create" : "edit",
      selectedChildId,
      explicitChildIds: explicitParticipantChildIds,
    });
    if (participantChildIds.length === 0) {
      setCycleDialogError(
        cyclePlanEditor === "create"
          ? "左のメニューで子供を選んでください。"
          : "一緒に計画する子供を1人以上選んでください。",
      );
      return;
    }
    const commitments = cyclePlanCommitments.map((commitment) => ({
      kind: commitment.kind,
      child_id: commitment.child_id,
      task_id: commitment.task_id,
      routine_id: commitment.routine_id,
      title: commitment.title.trim(),
      child_copy: commitment.child_copy.trim(),
      status: commitment.status,
    }));
    if (commitments.some((commitment) => commitment.title === "" || commitment.child_copy === "")) {
      setCycleDialogError("約束には親向けの名前と子供に見せることばの両方が必要です。");
      return;
    }
    if (
      commitments.some(
        (commitment) =>
          commitment.child_id !== null && !participantChildIds.includes(commitment.child_id),
      )
    ) {
      setCycleDialogError("約束の対象は参加する子供から選んでください。");
      return;
    }
    const isCreate = cyclePlanEditor === "create";
    setCycleBusyId(isCreate ? "create" : cyclePlanEditor.id);
    setCycleDialogError(null);
    try {
      const response = await api<{ cycle: FamilyCycle }>(
        isCreate
          ? `/api/v1/workspaces/${workspaceId}/cycles`
          : `/api/v1/workspaces/${workspaceId}/cycles/${cyclePlanEditor.id}`,
        {
          method: isCreate ? "POST" : "PATCH",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            ...(isCreate ? {} : { expected_version: cyclePlanEditor.version }),
            start_local_date: formString(fields, "start_local_date"),
            end_local_date: formString(fields, "end_local_date"),
            plan_summary: formString(fields, "plan_summary").trim(),
            participant_child_ids: participantChildIds,
            commitments,
          }),
        },
        workspaceId,
      );
      setCycles((current) =>
        isCreate
          ? [response.cycle, ...current]
          : current.map((cycle) => (cycle.id === response.cycle.id ? response.cycle : cycle)),
      );
      setCyclePlanEditor(null);
      setCyclePlanCommitments([]);
      setNotice(
        isCreate ? "家族サイクルの下書きを作成しました。" : "家族サイクルの計画を保存しました。",
      );
    } catch (caught) {
      setCycleDialogError(
        caught instanceof Error ? caught.message : "家族サイクルの計画を保存できませんでした。",
      );
    } finally {
      setCycleBusyId(null);
    }
  }

  function openCycleReviewEditor(cycle: FamilyCycle) {
    setNotice(null);
    setCycleDialogError(null);
    setCycleReviewActions(
      cycle.review_actions
        .filter(
          (action) =>
            action.status === "proposed" &&
            (action.child_id === selectedChildId || action.child_id === null),
        )
        .map((action) => ({
          key: action.id,
          kind: action.kind,
          summary: action.summary,
          target_id: action.target_id,
        })),
    );
    setCycleReviewEditor(cycle);
  }

  function closeCycleReviewEditor() {
    if (cycleBusyId !== null) return;
    setCycleDialogError(null);
    setCycleReviewActions([]);
    setCycleReviewEditor(null);
  }

  function addCycleReviewAction() {
    setCycleReviewActions((current) => [
      ...current,
      { key: crypto.randomUUID(), kind: "create_task", summary: "", target_id: null },
    ]);
  }

  function updateCycleReviewAction(
    key: string,
    patch: Partial<Pick<CycleReviewActionDraft, "kind" | "summary" | "target_id">>,
  ) {
    setCycleReviewActions((current) =>
      current.map((action) => {
        if (action.key !== key) return action;
        const next = { ...action, ...patch };
        if (patch.kind === "create_task" || patch.kind === "other") next.target_id = null;
        return next;
      }),
    );
  }

  function cycleActionIntentFor(
    cycle: FamilyCycle,
    action: FamilyCycle["review_actions"][number],
  ): CycleActionIntent | null {
    if (action.kind === "other") return null;
    return {
      cycle_id: cycle.id,
      action_id: action.id,
      expected_version: cycle.version,
      kind: action.kind,
      link_idempotency_key: crypto.randomUUID(),
      downstream_request_id: null,
      target_type: null,
      target_id: null,
    };
  }

  function startCycleAction(cycle: FamilyCycle, action: FamilyCycle["review_actions"][number]) {
    const intent = cycleActionIntentFor(cycle, action);
    if (intent === null) return;
    setCycleActionError(null);
    setCycleActionIntent(intent);
    if (intent.kind === "create_task") {
      openTaskCreator({ context: "plan", actionIntent: intent });
      return;
    }
    if (intent.kind === "reschedule_task") {
      const item = [...tasks, ...planTasks].find(
        (candidate) =>
          candidate.task.id === action.target_id &&
          candidate.assignment.child_id === selectedChildId,
      );
      if (item === undefined) {
        setCycleActionError(
          "対象タスクが現在の計画にありません。計画を再読み込みしてからやり直してください。",
        );
        return;
      }
      openTaskScheduleEditor(item, intent);
      return;
    }
    const routine = selectedChildRoutines.find((candidate) => candidate.id === action.target_id);
    if (routine === undefined) {
      setCycleActionError(
        "対象ルーティンが現在の一覧にありません。ルーティンを再読み込みしてからやり直してください。",
      );
      return;
    }
    openRoutineEditor(routine, intent);
  }

  async function linkCycleAction(
    intent: CycleActionIntent,
    commandRequestId: string,
    targetType: "task" | "routine",
    targetId: string,
  ): Promise<boolean> {
    const currentVersion =
      cycles.find((cycle) => cycle.id === intent.cycle_id)?.version ?? intent.expected_version;
    setCycleActionBusyId(intent.action_id);
    setCycleActionError(null);
    try {
      const response = await api<{ cycle: FamilyCycle; request_id: string }>(
        `/api/v1/workspaces/${workspaceId}/cycles/${intent.cycle_id}/actions/${intent.action_id}/commands/apply`,
        {
          method: "POST",
          headers: { "Idempotency-Key": intent.link_idempotency_key },
          body: JSON.stringify({
            expected_version: currentVersion,
            command_request_id: commandRequestId,
            target_type: targetType,
            target_id: targetId,
          }),
        },
        workspaceId,
      );
      setCycles((current) =>
        current.map((cycle) => (cycle.id === response.cycle.id ? response.cycle : cycle)),
      );
      setCycleActionIntent(null);
      setNotice("変更を実行し、ふりかえりのActionへ監査記録をリンクしました。");
      return true;
    } catch (caught) {
      setCycleActionIntent({
        ...intent,
        downstream_request_id: commandRequestId,
        target_type: targetType,
        target_id: targetId,
      });
      setCycleActionError(
        `変更自体は完了しましたが、Actionへの監査リンクは未完了です。再試行してください。${
          caught instanceof Error ? `（${caught.message}）` : ""
        }`,
      );
      return false;
    } finally {
      setCycleActionBusyId(null);
    }
  }

  async function retryCycleActionLink() {
    const intent = cycleActionIntent;
    if (intent === null) return;
    if (
      intent.downstream_request_id === null ||
      intent.target_type === null ||
      intent.target_id === null
    )
      return;
    await linkCycleAction(
      intent,
      intent.downstream_request_id,
      intent.target_type,
      intent.target_id,
    );
  }

  async function dismissCycleAction(event: FormSubmitEvent) {
    event.preventDefault();
    if (cycleActionDismissal === null || cycleActionBusyId !== null) return;
    const reason = formString(new FormData(event.currentTarget), "reason").trim();
    if (reason === "") {
      setCycleActionError("見送る理由を入力してください。");
      return;
    }
    setCycleActionBusyId(cycleActionDismissal.action_id);
    setCycleActionError(null);
    try {
      const response = await api<{ cycle: FamilyCycle; request_id: string }>(
        `/api/v1/workspaces/${workspaceId}/cycles/${cycleActionDismissal.cycle_id}/actions/${cycleActionDismissal.action_id}/commands/dismiss`,
        {
          method: "POST",
          headers: { "Idempotency-Key": cycleActionDismissal.idempotency_key },
          body: JSON.stringify({
            expected_version: cycleActionDismissal.expected_version,
            reason,
          }),
        },
        workspaceId,
      );
      setCycles((current) =>
        current.map((cycle) => (cycle.id === response.cycle.id ? response.cycle : cycle)),
      );
      setCycleActionDismissal(null);
      setNotice("Actionを理由付きで見送りにしました。");
    } catch (caught) {
      setCycleActionError(
        caught instanceof Error ? caught.message : "Actionを見送れませんでした。",
      );
    } finally {
      setCycleActionBusyId(null);
    }
  }

  async function saveCycleReview(event: FormSubmitEvent) {
    event.preventDefault();
    if (cycleReviewEditor === null || cycleBusyId !== null) return;
    const fields = new FormData(event.currentTarget);
    const noteCategories = ["good", "helped", "difficult", "next_change"] as const;
    const notes = [
      ...cycleReviewEditor.review_notes
        .filter((note) => note.child_id !== selectedChildId && note.child_id !== null)
        .map((note) => ({ category: note.category, child_id: note.child_id, note: note.note })),
      ...noteCategories.flatMap((category) =>
        formString(fields, category)
          .split("\n")
          .map((note) => note.trim())
          .filter((note) => note.length > 0)
          .map((note) => ({ category, child_id: selectedChildId, note })),
      ),
    ];
    const invalidAction = cycleReviewActions.find(
      (action) =>
        action.summary.trim() === "" ||
        ((action.kind === "reschedule_task" || action.kind === "update_routine") &&
          action.target_id === null),
    );
    if (invalidAction !== undefined) {
      setCycleDialogError(
        "Actionには内容が必要です。日程変更とルーティン変更は対象も選んでください。",
      );
      return;
    }
    const actions = [
      ...cycleReviewEditor.review_actions
        .filter(
          (action) =>
            action.status === "proposed" &&
            action.child_id !== selectedChildId &&
            action.child_id !== null,
        )
        .map((action) => ({
          kind: action.kind,
          child_id: action.child_id,
          summary: action.summary,
          target_type: action.target_type,
          target_id: action.target_id,
        })),
      ...cycleReviewActions.map((action) => ({
        kind: action.kind,
        child_id: selectedChildId,
        summary: action.summary.trim(),
        target_type:
          action.kind === "reschedule_task"
            ? ("task" as const)
            : action.kind === "update_routine"
              ? ("routine" as const)
              : null,
        target_id: action.target_id,
      })),
    ];
    setCycleBusyId(cycleReviewEditor.id);
    setCycleDialogError(null);
    try {
      const response = await api<{ cycle: FamilyCycle }>(
        `/api/v1/workspaces/${workspaceId}/cycles/${cycleReviewEditor.id}/review`,
        {
          method: "PATCH",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            expected_version: cycleReviewEditor.version,
            review_summary: formString(fields, "review_summary").trim(),
            notes,
            actions,
          }),
        },
        workspaceId,
      );
      setCycles((current) =>
        current.map((cycle) => (cycle.id === response.cycle.id ? response.cycle : cycle)),
      );
      setCycleReviewEditor(null);
      setCycleReviewActions([]);
      setNotice("親子の振り返りを保存しました。次のactionは未適用として残しています。");
    } catch (caught) {
      setCycleDialogError(
        caught instanceof Error ? caught.message : "家族サイクルの振り返りを保存できませんでした。",
      );
    } finally {
      setCycleBusyId(null);
    }
  }

  async function transitionCycle(
    cycle: FamilyCycle,
    command:
      | "plan"
      | "return_to_draft"
      | "activate"
      | "start_review"
      | "return_to_active"
      | "close"
      | "reopen_review",
  ) {
    if (cycleBusyId !== null) return;
    setCycleBusyId(cycle.id);
    setError(null);
    setNotice(null);
    try {
      const response = await api<{ cycle: FamilyCycle }>(
        `/api/v1/workspaces/${workspaceId}/cycles/${cycle.id}/commands/${command}`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({ expected_version: cycle.version }),
        },
        workspaceId,
      );
      setCycles((current) =>
        current.map((item) => (item.id === response.cycle.id ? response.cycle : item)),
      );
      setNotice("家族サイクルの段階を更新しました。");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "家族サイクルを更新できませんでした。");
    } finally {
      setCycleBusyId(null);
    }
  }

  const reloadRoutineReconciliations = useCallback(async (): Promise<
    RoutineReconciliationRun[]
  > => {
    const response = await api<{ items: RoutineReconciliationRun[] }>(
      `/api/v1/workspaces/${workspaceId}/routines/reconciliations`,
      undefined,
      workspaceId,
    );
    setRoutineReconciliations(response.items);
    return response.items;
  }, [workspaceId]);

  async function runRoutineReconciliation() {
    if (routineReconciliationBusy) return;
    setRoutineReconciliationBusy(true);
    setError(null);
    setNotice(null);
    try {
      const response = await api<{ run: RoutineReconciliationRun }>(
        `/api/v1/workspaces/${workspaceId}/routines/reconciliations`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({ window_days: 14 }),
        },
        workspaceId,
      );
      setRoutineReconciliations((current) => [
        response.run,
        ...current.filter((item) => item.id !== response.run.id),
      ]);
      let completed = response.run;
      for (let attempt = 0; attempt < 20; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 750));
        const runs = await reloadRoutineReconciliations();
        const latest = runs.find((item) => item.id === response.run.id);
        if (latest !== undefined) completed = latest;
        if (completed.status === "completed" || completed.status === "failed") break;
      }
      if (completed.status === "completed") {
        await reload();
        setNotice(
          completed.missing_count === 0
            ? "今後14日分を点検しました。生成漏れはありません。"
            : `今後14日分を点検し、生成漏れ${String(completed.missing_count)}件を${String(completed.repaired_count)}件修復しました。`,
        );
      } else if (completed.status === "failed") {
        setError("ルーティンの生成点検に失敗しました。再試行できます。");
      } else {
        setNotice("生成点検を受け付けました。バックグラウンドで続行しています。");
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "ルーティンを点検できませんでした。");
    } finally {
      setRoutineReconciliationBusy(false);
    }
  }

  async function changeRoutineStatus(routine: Routine) {
    if (routineStatusBusyId !== null) return;
    const command = routine.status === "active" ? "pause" : "resume";
    setRoutineStatusBusyId(routine.id);
    setError(null);
    setNotice(null);
    try {
      const response = await api<{ routine: Routine }>(
        `/api/v1/workspaces/${workspaceId}/routines/${routine.id}/commands/${command}`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({ expected_version: routine.version }),
        },
        workspaceId,
      );
      setRoutines((current) =>
        current.map((item) => (item.id === response.routine.id ? response.routine : item)),
      );
      setNotice(
        command === "pause"
          ? `${routine.name}の新しい予定の生成を停止しました。計画済みのTaskは残ります。`
          : `${routine.name}の予定生成を再開しました。`,
      );
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "ルーティンの状態を変更できませんでした。",
      );
    } finally {
      setRoutineStatusBusyId(null);
    }
  }

  function openProjectEditor(project: Project) {
    setError(null);
    setNotice(null);
    setEditingProject(project);
  }

  function closeProjectEditor() {
    if (updatingProjectId !== null) return;
    setError(null);
    setEditingProject(null);
  }

  function openMilestoneCreator(project: Project) {
    setError(null);
    setNotice(null);
    setMilestoneProject(project);
  }

  function closeMilestoneCreator() {
    if (milestoneBusyId !== null) return;
    setError(null);
    setMilestoneProject(null);
  }

  function openTaskCreator(options: {
    context: TaskCreateContext;
    localDate?: string;
    projectId?: string;
    placement?: TaskPlacement;
    actionIntent?: CycleActionIntent;
  }) {
    if (selectedChildId === null) return;
    setNotice(null);
    setTaskDialogError(null);
    setTaskCreateContext(options.context);
    setTaskCreateDate(options.localDate ?? workspaceLocalDate);
    setTaskCreatePlacement(
      options.context === "today" ? "scheduled" : (options.placement ?? "scheduled"),
    );
    setDraftProjectId(options.projectId ?? "");
    setTaskCreateIdempotencyKey(crypto.randomUUID());
    setCycleActionIntent(options.actionIntent ?? null);
    taskCreateFormRef.current?.reset();
    setTaskCreateOpen(true);
  }

  function closeTaskCreator() {
    if (creatingTask) return;
    setTaskDialogError(null);
    setCycleActionIntent(null);
    setTaskCreateOpen(false);
  }

  function openTaskScheduleEditor(
    item: TodayTask,
    actionIntent?: CycleActionIntent,
    targetPlacement?: TaskPlacement,
  ) {
    setNotice(null);
    setScheduleDialogError(null);
    setTaskScheduleIdempotencyKey(crypto.randomUUID());
    setSchedulePlacement(
      targetPlacement ?? (item.task.planned_local_date === null ? "backlog" : "scheduled"),
    );
    setCycleActionIntent(actionIntent ?? null);
    setEditingScheduleTask(item);
  }

  function closeTaskScheduleEditor() {
    if (reschedulingTask) return;
    setScheduleDialogError(null);
    setCycleActionIntent(null);
    setEditingScheduleTask(null);
  }

  function openTaskEditor(item: TodayTask) {
    setNotice(null);
    setTaskEditError(null);
    setEditingTask(item);
  }

  function closeTaskEditor() {
    if (updatingTask) return;
    setTaskEditError(null);
    setEditingTask(null);
  }

  function openInboxCreator() {
    setNotice(null);
    setInboxDialogError(null);
    setInboxCreateOpen(true);
  }

  function closeInboxCreator() {
    if (creatingInboxItem) return;
    setInboxDialogError(null);
    setInboxCreateOpen(false);
  }

  function openInboxPlanner(item: InboxItem) {
    if (selectedChildId === null) {
      setError("計画先の子供を先に追加してください。");
      return;
    }
    setNotice(null);
    setInboxPlanError(null);
    setPlanningInboxItem(item);
  }

  function closeInboxPlanner() {
    if (planningInboxBusy) return;
    setInboxPlanError(null);
    setPlanningInboxItem(null);
  }

  function openInboxCancellation(item: InboxItem) {
    setNotice(null);
    setCancellingInboxItem(item);
  }

  function closeInboxCancellation() {
    if (cancellingInboxBusy) return;
    setCancellingInboxItem(null);
  }

  function openChildCreator() {
    setError(null);
    setNotice(null);
    setChildDialogChild(null);
    setEnrollmentResult(null);
    setEnrollmentQrCode(null);
    setCopyNotice(null);
    childCreateFormRef.current?.reset();
    setChildDialogStep("create");
  }

  function openEnrollmentCreator(child: ChildProfile) {
    setError(null);
    setNotice(null);
    setChildDialogChild(child);
    setEnrollmentResult(null);
    setEnrollmentQrCode(null);
    setCopyNotice(null);
    setChildDialogStep("enroll");
  }

  function closeChildDialog() {
    if (creatingChild || enrollmentBusyChildId !== null) return;
    setError(null);
    setChildDialogStep("closed");
    setChildDialogChild(null);
    setEnrollmentResult(null);
    setEnrollmentQrCode(null);
    setCopyNotice(null);
  }

  const navigateParent = useCallback((view: ParentView) => {
    if (view !== "today") {
      setSelectedParentTodayTaskId(null);
      setParentTodayDropTarget(null);
    }
    setActiveView(view);
    const nextHash = `#parent/${view}`;
    if (location.hash !== nextHash) window.history.pushState(null, "", nextHash);
  }, []);

  useEffect(() => {
    const syncViewFromHistory = () => {
      const view = readParentView();
      if (view !== "today") {
        setSelectedParentTodayTaskId(null);
        setParentTodayDropTarget(null);
      }
      setActiveView(view);
    };
    if (location.hash === "#parent/family") {
      window.history.replaceState(null, "", "#parent/settings");
    } else if (location.hash === "#parent/create") {
      window.history.replaceState(null, "", "#parent/plan");
    } else if (
      !/^#parent\/(today|dashboard|inbox|plan|routines|projects|organization|search|review|settings)$/.test(
        location.hash,
      )
    ) {
      window.history.replaceState(null, "", "#parent/today");
    }
    window.addEventListener("popstate", syncViewFromHistory);
    window.addEventListener("hashchange", syncViewFromHistory);
    return () => {
      window.removeEventListener("popstate", syncViewFromHistory);
      window.removeEventListener("hashchange", syncViewFromHistory);
    };
  }, []);

  useEffect(() => {
    if (isGuardian) return;
    const allowed =
      ((activeView === "today" || activeView === "plan") && canReadTasks) ||
      (activeView === "dashboard" && canReadInsights) ||
      (activeView === "routines" && canReadRoutines) ||
      (activeView === "review" && canReadReviews);
    if (allowed) return;
    const timeout = window.setTimeout(() => {
      navigateParent(
        canReadTasks
          ? "today"
          : canReadRoutines
            ? "routines"
            : canReadInsights
              ? "dashboard"
              : canReadReviews
                ? "review"
                : "today",
      );
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [
    activeView,
    canReadInsights,
    canReadReviews,
    canReadRoutines,
    canReadTasks,
    isGuardian,
    navigateParent,
  ]);

  const reload = useCallback(async () => {
    setFamilyLoading(true);
    setRoutinesLoading(true);
    setCyclesLoading(true);
    try {
      if (session.actor.membership_role === "supporter") {
        const capabilities = new Set(
          session.actor.membership_scopes.map((scope) => scope.capability),
        );
        const [childPage, routinePage, cyclePage] = await Promise.all([
          api<{ items: ChildProfile[] }>(
            `/api/v1/workspaces/${workspaceId}/children`,
            undefined,
            workspaceId,
          ),
          capabilities.has("routines:read")
            ? api<{ items: Routine[] }>(
                `/api/v1/workspaces/${workspaceId}/routines`,
                undefined,
                workspaceId,
              )
            : Promise.resolve({ items: [] as Routine[] }),
          capabilities.has("reviews:read")
            ? api<{ items: FamilyCycle[] }>(
                `/api/v1/workspaces/${workspaceId}/cycles?include_closed=true`,
                undefined,
                workspaceId,
              )
            : Promise.resolve({ items: [] as FamilyCycle[] }),
        ]);
        setChildren(childPage.items);
        setRoutines(routinePage.items);
        setCycles(cyclePage.items);
        setDevices([]);
        setProjects([]);
        setRoutineTemplates([]);
        setRoutineReconciliations([]);
        setWorkspaceSettings(null);
        setMemberships([]);
        setMembershipInvitations([]);
        setPasskeys([]);
        setAgentGrants([]);
        setWorkspaceExports([]);
        setAreas([]);
        setTerms([]);
        setWorkspaceCalendars([]);
        setNotifications([]);
        setNotificationPreferences([]);
        setNotificationsLoading(false);
        setFamilyViewNow(Date.now());
        setSelectedChildId((current) => {
          const stored = localStorage.getItem(selectedChildStorageKey(workspaceId));
          const preferred = current ?? stored;
          const activeChildren = childPage.items.filter((child) => child.archived_at === null);
          return activeChildren.some((child) => child.id === preferred)
            ? preferred
            : (activeChildren[0]?.id ?? null);
        });
        return;
      }
      const [
        childPage,
        devicePage,
        projectPage,
        routinePage,
        routineTemplatePage,
        routineReconciliationPage,
        workspaceResponse,
        membershipPage,
        membershipInvitationPage,
        passkeyPage,
        agentGrantPage,
        areaPage,
        termPage,
        calendarPage,
        cyclePage,
        notificationPage,
        notificationPreferencePage,
        exportPage,
      ] = await Promise.all([
        api<{ items: ChildProfile[] }>(
          `/api/v1/workspaces/${workspaceId}/children`,
          undefined,
          workspaceId,
        ),
        api<{ items: Device[] }>(
          `/api/v1/workspaces/${workspaceId}/devices`,
          undefined,
          workspaceId,
        ),
        api<{ items: Project[] }>(
          `/api/v1/workspaces/${workspaceId}/projects?include_archived=true`,
          undefined,
          workspaceId,
        ),
        api<{ items: Routine[] }>(
          `/api/v1/workspaces/${workspaceId}/routines`,
          undefined,
          workspaceId,
        ),
        api<{ items: RoutineTemplate[] }>(
          `/api/v1/workspaces/${workspaceId}/routine-templates?include_archived=true`,
          undefined,
          workspaceId,
        ),
        api<{ items: RoutineReconciliationRun[] }>(
          `/api/v1/workspaces/${workspaceId}/routines/reconciliations`,
          undefined,
          workspaceId,
        ),
        api<{ workspace: WorkspaceSettings }>(
          `/api/v1/workspaces/${workspaceId}`,
          undefined,
          workspaceId,
        ),
        api<{ items: WorkspaceMembership[] }>(
          `/api/v1/workspaces/${workspaceId}/memberships`,
          undefined,
          workspaceId,
        ),
        api<{ items: MembershipInvitation[] }>(
          `/api/v1/workspaces/${workspaceId}/membership-invitations`,
          undefined,
          workspaceId,
        ),
        api<{ items: PasskeyCredential[] }>(
          `/api/v1/workspaces/${workspaceId}/passkeys`,
          undefined,
          workspaceId,
        ),
        api<{ items: AgentGrant[] }>(
          `/api/v1/workspaces/${workspaceId}/mcp/grants`,
          undefined,
          workspaceId,
        ),
        api<{ items: Area[] }>(
          `/api/v1/workspaces/${workspaceId}/areas?include_archived=true`,
          undefined,
          workspaceId,
        ),
        api<{ items: Term[] }>(
          `/api/v1/workspaces/${workspaceId}/terms?include_archived=true`,
          undefined,
          workspaceId,
        ),
        api<{ items: WorkspaceCalendar[] }>(
          `/api/v1/workspaces/${workspaceId}/calendars?include_archived=true`,
          undefined,
          workspaceId,
        ),
        api<{ items: FamilyCycle[] }>(
          `/api/v1/workspaces/${workspaceId}/cycles?include_closed=true`,
          undefined,
          workspaceId,
        ),
        api<{ items: ParentNotification[] }>(
          `/api/v1/workspaces/${workspaceId}/notifications?limit=50`,
          undefined,
          workspaceId,
        ),
        api<{ items: NotificationPreference[] }>(
          `/api/v1/workspaces/${workspaceId}/notification-preferences`,
          undefined,
          workspaceId,
        ),
        api<{ items: WorkspaceExport[] }>(
          `/api/v1/workspaces/${workspaceId}/exports`,
          undefined,
          workspaceId,
        ),
      ]);
      setChildren(childPage.items);
      setDevices(devicePage.items);
      setProjects(projectPage.items);
      setRoutines(routinePage.items);
      setRoutineTemplates(routineTemplatePage.items);
      setRoutineReconciliations(routineReconciliationPage.items);
      setWorkspaceSettings(workspaceResponse.workspace);
      setMemberships(membershipPage.items);
      setMembershipInvitations(membershipInvitationPage.items);
      setPasskeys(passkeyPage.items);
      setAgentGrants(agentGrantPage.items);
      setAreas(areaPage.items);
      setTerms(termPage.items);
      setWorkspaceCalendars(calendarPage.items);
      setCycles(cyclePage.items);
      setNotifications(notificationPage.items);
      setNotificationPreferences(notificationPreferencePage.items);
      setWorkspaceExports(exportPage.items);
      setNotificationsLoading(false);
      setFamilyViewNow(Date.now());
      setSelectedChildId((current) => {
        const stored = localStorage.getItem(selectedChildStorageKey(workspaceId));
        const preferred = current ?? stored;
        const activeChildren = childPage.items.filter((child) => child.archived_at === null);
        return activeChildren.some((child) => child.id === preferred)
          ? preferred
          : (activeChildren[0]?.id ?? null);
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "家族の情報を読み込めませんでした。");
    } finally {
      setFamilyLoading(false);
      setRoutinesLoading(false);
      setCyclesLoading(false);
      setNotificationsLoading(false);
    }
  }, [session.actor.membership_role, session.actor.membership_scopes, workspaceId]);

  useEffect(() => {
    void Promise.resolve().then(reload);
  }, [reload]);

  const reloadNotifications = useCallback(async (): Promise<ParentNotification[]> => {
    setNotificationsLoading(true);
    try {
      const response = await api<{ items: ParentNotification[] }>(
        `/api/v1/workspaces/${workspaceId}/notifications?limit=50`,
        undefined,
        workspaceId,
      );
      setNotifications(response.items);
      return response.items;
    } finally {
      setNotificationsLoading(false);
    }
  }, [workspaceId]);

  const reloadNotificationPreferences = useCallback(async (): Promise<NotificationPreference[]> => {
    const response = await api<{ items: NotificationPreference[] }>(
      `/api/v1/workspaces/${workspaceId}/notification-preferences`,
      undefined,
      workspaceId,
    );
    setNotificationPreferences(response.items);
    return response.items;
  }, [workspaceId]);

  const reloadWorkspaceExports = useCallback(async (): Promise<WorkspaceExport[]> => {
    const response = await api<{ items: WorkspaceExport[] }>(
      `/api/v1/workspaces/${workspaceId}/exports`,
      undefined,
      workspaceId,
    );
    setWorkspaceExports(response.items);
    return response.items;
  }, [workspaceId]);

  useEffect(() => {
    if (
      !isGuardian ||
      activeView !== "settings" ||
      !workspaceExports.some((item) => ["queued", "running", "deleting"].includes(item.status))
    ) {
      return;
    }
    const interval = window.setInterval(() => {
      void reloadWorkspaceExports().catch(() => undefined);
    }, 2_000);
    return () => window.clearInterval(interval);
  }, [activeView, isGuardian, reloadWorkspaceExports, workspaceExports]);

  function openWorkspaceExport(scope: "workspace" | "child") {
    setExportError(null);
    setExportScope(scope);
    setExportIncludeMedia(true);
    setExportDialogOpen(true);
  }

  function closeWorkspaceExport() {
    if (exportBusy === "create") return;
    setExportError(null);
    setExportDialogOpen(false);
  }

  async function createWorkspaceExport() {
    if (exportBusy !== null) return;
    if (exportScope === "child" && selectedChildId === null) {
      setExportError("先に画面上部で子供を選んでください。");
      return;
    }
    setExportBusy("create");
    setExportError(null);
    try {
      const response = await api<{ export: WorkspaceExport }>(
        `/api/v1/workspaces/${workspaceId}/export`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            scope: exportScope,
            child_id: exportScope === "child" ? selectedChildId : null,
            include_media: exportIncludeMedia,
            confirmation: "create_portable_export",
          }),
        },
        workspaceId,
      );
      setWorkspaceExports((current) => [
        response.export,
        ...current.filter((item) => item.id !== response.export.id),
      ]);
      setExportDialogOpen(false);
      setNotice("書き出しを開始しました。完成するとここからダウンロードできます。");
    } catch (caught) {
      setExportError(workspaceExportRequestError(caught, "書き出しを開始できませんでした。"));
    } finally {
      setExportBusy(null);
    }
  }

  async function downloadWorkspaceExport(item: WorkspaceExport) {
    if (exportBusy !== null) return;
    setExportBusy(item.id);
    setExportError(null);
    try {
      const response = await fetch(
        `/api/v1/workspaces/${workspaceId}/exports/${item.id}/download`,
        { credentials: "same-origin", headers: { "X-Workspace-Id": workspaceId } },
      );
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as ApiErrorEnvelope;
        throw new ApiRequestError(response.status, body);
      }
      const objectUrl = URL.createObjectURL(await response.blob());
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = `oyako-flow-${item.scope}-${item.requested_at.slice(0, 10)}.zip`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
      setNotice("書き出しファイルをダウンロードしました。");
    } catch (caught) {
      setError(workspaceExportRequestError(caught, "書き出しをダウンロードできませんでした。"));
    } finally {
      setExportBusy(null);
    }
  }

  async function deleteWorkspaceExport() {
    const item = exportDeleteTarget;
    if (item === null || exportBusy !== null) return;
    setExportBusy(item.id);
    setExportError(null);
    try {
      const response = await api<{ export: WorkspaceExport }>(
        `/api/v1/workspaces/${workspaceId}/exports/${item.id}/commands/delete`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            expected_version: item.version,
            confirmation: "delete_export_artifact",
          }),
        },
        workspaceId,
      );
      setWorkspaceExports((current) =>
        current.map((currentItem) =>
          currentItem.id === response.export.id ? response.export : currentItem,
        ),
      );
      setExportDeleteTarget(null);
      setNotice("書き出しファイルの削除を開始しました。");
    } catch (caught) {
      setExportError(workspaceExportRequestError(caught, "書き出しを削除できませんでした。"));
    } finally {
      setExportBusy(null);
    }
  }

  function openNotificationPreference(preference: NotificationPreference | null = null) {
    setNotificationPreferenceError(null);
    setNotificationPreferenceDraft(
      preference === null
        ? {
            current: null,
            recipient_type: "membership",
            child_id: null,
            related_child_id: null,
            routine_id: null,
            channel: "in_app",
            event_types: [...guardianNotificationEvents],
            quiet_hours_behavior: "defer",
            batching_mode: "immediate",
            enabled: true,
            active_weekdays: [0, 1, 2, 3, 4, 5, 6],
            active_from_local_time: "",
            active_until_local_time: "",
            digest_local_time: "19:30",
          }
        : {
            current: preference,
            recipient_type: preference.recipient_type,
            child_id: preference.child_id,
            related_child_id: preference.related_child_id,
            routine_id: preference.routine_id,
            channel: preference.channel,
            event_types: preference.event_types,
            quiet_hours_behavior: preference.quiet_hours_behavior,
            batching_mode: preference.batching_mode,
            enabled: preference.enabled,
            active_weekdays: preference.active_weekdays,
            active_from_local_time: preference.active_from_local_time ?? "",
            active_until_local_time: preference.active_until_local_time ?? "",
            digest_local_time: preference.digest_local_time ?? "19:30",
          },
    );
  }

  async function saveNotificationPreference(event: FormSubmitEvent) {
    event.preventDefault();
    const draft = notificationPreferenceDraft;
    if (draft === null || notificationPreferenceBusy) return;
    setNotificationPreferenceError(null);
    if (draft.event_types.length === 0) {
      setNotificationPreferenceError("通知する出来事を1つ以上選んでください。");
      return;
    }
    if (
      (draft.active_from_local_time.length === 0) !==
      (draft.active_until_local_time.length === 0)
    ) {
      setNotificationPreferenceError("通知する時間帯は開始と終了を両方入力してください。");
      return;
    }
    setNotificationPreferenceBusy(true);
    try {
      const response = await api<{ preference: NotificationPreference }>(
        `/api/v1/workspaces/${workspaceId}/notification-preferences`,
        {
          method: "PUT",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            expected_version: draft.current?.version ?? null,
            recipient:
              draft.recipient_type === "membership"
                ? { type: "membership" }
                : { type: "child", child_id: draft.child_id },
            related_child_id: draft.recipient_type === "membership" ? draft.related_child_id : null,
            routine_id: draft.routine_id,
            channel: draft.channel,
            event_types: draft.event_types,
            quiet_hours_behavior: draft.quiet_hours_behavior,
            batching_mode: draft.recipient_type === "child" ? "immediate" : draft.batching_mode,
            enabled: draft.enabled,
            active_weekdays: draft.active_weekdays,
            active_from_local_time: draft.active_from_local_time || null,
            active_until_local_time: draft.active_until_local_time || null,
            digest_local_time:
              draft.recipient_type === "membership" && draft.batching_mode === "digest"
                ? draft.digest_local_time
                : null,
          }),
        },
        workspaceId,
      );
      setNotificationPreferences((current) => [
        response.preference,
        ...current.filter((item) => item.id !== response.preference.id),
      ]);
      setNotificationPreferenceDraft(null);
      setNotice("通知設定を保存しました。次回の通知から反映されます。");
    } catch (caught) {
      setNotificationPreferenceError(
        caught instanceof Error ? caught.message : "通知設定を保存できませんでした。",
      );
    } finally {
      setNotificationPreferenceBusy(false);
    }
  }

  useEffect(() => {
    const refresh = window.setInterval(() => {
      void reloadNotifications().catch(() => undefined);
    }, 60_000);
    return () => window.clearInterval(refresh);
  }, [reloadNotifications]);

  async function commandNotification(
    notification: ParentNotification,
    command: "read" | "unread" | "snooze",
    snoozedUntil?: number,
  ): Promise<ParentNotification | null> {
    if (notificationBusyId !== null) return null;
    setNotificationBusyId(notification.id);
    setError(null);
    try {
      const response = await api<{ notification: ParentNotification }>(
        `/api/v1/workspaces/${workspaceId}/notifications/${notification.id}/commands/${command}`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            expected_version: notification.version,
            ...(command === "snooze" ? { snoozed_until: snoozedUntil } : {}),
          }),
        },
        workspaceId,
      );
      setNotifications((current) =>
        command === "snooze"
          ? current.filter((item) => item.id !== notification.id)
          : current.map((item) =>
              item.id === response.notification.id ? response.notification : item,
            ),
      );
      return response.notification;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "通知を更新できませんでした。");
      return null;
    } finally {
      setNotificationBusyId(null);
    }
  }

  async function openNotificationDestination(notification: ParentNotification) {
    const updated =
      notification.read_at === null
        ? await commandNotification(notification, "read")
        : notification;
    if (updated === null) return;
    if (updated.deep_link.route === "parent_routines") navigateParent("routines");
    else if (updated.deep_link.route === "parent_review") navigateParent("review");
    else navigateParent("today");
    setNotificationCenterOpen(false);
  }

  useEffect(() => {
    if (selectedChildId === null) return;
    localStorage.setItem(selectedChildStorageKey(workspaceId), selectedChildId);
  }, [selectedChildId, workspaceId]);

  const reloadTasks = useCallback(
    async (mode: TaskCollectionRefreshMode = "background") => {
      const requestSequence = taskRequestSequence.current + 1;
      taskRequestSequence.current = requestSequence;
      if (mode === "replace") {
        setTasks([]);
        setTasksLoading(true);
      }
      if (selectedChildId === null || !canReadTasks) {
        setTasks([]);
        setTasksLoading(false);
        return;
      }
      try {
        const today = await api<{ items: TodayTask[] }>(
          `/api/v1/workspaces/${workspaceId}/today?child_id=${encodeURIComponent(selectedChildId)}`,
          undefined,
          workspaceId,
        );
        if (taskRequestSequence.current === requestSequence) setTasks(today.items);
      } catch (caught) {
        if (taskRequestSequence.current === requestSequence) {
          setError(caught instanceof Error ? caught.message : "Todayを読み込めませんでした。");
        }
      } finally {
        if (taskRequestSequence.current === requestSequence) setTasksLoading(false);
      }
    },
    [canReadTasks, selectedChildId, workspaceId],
  );

  useEffect(() => {
    void Promise.resolve().then(() => reloadTasks("replace"));
  }, [reloadTasks]);

  const reloadCarryoverTasks = useCallback(
    async (mode: TaskCollectionRefreshMode = "background") => {
      const requestSequence = carryoverTaskRequestSequence.current + 1;
      carryoverTaskRequestSequence.current = requestSequence;
      if (mode === "replace") {
        setCarryoverTasks([]);
        setCarryoverTasksLoading(true);
      }
      if (selectedChildId === null || !canReadTasks) {
        setCarryoverTasks([]);
        setCarryoverTasksLoading(false);
        return;
      }
      try {
        const items: CarryoverTask[] = [];
        let cursor: string | null = null;
        do {
          const parameters = new URLSearchParams({
            child_id: selectedChildId,
            local_date: workspaceLocalDate,
            limit: "100",
          });
          if (cursor !== null) parameters.set("cursor", cursor);
          const page = await api<{ items: CarryoverTask[]; next_cursor: string | null }>(
            `/api/v1/workspaces/${workspaceId}/tasks/carryovers?${parameters.toString()}`,
            undefined,
            workspaceId,
          );
          items.push(...page.items);
          cursor = page.next_cursor;
        } while (cursor !== null && items.length < 1_000);
        if (carryoverTaskRequestSequence.current === requestSequence) setCarryoverTasks(items);
      } catch (caught) {
        if (carryoverTaskRequestSequence.current === requestSequence) {
          setError(caught instanceof Error ? caught.message : "持ち越しを読み込めませんでした。");
        }
      } finally {
        if (carryoverTaskRequestSequence.current === requestSequence) {
          setCarryoverTasksLoading(false);
        }
      }
    },
    [canReadTasks, selectedChildId, workspaceId, workspaceLocalDate],
  );

  useEffect(() => {
    void Promise.resolve().then(() => reloadCarryoverTasks("replace"));
  }, [reloadCarryoverTasks]);

  const reloadPlanTasks = useCallback(
    async (mode: TaskCollectionRefreshMode = "background") => {
      const requestSequence = planTaskRequestSequence.current + 1;
      planTaskRequestSequence.current = requestSequence;
      if (mode === "replace") {
        setPlanTasks([]);
        setPlanTasksLoading(true);
      }
      if (selectedChildId === null || !canReadTasks) {
        setPlanTasks([]);
        setPlanTasksLoading(false);
        return;
      }
      const monthDays = planningMonth(planAnchorDate);
      const dateFrom =
        planDisplayMode === "week"
          ? startOfPlanningWeek(planAnchorDate)
          : (monthDays[0]?.localDate ?? planAnchorDate);
      const dateTo =
        planDisplayMode === "week"
          ? addLocalDays(dateFrom, 6)
          : (monthDays.at(-1)?.localDate ?? planAnchorDate);
      try {
        const items: ScheduledTask[] = [];
        let cursor: string | null = null;
        do {
          const parameters = new URLSearchParams({
            child_id: selectedChildId,
            date_from: dateFrom,
            date_to: dateTo,
            limit: "100",
          });
          if (cursor !== null) parameters.set("cursor", cursor);
          const page = await api<{ items: ScheduledTask[]; next_cursor: string | null }>(
            `/api/v1/workspaces/${workspaceId}/tasks?${parameters.toString()}`,
            undefined,
            workspaceId,
          );
          items.push(...page.items);
          cursor = page.next_cursor;
        } while (cursor !== null && items.length < 1_000);
        if (requestSequence === planTaskRequestSequence.current) setPlanTasks(items);
      } catch (caught) {
        if (requestSequence === planTaskRequestSequence.current) {
          setError(caught instanceof Error ? caught.message : "計画を読み込めませんでした。");
        }
      } finally {
        if (requestSequence === planTaskRequestSequence.current) setPlanTasksLoading(false);
      }
    },
    [canReadTasks, planAnchorDate, planDisplayMode, selectedChildId, workspaceId],
  );

  useEffect(() => {
    void Promise.resolve().then(() => reloadPlanTasks("replace"));
  }, [reloadPlanTasks]);

  const reloadBacklogTasks = useCallback(
    async (mode: TaskCollectionRefreshMode = "background") => {
      const requestSequence = backlogTaskRequestSequence.current + 1;
      backlogTaskRequestSequence.current = requestSequence;
      if (mode === "replace") {
        setBacklogTasks([]);
        setBacklogTasksLoading(true);
      }
      if (selectedChildId === null || !canReadTasks) {
        setBacklogTasks([]);
        setBacklogTasksLoading(false);
        return;
      }
      try {
        const items: TodayTask[] = [];
        let cursor: string | null = null;
        do {
          const parameters = new URLSearchParams({ child_id: selectedChildId, limit: "100" });
          if (cursor !== null) parameters.set("cursor", cursor);
          const page = await api<{ items: TodayTask[]; next_cursor: string | null }>(
            `/api/v1/workspaces/${workspaceId}/tasks/backlog?${parameters.toString()}`,
            undefined,
            workspaceId,
          );
          items.push(...page.items);
          cursor = page.next_cursor;
        } while (cursor !== null && items.length < 1_000);
        if (requestSequence === backlogTaskRequestSequence.current) setBacklogTasks(items);
      } catch (caught) {
        if (requestSequence === backlogTaskRequestSequence.current) {
          setError(caught instanceof Error ? caught.message : "Backlogを読み込めませんでした。");
        }
      } finally {
        if (requestSequence === backlogTaskRequestSequence.current) setBacklogTasksLoading(false);
      }
    },
    [canReadTasks, selectedChildId, workspaceId],
  );

  useEffect(() => {
    void Promise.resolve().then(() => reloadBacklogTasks("replace"));
  }, [reloadBacklogTasks]);

  const refreshTaskSurfaces = useCallback(() => {
    void Promise.all([
      reloadTasks(),
      reloadCarryoverTasks(),
      reloadPlanTasks(),
      reloadBacklogTasks(),
    ]);
  }, [reloadBacklogTasks, reloadCarryoverTasks, reloadPlanTasks, reloadTasks]);

  const applyAuthoritativeTask = useCallback((replacement: TodayTask) => {
    setTasks((current) => replaceTaskCollectionItem(current, replacement));
    setCarryoverTasks((current) => replaceTaskCollectionItem(current, replacement));
    setPlanTasks((current) => replaceTaskCollectionItem(current, replacement));
    setBacklogTasks((current) => replaceTaskCollectionItem(current, replacement));
  }, []);

  const applyScheduledTask = useCallback(
    (replacement: TodayTask, plannedLocalDate: string | null) => {
      const excludedFromTodayAndPlan = ["cancelled", "skipped"].includes(
        replacement.assignment.state,
      );
      const excludedFromCarryover = [
        "done",
        "verified",
        "deferred",
        "skipped",
        "cancelled",
      ].includes(replacement.assignment.state);
      const excludedFromBacklog = ["done", "verified", "skipped", "cancelled"].includes(
        replacement.assignment.state,
      );
      const monthDays = planningMonth(planAnchorDate);
      const planDateFrom =
        planDisplayMode === "week"
          ? startOfPlanningWeek(planAnchorDate)
          : (monthDays[0]?.localDate ?? planAnchorDate);
      const planDateTo =
        planDisplayMode === "week"
          ? addLocalDays(planDateFrom, 6)
          : (monthDays.at(-1)?.localDate ?? planAnchorDate);

      setTasks((current) => {
        const withoutTask = current.filter((item) => item.task.id !== replacement.task.id);
        return plannedLocalDate === workspaceLocalDate && !excludedFromTodayAndPlan
          ? [...withoutTask, replacement]
          : withoutTask;
      });
      setCarryoverTasks((current) => {
        const withoutTask = current.filter((item) => item.task.id !== replacement.task.id);
        return plannedLocalDate !== null &&
          plannedLocalDate < workspaceLocalDate &&
          !excludedFromCarryover
          ? [...withoutTask, { ...replacement, missed_planned_local_date: plannedLocalDate }]
          : withoutTask;
      });
      setPlanTasks((current) => {
        const withoutTask = current.filter((item) => item.task.id !== replacement.task.id);
        return plannedLocalDate !== null &&
          plannedLocalDate >= planDateFrom &&
          plannedLocalDate <= planDateTo &&
          !excludedFromTodayAndPlan
          ? [...withoutTask, { ...replacement, schedule_local_date: plannedLocalDate }]
          : withoutTask;
      });
      setBacklogTasks((current) => {
        const withoutTask = current.filter((item) => item.task.id !== replacement.task.id);
        return plannedLocalDate === null && !excludedFromBacklog
          ? [replacement, ...withoutTask]
          : withoutTask;
      });
    },
    [planAnchorDate, planDisplayMode, workspaceLocalDate],
  );

  const reloadInbox = useCallback(async () => {
    if (!isGuardian) {
      setInboxItems([]);
      setInboxLoading(false);
      return;
    }
    setInboxLoading(true);
    try {
      const response = await api<{ items: InboxItem[] }>(
        `/api/v1/workspaces/${workspaceId}/inbox-items?status=untriaged`,
        undefined,
        workspaceId,
      );
      setInboxItems(response.items);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Inboxを読み込めませんでした。");
    } finally {
      setInboxLoading(false);
    }
  }, [isGuardian, workspaceId]);

  useEffect(() => {
    void Promise.resolve().then(reloadInbox);
  }, [reloadInbox]);

  async function createChild(event: FormSubmitEvent) {
    event.preventDefault();
    if (creatingChild) return;
    setCreatingChild(true);
    setError(null);
    const form = event.currentTarget;
    const fields = new FormData(form);
    try {
      const result = await api<{ child: ChildProfile }>(
        `/api/v1/workspaces/${workspaceId}/children`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            display_name: fields.get("display_name"),
            theme_color: fields.get("theme_color"),
            reading_level: fields.get("reading_level"),
            display_density: fields.get("display_density"),
          }),
        },
        workspaceId,
      );
      form.reset();
      await reload();
      selectChild(result.child.id);
      setChildDialogChild(result.child);
      setChildDialogStep("created");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "子供を追加できませんでした。");
    } finally {
      setCreatingChild(false);
    }
  }

  async function createTask(event: FormSubmitEvent) {
    event.preventDefault();
    if (selectedChildId === null || creatingTask) return;
    setCreatingTask(true);
    setTaskDialogError(null);
    setNotice(null);
    const form = event.currentTarget;
    const fields = new FormData(form);
    const plannedLocalDate =
      taskCreatePlacement === "backlog" ? null : formString(fields, "planned_local_date");
    const dueLocalDate = formString(fields, "due_local_date") || null;
    const availableFromLocalTime = formString(fields, "available_from_local_time") || null;
    const availableUntilLocalTime = formString(fields, "available_until_local_time") || null;
    if (plannedLocalDate !== null && dueLocalDate !== null && dueLocalDate < plannedLocalDate) {
      setTaskDialogError("期限は、やる日と同じ日か後の日を選んでください。");
      setCreatingTask(false);
      return;
    }
    if (
      (availableFromLocalTime === null) !== (availableUntilLocalTime === null) ||
      (availableFromLocalTime !== null &&
        availableUntilLocalTime !== null &&
        availableUntilLocalTime <= availableFromLocalTime)
    ) {
      setTaskDialogError("時間帯は、開始と終了を両方選び、終了を開始より後にしてください。");
      setCreatingTask(false);
      return;
    }
    const completionPolicy = formString(fields, "completion_policy", "none");
    const evidenceRequired =
      completionPolicy === "evidence_photo"
        ? "photo"
        : completionPolicy === "evidence_note"
          ? "note"
          : completionPolicy === "evidence_photo_or_note"
            ? "photo_or_note"
            : "none";
    const verificationPolicy = evidenceRequired === "none" ? completionPolicy : "evidence_required";
    try {
      const response = await api<{ item: TodayTask; request_id: string }>(
        `/api/v1/workspaces/${workspaceId}/tasks`,
        {
          method: "POST",
          headers: { "Idempotency-Key": taskCreateIdempotencyKey },
          body: JSON.stringify({
            child_id: selectedChildId,
            project_id: formString(fields, "project_id") || null,
            title: formString(fields, "title"),
            child_copy: formString(fields, "child_copy"),
            planned_local_date: plannedLocalDate,
            due_local_date: dueLocalDate,
            available_from_local_time: availableFromLocalTime,
            available_until_local_time: availableUntilLocalTime,
            today_pinned: fields.get("today_pinned") === "on",
            estimated_minutes: Number(formString(fields, "estimated_minutes", "0")),
            priority: 0,
            verification_policy: verificationPolicy,
            evidence_policy: { required: evidenceRequired },
          }),
        },
        workspaceId,
      );
      form.reset();
      setDraftProjectId("");
      setTaskCreateOpen(false);
      applyScheduledTask(response.item, plannedLocalDate);
      const intent = cycleActionIntent?.kind === "create_task" ? cycleActionIntent : null;
      if (intent === null) {
        setNotice(
          taskCreateContext === "today"
            ? "今日のやることに追加しました。"
            : taskCreatePlacement === "backlog"
              ? "タスクをBacklogに追加しました。"
              : "タスクを計画に追加しました。",
        );
      } else {
        await linkCycleAction(intent, response.request_id, "task", response.item.task.id);
      }
      refreshTaskSurfaces();
    } catch (caught) {
      setTaskDialogError(
        caught instanceof Error ? caught.message : "タスクを追加できませんでした。",
      );
    } finally {
      setCreatingTask(false);
    }
  }

  async function rescheduleTask(event: FormSubmitEvent) {
    event.preventDefault();
    if (editingScheduleTask === null || reschedulingTask) return;
    setReschedulingTask(true);
    setScheduleDialogError(null);
    setNotice(null);
    const fields = new FormData(event.currentTarget);
    const plannedLocalDate =
      schedulePlacement === "backlog" ? null : formString(fields, "planned_local_date");
    const dueLocalDate = formString(fields, "due_local_date") || null;
    const availableFromLocalTime = formString(fields, "available_from_local_time") || null;
    const availableUntilLocalTime = formString(fields, "available_until_local_time") || null;
    if (plannedLocalDate !== null && dueLocalDate !== null && dueLocalDate < plannedLocalDate) {
      setScheduleDialogError("期限は、やる日と同じ日か後の日を選んでください。");
      setReschedulingTask(false);
      return;
    }
    if (
      (availableFromLocalTime === null) !== (availableUntilLocalTime === null) ||
      (availableFromLocalTime !== null &&
        availableUntilLocalTime !== null &&
        availableUntilLocalTime <= availableFromLocalTime)
    ) {
      setScheduleDialogError("時間帯は、開始と終了を両方選び、終了を開始より後にしてください。");
      setReschedulingTask(false);
      return;
    }
    try {
      const response = await api<{ item: TodayTask; request_id: string }>(
        `/api/v1/workspaces/${workspaceId}/tasks/${editingScheduleTask.task.id}/schedule`,
        {
          method: "PATCH",
          headers: { "Idempotency-Key": taskScheduleIdempotencyKey },
          body: JSON.stringify({
            assignment_id: editingScheduleTask.assignment.id,
            expected_version: editingScheduleTask.task.version,
            planned_local_date: plannedLocalDate,
            due_local_date: dueLocalDate,
            available_from_local_time: availableFromLocalTime,
            available_until_local_time: availableUntilLocalTime,
            today_pinned: fields.get("today_pinned") === "on",
          }),
        },
        workspaceId,
      );
      const targetId = editingScheduleTask.task.id;
      applyScheduledTask(response.item, plannedLocalDate);
      const intent = cycleActionIntent?.kind === "reschedule_task" ? cycleActionIntent : null;
      setEditingScheduleTask(null);
      if (intent === null)
        setNotice(
          schedulePlacement === "backlog"
            ? "タスクをBacklogへ戻しました。"
            : "タスクの日程を変更しました。",
        );
      else await linkCycleAction(intent, response.request_id, "task", targetId);
      refreshTaskSurfaces();
    } catch (caught) {
      setScheduleDialogError(
        caught instanceof Error ? caught.message : "タスクの日程を変更できませんでした。",
      );
    } finally {
      setReschedulingTask(false);
    }
  }

  async function updateTask(event: FormSubmitEvent) {
    event.preventDefault();
    if (editingTask === null || updatingTask) return;
    setUpdatingTask(true);
    setTaskEditError(null);
    setNotice(null);
    const fields = new FormData(event.currentTarget);
    const estimatedMinutes = Number(formString(fields, "estimated_minutes", "0"));
    try {
      const response = await api<{ item: TodayTask; request_id: string }>(
        `/api/v1/workspaces/${workspaceId}/tasks/${editingTask.task.id}`,
        {
          method: "PATCH",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            assignment_id: editingTask.assignment.id,
            expected_version: editingTask.task.version,
            title: formString(fields, "title"),
            child_copy: formString(fields, "child_copy"),
            estimated_minutes: estimatedMinutes,
          }),
        },
        workspaceId,
      );
      applyAuthoritativeTask(response.item);
      setEditingTask(null);
      setNotice("タスクの内容を更新しました。");
      refreshTaskSurfaces();
    } catch (caught) {
      setTaskEditError(
        caught instanceof Error ? caught.message : "タスクの内容を更新できませんでした。",
      );
    } finally {
      setUpdatingTask(false);
    }
  }

  async function moveEditingTaskToBacklog() {
    if (editingTask === null || updatingTask || editingTask.task.planned_local_date === null)
      return;
    const item = editingTask;
    setUpdatingTask(true);
    setTaskEditError(null);
    setNotice(null);
    try {
      const response = await api<{ item: TodayTask; request_id: string }>(
        `/api/v1/workspaces/${workspaceId}/tasks/${item.task.id}/schedule`,
        {
          method: "PATCH",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            assignment_id: item.assignment.id,
            expected_version: item.task.version,
            planned_local_date: null,
            due_local_date: item.task.due_local_date,
            available_from_local_time: item.task.available_from_local_time,
            available_until_local_time: item.task.available_until_local_time,
            today_pinned: item.task.today_pinned,
          }),
        },
        workspaceId,
      );
      applyScheduledTask(response.item, null);
      setEditingTask(null);
      setNotice("タスクをBacklogへ戻しました。");
      refreshTaskSurfaces();
    } catch (caught) {
      setTaskEditError(
        caught instanceof Error ? caught.message : "タスクをBacklogへ戻せませんでした。",
      );
    } finally {
      setUpdatingTask(false);
    }
  }

  function canMovePlanTask(item: TodayTask): boolean {
    return (
      canWriteTasks &&
      !bulkPlanningMode &&
      dragMoveBusyTaskId === null &&
      !["done", "verified", "cancelled"].includes(item.task.status) &&
      !["done", "verified", "cancelled"].includes(item.assignment.state)
    );
  }

  function canEditPlanTask(item: TodayTask): boolean {
    return (
      canWriteTasks &&
      !planningTogether &&
      !bulkPlanningMode &&
      !["done", "verified", "cancelled"].includes(item.task.status)
    );
  }

  async function movePlanTaskToDate(item: TodayTask, plannedLocalDate: string) {
    if (!canMovePlanTask(item) || item.task.planned_local_date === plannedLocalDate) return;
    if (item.task.due_local_date !== null && item.task.due_local_date < plannedLocalDate) {
      setPlanInteractionError(
        `「${item.task.title}」は期限より後へ移動できません。期限を編集してから移動してください。`,
      );
      return;
    }
    setDragMoveBusyTaskId(item.task.id);
    setPlanInteractionError(null);
    setNotice(null);
    const optimisticItem: TodayTask = {
      ...item,
      task: { ...item.task, planned_local_date: plannedLocalDate },
    };
    applyScheduledTask(optimisticItem, plannedLocalDate);
    try {
      const response = await api<{ item: TodayTask; request_id: string }>(
        `/api/v1/workspaces/${workspaceId}/tasks/${item.task.id}/schedule`,
        {
          method: "PATCH",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            assignment_id: item.assignment.id,
            expected_version: item.task.version,
            planned_local_date: plannedLocalDate,
            due_local_date: item.task.due_local_date,
            available_from_local_time: item.task.available_from_local_time,
            available_until_local_time: item.task.available_until_local_time,
            today_pinned: item.task.today_pinned,
          }),
        },
        workspaceId,
      );
      applyScheduledTask(response.item, plannedLocalDate);
      setNotice(`「${item.task.title}」を${plannedLocalDate}へ移動しました。`);
      refreshTaskSurfaces();
    } catch (caught) {
      applyScheduledTask(item, item.task.planned_local_date);
      setPlanInteractionError(
        caught instanceof Error ? caught.message : "タスクの日程を移動できませんでした。",
      );
    } finally {
      setDragMoveBusyTaskId(null);
    }
  }

  function applyPlanDateOrder(localDate: string, ordered: TodayTask[]) {
    setPlanTasks((current) => {
      const scheduled = ordered.map((item) => ({ ...item, schedule_local_date: localDate }));
      const next: ScheduledTask[] = [];
      let inserted = false;
      for (const item of current) {
        if (item.schedule_local_date === localDate) {
          if (!inserted) {
            next.push(...scheduled);
            inserted = true;
          }
          continue;
        }
        next.push(item);
      }
      if (!inserted) next.push(...scheduled);
      return next;
    });
  }

  async function movePlanTaskToPosition(item: ScheduledTask, target: PlanTaskDropTarget) {
    if (!canMovePlanTask(item)) return;
    if (item.task.due_local_date !== null && item.task.due_local_date < target.localDate) {
      setPlanInteractionError(
        `「${item.task.title}」は期限より後へ移動できません。期限を編集してから移動してください。`,
      );
      return;
    }
    const previous = planTasks;
    let next = movePlanTaskToDrop(
      previous,
      item.task.id,
      target.localDate,
      target.taskId,
      target.placement,
    );
    if (next === null) return;

    const dateChanged = item.schedule_local_date !== target.localDate;
    let scheduleSaved = false;
    setDragMoveBusyTaskId(item.task.id);
    setPlanInteractionError(null);
    setNotice(null);
    setPlanTasks(next);
    try {
      let shouldRefresh = true;
      if (dateChanged) {
        const response = await api<{ item: TodayTask; request_id: string }>(
          `/api/v1/workspaces/${workspaceId}/tasks/${item.task.id}/schedule`,
          {
            method: "PATCH",
            headers: { "Idempotency-Key": crypto.randomUUID() },
            body: JSON.stringify({
              assignment_id: item.assignment.id,
              expected_version: item.task.version,
              planned_local_date: target.localDate,
              due_local_date: item.task.due_local_date,
              available_from_local_time: item.task.available_from_local_time,
              available_until_local_time: item.task.available_until_local_time,
              today_pinned: item.task.today_pinned,
            }),
          },
          workspaceId,
        );
        scheduleSaved = true;
        next = replaceTaskCollectionItem(next, {
          ...response.item,
          schedule_local_date: target.localDate,
        });
        setPlanTasks(next);
      }

      const targetItems = next.filter(
        (candidate) => candidate.schedule_local_date === target.localDate,
      );
      if (targetItems.length >= 2) {
        const response = await enqueueTodayTaskReorder(
          session,
          item.assignment.child_id,
          target.localDate,
          targetItems,
        );
        if (response === null) {
          setNotice("並び順をオフライン操作としてこの端末に保存しました。");
          shouldRefresh = false;
        } else {
          applyPlanDateOrder(target.localDate, response.items);
          setNotice(
            dateChanged
              ? `「${item.task.title}」を移動し、順番を更新しました。`
              : "同じ日の順番を更新しました。",
          );
        }
      } else {
        setNotice(`「${item.task.title}」を${target.localDate}へ移動しました。`);
      }
      if (shouldRefresh) refreshTaskSurfaces();
    } catch (caught) {
      if (!scheduleSaved) {
        setPlanTasks(previous);
        if (caught instanceof ApiRequestError && caught.code === "TASK_ORDER_DEPENDENCY_CONFLICT") {
          setPlanInteractionError("先に終えるタスクより前には移動できません。");
        } else if (
          caught instanceof ApiRequestError &&
          caught.code === "TASK_ORDER_PINNED_CONFLICT"
        ) {
          setPlanInteractionError("📌 固定したタスクは、通常タスクより後ろへ移動できません。");
        } else if (
          caught instanceof ApiRequestError &&
          caught.code === "TASK_ORDER_TIME_WINDOW_CONFLICT"
        ) {
          setPlanInteractionError("開始できる時間帯の順番に反する位置へは移動できません。");
        } else if (caught instanceof ApiRequestError && caught.status === 409) {
          setPlanInteractionError(
            "別の端末で順番またはタスクが変わりました。最新の計画を読み込みます。",
          );
          void reloadPlanTasks();
        } else {
          setPlanInteractionError(
            caught instanceof Error ? caught.message : "タスクの順番を更新できませんでした。",
          );
        }
      } else {
        void reloadPlanTasks();
        setPlanInteractionError(
          "日付の移動は保存されましたが、移動先の順番は別の変更と競合しました。最新の順番を読み込みます。",
        );
      }
    } finally {
      setDragMoveBusyTaskId(null);
    }
  }

  function handlePlanTaskKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>, item: TodayTask) {
    if (!canMovePlanTask(item)) return;
    if (event.shiftKey && event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      openTaskScheduleEditor(
        item,
        undefined,
        item.task.planned_local_date === null ? "scheduled" : undefined,
      );
      return;
    }
    const scheduled = planTasks.find((candidate) => candidate.task.id === item.task.id);
    if (scheduled === undefined) return;
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      const dayTasks = planTasks.filter(
        (candidate) => candidate.schedule_local_date === scheduled.schedule_local_date,
      );
      const currentIndex = dayTasks.findIndex((candidate) => candidate.task.id === item.task.id);
      const targetIndex = event.key === "ArrowUp" ? currentIndex - 1 : currentIndex + 1;
      const target = dayTasks[targetIndex];
      if (target === undefined) return;
      event.preventDefault();
      event.stopPropagation();
      void movePlanTaskToPosition(scheduled, {
        localDate: scheduled.schedule_local_date,
        taskId: target.task.id,
        placement: event.key === "ArrowUp" ? "before" : "after",
      });
      return;
    }
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    const dayIndex = visiblePlanDays.findIndex(
      (day) => day.localDate === scheduled.schedule_local_date,
    );
    const targetDay = visiblePlanDays[event.key === "ArrowLeft" ? dayIndex - 1 : dayIndex + 1];
    if (targetDay === undefined) return;
    event.preventDefault();
    event.stopPropagation();
    void movePlanTaskToPosition(scheduled, {
      localDate: targetDay.localDate,
      taskId: null,
      placement: null,
    });
  }

  async function moveCarryoverTaskToToday(item: CarryoverTask) {
    if (!canWriteTasks || dragMoveBusyTaskId !== null) return;
    if (item.task.due_local_date !== null && item.task.due_local_date < workspaceLocalDate) {
      openTaskScheduleEditor(item, undefined, "scheduled");
      setScheduleDialogError("期限も過ぎています。期限を今日以降へ見直してから保存してください。");
      return;
    }
    setDragMoveBusyTaskId(item.task.id);
    setPlanInteractionError(null);
    setNotice(null);
    const optimisticItem: TodayTask = {
      ...item,
      task: { ...item.task, planned_local_date: workspaceLocalDate },
    };
    applyScheduledTask(optimisticItem, workspaceLocalDate);
    try {
      const response = await api<{ item: TodayTask; request_id: string }>(
        `/api/v1/workspaces/${workspaceId}/tasks/${item.task.id}/schedule`,
        {
          method: "PATCH",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            assignment_id: item.assignment.id,
            expected_version: item.task.version,
            planned_local_date: workspaceLocalDate,
            due_local_date: item.task.due_local_date,
            available_from_local_time: item.task.available_from_local_time,
            available_until_local_time: item.task.available_until_local_time,
            today_pinned: item.task.today_pinned,
          }),
        },
        workspaceId,
      );
      applyScheduledTask(response.item, workspaceLocalDate);
      setNotice(`「${item.task.title}」を今日やるタスクにしました。`);
      refreshTaskSurfaces();
    } catch (caught) {
      applyScheduledTask(item, item.task.planned_local_date);
      setPlanInteractionError(
        caught instanceof Error ? caught.message : "今日の予定へ変更できませんでした。",
      );
    } finally {
      setDragMoveBusyTaskId(null);
    }
  }

  function planDropTargetAtPoint(clientX: number, clientY: number): PlanTaskDropTarget | null {
    const element = document.elementFromPoint(clientX, clientY);
    const day = element?.closest<HTMLElement>("[data-plan-drop-date]");
    const localDate = day?.dataset.planDropDate;
    if (localDate === undefined) return null;
    const card = element?.closest<HTMLElement>("[data-plan-task-id]");
    if (card?.dataset.planTaskDate !== localDate) {
      return { localDate, taskId: null, placement: null };
    }
    const bounds = card.getBoundingClientRect();
    return {
      localDate,
      taskId: card.dataset.planTaskId ?? null,
      placement: clientY >= bounds.top + bounds.height / 2 ? "after" : "before",
    };
  }

  function autoScrollPlanAtPointer(clientX: number, clientY: number) {
    const rail = document.querySelector<HTMLElement>(
      ".plan-workspace .plan-week, .plan-workspace .plan-month",
    );
    if (rail === null || rail.scrollWidth <= rail.clientWidth) return;
    const bounds = rail.getBoundingClientRect();
    if (clientY < bounds.top || clientY > bounds.bottom) return;
    const delta = planDragAutoScrollDelta(clientX, bounds.left, bounds.right);
    if (delta !== 0) rail.scrollLeft += delta;
  }

  function beginPlanPointerDrag(event: ReactPointerEvent<HTMLLIElement>, item: TodayTask) {
    if (
      !event.isPrimary ||
      (event.pointerType === "mouse" && event.button !== 0) ||
      !canMovePlanTask(item)
    ) {
      return;
    }
    const sourceCard = event.currentTarget;
    const bounds = sourceCard.getBoundingClientRect();
    event.currentTarget.setPointerCapture(event.pointerId);
    planPointerDragRef.current = {
      pointerId: event.pointerId,
      item,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - bounds.left,
      offsetY: event.clientY - bounds.top,
      active: false,
      handle: event.currentTarget,
      sourceCard,
      preview: null,
    };
  }

  function movePlanPointerDrag(event: ReactPointerEvent<HTMLLIElement>) {
    const drag = planPointerDragRef.current;
    if (drag?.pointerId !== event.pointerId) return;
    if (
      !drag.active &&
      !planPointerDragActivated(drag.startX, drag.startY, event.clientX, event.clientY)
    ) {
      return;
    }
    event.preventDefault();
    if (!drag.active) {
      drag.active = true;
      createPlanTaskDragPreview(drag.sourceCard, drag);
      drag.sourceCard.classList.add("plan-task-drag-source");
      document.body.classList.add("plan-task-dragging");
      const source = planTasks.find((candidate) => candidate.task.id === drag.item.task.id);
      setPlanDropTarget(
        source === undefined
          ? null
          : {
              localDate: source.schedule_local_date,
              taskId: source.task.id,
              placement: null,
            },
      );
      setPlanInteractionError(null);
      window.getSelection()?.removeAllRanges();
    }
    autoScrollPlanAtPointer(event.clientX, event.clientY);
    positionPlanTaskDragPreview(drag, event.clientX, event.clientY);
    setPlanDropTarget(planDropTargetAtPoint(event.clientX, event.clientY));
  }

  function finishPlanPointerDrag(event: ReactPointerEvent<HTMLLIElement>, cancelled = false) {
    const drag = planPointerDragRef.current;
    if (drag?.pointerId !== event.pointerId) return;
    planPointerDragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (!drag.active) {
      if (!cancelled) openPlanTaskFromCard(drag.item);
      return;
    }
    event.preventDefault();
    const target = cancelled ? null : planDropTargetAtPoint(event.clientX, event.clientY);
    removePlanTaskDragPreview(drag);
    setPlanDropTarget(null);
    suppressNextPlanDragClick();
    if (target !== null) {
      const scheduled = planTasks.find((candidate) => candidate.task.id === drag.item.task.id);
      if (scheduled === undefined) void movePlanTaskToDate(drag.item, target.localDate);
      else void movePlanTaskToPosition(scheduled, target);
    }
  }

  function suppressNextPlanDragClick() {
    suppressPlanDragClickRef.current = true;
    if (planDragClickResetTimerRef.current !== null) {
      window.clearTimeout(planDragClickResetTimerRef.current);
    }
    planDragClickResetTimerRef.current = window.setTimeout(() => {
      suppressPlanDragClickRef.current = false;
      planDragClickResetTimerRef.current = null;
    }, 750);
  }

  function cancelPlanPointerDrag() {
    const drag = planPointerDragRef.current;
    if (drag === null) return;
    planPointerDragRef.current = null;
    if (drag.handle.hasPointerCapture(drag.pointerId)) {
      drag.handle.releasePointerCapture(drag.pointerId);
    }
    removePlanTaskDragPreview(drag);
    setPlanDropTarget(null);
    if (drag.active) suppressNextPlanDragClick();
  }

  function openPlanTaskFromCard(item: TodayTask) {
    if (suppressPlanDragClickRef.current) {
      suppressPlanDragClickRef.current = false;
      if (planDragClickResetTimerRef.current !== null) {
        window.clearTimeout(planDragClickResetTimerRef.current);
        planDragClickResetTimerRef.current = null;
      }
      return;
    }
    if (canEditPlanTask(item)) {
      openTaskEditor(item);
    } else if (canMovePlanTask(item)) {
      openTaskScheduleEditor(
        item,
        undefined,
        item.task.planned_local_date === null ? "scheduled" : undefined,
      );
    }
  }

  function toggleBulkPlanningMode() {
    if (bulkPlanningBusy !== null) return;
    setBulkPlanningMode((current) => !current);
    setBulkSelectedTaskIds([]);
    setBulkPlanningError(null);
    setBulkPreview(null);
  }

  function selectChild(childId: string) {
    setSelectedParentTodayTaskId(null);
    setParentTodayDropTarget(null);
    setBulkPlanningMode(false);
    setBulkSelectedTaskIds([]);
    setBulkPlanningDialogOpen(false);
    setBulkPreview(null);
    setBulkPlanningError(null);
    setReturningTask(null);
    setTaskReturnError(null);
    setEditingTask(null);
    setTaskEditError(null);
    cancelPlanPointerDrag();
    setPlanInteractionError(null);
    setSelectedChildId(childId);
  }

  function toggleBulkTask(taskId: string) {
    setBulkPlanningError(null);
    setBulkSelectedTaskIds((current) => {
      if (current.includes(taskId)) return current.filter((id) => id !== taskId);
      if (current.length >= 20) {
        setBulkPlanningError("一度に変更できるタスクは20件までです。");
        return current;
      }
      return [...current, taskId];
    });
  }

  function openBulkPlanningDialog() {
    if (bulkSelectedTaskIds.length < 2) {
      setBulkPlanningError("まとめて変更するタスクを2件以上選んでください。");
      return;
    }
    setBulkPlanningError(null);
    setBulkPreview(null);
    setBulkPreviewIdempotencyKey(crypto.randomUUID());
    setBulkApplyIdempotencyKey(crypto.randomUUID());
    setBulkPlanningDialogOpen(true);
  }

  function closeBulkPlanningDialog() {
    if (bulkPlanningBusy !== null) return;
    setBulkPlanningError(null);
    setBulkPreview(null);
    setBulkPlanningDialogOpen(false);
  }

  async function previewBulkPlanning(event: FormSubmitEvent) {
    event.preventDefault();
    if (bulkPlanningBusy !== null || selectedChildId === null) return;
    const targetDate = formString(new FormData(event.currentTarget), "planned_local_date");
    const selected = bulkSelectedTaskIds
      .map((taskId) => planTasks.find((item) => item.task.id === taskId))
      .filter((item): item is ScheduledTask => item !== undefined);
    if (selected.length !== bulkSelectedTaskIds.length || selected.length < 2) {
      setBulkPlanningError("選択したタスクが変わりました。計画を再読み込みしてください。");
      return;
    }
    const invalidDeadline = selected.find(
      (item) => item.task.due_local_date !== null && item.task.due_local_date < targetDate,
    );
    if (invalidDeadline !== undefined) {
      setBulkPlanningError(
        `「${invalidDeadline.task.title}」の期限より後には移せません。期限は個別に見直してください。`,
      );
      return;
    }
    const unchanged = selected.find((item) => item.task.planned_local_date === targetDate);
    if (unchanged !== undefined) {
      setBulkPlanningError(
        `「${unchanged.task.title}」はすでにその日です。変更対象から外してください。`,
      );
      return;
    }
    setBulkPlanningBusy("preview");
    setBulkPlanningError(null);
    try {
      const response = await api<{ preview: TaskBulkReschedulePreview }>(
        `/api/v1/workspaces/${workspaceId}/tasks/reschedule-previews`,
        {
          method: "POST",
          headers: { "Idempotency-Key": bulkPreviewIdempotencyKey },
          body: JSON.stringify({
            changes: selected.map((item) => ({
              child_id: selectedChildId,
              task_id: item.task.id,
              assignment_id: item.assignment.id,
              expected_version: item.task.version,
              planned_local_date: targetDate,
              due_local_date: item.task.due_local_date,
            })),
          }),
        },
        workspaceId,
      );
      setBulkPreview(response.preview);
    } catch (caught) {
      setBulkPlanningError(
        caught instanceof Error ? caught.message : "一括変更の確認内容を作成できませんでした。",
      );
    } finally {
      setBulkPlanningBusy(null);
    }
  }

  async function applyBulkPlanning() {
    if (bulkPreview === null || bulkPlanningBusy !== null) return;
    setBulkPlanningBusy("apply");
    setBulkPlanningError(null);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/tasks/reschedule-previews/${bulkPreview.id}/apply`,
        {
          method: "POST",
          headers: { "Idempotency-Key": bulkApplyIdempotencyKey },
          body: JSON.stringify({
            expected_affected_count: bulkPreview.affected_count,
            confirmation: "apply_preview",
          }),
        },
        workspaceId,
      );
      setNotice(`${String(bulkPreview.affected_count)}件のやる日をまとめて変更しました。`);
      setBulkPlanningDialogOpen(false);
      setBulkPreview(null);
      setBulkPlanningMode(false);
      setBulkSelectedTaskIds([]);
      refreshTaskSurfaces();
    } catch (caught) {
      setBulkPlanningError(
        caught instanceof Error ? caught.message : "一括変更を確定できませんでした。",
      );
    } finally {
      setBulkPlanningBusy(null);
    }
  }

  async function createInboxItem(event: FormSubmitEvent) {
    event.preventDefault();
    if (creatingInboxItem) return;
    setCreatingInboxItem(true);
    setInboxDialogError(null);
    setNotice(null);
    const form = event.currentTarget;
    const fields = new FormData(form);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/inbox-items`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            title: formString(fields, "title"),
            notes: formString(fields, "notes"),
          }),
        },
        workspaceId,
      );
      form.reset();
      setInboxCreateOpen(false);
      setNotice("Inboxに保存しました。日付や子供はあとから決められます。");
      await reloadInbox();
    } catch (caught) {
      setInboxDialogError(
        caught instanceof Error ? caught.message : "Inboxに保存できませんでした。",
      );
    } finally {
      setCreatingInboxItem(false);
    }
  }

  async function planInboxItem(event: FormSubmitEvent) {
    event.preventDefault();
    if (planningInboxItem === null || selectedChildId === null || planningInboxBusy) return;
    setPlanningInboxBusy(true);
    setInboxPlanError(null);
    setNotice(null);
    const fields = new FormData(event.currentTarget);
    const plannedLocalDate = formString(fields, "planned_local_date");
    const dueLocalDate = formString(fields, "due_local_date") || null;
    if (dueLocalDate !== null && dueLocalDate < plannedLocalDate) {
      setInboxPlanError("期限は、やる日と同じ日か後の日を選んでください。");
      setPlanningInboxBusy(false);
      return;
    }
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/inbox-items/${planningInboxItem.id}/commands/plan`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            expected_version: planningInboxItem.version,
            child_id: selectedChildId,
            project_id: formString(fields, "project_id") || null,
            title: formString(fields, "title"),
            child_copy: formString(fields, "child_copy"),
            planned_local_date: plannedLocalDate,
            due_local_date: dueLocalDate,
            estimated_minutes: Number(formString(fields, "estimated_minutes", "0")),
            priority: 0,
            verification_policy: "none",
          }),
        },
        workspaceId,
      );
      setPlanningInboxItem(null);
      setNotice("Inboxの項目を計画に移しました。");
      await Promise.all([reloadInbox(), reloadTasks(), reloadPlanTasks()]);
    } catch (caught) {
      setInboxPlanError(
        caught instanceof Error ? caught.message : "Inboxの項目を計画できませんでした。",
      );
    } finally {
      setPlanningInboxBusy(false);
    }
  }

  async function cancelInboxItem() {
    if (cancellingInboxItem === null || cancellingInboxBusy) return;
    setCancellingInboxBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/inbox-items/${cancellingInboxItem.id}/commands/cancel`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({ expected_version: cancellingInboxItem.version }),
        },
        workspaceId,
      );
      setCancellingInboxItem(null);
      setNotice("Inboxから取り消しました。タスクは作成されていません。");
      await reloadInbox();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Inbox項目を取り消せませんでした。");
    } finally {
      setCancellingInboxBusy(false);
    }
  }

  async function createProject(event: FormSubmitEvent) {
    event.preventDefault();
    if (creatingProject) return;
    const childIds = childIdsForContextualForm({
      mode: "create",
      selectedChildId,
      explicitChildIds: [],
    });
    if (childIds.length === 0) {
      setError("左のメニューで子供を選んでください。");
      return;
    }
    setCreatingProject(true);
    setError(null);
    setNotice(null);
    const form = event.currentTarget;
    const fields = new FormData(form);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/projects`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            name: formString(fields, "name"),
            child_copy: formString(fields, "child_copy"),
            description: formString(fields, "description"),
            area_id: formString(fields, "area_id") || null,
            term_id: formString(fields, "term_id") || null,
            status: formString(fields, "status", "active"),
            start_local_date: formString(fields, "start_local_date") || null,
            target_local_date: formString(fields, "target_local_date") || null,
            color: formString(fields, "color", "#4f8f72"),
            child_ids: childIds,
          }),
        },
        workspaceId,
      );
      form.reset();
      setNotice("プロジェクトを作成しました。");
      await reload();
      setProjectCreateOpen(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "プロジェクトを作成できませんでした。");
    } finally {
      setCreatingProject(false);
    }
  }

  async function saveArea(event: FormSubmitEvent) {
    event.preventDefault();
    if (editingArea === null || organizationBusyId !== null) return;
    const current = editingArea === "create" ? null : editingArea;
    const busyId = current?.id ?? "area:create";
    setOrganizationBusyId(busyId);
    setOrganizationDialogError(null);
    const fields = new FormData(event.currentTarget);
    try {
      await api(
        current === null
          ? `/api/v1/workspaces/${workspaceId}/areas`
          : `/api/v1/workspaces/${workspaceId}/areas/${current.id}`,
        {
          method: current === null ? "POST" : "PATCH",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            ...(current === null ? {} : { expected_version: current.version }),
            name: formString(fields, "name"),
            child_copy: formString(fields, "child_copy"),
            icon: formString(fields, "icon") || null,
            color: formString(fields, "color", "#4f8f72"),
          }),
        },
        workspaceId,
      );
      setEditingArea(null);
      setNotice(current === null ? "領域を追加しました。" : "領域を更新しました。");
      await reload();
    } catch (caught) {
      setOrganizationDialogError(
        caught instanceof Error ? caught.message : "領域を保存できませんでした。",
      );
    } finally {
      setOrganizationBusyId(null);
    }
  }

  async function runAreaArchiveCommand(area: Area, command: "archive" | "restore") {
    if (organizationBusyId !== null) return;
    setOrganizationBusyId(area.id);
    setError(null);
    setNotice(null);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/areas/${area.id}/commands/${command}`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({ expected_version: area.version }),
        },
        workspaceId,
      );
      setNotice(command === "archive" ? "領域をアーカイブしました。" : "領域を復元しました。");
      await reload();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "領域の状態を変更できませんでした。");
    } finally {
      setOrganizationBusyId(null);
    }
  }

  async function moveArea(areaId: string, direction: -1 | 1) {
    if (organizationBusyId !== null) return;
    const ordered = areas.filter((area) => area.archived_at === null);
    const index = ordered.findIndex((area) => area.id === areaId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= ordered.length) return;
    const next = [...ordered];
    const current = next[index];
    const destination = next[target];
    if (current === undefined || destination === undefined) return;
    next[index] = destination;
    next[target] = current;
    setOrganizationBusyId("area:reorder");
    setError(null);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/areas/reorder`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            items: next.map((area) => ({ id: area.id, expected_version: area.version })),
          }),
        },
        workspaceId,
      );
      setNotice("領域の順番を更新しました。");
      await reload();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "領域を並べ替えられませんでした。");
    } finally {
      setOrganizationBusyId(null);
    }
  }

  async function saveTerm(event: FormSubmitEvent) {
    event.preventDefault();
    if (editingTerm === null || organizationBusyId !== null) return;
    const current = editingTerm === "create" ? null : editingTerm;
    const busyId = current?.id ?? "term:create";
    setOrganizationBusyId(busyId);
    setOrganizationDialogError(null);
    const fields = new FormData(event.currentTarget);
    try {
      await api(
        current === null
          ? `/api/v1/workspaces/${workspaceId}/terms`
          : `/api/v1/workspaces/${workspaceId}/terms/${current.id}`,
        {
          method: current === null ? "POST" : "PATCH",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            ...(current === null ? {} : { expected_version: current.version }),
            name: formString(fields, "name"),
            school_year_label: formString(fields, "school_year_label") || null,
            start_local_date: formString(fields, "start_local_date"),
            end_local_date: formString(fields, "end_local_date"),
          }),
        },
        workspaceId,
      );
      setEditingTerm(null);
      setNotice(current === null ? "期間を追加しました。" : "期間を更新しました。");
      await reload();
    } catch (caught) {
      setOrganizationDialogError(
        caught instanceof Error ? caught.message : "期間を保存できませんでした。",
      );
    } finally {
      setOrganizationBusyId(null);
    }
  }

  async function runTermArchiveCommand(term: Term, command: "archive" | "restore") {
    if (organizationBusyId !== null) return;
    setOrganizationBusyId(term.id);
    setError(null);
    setNotice(null);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/terms/${term.id}/commands/${command}`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({ expected_version: term.version }),
        },
        workspaceId,
      );
      setNotice(command === "archive" ? "期間をアーカイブしました。" : "期間を復元しました。");
      await reload();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "期間の状態を変更できませんでした。");
    } finally {
      setOrganizationBusyId(null);
    }
  }

  async function saveWorkspaceCalendar(event: FormSubmitEvent) {
    event.preventDefault();
    if (editingCalendar === null || calendarBusyId !== null) return;
    const current = editingCalendar === "create" ? null : editingCalendar;
    const fields = new FormData(event.currentTarget);
    const explicitChildIds = fields
      .getAll("calendar_child_ids")
      .filter((value): value is string => typeof value === "string");
    const childIds = childIdsForContextualForm({
      mode: current === null ? "create" : "edit",
      selectedChildId,
      explicitChildIds,
    });
    if (childIds.length === 0) {
      setCalendarDialogError(
        current === null
          ? "左のメニューで子供を選んでください。"
          : "このカレンダーを使う子供を1人以上選んでください。",
      );
      return;
    }
    const periods = calendarPeriodDrafts.map(({ name, start_local_date, end_local_date }) => ({
      name: name.trim(),
      start_local_date,
      end_local_date,
    }));
    if (periods.length === 0 || periods.some((period) => period.name.length === 0)) {
      setCalendarDialogError("お休み期間の名前と日付を1件以上入力してください。");
      return;
    }
    if (periods.some((period) => period.end_local_date < period.start_local_date)) {
      setCalendarDialogError("お休み期間の終了日は開始日以降にしてください。");
      return;
    }
    const busyId = current?.id ?? "calendar:create";
    setCalendarBusyId(busyId);
    setCalendarDialogError(null);
    try {
      await api(
        current === null
          ? `/api/v1/workspaces/${workspaceId}/calendars`
          : `/api/v1/workspaces/${workspaceId}/calendars/${current.id}`,
        {
          method: current === null ? "POST" : "PATCH",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            ...(current === null ? {} : { expected_version: current.version }),
            name: formString(fields, "name"),
            kind: formString(fields, "kind", "school_holiday"),
            periods,
            child_ids: childIds,
          }),
        },
        workspaceId,
      );
      setEditingCalendar(null);
      setCalendarPeriodDrafts([]);
      setNotice(
        current === null ? "お休みカレンダーを追加しました。" : "お休みカレンダーを更新しました。",
      );
      await reload();
    } catch (caught) {
      setCalendarDialogError(
        caught instanceof Error ? caught.message : "お休みカレンダーを保存できませんでした。",
      );
    } finally {
      setCalendarBusyId(null);
    }
  }

  async function runCalendarArchiveCommand(
    calendar: WorkspaceCalendar,
    command: "archive" | "restore",
  ) {
    if (calendarBusyId !== null) return;
    setCalendarBusyId(calendar.id);
    setError(null);
    setNotice(null);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/calendars/${calendar.id}/commands/${command}`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({ expected_version: calendar.version }),
        },
        workspaceId,
      );
      setNotice(
        command === "archive"
          ? "お休みカレンダーをアーカイブしました。"
          : "お休みカレンダーを復元しました。",
      );
      await reload();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "お休みカレンダーの状態を変更できませんでした。",
      );
    } finally {
      setCalendarBusyId(null);
    }
  }

  async function previewRoutineCreate(event: FormSubmitEvent) {
    event.preventDefault();
    if (routineBusy !== null || selectedChildId === null) return;
    setRoutineBusy("preview");
    setRoutineDialogError(null);
    const fields = new FormData(event.currentTarget);
    try {
      const schedule = routineScheduleFromForm(fields, routineScheduleKind);

      const startLocalDate = formString(fields, "start_local_date");
      const endLocalDate = formString(fields, "end_local_date") || null;
      const effectiveFromLocalDate = routineEditing === null ? startLocalDate : workspaceLocalDate;
      const windowEnd = addLocalDays(effectiveFromLocalDate, 41);
      const materializeUntil =
        endLocalDate !== null && endLocalDate < windowEnd ? endLocalDate : windowEnd;
      const explicitChildIds = fields
        .getAll("routine_child_ids")
        .filter((value): value is string => typeof value === "string");
      const childIds = childIdsForContextualForm({
        mode: routineEditing === null ? "create" : "edit",
        selectedChildId,
        explicitChildIds,
      });
      if (childIds.length === 0) {
        throw new Error(
          routineEditing === null
            ? "左のメニューで子供を選んでください。"
            : "取り組む子供を1人以上選んでください。",
        );
      }
      const exceptionDates = [
        ...new Set(
          formString(fields, "exception_dates")
            .split(/[,、\s]+/u)
            .map((value) => value.trim())
            .filter((value) => value.length > 0),
        ),
      ];
      if (exceptionDates.some((date) => !/^\d{4}-\d{2}-\d{2}$/u.test(date))) {
        throw new Error("お休み日はYYYY-MM-DDで入力してください。");
      }
      const pauseStarts = fields
        .getAll("pause_start_local_dates")
        .filter((value): value is string => typeof value === "string");
      const pauseEnds = fields
        .getAll("pause_end_local_dates")
        .filter((value): value is string => typeof value === "string");
      const pauseRanges = pauseStarts.flatMap((start, index) => {
        const end = pauseEnds[index] ?? "";
        if (start.length === 0 && end.length === 0) return [];
        if (start.length === 0 || end.length === 0) {
          throw new Error("一時停止は開始日と終了日を両方入力してください。");
        }
        if (end < start) throw new Error("一時停止の終了日は開始日以降にしてください。");
        return [{ start_local_date: start, end_local_date: end }];
      });
      const calendarIds = fields
        .getAll("routine_calendar_ids")
        .filter((value): value is string => typeof value === "string");
      const draft: RoutineCreateDraft = {
        name: formString(fields, "name"),
        child_copy: formString(fields, "child_copy"),
        notes: formString(fields, "notes"),
        area_id: formString(fields, "area_id") || null,
        project_id: formString(fields, "project_id") || null,
        term_id: formString(fields, "term_id") || null,
        child_ids: childIds,
        color: formString(fields, "color", "#4f8f72"),
        schedule,
        timezone: session.workspace.timezone,
        start_local_date: startLocalDate,
        end_local_date: endLocalDate,
        availability: {
          period: routineAvailability,
          from_local_time:
            routineAvailability === "custom"
              ? formString(fields, "available_from_local_time")
              : null,
          until_local_time:
            routineAvailability === "custom"
              ? formString(fields, "available_until_local_time")
              : null,
        },
        due_offset_days: Number(formString(fields, "due_offset_days", "0")),
        default_duration_minutes:
          formString(fields, "default_duration_minutes").length === 0
            ? null
            : Number(formString(fields, "default_duration_minutes")),
        verification_policy: "none",
        evidence_policy: { required: "none" },
        notification_policy: {
          child_reminder: fields.has("child_reminder"),
          guardian_digest: fields.has("guardian_digest"),
        },
        exception_dates: exceptionDates,
        pause_ranges: pauseRanges,
        calendar_ids: calendarIds,
        materialize_from_local_date: effectiveFromLocalDate,
        materialize_until_local_date: materializeUntil,
      };
      const { materialize_from_local_date: _materializeFrom, ...updateDraft } = draft;
      void _materializeFrom;
      const response = await api<{
        preview: RoutinePreview;
        idempotency_replayed: boolean;
      }>(
        routineEditing === null
          ? `/api/v1/workspaces/${workspaceId}/routines/previews`
          : `/api/v1/workspaces/${workspaceId}/routines/${routineEditing.id}/previews`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify(
            routineEditing === null
              ? draft
              : {
                  expected_version: routineEditing.version,
                  ...updateDraft,
                  effective_from_local_date: effectiveFromLocalDate,
                },
          ),
        },
        workspaceId,
      );
      setRoutineDraft(draft);
      setRoutinePreview(response.preview);
    } catch (caught) {
      setRoutineDialogError(
        caught instanceof Error ? caught.message : "ルーティンの確認内容を作れませんでした。",
      );
    } finally {
      setRoutineBusy(null);
    }
  }

  async function applyRoutineCreate() {
    if (routineBusy !== null || routinePreview === null || routineDraft === null) return;
    setRoutineBusy("apply");
    setRoutineDialogError(null);
    try {
      const response = await api<{
        routine: Routine;
        materialized_count?: number;
        created_count?: number;
        updated_count?: number;
        cancelled_count?: number;
        protected_count?: number;
        request_id: string;
      }>(
        routineEditing === null
          ? `/api/v1/workspaces/${workspaceId}/routines/apply`
          : `/api/v1/workspaces/${workspaceId}/routines/${routineEditing.id}/apply`,
        {
          method: "POST",
          headers: { "Idempotency-Key": routineApplyIdempotencyKey },
          body: JSON.stringify({
            preview_id: routinePreview.id,
            expected_affected_count: routinePreview.affected_count,
            confirmation: routineEditing === null ? "create_routine" : "update_routine",
          }),
        },
        workspaceId,
      );
      const intent = cycleActionIntent?.kind === "update_routine" ? cycleActionIntent : null;
      if (intent !== null) {
        await linkCycleAction(intent, response.request_id, "routine", response.routine.id);
      } else if (routineEditing === null) {
        setNotice(
          `${response.routine.name}を作成し、${String(response.materialized_count ?? 0)}件を計画に追加しました。`,
        );
      } else {
        setNotice(
          `${response.routine.name}を更新しました（追加${String(response.created_count ?? 0)}件・変更${String(response.updated_count ?? 0)}件・取消${String(response.cancelled_count ?? 0)}件・保護${String(response.protected_count ?? 0)}件）。`,
        );
      }
      setRoutineCreateOpen(false);
      setRoutinePreview(null);
      setRoutineDraft(null);
      setRoutineEditing(null);
      setRoutineTemplatePreset(null);
      await Promise.all([reload(), reloadTasks(), reloadPlanTasks()]);
    } catch (caught) {
      setRoutineDialogError(
        caught instanceof Error ? caught.message : "ルーティンを作成できませんでした。",
      );
    } finally {
      setRoutineBusy(null);
    }
  }

  async function createMilestone(event: FormSubmitEvent, projectId: string) {
    event.preventDefault();
    if (milestoneBusyId !== null) return;
    setMilestoneBusyId(projectId);
    setError(null);
    const form = event.currentTarget;
    const fields = new FormData(form);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/projects/${projectId}/milestones`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            title: formString(fields, "title"),
            description: "",
            due_local_date: formString(fields, "due_local_date") || null,
          }),
        },
        workspaceId,
      );
      form.reset();
      await reload();
      setMilestoneProject(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "マイルストーンを追加できませんでした。");
    } finally {
      setMilestoneBusyId(null);
    }
  }

  async function updateProject(event: FormSubmitEvent, project: Project) {
    event.preventDefault();
    if (updatingProjectId !== null) return;
    setUpdatingProjectId(project.id);
    setError(null);
    setNotice(null);
    const fields = new FormData(event.currentTarget);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/projects/${project.id}`,
        {
          method: "PATCH",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({
            expected_version: project.version,
            name: formString(fields, "name"),
            child_copy: formString(fields, "child_copy"),
            description: formString(fields, "description"),
            area_id: formString(fields, "area_id") || null,
            term_id: formString(fields, "term_id") || null,
            status: formString(fields, "status"),
            start_local_date: formString(fields, "start_local_date") || null,
            target_local_date: formString(fields, "target_local_date") || null,
            color: formString(fields, "color"),
            child_ids: fields
              .getAll("child_ids")
              .filter((value): value is string => typeof value === "string"),
          }),
        },
        workspaceId,
      );
      setNotice("プロジェクトを更新しました。");
      await reload();
      setEditingProject(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "プロジェクトを更新できませんでした。");
    } finally {
      setUpdatingProjectId(null);
    }
  }

  async function runMilestoneCommand(
    projectId: string,
    milestone: Milestone,
    command: "start" | "complete",
  ) {
    if (milestoneBusyId !== null) return;
    setMilestoneBusyId(milestone.id);
    setError(null);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/projects/${projectId}/milestones/${milestone.id}/commands/${command}`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({ expected_version: milestone.version }),
        },
        workspaceId,
      );
      await reload();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "マイルストーンを更新できませんでした。");
    } finally {
      setMilestoneBusyId(null);
    }
  }

  async function runProjectArchiveCommand(project: Project, command: "archive" | "restore") {
    if (updatingProjectId !== null) return;
    setUpdatingProjectId(project.id);
    setError(null);
    setNotice(null);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/projects/${project.id}/commands/${command}`,
        {
          method: "POST",
          headers: { "Idempotency-Key": crypto.randomUUID() },
          body: JSON.stringify({ expected_version: project.version }),
        },
        workspaceId,
      );
      setNotice(
        command === "archive"
          ? "プロジェクトをアーカイブしました。タスクの記録は残ります。"
          : "プロジェクトを復元しました。",
      );
      await reload();
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : `プロジェクトを${command === "archive" ? "アーカイブ" : "復元"}できませんでした。`,
      );
    } finally {
      setUpdatingProjectId(null);
    }
  }

  async function parentTaskCommand(
    item: TodayTask,
    operation: TaskLifecycleOperation,
    options: { note?: string } = {},
  ): Promise<boolean> {
    if (taskCommandBusyId !== null) return false;
    setTaskCommandBusyId(item.task.id);
    setError(null);
    setNotice(null);
    try {
      const response = await enqueueTaskCommand(
        session,
        item,
        operation,
        operation === "request_help" ? { reason: "stuck", note: "保護者が代理で記録" } : options,
      );
      if (response === null) {
        setNotice("オフライン操作としてこの端末に保存しました。");
      } else {
        applyScheduledTask(response.item, response.item.task.planned_local_date);
        void reloadNotifications();
        refreshTaskSurfaces();
        if (operation === "verify") setNotice("確認済みにしました。子供の記録は残ります。");
        if (operation === "return")
          setNotice("直してほしいことを記録しました。子供の画面へ戻ります。");
        if (operation === "defer")
          setNotice("タスクをあとに回しました。記録と日付は残っています。");
        if (operation === "skip") setNotice("今回はやらない予定として記録しました。");
        if (operation === "cancel") setNotice("予定を取り消しました。タスクは削除していません。");
        if (operation === "restore") setNotice("タスクをやる予定に戻しました。");
        if (operation === "reopen")
          setNotice("完了を取り消し、進行中に戻しました。完了履歴は残っています。");
      }
      return true;
    } catch (caught) {
      const message =
        caught instanceof ApiRequestError && caught.code === "TASK_DEPENDENCY_BLOCKED"
          ? "先に終える必要があるタスクがあります。"
          : caught instanceof ApiRequestError && caught.code === "TASK_CHECKLIST_INCOMPLETE"
            ? "このタスクには未完了の既存手順があります。子供と確認してください。"
            : caught instanceof ApiRequestError && caught.code === "TASK_EVIDENCE_REQUIRED"
              ? "完了前に必要な写真またはメモを追加してください。"
              : caught instanceof Error
                ? caught.message
                : "タスクを更新できませんでした。";
      setError(message);
      if (operation === "return") setTaskReturnError(message);
      if (["defer", "skip", "cancel", "restore"].includes(operation))
        setTaskPlanDecisionError(message);
      return false;
    } finally {
      setTaskCommandBusyId(null);
    }
  }

  async function persistParentTaskOrder(
    previous: TodayTask[],
    next: TodayTask[],
    busyItem: TodayTask,
  ) {
    if (taskCommandBusyId !== null || selectedChildId === null) return;
    setTaskCommandBusyId(busyItem.task.id);
    setError(null);
    setNotice(null);
    setTasks(next);
    try {
      const response = await enqueueTodayTaskReorder(
        session,
        selectedChildId,
        workspaceLocalDate,
        next,
      );
      if (response === null) {
        setNotice("並び替えをオフライン操作としてこの端末に保存しました。");
      } else {
        setTasks(response.items);
        setNotice("今日の順番を更新しました。");
      }
    } catch (caught) {
      setTasks(previous);
      if (caught instanceof ApiRequestError && caught.code === "TASK_ORDER_DEPENDENCY_CONFLICT") {
        setError("先に終える必要があるタスクより後ろには移動できません。");
      } else if (
        caught instanceof ApiRequestError &&
        caught.code === "TASK_ORDER_PINNED_CONFLICT"
      ) {
        setError("📌 固定したタスクは、通常タスクより後ろへ移動できません。");
      } else if (
        caught instanceof ApiRequestError &&
        caught.code === "TASK_ORDER_TIME_WINDOW_CONFLICT"
      ) {
        setError("開始できる時間帯の順番に反する位置へは移動できません。");
      } else if (
        caught instanceof ApiRequestError &&
        (caught.code === "TASK_ORDER_VERSION_CONFLICT" || caught.status === 409)
      ) {
        setError("別の端末で順番またはタスクが変わりました。最新の順番を読み込み直しました。");
        await reloadTasks();
      } else {
        setError(caught instanceof Error ? caught.message : "今日の順番を更新できませんでした。");
      }
    } finally {
      setTaskCommandBusyId(null);
    }
  }

  async function reorderParentTask(item: TodayTask, direction: "earlier" | "later") {
    const previous = tasks;
    const next = moveTodayTaskWithinLane(tasks, item.task.id, direction);
    if (next === null) return;
    await persistParentTaskOrder(previous, next, item);
  }

  async function reorderParentTaskByDrop(
    item: TodayTask,
    targetTaskId: string,
    placement: TodayTaskDropPlacement,
  ) {
    const previous = tasks;
    const next = moveTodayTaskToDrop(tasks, item.task.id, targetTaskId, placement);
    if (next === null) return;
    await persistParentTaskOrder(previous, next, item);
  }

  function canDirectDragParentTodayTask(item: TodayTask): boolean {
    if (taskCommandBusyId !== null) return false;
    const lane = todayLaneForState(item.assignment.state);
    const canReorderWithinLane =
      canWriteTasks &&
      tasks.filter((candidate) => todayLaneForState(candidate.assignment.state) === lane).length >
        1;
    if (lane === "todo" || lane === "doing") return canReorderWithinLane || canProxyTasks;
    return canReorderWithinLane || (item.assignment.state === "done" && canProxyTasks);
  }

  function parentTodayDropTargetAtPoint(
    clientX: number,
    clientY: number,
    sourceItem: TodayTask,
  ): {
    lane: TodayLaneId;
    taskId: string | null;
    placement: TodayTaskDropPlacement | null;
  } | null {
    const pointed = document.elementFromPoint(clientX, clientY);
    const laneElement = pointed?.closest<HTMLElement>("[data-today-drop-lane]");
    const lane = laneElement?.dataset.todayDropLane as TodayLaneId | undefined;
    if (laneElement === null || laneElement === undefined || lane === undefined) return null;
    const sourceLane = todayLaneForState(sourceItem.assignment.state);
    if (sourceLane !== lane) {
      return todayLifecycleCommandForDrop(sourceItem.assignment.state, lane) !== null &&
        canProxyTasks
        ? { lane, taskId: null, placement: null }
        : null;
    }
    if (!canWriteTasks) return null;
    const cards = Array.from(
      laneElement.querySelectorAll<HTMLElement>("[data-today-drag-task-id]"),
    ).filter((card) => card.dataset.todayDragTaskId !== sourceItem.task.id);
    if (cards.length === 0) return null;
    const directCard = pointed?.closest<HTMLElement>("[data-today-drag-task-id]");
    const targetCard =
      directCard?.dataset.todayDragTaskId === sourceItem.task.id
        ? cards.reduce((closest, candidate) => {
            const closestBounds = closest.getBoundingClientRect();
            const candidateBounds = candidate.getBoundingClientRect();
            return Math.abs(clientY - (candidateBounds.top + candidateBounds.height / 2)) <
              Math.abs(clientY - (closestBounds.top + closestBounds.height / 2))
              ? candidate
              : closest;
          })
        : (directCard ??
          cards.reduce((closest, candidate) => {
            const closestBounds = closest.getBoundingClientRect();
            const candidateBounds = candidate.getBoundingClientRect();
            return Math.abs(clientY - (candidateBounds.top + candidateBounds.height / 2)) <
              Math.abs(clientY - (closestBounds.top + closestBounds.height / 2))
              ? candidate
              : closest;
          }));
    const taskId = targetCard.dataset.todayDragTaskId;
    if (taskId === undefined) return null;
    const bounds = targetCard.getBoundingClientRect();
    return {
      lane,
      taskId,
      placement: clientY >= bounds.top + bounds.height / 2 ? "after" : "before",
    };
  }

  function autoScrollParentTodayAtPointer(clientX: number, clientY: number) {
    const edgeSize = 72;
    const maximumDelta = 14;
    if (clientY < edgeSize) {
      window.scrollBy(0, -Math.ceil(maximumDelta * (1 - clientY / edgeSize)));
    } else if (clientY > window.innerHeight - edgeSize) {
      window.scrollBy(0, Math.ceil(maximumDelta * (1 - (window.innerHeight - clientY) / edgeSize)));
    }
    const board = document.querySelector<HTMLElement>(".parent-kanban");
    if (board === null || board.scrollWidth <= board.clientWidth) return;
    const bounds = board.getBoundingClientRect();
    if (clientY < bounds.top || clientY > bounds.bottom) return;
    const delta = planDragAutoScrollDelta(clientX, bounds.left, bounds.right);
    if (delta !== 0) board.scrollLeft += delta;
  }

  function beginParentTodayPointerDrag(event: ReactPointerEvent<HTMLLIElement>, item: TodayTask) {
    if (
      !event.isPrimary ||
      (event.pointerType === "mouse" && event.button !== 0) ||
      !canDirectDragParentTodayTask(item)
    ) {
      return;
    }
    const sourceCard = event.currentTarget;
    const bounds = sourceCard.getBoundingClientRect();
    event.currentTarget.setPointerCapture(event.pointerId);
    parentTodayPointerDragRef.current = {
      pointerId: event.pointerId,
      item,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - bounds.left,
      offsetY: event.clientY - bounds.top,
      active: false,
      handle: event.currentTarget,
      sourceCard,
      preview: null,
    };
  }

  function moveParentTodayPointerDrag(event: ReactPointerEvent<HTMLLIElement>) {
    const drag = parentTodayPointerDragRef.current;
    if (drag?.pointerId !== event.pointerId) return;
    if (
      !drag.active &&
      !planPointerDragActivated(drag.startX, drag.startY, event.clientX, event.clientY)
    ) {
      return;
    }
    event.preventDefault();
    if (!drag.active) {
      drag.active = true;
      createParentTodayTaskDragPreview(drag.sourceCard, drag);
      drag.sourceCard.classList.add("parent-today-task-drag-source");
      document.body.classList.add("parent-today-task-dragging");
      setError(null);
      setNotice(null);
      window.getSelection()?.removeAllRanges();
    }
    autoScrollParentTodayAtPointer(event.clientX, event.clientY);
    positionParentTodayTaskDragPreview(drag, event.clientX, event.clientY);
    const nextTarget = parentTodayDropTargetAtPoint(event.clientX, event.clientY, drag.item);
    setParentTodayDropTarget((current) =>
      current !== null &&
      nextTarget !== null &&
      current.lane === nextTarget.lane &&
      current.taskId === nextTarget.taskId &&
      current.placement === nextTarget.placement
        ? current
        : nextTarget,
    );
  }

  function suppressNextParentTodayDragClick() {
    suppressParentTodayDragClickRef.current = true;
    if (parentTodayDragClickResetTimerRef.current !== null) {
      window.clearTimeout(parentTodayDragClickResetTimerRef.current);
    }
    parentTodayDragClickResetTimerRef.current = window.setTimeout(() => {
      suppressParentTodayDragClickRef.current = false;
      parentTodayDragClickResetTimerRef.current = null;
    }, 750);
  }

  function finishParentTodayPointerDrag(
    event: ReactPointerEvent<HTMLLIElement>,
    cancelled = false,
  ) {
    const drag = parentTodayPointerDragRef.current;
    if (drag?.pointerId !== event.pointerId) return;
    parentTodayPointerDragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (!drag.active) {
      if (!cancelled) setSelectedParentTodayTaskId(drag.item.task.id);
      return;
    }
    event.preventDefault();
    const target = cancelled
      ? null
      : parentTodayDropTargetAtPoint(event.clientX, event.clientY, drag.item);
    removeParentTodayTaskDragPreview(drag);
    setParentTodayDropTarget(null);
    suppressNextParentTodayDragClick();
    if (target === null) return;
    const command = todayLifecycleCommandForDrop(drag.item.assignment.state, target.lane);
    if (command !== null) {
      void parentTaskCommand(drag.item, command);
    } else if (target.taskId !== null && target.placement !== null) {
      void reorderParentTaskByDrop(drag.item, target.taskId, target.placement);
    }
  }

  function openParentTodayTaskFromCard(item: TodayTask) {
    if (suppressParentTodayDragClickRef.current) {
      suppressParentTodayDragClickRef.current = false;
      if (parentTodayDragClickResetTimerRef.current !== null) {
        window.clearTimeout(parentTodayDragClickResetTimerRef.current);
        parentTodayDragClickResetTimerRef.current = null;
      }
      return;
    }
    setSelectedParentTodayTaskId(item.task.id);
  }

  async function returnTaskForChanges(event: FormSubmitEvent) {
    event.preventDefault();
    if (returningTask === null || taskCommandBusyId !== null) return;
    const note = formString(new FormData(event.currentTarget), "note").trim();
    if (note.length === 0) {
      setTaskReturnError("子供に伝える、短く具体的な内容を入力してください。");
      return;
    }
    setTaskReturnError(null);
    if (await parentTaskCommand(returningTask, "return", { note })) setReturningTask(null);
  }

  function openTaskPlanDecision(item: TodayTask) {
    const commands = taskPlanCommandsFor(item.assignment.state);
    const command = commands[0];
    if (command === undefined) return;
    setTaskPlanDecisionError(null);
    setTaskPlanDecision({ item, command });
  }

  async function submitTaskPlanDecision(event: FormSubmitEvent) {
    event.preventDefault();
    if (taskPlanDecision === null || taskCommandBusyId !== null) return;
    const note = formString(new FormData(event.currentTarget), "note").trim();
    setTaskPlanDecisionError(null);
    if (
      await parentTaskCommand(taskPlanDecision.item, taskPlanDecision.command, {
        ...(note.length === 0 ? {} : { note }),
      })
    ) {
      setTaskPlanDecision(null);
    }
  }

  async function issueEnrollment(event: FormSubmitEvent) {
    event.preventDefault();
    if (childDialogChild === null || enrollmentBusyChildId !== null) return;
    const form = event.currentTarget;
    const fields = new FormData(form);
    const allowedCapabilities = new Set(childDeviceCapabilities.map(({ value }) => value));
    const capabilities = fields
      .getAll("capabilities")
      .filter(
        (value): value is SessionActor["capabilities"][number] =>
          typeof value === "string" &&
          allowedCapabilities.has(value as SessionActor["capabilities"][number]),
      );
    if (capabilities.length === 0) {
      setError("この端末で使う機能を1つ以上選んでください。");
      return;
    }
    setEnrollmentBusyChildId(childDialogChild.id);
    setError(null);
    setCopyNotice(null);
    try {
      const result = await api<{ enrollment_url: string; expires_at: string }>(
        `/api/v1/workspaces/${workspaceId}/child-enrollments`,
        {
          method: "POST",
          body: JSON.stringify({
            child_id: childDialogChild.id,
            device_label: fields.get("device_label"),
            capabilities,
          }),
        },
        workspaceId,
      );
      setEnrollmentQrCode(null);
      setEnrollmentResult({ url: result.enrollment_url, expiresAt: result.expires_at });
      setChildDialogStep("enrollment-ready");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "登録リンクを作れませんでした。");
    } finally {
      setEnrollmentBusyChildId(null);
    }
  }

  async function copyEnrollmentLink() {
    if (enrollmentResult === null) return;
    try {
      await navigator.clipboard.writeText(enrollmentResult.url);
      setCopyNotice("登録リンクをコピーしました。");
    } catch {
      setCopyNotice(null);
      setError("登録リンクをコピーできませんでした。URLを選択してコピーしてください。");
    }
  }

  async function revokeDevice(deviceId: string) {
    if (revokingDeviceId !== null) return;
    setRevokingDeviceId(deviceId);
    setError(null);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/devices/${deviceId}/revoke`,
        {
          method: "POST",
          body: JSON.stringify({ reason: "保護者が端末設定から失効" }),
        },
        workspaceId,
      );
      await reload();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "端末を失効できませんでした。");
    } finally {
      setRevokingDeviceId(null);
    }
  }

  function openMembershipInvitation() {
    setMembershipInviteRole("guardian");
    setMembershipInviteChildIds(selectedChildId === null ? [] : [selectedChildId]);
    setMembershipInviteAreaId("");
    setMembershipInviteCapabilities(["tasks:read"]);
    setMembershipInviteUrl(null);
    setMembershipInviteError(null);
    setMembershipInviteOpen(true);
  }

  function closeMembershipInvitation() {
    if (familyMutationBusy === "membership-invite") return;
    setMembershipInviteOpen(false);
    setMembershipInviteUrl(null);
    setMembershipInviteError(null);
  }

  async function createMembershipInvitationFromForm(event: FormSubmitEvent) {
    event.preventDefault();
    if (familyMutationBusy !== null) return;
    const fields = new FormData(event.currentTarget);
    const role = membershipInviteRole;
    if (
      role === "supporter" &&
      (membershipInviteCapabilities.length === 0 ||
        (membershipInviteChildIds.length === 0 && membershipInviteAreaId === ""))
    ) {
      setMembershipInviteError("サポーターには、対象と許可する操作を選んでください。");
      return;
    }
    const targets =
      membershipInviteChildIds.length === 0
        ? [{ childId: null, areaId: membershipInviteAreaId || null }]
        : membershipInviteChildIds.map((childId) => ({
            childId,
            areaId: membershipInviteAreaId || null,
          }));
    const scopes =
      role === "guardian"
        ? []
        : targets.flatMap((target) =>
            membershipInviteCapabilities.map((capability) => ({
              child_id: target.childId,
              area_id: target.areaId,
              capability,
            })),
          );
    setFamilyMutationBusy("membership-invite");
    setMembershipInviteError(null);
    try {
      const response = await api<{
        invitation: MembershipInvitation;
        invitation_url: string;
      }>(
        `/api/v1/workspaces/${workspaceId}/membership-invitations`,
        {
          method: "POST",
          body: JSON.stringify({
            role,
            scopes,
            expires_in_hours: Number(formString(fields, "expires_in_hours", "72")),
          }),
        },
        workspaceId,
      );
      setMembershipInviteUrl(response.invitation_url);
      setMembershipInvitations((current) => [
        response.invitation,
        ...current.filter((item) => item.id !== response.invitation.id),
      ]);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.code === "RECENT_AUTH_REQUIRED") {
        setMembershipInviteError(
          "安全のため、招待の作成には直前のパスキーログインが必要です。一度ログアウトして、パスキーで入り直してください。",
        );
      } else {
        setMembershipInviteError(
          caught instanceof Error ? caught.message : "招待リンクを作れませんでした。",
        );
      }
    } finally {
      setFamilyMutationBusy(null);
    }
  }

  async function copyMembershipInvitation() {
    if (membershipInviteUrl === null) return;
    try {
      await navigator.clipboard.writeText(membershipInviteUrl);
      setNotice("招待リンクをコピーしました。");
    } catch {
      setMembershipInviteError("コピーできませんでした。URLを選択してコピーしてください。");
    }
  }

  async function revokeMembershipInvitationFromSettings(invitation: MembershipInvitation) {
    if (familyMutationBusy !== null) return;
    setFamilyMutationBusy(`membership-invite:${invitation.id}`);
    setError(null);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/membership-invitations/${invitation.id}/revoke`,
        {
          method: "POST",
          body: JSON.stringify({ reason: "家族設定から招待を取り消し" }),
        },
        workspaceId,
      );
      await reload();
      setNotice("招待を取り消しました。");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "招待を取り消せませんでした。");
    } finally {
      setFamilyMutationBusy(null);
    }
  }

  function openMembershipEditor(membership: WorkspaceMembership) {
    if (membership.role === "owner") return;
    setEditingMembership(membership);
    setMembershipEditRole(membership.role);
    setMembershipEditStatus(membership.status === "suspended" ? "suspended" : "active");
    setMembershipScopeDrafts(
      membership.scopes.map((scope) => ({
        child_id: scope.child_id,
        area_id: scope.area_id,
        capability: scope.capability as SupporterCapability,
      })),
    );
    setMembershipEditError(null);
  }

  function closeMembershipEditor() {
    if (familyMutationBusy?.startsWith("membership:") === true) return;
    setEditingMembership(null);
    setMembershipEditError(null);
  }

  function addMembershipScopeDraft() {
    const childId = selectedChildId ?? activeChildren[0]?.id ?? null;
    const areaId =
      childId === null ? (areas.find((area) => area.archived_at === null)?.id ?? null) : null;
    if (childId === null && areaId === null) {
      setMembershipEditError("先に対象となる子供またはAreaを作成してください。");
      return;
    }
    setMembershipScopeDrafts((current) => [
      ...current,
      { child_id: childId, area_id: areaId, capability: "tasks:read" },
    ]);
  }

  async function saveMembership(event: FormSubmitEvent) {
    event.preventDefault();
    if (editingMembership === null || familyMutationBusy !== null) return;
    const scopes = membershipEditRole === "guardian" ? [] : membershipScopeDrafts;
    if (
      membershipEditRole === "supporter" &&
      (scopes.length === 0 ||
        scopes.some((scope) => scope.child_id === null && scope.area_id === null))
    ) {
      setMembershipEditError("サポーターには、少なくとも1つの対象と操作を設定してください。");
      return;
    }
    const keys = scopes.map(
      (scope) => `${scope.child_id ?? ""}:${scope.area_id ?? ""}:${scope.capability}`,
    );
    if (new Set(keys).size !== keys.length) {
      setMembershipEditError("同じ対象と操作の組み合わせが重複しています。");
      return;
    }
    setFamilyMutationBusy(`membership:${editingMembership.id}`);
    setMembershipEditError(null);
    try {
      const response = await api<{ membership: WorkspaceMembership }>(
        `/api/v1/workspaces/${workspaceId}/memberships/${editingMembership.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            role: membershipEditRole,
            status: membershipEditStatus,
            scopes,
            version: editingMembership.version,
          }),
        },
        workspaceId,
      );
      setMemberships((current) =>
        current.map((membership) =>
          membership.id === response.membership.id ? response.membership : membership,
        ),
      );
      setEditingMembership(null);
      setNotice(`${response.membership.display_name}さんのアクセス設定を更新しました。`);
    } catch (caught) {
      setMembershipEditError(
        caught instanceof ApiRequestError && caught.code === "RECENT_AUTH_REQUIRED"
          ? "安全のため、変更には直前のパスキーログインが必要です。入り直してから再実行してください。"
          : caught instanceof Error
            ? caught.message
            : "アクセス設定を更新できませんでした。",
      );
    } finally {
      setFamilyMutationBusy(null);
    }
  }

  async function transferOwnershipToEditedMembership() {
    if (
      editingMembership?.role !== "guardian" ||
      editingMembership.status !== "active" ||
      session.actor.membership_role !== "owner" ||
      familyMutationBusy !== null
    )
      return;
    const currentMembership = memberships.find(
      (membership) => membership.account_id === session.actor.id,
    );
    if (currentMembership === undefined) {
      setMembershipEditError("現在のオーナー情報を再読込してください。");
      return;
    }
    setFamilyMutationBusy(`membership:transfer:${editingMembership.id}`);
    setMembershipEditError(null);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/ownership-transfer`,
        {
          method: "POST",
          body: JSON.stringify({
            target_membership_id: editingMembership.id,
            expected_actor_version: currentMembership.version,
            expected_target_version: editingMembership.version,
          }),
        },
        workspaceId,
      );
      window.location.reload();
    } catch (caught) {
      setMembershipEditError(
        caught instanceof ApiRequestError && caught.code === "RECENT_AUTH_REQUIRED"
          ? "オーナー移譲には直前のパスキーログインが必要です。入り直してから再実行してください。"
          : caught instanceof Error
            ? caught.message
            : "オーナーを移譲できませんでした。",
      );
    } finally {
      setFamilyMutationBusy(null);
    }
  }

  async function leaveWorkspace() {
    if (familyMutationBusy !== null) return;
    const currentMembership = memberships.find(
      (membership) => membership.account_id === session.actor.id,
    );
    if (currentMembership === undefined || currentMembership.role === "owner") return;
    setFamilyMutationBusy("membership:leave");
    setMembershipEditError(null);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/memberships/leave`,
        { method: "POST", body: JSON.stringify({ version: currentMembership.version }) },
        workspaceId,
      );
      setLeaveWorkspaceOpen(false);
      onLogout();
    } catch (caught) {
      setMembershipEditError(
        caught instanceof ApiRequestError && caught.code === "RECENT_AUTH_REQUIRED"
          ? "退出には直前のパスキーログインが必要です。入り直してから再実行してください。"
          : caught instanceof Error
            ? caught.message
            : "Workspaceから退出できませんでした。",
      );
    } finally {
      setFamilyMutationBusy(null);
    }
  }

  async function updateWorkspace(event: FormSubmitEvent) {
    event.preventDefault();
    if (workspaceSettings === null || familyMutationBusy !== null) return;
    setFamilyMutationBusy("workspace");
    setError(null);
    setNotice(null);
    const fields = new FormData(event.currentTarget);
    try {
      const response = await api<{ workspace: WorkspaceSettings }>(
        `/api/v1/workspaces/${workspaceId}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            name: formString(fields, "name"),
            timezone: formString(fields, "timezone"),
            week_starts_on: Number(formString(fields, "week_starts_on", "1")),
            default_cycle_days: Number(formString(fields, "default_cycle_days", "7")),
            quiet_hours: {
              enabled: fields.get("quiet_hours_enabled") === "on",
              start: formString(fields, "quiet_hours_start", "21:00"),
              end: formString(fields, "quiet_hours_end", "06:00"),
            },
            locale: formString(fields, "locale", "ja-JP"),
            version: workspaceSettings.version,
          }),
        },
        workspaceId,
      );
      setWorkspaceSettings(response.workspace);
      onWorkspaceUpdated({
        ...session.workspace,
        name: response.workspace.name,
        timezone: response.workspace.timezone,
      });
      setNotice("家族の設定を保存しました。");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "家族の設定を保存できませんでした。");
    } finally {
      setFamilyMutationBusy(null);
    }
  }

  async function updateChild(event: FormSubmitEvent, child: ChildProfile) {
    event.preventDefault();
    if (familyMutationBusy !== null) return;
    setFamilyMutationBusy(`child:${child.id}`);
    setError(null);
    setNotice(null);
    const fields = new FormData(event.currentTarget);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/children/${child.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            display_name: formString(fields, "display_name"),
            theme_color: formString(fields, "theme_color"),
            reading_level: formString(fields, "reading_level"),
            display_density: formString(fields, "display_density"),
            version: child.version,
          }),
        },
        workspaceId,
      );
      await reload();
      setEditingChild(null);
      setNotice("子供プロフィールを更新しました。");
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "子供プロフィールを更新できませんでした。",
      );
    } finally {
      setFamilyMutationBusy(null);
    }
  }

  async function setChildArchived(child: ChildProfile, archived: boolean) {
    if (familyMutationBusy !== null) return;
    if (
      archived &&
      !window.confirm(
        `${child.display_name}をアーカイブします。この子供の登録端末はすべて失効します。タスクや記録は削除されません。続けますか？`,
      )
    ) {
      return;
    }
    setFamilyMutationBusy(`child:${child.id}`);
    setError(null);
    setNotice(null);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/children/${child.id}/${archived ? "archive" : "restore"}`,
        { method: "POST", body: JSON.stringify({ version: child.version }) },
        workspaceId,
      );
      await reload();
      setNotice(
        archived ? "子供プロフィールをアーカイブしました。" : "子供プロフィールを戻しました。",
      );
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : archived
            ? "子供プロフィールをアーカイブできませんでした。"
            : "子供プロフィールを戻せませんでした。",
      );
    } finally {
      setFamilyMutationBusy(null);
    }
  }

  async function updateDevice(event: FormSubmitEvent, device: Device) {
    event.preventDefault();
    if (familyMutationBusy !== null) return;
    setFamilyMutationBusy(`device:${device.id}`);
    setError(null);
    const fields = new FormData(event.currentTarget);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/devices/${device.id}`,
        { method: "PATCH", body: JSON.stringify({ label: formString(fields, "label") }) },
        workspaceId,
      );
      await reload();
      setEditingDevice(null);
      setNotice("端末名を更新しました。");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "端末名を更新できませんでした。");
    } finally {
      setFamilyMutationBusy(null);
    }
  }

  async function updatePasskey(event: FormSubmitEvent, credential: PasskeyCredential) {
    event.preventDefault();
    if (familyMutationBusy !== null) return;
    setFamilyMutationBusy(`passkey:${credential.id}`);
    setError(null);
    const fields = new FormData(event.currentTarget);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/passkeys/${credential.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({ device_label: formString(fields, "device_label") }),
        },
        workspaceId,
      );
      await reload();
      setEditingPasskey(null);
      setNotice("パスキー名を更新しました。");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "パスキー名を更新できませんでした。");
    } finally {
      setFamilyMutationBusy(null);
    }
  }

  async function revokePasskey(credential: PasskeyCredential) {
    if (familyMutationBusy !== null) return;
    if (
      !window.confirm(
        `パスキー「${credential.device_label}」を削除します。このパスキーではログインできなくなります。続けますか？`,
      )
    ) {
      return;
    }
    setFamilyMutationBusy(`passkey:${credential.id}`);
    setError(null);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/passkeys/${credential.id}`,
        { method: "DELETE" },
        workspaceId,
      );
      await reload();
      setNotice("パスキーを削除しました。");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "パスキーを削除できませんでした。");
    } finally {
      setFamilyMutationBusy(null);
    }
  }

  async function revokeAgentGrant(grant: AgentGrant) {
    if (familyMutationBusy !== null) return;
    if (
      !window.confirm(
        `「${grant.client_name}」との連携を解除します。発行済みのアクセストークンと更新トークンはすぐに使えなくなります。続けますか？`,
      )
    ) {
      return;
    }
    setFamilyMutationBusy(`grant:${grant.id}`);
    setError(null);
    try {
      await api(
        `/api/v1/workspaces/${workspaceId}/mcp/grants/${grant.id}/revoke`,
        { method: "POST", body: JSON.stringify({ version: grant.version }) },
        workspaceId,
      );
      await reload();
      setNotice("Remote MCP連携を解除しました。");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Remote MCP連携を解除できませんでした。");
    } finally {
      setFamilyMutationBusy(null);
    }
  }

  async function runStagingProof() {
    setStagingProofBusy(true);
    setError(null);
    setStagingProof("D1・R2・Queue・Workflow・Realtimeを確認しています…");
    try {
      const proof = await api<StagingRuntimeProofResponse>(
        `/api/v1/workspaces/${workspaceId}/operations/runtime-proof`,
        {
          method: "POST",
          body: JSON.stringify({
            proof_id: crypto.randomUUID(),
            value: `browser-proof-${crypto.randomUUID()}`,
          }),
        },
        workspaceId,
      );
      let completedJobs: BackgroundJob[] | null = null;
      for (let attempt = 0; attempt < 30; attempt += 1) {
        const jobs = await Promise.all(
          [proof.queue_job_id, proof.workflow_job_id].map(async (jobId) => {
            const response = await api<{ job: BackgroundJob }>(
              `/api/v1/workspaces/${workspaceId}/operations/jobs/${jobId}`,
              undefined,
              workspaceId,
            ).catch((caught: unknown) => {
              if (caught instanceof ApiRequestError && caught.status === 404) return null;
              throw caught;
            });
            return response?.job ?? null;
          }),
        );
        if (jobs.every((job): job is BackgroundJob => job?.status === "completed")) {
          completedJobs = jobs;
          break;
        }
        await wait(500);
      }
      if (completedJobs === null) {
        throw new Error("QueueまたはWorkflowの完了を確認できませんでした。");
      }
      const firstConnection = await proveRealtime(workspaceId, 0);
      const reconnectCursor = 42;
      const secondConnection = await proveRealtime(workspaceId, reconnectCursor);
      if (
        secondConnection.connections_seen < firstConnection.connections_seen + 1 ||
        secondConnection.resume_cursor !== reconnectCursor ||
        secondConnection.pong_cursor !== reconnectCursor
      ) {
        throw new Error("Realtimeの再接続カーソル検証に失敗しました。");
      }
      setStagingProof(
        JSON.stringify(
          {
            d1_persisted: proof.d1_persisted,
            r2_private_roundtrip_deleted: proof.r2_roundtrip.deleted,
            queue: completedJobs.find((job) => job.source === "queue")?.status,
            workflow: completedJobs.find((job) => job.source === "workflow")?.status,
            realtime_reconnect_cursor: secondConnection.resume_cursor,
            realtime_connections_seen: secondConnection.connections_seen,
            request_id: proof.request_id,
          },
          null,
          2,
        ),
      );
    } catch (caught) {
      setStagingProof(null);
      setError(caught instanceof Error ? caught.message : "staging結合診断に失敗しました。");
    } finally {
      setStagingProofBusy(false);
    }
  }

  const activeChildren = children.filter((child) => child.archived_at === null);
  const archivedChildren = children.filter((child) => child.archived_at !== null);
  const selectedChild = activeChildren.find((child) => child.id === selectedChildId) ?? null;
  const activeProjects = projects.filter((project) => project.archived_at === null);
  const selectedChildProjects = activeProjects.filter((project) =>
    project.participants.some((participant) => participant.child_id === selectedChildId),
  );
  const archivedChildProjects = projects.filter(
    (project) =>
      project.archived_at !== null &&
      project.participants.some((participant) => participant.child_id === selectedChildId),
  );
  const selectedChildRoutines = routines.filter((routine) =>
    routine.participants.some((participant) => participant.child_id === selectedChildId),
  );
  const activeRoutineTemplates = routineTemplates.filter(
    (template) => template.archived_at === null,
  );
  const archivedRoutineTemplates = routineTemplates.filter(
    (template) => template.archived_at !== null,
  );
  const activeAreas = areas.filter((area) => area.archived_at === null);
  const archivedAreas = areas.filter((area) => area.archived_at !== null);
  const activeTerms = terms.filter((term) => term.archived_at === null);
  const archivedTerms = terms.filter((term) => term.archived_at !== null);
  const activeCalendars = workspaceCalendars.filter((calendar) => calendar.archived_at === null);
  const selectedChildCalendars = activeCalendars.filter((calendar) =>
    calendar.child_ids.includes(selectedChildId ?? ""),
  );
  const archivedCalendars = workspaceCalendars.filter((calendar) => calendar.archived_at !== null);
  const routineCalendarOptions = activeCalendars.filter((calendar) =>
    calendar.child_ids.some((childId) =>
      routineEditing === null
        ? childId === selectedChildId
        : routineEditing.participants.some((participant) => participant.child_id === childId),
    ),
  );
  const routineFormSchedule =
    routineEditing?.current_version.schedule ?? routineTemplatePreset?.schedule ?? null;
  const routineFormAvailability =
    routineEditing?.current_version.availability ?? routineTemplatePreset?.availability ?? null;
  const routineTemplateRecord =
    editingRoutineTemplate === null || editingRoutineTemplate === "create"
      ? null
      : editingRoutineTemplate;
  const routineTemplateFormSchedule =
    routineTemplateRecord?.schedule ?? routineTemplateSource?.current_version.schedule ?? null;
  const routineTemplateFormAvailability =
    routineTemplateRecord?.availability ??
    routineTemplateSource?.current_version.availability ??
    null;
  const selectedChildCycles = cycles.filter((cycle) =>
    cycle.participants.some(
      (participant) =>
        participant.participant_type === "child" && participant.participant_id === selectedChildId,
    ),
  );

  useEffect(() => {
    if (selectedChild === null || familyLoading) return;
    const childId = selectedChild.id;
    const childName = selectedChild.display_name;
    const readToday = async (signal: AbortSignal) =>
      api<{ items: TodayTask[] }>(
        `/api/v1/workspaces/${workspaceId}/today?child_id=${encodeURIComponent(childId)}`,
        { signal },
        workspaceId,
      );
    const visibleTasks = [...tasks, ...planTasks, ...backlogTasks].filter(
      (item, index, all) =>
        all.findIndex((candidate) => candidate.task.id === item.task.id) === index,
    );
    const requireVisibleTask = (taskId: string) => {
      const item = visibleTasks.find(
        (candidate) =>
          candidate.task.id === taskId && candidate.assignment.child_id === selectedChild.id,
      );
      if (item === undefined)
        throw new Error("選択中の子供について現在のTodayまたは計画に表示されていないタスクです。");
      return item;
    };
    const requireSelectedCycle = (cycleId: string) => {
      const cycle = cycles.find(
        (candidate) =>
          candidate.id === cycleId &&
          candidate.participants.some(
            (participant) =>
              participant.participant_type === "child" && participant.participant_id === childId,
          ),
      );
      if (cycle === undefined)
        throw new Error("選択中の子供が参加する、現在読み込み済みのCycleではありません。");
      return cycle;
    };
    const requireSelectedCalendar = (calendarId: string, includeArchived = false) => {
      const calendar = workspaceCalendars.find(
        (candidate) =>
          candidate.id === calendarId &&
          (includeArchived || candidate.archived_at === null) &&
          candidate.child_ids.includes(childId),
      );
      if (calendar === undefined)
        throw new Error("選択中の子供に適用される、現在有効なお休みカレンダーではありません。");
      if (calendar.child_ids.length !== 1)
        throw new Error("複数の子供で共有するカレンダーは親画面から編集してください。");
      return calendar;
    };
    const requireCalendarPeriods = (
      input: Record<string, unknown>,
    ): WorkspaceCalendar["periods"] => {
      if (!Array.isArray(input.periods) || input.periods.length < 1 || input.periods.length > 64)
        throw new Error("periods must contain 1 to 64 date ranges.");
      return input.periods.map((value) => {
        if (typeof value !== "object" || value === null || Array.isArray(value))
          throw new Error("Each calendar period must be an object.");
        const period = value as Record<string, unknown>;
        const name = requireWebMcpString(period, "name", 120);
        const startLocalDate = requireWebMcpDate(period, "start_local_date");
        const endLocalDate = requireWebMcpDate(period, "end_local_date");
        if (endLocalDate < startLocalDate)
          throw new Error("Calendar period end must not be earlier than start.");
        return { name, start_local_date: startLocalDate, end_local_date: endLocalDate };
      });
    };
    const webMcpTaskStructure = (item: TodayTask, signal?: AbortSignal) =>
      api<{ structure: TaskStructure; request_id: string }>(
        `/api/v1/workspaces/${workspaceId}/tasks/${item.task.id}/structure?assignment_id=${encodeURIComponent(item.assignment.id)}`,
        signal === undefined ? undefined : { signal },
        workspaceId,
      );
    const routineTemplatePayloadFromTool = (input: Record<string, unknown>) => {
      const availabilityPeriod =
        input.availability_period === undefined
          ? "anytime"
          : requireWebMcpString(input, "availability_period", 32);
      if (
        !new Set(["anytime", "morning", "after_school", "evening", "custom"]).has(
          availabilityPeriod,
        )
      )
        throw new Error("availability_period is invalid.");
      const fromLocalTime =
        availabilityPeriod === "custom"
          ? requireWebMcpString(input, "available_from_local_time", 5)
          : null;
      const untilLocalTime =
        availabilityPeriod === "custom"
          ? requireWebMcpString(input, "available_until_local_time", 5)
          : null;
      if (
        (fromLocalTime !== null && !/^\d{2}:\d{2}$/u.test(fromLocalTime)) ||
        (untilLocalTime !== null && !/^\d{2}:\d{2}$/u.test(untilLocalTime)) ||
        (fromLocalTime !== null && untilLocalTime !== null && untilLocalTime <= fromLocalTime)
      )
        throw new Error("Custom availability requires an increasing HH:MM range.");
      if (
        (input.child_reminder !== undefined && typeof input.child_reminder !== "boolean") ||
        (input.guardian_digest !== undefined && typeof input.guardian_digest !== "boolean")
      )
        throw new Error("Reminder controls must be boolean.");
      const childReminder = input.child_reminder === true;
      const guardianDigest = input.guardian_digest === true;
      if ((childReminder || guardianDigest) && availabilityPeriod !== "custom")
        throw new Error("Routine reminders require an explicit custom time range.");
      return {
        name: requireWebMcpString(input, "name", 160),
        child_copy: requireWebMcpString(input, "child_copy", 160),
        notes: input.notes === undefined ? "" : requireWebMcpString(input, "notes", 8000),
        color: input.color === undefined ? "#4f8f72" : requireWebMcpString(input, "color", 7),
        schedule: requireWebMcpRoutineSchedule(input),
        availability: {
          period: availabilityPeriod,
          from_local_time: fromLocalTime,
          until_local_time: untilLocalTime,
        },
        due_offset_days:
          input.due_offset_days === undefined
            ? 0
            : requireWebMcpInteger(input, "due_offset_days", 0, 30),
        default_duration_minutes:
          input.default_duration_minutes === undefined
            ? null
            : requireWebMcpInteger(input, "default_duration_minutes", 0, 1440),
        verification_policy: "none" as const,
        evidence_policy: { required: "none" as const },
        notification_policy: {
          child_reminder: childReminder,
          guardian_digest: guardianDigest,
        },
      };
    };
    const routineTemplateToolProperties = {
      name: { type: "string", minLength: 1, maxLength: 160 },
      child_copy: { type: "string", minLength: 1, maxLength: 160 },
      notes: { type: "string", maxLength: 8000 },
      color: { type: "string", pattern: "^#[0-9a-fA-F]{6}$" },
      schedule_kind: {
        type: "string",
        enum: ["daily", "weekly", "monthly_days", "monthly_nth_weekday"],
      },
      interval: { type: "integer", minimum: 1, maximum: 365 },
      weekdays: {
        type: "array",
        minItems: 1,
        maxItems: 7,
        uniqueItems: true,
        items: { type: "integer", minimum: 1, maximum: 7 },
      },
      month_days: {
        type: "array",
        minItems: 1,
        maxItems: 32,
        uniqueItems: true,
        items: {
          anyOf: [
            { type: "integer", minimum: 1, maximum: 31 },
            { type: "string", const: "last" },
          ],
        },
      },
      month_ordinal: { type: "integer", enum: [1, 2, 3, 4, 5, -1] },
      month_weekday: { type: "integer", minimum: 1, maximum: 7 },
      availability_period: {
        type: "string",
        enum: ["anytime", "morning", "after_school", "evening", "custom"],
      },
      available_from_local_time: { type: "string", pattern: "^[0-2][0-9]:[0-5][0-9]$" },
      available_until_local_time: { type: "string", pattern: "^[0-2][0-9]:[0-5][0-9]$" },
      child_reminder: { type: "boolean", default: false },
      guardian_digest: { type: "boolean", default: false },
      due_offset_days: { type: "integer", minimum: 0, maximum: 30 },
      default_duration_minutes: { type: "integer", minimum: 0, maximum: 1440 },
    };
    const tools: WebMcpTool[] = [
      {
        name: "oyako_read_selected_child_dashboard_v1",
        title: `${childName}の集計を見る`,
        description: `現在選択中の「${childName}」について、期間を明示してTask内訳、自己開始、開始案内、たすけて、負荷、Routine・Project・Term進捗、写真・メモのメタデータを読みます。兄弟比較は返しません。`,
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["from_local_date", "to_local_date"],
          properties: {
            from_local_date: { type: "string", format: "date" },
            to_local_date: { type: "string", format: "date" },
          },
        },
        annotations: { readOnlyHint: true, untrustedContentHint: true },
        execute: async (input, { signal }) => {
          const fromLocalDate = requireWebMcpDate(input, "from_local_date");
          const toLocalDate = requireWebMcpDate(input, "to_local_date");
          const rangeDays =
            (Date.parse(`${toLocalDate}T00:00:00Z`) - Date.parse(`${fromLocalDate}T00:00:00Z`)) /
            86_400_000;
          if (rangeDays < 0 || rangeDays > 365)
            throw new Error("集計期間は1日から366日の範囲で指定してください。");
          const query = new URLSearchParams({
            from: fromLocalDate,
            to: toLocalDate,
            child_id: childId,
          });
          return api<DashboardResponse>(
            `/api/v1/workspaces/${workspaceId}/dashboard?${query.toString()}`,
            { signal },
            workspaceId,
          );
        },
      },
      {
        name: "oyako_list_calendars_v1",
        title: `${childName}のお休みカレンダーを見る`,
        description: `現在選択中の「${childName}」について、ルーティンの新規Task生成を休む日付範囲を読みます。`,
        inputSchema: { type: "object", additionalProperties: false },
        annotations: { readOnlyHint: true, untrustedContentHint: true },
        execute: async (_input, { signal }) => {
          const response = await api<{ items: WorkspaceCalendar[]; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/calendars`,
            { signal },
            workspaceId,
          );
          return {
            schema_version: 1,
            child: { id: childId, display_name: childName },
            items: response.items.filter((calendar) => calendar.child_ids.includes(childId)),
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_create_calendar_v1",
        title: `${childName}のお休みカレンダーを追加`,
        description: `選択中の「${childName}」だけに適用する、学校の休み・祝日・旅行・体調不良などの日付範囲を追加します。すでに予定済みのTaskは変更しません。`,
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["name", "kind", "periods", "idempotency_key"],
          properties: {
            name: { type: "string", minLength: 1, maxLength: 160 },
            kind: {
              type: "string",
              enum: ["school_holiday", "public_holiday", "travel", "sick", "custom"],
            },
            periods: {
              type: "array",
              minItems: 1,
              maxItems: 64,
              items: {
                type: "object",
                additionalProperties: false,
                required: ["name", "start_local_date", "end_local_date"],
                properties: {
                  name: { type: "string", minLength: 1, maxLength: 120 },
                  start_local_date: { type: "string", format: "date" },
                  end_local_date: { type: "string", format: "date" },
                },
              },
            },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: true },
        execute: async (input, { signal }) => {
          const name = requireWebMcpString(input, "name", 160);
          const kind = requireWebMcpString(input, "kind", 32);
          if (!["school_holiday", "public_holiday", "travel", "sick", "custom"].includes(kind))
            throw new Error("Unsupported calendar kind.");
          const periods = requireCalendarPeriods(input);
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<{ calendar: WorkspaceCalendar; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/calendars`,
            {
              method: "POST",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({ name, kind, periods, child_ids: [childId] }),
            },
            workspaceId,
          );
          await reload();
          return {
            schema_version: 1,
            outcome: "created",
            calendar: response.calendar,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_update_calendar_v1",
        title: `${childName}のお休みカレンダーを編集`,
        description: `選択中の「${childName}」だけに適用されるカレンダーをversion付きで編集します。共有カレンダーは親画面での確認が必要です。`,
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: [
            "calendar_id",
            "expected_version",
            "name",
            "kind",
            "periods",
            "idempotency_key",
          ],
          properties: {
            calendar_id: { type: "string", format: "uuid" },
            expected_version: { type: "integer", minimum: 1 },
            name: { type: "string", minLength: 1, maxLength: 160 },
            kind: {
              type: "string",
              enum: ["school_holiday", "public_holiday", "travel", "sick", "custom"],
            },
            periods: {
              type: "array",
              minItems: 1,
              maxItems: 64,
              items: {
                type: "object",
                additionalProperties: false,
                required: ["name", "start_local_date", "end_local_date"],
                properties: {
                  name: { type: "string", minLength: 1, maxLength: 120 },
                  start_local_date: { type: "string", format: "date" },
                  end_local_date: { type: "string", format: "date" },
                },
              },
            },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: true },
        execute: async (input, { signal }) => {
          const calendarId = requireWebMcpString(input, "calendar_id", 128);
          requireSelectedCalendar(calendarId);
          const expectedVersion = requireWebMcpInteger(input, "expected_version", 1, 1_000_000);
          const name = requireWebMcpString(input, "name", 160);
          const kind = requireWebMcpString(input, "kind", 32);
          if (!["school_holiday", "public_holiday", "travel", "sick", "custom"].includes(kind))
            throw new Error("Unsupported calendar kind.");
          const periods = requireCalendarPeriods(input);
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<{ calendar: WorkspaceCalendar; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/calendars/${calendarId}`,
            {
              method: "PATCH",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({
                expected_version: expectedVersion,
                name,
                kind,
                periods,
                child_ids: [childId],
              }),
            },
            workspaceId,
          );
          await reload();
          return {
            schema_version: 1,
            outcome: "updated",
            calendar: response.calendar,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_set_calendar_archive_v1",
        title: `${childName}のお休みカレンダーをアーカイブまたは復元`,
        description: `選択中の「${childName}」だけに適用されるカレンダーをversion付きでアーカイブまたは復元します。履歴や既存Taskは削除しません。`,
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["calendar_id", "command", "expected_version", "idempotency_key"],
          properties: {
            calendar_id: { type: "string", format: "uuid" },
            expected_version: { type: "integer", minimum: 1 },
            command: { type: "string", enum: ["archive", "restore"] },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: true },
        execute: async (input, { signal }) => {
          const calendarId = requireWebMcpString(input, "calendar_id", 128);
          const calendar = requireSelectedCalendar(calendarId, true);
          const command = requireWebMcpString(input, "command", 16);
          if (command !== "archive" && command !== "restore")
            throw new Error("command must be archive or restore.");
          if (
            (command === "archive" && calendar.archived_at !== null) ||
            (command === "restore" && calendar.archived_at === null)
          )
            throw new Error("The calendar is already in the requested state.");
          const expectedVersion = requireWebMcpInteger(input, "expected_version", 1, 1_000_000);
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<{ calendar: WorkspaceCalendar; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/calendars/${calendarId}/commands/${command}`,
            {
              method: "POST",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({ expected_version: expectedVersion }),
            },
            workspaceId,
          );
          await reload();
          return {
            schema_version: 1,
            outcome: command === "archive" ? "archived" : "restored",
            calendar: response.calendar,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_list_today_tasks_v1",
        title: `${childName}の今日を見る`,
        description: `現在画面で選択中の子供「${childName}」について、今日のタスクと進行状態だけを読みます。`,
        inputSchema: { type: "object", additionalProperties: false },
        annotations: { readOnlyHint: true, untrustedContentHint: true },
        execute: async (_input, { signal }) => {
          const today = await readToday(signal);
          return {
            schema_version: 1,
            child: { id: childId, display_name: childName },
            local_date: workspaceLocalDate,
            items: today.items.map((item) => ({
              task_id: item.task.id,
              title: item.task.title,
              child_copy: item.task.child_copy,
              state: item.assignment.state,
              estimated_minutes: item.task.estimated_minutes,
              planned_local_date: item.task.planned_local_date,
              due_local_date: item.task.due_local_date,
              version: item.assignment.version,
              needs_help: item.help !== null,
            })),
          };
        },
      },
      {
        name: "oyako_list_carryover_tasks_v1",
        title: `${childName}の持ち越しを見る`,
        description: `選択中の「${childName}」について、今日より前にやる予定だった未完了タスクを読みます。進行状態や期限から今日やるとは推測しません。`,
        inputSchema: { type: "object", additionalProperties: false },
        annotations: { readOnlyHint: true, untrustedContentHint: true },
        execute: async (_input, { signal }) => {
          const response = await api<{ items: CarryoverTask[]; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/tasks/carryovers?${new URLSearchParams({
              child_id: childId,
              local_date: workspaceLocalDate,
              limit: "100",
            }).toString()}`,
            { signal },
            workspaceId,
          );
          return {
            schema_version: 1,
            child: { id: childId, display_name: childName },
            local_date: workspaceLocalDate,
            items: response.items,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_list_planned_tasks_v1",
        title: `${childName}の予定を期間で見る`,
        description: `選択中の「${childName}」について、指定した最大32日間のやる日・期限・負荷をCore APIから読みます。`,
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["date_from", "date_to"],
          properties: {
            date_from: { type: "string", format: "date" },
            date_to: { type: "string", format: "date" },
          },
        },
        annotations: { readOnlyHint: true, untrustedContentHint: true },
        execute: async (input, { signal }) => {
          const dateFrom = requireWebMcpDate(input, "date_from");
          const dateTo = requireWebMcpDate(input, "date_to");
          const rangeDays =
            (Date.parse(`${dateTo}T00:00:00Z`) - Date.parse(`${dateFrom}T00:00:00Z`)) / 86_400_000;
          if (rangeDays < 0 || rangeDays > 31)
            throw new Error("date range must contain at most 32 calendar days.");
          const response = await api<{ items: ScheduledTask[]; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/tasks?${new URLSearchParams({
              child_id: childId,
              date_from: dateFrom,
              date_to: dateTo,
              limit: "100",
            }).toString()}`,
            { signal },
            workspaceId,
          );
          return {
            schema_version: 1,
            child: { id: childId, display_name: childName },
            date_from: dateFrom,
            date_to: dateTo,
            items: response.items,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_list_backlog_tasks_v1",
        title: `${childName}のBacklogを見る`,
        description: `選択中の「${childName}」について、やる日がまだ決まっていない未完了タスクを読みます。日付を推測しません。`,
        inputSchema: { type: "object", additionalProperties: false },
        annotations: { readOnlyHint: true, untrustedContentHint: true },
        execute: async (_input, { signal }) => {
          const response = await api<{ items: TodayTask[]; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/tasks/backlog?${new URLSearchParams({
              child_id: childId,
              limit: "100",
            }).toString()}`,
            { signal },
            workspaceId,
          );
          return {
            schema_version: 1,
            child: { id: childId, display_name: childName },
            items: response.items,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_get_task_structure_v1",
        title: `${childName}のタスク手順を見る`,
        description: `選択中の「${childName}」について、画面に表示されているタスク1件のチェックリストと依存関係を読みます。`,
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["task_id"],
          properties: { task_id: { type: "string", format: "uuid" } },
        },
        annotations: { readOnlyHint: true, untrustedContentHint: true },
        execute: async (input, { signal }) => {
          const taskId = requireWebMcpString(input, "task_id", 128);
          const task = requireVisibleTask(taskId);
          const response = await webMcpTaskStructure(task, signal);
          return {
            schema_version: 1,
            child: { id: childId, display_name: childName },
            structure: response.structure,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_add_task_checklist_item_v1",
        title: `${childName}のタスクに手順を追加`,
        description: `選択中の「${childName}」について、画面に表示されているタスク1件へ親向け文言と子供向け文言を追加します。`,
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["task_id", "text", "child_copy", "idempotency_key"],
          properties: {
            task_id: { type: "string", format: "uuid" },
            text: { type: "string", minLength: 1, maxLength: 500 },
            child_copy: { type: "string", minLength: 1, maxLength: 300 },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: true },
        execute: async (input, { signal }) => {
          const taskId = requireWebMcpString(input, "task_id", 128);
          const task = requireVisibleTask(taskId);
          const text = requireWebMcpString(input, "text", 500);
          const childCopy = requireWebMcpString(input, "child_copy", 300);
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<TaskChecklistItemMutationResponse & { request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/tasks/${taskId}/checklist`,
            {
              method: "POST",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({
                assignment_id: task.assignment.id,
                text,
                child_copy: childCopy,
              }),
            },
            workspaceId,
          );
          return {
            schema_version: 1,
            outcome: "created",
            checklist_item: response.item,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_update_task_checklist_item_v1",
        title: `${childName}のタスク手順を編集`,
        description: `選択中の「${childName}」の表示中タスクにあるチェック項目の文言を、期待version付きで更新します。`,
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: [
            "task_id",
            "checklist_item_id",
            "expected_version",
            "text",
            "child_copy",
            "idempotency_key",
          ],
          properties: {
            task_id: { type: "string", format: "uuid" },
            checklist_item_id: { type: "string", format: "uuid" },
            expected_version: { type: "integer", minimum: 1 },
            text: { type: "string", minLength: 1, maxLength: 500 },
            child_copy: { type: "string", minLength: 1, maxLength: 300 },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: true },
        execute: async (input, { signal }) => {
          const taskId = requireWebMcpString(input, "task_id", 128);
          const task = requireVisibleTask(taskId);
          const checklistItemId = requireWebMcpString(input, "checklist_item_id", 128);
          const expectedVersion = requireWebMcpInteger(input, "expected_version", 1, 1_000_000);
          const text = requireWebMcpString(input, "text", 500);
          const childCopy = requireWebMcpString(input, "child_copy", 300);
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<TaskChecklistItemMutationResponse & { request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/tasks/${taskId}/checklist/${checklistItemId}`,
            {
              method: "PATCH",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({
                assignment_id: task.assignment.id,
                expected_version: expectedVersion,
                text,
                child_copy: childCopy,
              }),
            },
            workspaceId,
          );
          return {
            schema_version: 1,
            outcome: "updated",
            checklist_item: response.item,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_set_task_checklist_item_state_v1",
        title: `${childName}のタスク手順の状態を変更`,
        description: `選択中の「${childName}」の表示中タスクにあるチェック項目を完了・未完了・削除へ明示変更します。`,
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: [
            "task_id",
            "checklist_item_id",
            "expected_version",
            "command",
            "idempotency_key",
          ],
          properties: {
            task_id: { type: "string", format: "uuid" },
            checklist_item_id: { type: "string", format: "uuid" },
            expected_version: { type: "integer", minimum: 1 },
            command: { type: "string", enum: ["complete", "reopen", "remove"] },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: false },
        execute: async (input, { signal }) => {
          const taskId = requireWebMcpString(input, "task_id", 128);
          const task = requireVisibleTask(taskId);
          const checklistItemId = requireWebMcpString(input, "checklist_item_id", 128);
          const expectedVersion = requireWebMcpInteger(input, "expected_version", 1, 1_000_000);
          const command = requireWebMcpString(input, "command", 16);
          if (!new Set(["complete", "reopen", "remove"]).has(command))
            throw new Error("command must be complete, reopen, or remove.");
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<TaskChecklistItemMutationResponse & { request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/tasks/${taskId}/checklist/${checklistItemId}/commands/${command}`,
            {
              method: "POST",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({
                assignment_id: task.assignment.id,
                expected_version: expectedVersion,
              }),
            },
            workspaceId,
          );
          return {
            schema_version: 1,
            outcome:
              command === "remove" ? "removed" : command === "complete" ? "completed" : "reopened",
            checklist_item: response.item,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_add_task_dependency_v1",
        title: `${childName}のタスク順を設定`,
        description: `選択中の「${childName}」について、画面に表示中の2タスク間へ循環しない「先に終える」関係を追加します。`,
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["task_id", "depends_on_task_id", "idempotency_key"],
          properties: {
            task_id: { type: "string", format: "uuid" },
            depends_on_task_id: { type: "string", format: "uuid" },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: false },
        execute: async (input, { signal }) => {
          const taskId = requireWebMcpString(input, "task_id", 128);
          const dependsOnTaskId = requireWebMcpString(input, "depends_on_task_id", 128);
          const task = requireVisibleTask(taskId);
          requireVisibleTask(dependsOnTaskId);
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<{ dependency: TaskDependency; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/tasks/${taskId}/dependencies`,
            {
              method: "POST",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({
                assignment_id: task.assignment.id,
                depends_on_task_id: dependsOnTaskId,
              }),
            },
            workspaceId,
          );
          return {
            schema_version: 1,
            outcome: "created",
            dependency: response.dependency,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_remove_task_dependency_v1",
        title: `${childName}のタスク順を外す`,
        description: `選択中の「${childName}」について、画面に表示中のタスクから指定した依存関係だけを外します。`,
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["task_id", "dependency_id", "expected_version", "idempotency_key"],
          properties: {
            task_id: { type: "string", format: "uuid" },
            dependency_id: { type: "string", format: "uuid" },
            expected_version: { type: "integer", minimum: 1 },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: false },
        execute: async (input, { signal }) => {
          const taskId = requireWebMcpString(input, "task_id", 128);
          const task = requireVisibleTask(taskId);
          const dependencyId = requireWebMcpString(input, "dependency_id", 128);
          const expectedVersion = requireWebMcpInteger(input, "expected_version", 1, 1_000_000);
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<{ dependency: TaskDependency; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/tasks/${taskId}/dependencies/${dependencyId}/commands/remove`,
            {
              method: "POST",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({
                assignment_id: task.assignment.id,
                expected_version: expectedVersion,
              }),
            },
            workspaceId,
          );
          return {
            schema_version: 1,
            outcome: "removed",
            dependency: response.dependency,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_list_routines_v1",
        title: `${childName}のルーティンを見る`,
        description: `現在画面で選択中の子供「${childName}」に設定された繰り返し予定と次回Taskだけを読みます。`,
        inputSchema: { type: "object", additionalProperties: false },
        annotations: { readOnlyHint: true, untrustedContentHint: true },
        execute: async (_input, { signal }) => {
          const response = await api<{ items: Routine[]; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/routines?child_id=${encodeURIComponent(childId)}`,
            { signal },
            workspaceId,
          );
          return {
            schema_version: 1,
            child: { id: childId, display_name: childName },
            items: response.items.map((routine) => ({
              routine_id: routine.id,
              name: routine.name,
              child_copy: routine.child_copy,
              status: routine.status,
              schedule: routine.current_version.schedule,
              next_dates: routine.upcoming.map((item) => item.planned_local_date),
              version: routine.current_version.version_number,
            })),
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_list_routine_templates_v1",
        title: "ルーティンのテンプレートを見る",
        description:
          "家族Workspaceで再利用できるルーティン設定を読みます。テンプレートは子供、開始日、プロジェクト、お休み日を含みません。",
        inputSchema: {
          type: "object",
          additionalProperties: false,
          properties: { include_archived: { type: "boolean", default: false } },
        },
        annotations: { readOnlyHint: true, untrustedContentHint: true },
        execute: async (input, { signal }) => {
          const includeArchived = input.include_archived === true;
          const response = await api<{ items: RoutineTemplate[]; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/routine-templates?include_archived=${String(includeArchived)}`,
            { signal },
            workspaceId,
          );
          return { schema_version: 1, items: response.items, request_id: response.request_id };
        },
      },
      {
        name: "oyako_create_routine_template_v1",
        title: "ルーティンのテンプレートを作成",
        description:
          "家族で再利用する繰り返し、親子向け文言、時間帯、目安、期限を保存します。子供や開始日は保存しません。",
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["name", "child_copy", "schedule_kind", "idempotency_key"],
          properties: {
            ...routineTemplateToolProperties,
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: true },
        execute: async (input, { signal }) => {
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<{ template: RoutineTemplate; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/routine-templates`,
            {
              method: "POST",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify(routineTemplatePayloadFromTool(input)),
            },
            workspaceId,
          );
          await reload();
          return {
            schema_version: 1,
            outcome: "created",
            template: response.template,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_update_routine_template_v1",
        title: "ルーティンのテンプレートを更新",
        description:
          "現在のversionを指定してテンプレートを更新します。すでに作成済みのルーティンは変更しません。",
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: [
            "template_id",
            "expected_version",
            "name",
            "child_copy",
            "schedule_kind",
            "idempotency_key",
          ],
          properties: {
            ...routineTemplateToolProperties,
            template_id: { type: "string", format: "uuid" },
            expected_version: { type: "integer", minimum: 1 },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: true },
        execute: async (input, { signal }) => {
          const templateId = requireWebMcpString(input, "template_id", 128);
          const expectedVersion = requireWebMcpInteger(input, "expected_version", 1, 1_000_000);
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<{ template: RoutineTemplate; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/routine-templates/${templateId}`,
            {
              method: "PATCH",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({
                ...routineTemplatePayloadFromTool(input),
                expected_version: expectedVersion,
              }),
            },
            workspaceId,
          );
          await reload();
          return {
            schema_version: 1,
            outcome: "updated",
            template: response.template,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_set_routine_template_archive_v1",
        title: "ルーティンのテンプレートを保管・復元",
        description:
          "テンプレートをアーカイブまたは復元します。作成済みルーティンには影響しません。",
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["template_id", "command", "expected_version", "idempotency_key"],
          properties: {
            template_id: { type: "string", format: "uuid" },
            command: { type: "string", enum: ["archive", "restore"] },
            expected_version: { type: "integer", minimum: 1 },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: false },
        execute: async (input, { signal }) => {
          const templateId = requireWebMcpString(input, "template_id", 128);
          const command = requireWebMcpString(input, "command", 16);
          if (command !== "archive" && command !== "restore")
            throw new Error("command must be archive or restore.");
          const expectedVersion = requireWebMcpInteger(input, "expected_version", 1, 1_000_000);
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<{ template: RoutineTemplate; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/routine-templates/${templateId}/commands/${command}`,
            {
              method: "POST",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({ expected_version: expectedVersion }),
            },
            workspaceId,
          );
          await reload();
          return {
            schema_version: 1,
            outcome: command === "archive" ? "archived" : "restored",
            template: response.template,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_list_routine_versions_v1",
        title: "ルーティンの変更履歴を見る",
        description:
          "選択中の子供に設定されたルーティンの不変バージョンを、署名付きcursorで新しい順に読みます。",
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["routine_id"],
          properties: {
            routine_id: { type: "string", format: "uuid" },
            limit: { type: "integer", minimum: 1, maximum: 50 },
            cursor: { type: "string", minLength: 1, maxLength: 4096 },
          },
        },
        annotations: { readOnlyHint: true, untrustedContentHint: true },
        execute: async (input, { signal }) => {
          const routineId = requireWebMcpString(input, "routine_id", 128);
          if (
            !routines.some(
              (routine) =>
                routine.id === routineId &&
                routine.participants.some((participant) => participant.child_id === childId),
            )
          )
            throw new Error("選択中の子供に設定されたルーティンではありません。");
          const query = new URLSearchParams({
            limit: String(
              input.limit === undefined ? 20 : requireWebMcpInteger(input, "limit", 1, 50),
            ),
          });
          if (input.cursor !== undefined)
            query.set("cursor", requireWebMcpString(input, "cursor", 4096));
          const response = await api<{
            items: RoutineVersion[];
            next_cursor: string | null;
            request_id: string;
          }>(
            `/api/v1/workspaces/${workspaceId}/routines/${routineId}/versions?${query.toString()}`,
            { signal },
            workspaceId,
          );
          return { schema_version: 1, ...response };
        },
      },
      {
        name: "oyako_compare_routine_versions_v1",
        title: "ルーティンのバージョンを比較",
        description:
          "選択中の子供に設定されたルーティンの2つの不変バージョンを比較し、変更項目と両方の値を返します。",
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["routine_id", "from_version", "to_version"],
          properties: {
            routine_id: { type: "string", format: "uuid" },
            from_version: { type: "integer", minimum: 1 },
            to_version: { type: "integer", minimum: 1 },
          },
        },
        annotations: { readOnlyHint: true, untrustedContentHint: true },
        execute: async (input, { signal }) => {
          const routineId = requireWebMcpString(input, "routine_id", 128);
          if (
            !routines.some(
              (routine) =>
                routine.id === routineId &&
                routine.participants.some((participant) => participant.child_id === childId),
            )
          )
            throw new Error("選択中の子供に設定されたルーティンではありません。");
          const query = new URLSearchParams({
            from_version: String(requireWebMcpInteger(input, "from_version", 1, 1_000_000)),
            to_version: String(requireWebMcpInteger(input, "to_version", 1, 1_000_000)),
          });
          const response = await api<RoutineVersionComparison>(
            `/api/v1/workspaces/${workspaceId}/routines/${routineId}/versions/compare?${query.toString()}`,
            { signal },
            workspaceId,
          );
          return { schema_version: 1, ...response };
        },
      },
      {
        name: "oyako_list_routine_reconciliations_v1",
        title: "ルーティン生成の点検履歴を見る",
        description:
          "家族全体の直近の生成漏れ点検について、対象期間、処理状態、漏れ件数、修復件数を読みます。",
        inputSchema: { type: "object", additionalProperties: false },
        annotations: { readOnlyHint: true, untrustedContentHint: false },
        execute: async (_input, { signal }) => {
          const response = await api<{
            items: RoutineReconciliationRun[];
            request_id: string;
          }>(`/api/v1/workspaces/${workspaceId}/routines/reconciliations`, { signal }, workspaceId);
          return { schema_version: 1, items: response.items, request_id: response.request_id };
        },
      },
      {
        name: "oyako_reconcile_routines_v1",
        title: "ルーティンの生成漏れを点検・修復",
        description:
          "親の家族Workspace全体について、今後7〜31日分をQueueとWorkflowで点検し、存在しないTaskだけを同じ一意キーで補います。既存Taskは上書きしません。",
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["window_days", "idempotency_key"],
          properties: {
            window_days: { type: "integer", minimum: 7, maximum: 31 },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: false },
        execute: async (input, { signal }) => {
          const windowDays = requireWebMcpInteger(input, "window_days", 7, 31);
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<{
            run: RoutineReconciliationRun;
            request_id: string;
          }>(
            `/api/v1/workspaces/${workspaceId}/routines/reconciliations`,
            {
              method: "POST",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({ window_days: windowDays }),
            },
            workspaceId,
          );
          await reloadRoutineReconciliations();
          return {
            schema_version: 1,
            outcome: "queued",
            run: response.run,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_list_family_cycles_v1",
        title: `${childName}の家族サイクルを見る`,
        description: `現在選択中の子供「${childName}」が参加するCycleの約束、振り返り、未適用actionを読みます。完了数だけで評価しません。`,
        inputSchema: { type: "object", additionalProperties: false },
        annotations: { readOnlyHint: true, untrustedContentHint: true },
        execute: async (_input, { signal }) => {
          const response = await api<{ items: FamilyCycle[]; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/cycles?include_closed=true`,
            { signal },
            workspaceId,
          );
          return {
            schema_version: 1,
            child: { id: childId, display_name: childName },
            items: response.items.filter((cycle) =>
              cycle.participants.some(
                (participant) =>
                  participant.participant_type === "child" &&
                  participant.participant_id === childId,
              ),
            ),
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_save_family_cycle_review_v1",
        title: `${childName}とのふりかえりを保存`,
        description: `選択中の「${childName}」が参加し、実行中またはふりかえり中のCycleへ、会話の4分類と未適用actionを保存します。TaskやRoutineを変更したとは記録しません。`,
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: [
            "cycle_id",
            "expected_version",
            "review_summary",
            "good",
            "helped",
            "difficult",
            "next_change",
            "actions",
            "idempotency_key",
          ],
          properties: {
            cycle_id: { type: "string", format: "uuid" },
            expected_version: { type: "integer", minimum: 1 },
            review_summary: { type: "string", maxLength: 8000 },
            good: {
              type: "array",
              maxItems: 50,
              items: { type: "string", minLength: 1, maxLength: 2000 },
            },
            helped: {
              type: "array",
              maxItems: 50,
              items: { type: "string", minLength: 1, maxLength: 2000 },
            },
            difficult: {
              type: "array",
              maxItems: 50,
              items: { type: "string", minLength: 1, maxLength: 2000 },
            },
            next_change: {
              type: "array",
              maxItems: 50,
              items: { type: "string", minLength: 1, maxLength: 2000 },
            },
            actions: {
              type: "array",
              maxItems: 50,
              items: {
                type: "object",
                additionalProperties: false,
                required: ["kind", "summary"],
                properties: {
                  kind: {
                    type: "string",
                    enum: ["create_task", "reschedule_task", "update_routine", "other"],
                  },
                  summary: { type: "string", minLength: 1, maxLength: 2000 },
                  target_id: { type: ["string", "null"], format: "uuid" },
                },
              },
            },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: true },
        execute: async (input, { signal }) => {
          const cycleId = requireWebMcpString(input, "cycle_id", 64);
          const expectedVersion = requireWebMcpInteger(input, "expected_version", 1, 1_000_000);
          const currentCycle = requireSelectedCycle(cycleId);
          if (typeof input.review_summary !== "string" || input.review_summary.length > 8000)
            throw new Error("review_summary must be a string of at most 8000 characters.");
          const reviewSummary = input.review_summary.trim();
          const noteCategories = ["good", "helped", "difficult", "next_change"] as const;
          const notes = noteCategories.flatMap((category) => {
            const values = input[category];
            if (
              !Array.isArray(values) ||
              values.length > 50 ||
              values.some(
                (value) =>
                  typeof value !== "string" ||
                  value.trim().length === 0 ||
                  value.trim().length > 2000,
              )
            )
              throw new Error(`${category} must be an array of bounded non-empty strings.`);
            return (values as string[]).map((note) => ({
              category,
              child_id: childId,
              note: note.trim(),
            }));
          });
          const actionValues = input.actions;
          if (
            !Array.isArray(actionValues) ||
            actionValues.length > 50 ||
            actionValues.some((value) => typeof value !== "object" || value === null)
          )
            throw new Error("actions must be an array of structured action objects.");
          const actions = (actionValues as Record<string, unknown>[]).map((value) => {
            const kind = requireWebMcpString(value, "kind", 32);
            if (!new Set(["create_task", "reschedule_task", "update_routine", "other"]).has(kind))
              throw new Error("action kind is invalid.");
            const summary = requireWebMcpString(value, "summary", 2000);
            const rawTargetId = value.target_id;
            const targetId =
              rawTargetId === undefined || rawTargetId === null
                ? null
                : requireWebMcpString(value, "target_id", 128);
            if ((kind === "reschedule_task" || kind === "update_routine") && targetId === null)
              throw new Error("reschedule_task and update_routine require target_id.");
            if ((kind === "create_task" || kind === "other") && targetId !== null)
              throw new Error("create_task and other cannot have target_id.");
            return {
              kind: kind as ReviewActionKind,
              child_id: childId,
              summary,
              target_type:
                kind === "reschedule_task"
                  ? ("task" as const)
                  : kind === "update_routine"
                    ? ("routine" as const)
                    : null,
              target_id: targetId,
            };
          });
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<{ cycle: FamilyCycle; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/cycles/${cycleId}/review`,
            {
              method: "PATCH",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({
                expected_version: expectedVersion,
                review_summary: reviewSummary,
                notes: [
                  ...currentCycle.review_notes
                    .filter((note) => note.child_id !== childId && note.child_id !== null)
                    .map((note) => ({
                      category: note.category,
                      child_id: note.child_id,
                      note: note.note,
                    })),
                  ...notes,
                ],
                actions: [
                  ...currentCycle.review_actions
                    .filter(
                      (action) =>
                        action.status === "proposed" &&
                        action.child_id !== childId &&
                        action.child_id !== null,
                    )
                    .map((action) => ({
                      kind: action.kind,
                      child_id: action.child_id,
                      summary: action.summary,
                      target_type: action.target_type,
                      target_id: action.target_id,
                    })),
                  ...actions,
                ],
              }),
            },
            workspaceId,
          );
          await reload();
          return {
            schema_version: 1,
            outcome: "review_saved_actions_unapplied",
            cycle_id: response.cycle.id,
            version: response.cycle.version,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_apply_family_cycle_action_v1",
        title: `${childName}のふりかえりActionを反映済みにする`,
        description: `選択中の「${childName}」が参加するCycleの候補へ、先に実行済みのTask作成・日程変更・Routine更新のrequest IDを監査確認してリンクします。このツール自身はTaskやRoutineを変更しません。`,
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: [
            "cycle_id",
            "action_id",
            "expected_version",
            "command_request_id",
            "target_type",
            "target_id",
            "idempotency_key",
          ],
          properties: {
            cycle_id: { type: "string", format: "uuid" },
            action_id: { type: "string", format: "uuid" },
            expected_version: { type: "integer", minimum: 1 },
            command_request_id: { type: "string", minLength: 1, maxLength: 128 },
            target_type: { type: "string", enum: ["task", "routine"] },
            target_id: { type: "string", format: "uuid" },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: false },
        execute: async (input, { signal }) => {
          const cycleId = requireWebMcpString(input, "cycle_id", 128);
          const actionId = requireWebMcpString(input, "action_id", 128);
          const expectedVersion = requireWebMcpInteger(input, "expected_version", 1, 1_000_000);
          const commandRequestId = requireWebMcpString(input, "command_request_id", 128);
          const targetType = requireWebMcpString(input, "target_type", 16);
          if (targetType !== "task" && targetType !== "routine")
            throw new Error("target_type must be task or routine.");
          const targetId = requireWebMcpString(input, "target_id", 128);
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const cycle = requireSelectedCycle(cycleId);
          const action = cycle.review_actions.find(
            (candidate) =>
              candidate.id === actionId &&
              candidate.status === "proposed" &&
              (candidate.child_id === childId || candidate.child_id === null),
          );
          if (action === undefined)
            throw new Error("選択中の子供に適用できる未解決Actionではありません。");
          const response = await api<{ cycle: FamilyCycle; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/cycles/${cycleId}/actions/${actionId}/commands/apply`,
            {
              method: "POST",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({
                expected_version: expectedVersion,
                command_request_id: commandRequestId,
                target_type: targetType,
                target_id: targetId,
              }),
            },
            workspaceId,
          );
          await reload();
          return {
            schema_version: 1,
            outcome: "applied_with_verified_command",
            action_id: actionId,
            cycle_version: response.cycle.version,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_dismiss_family_cycle_action_v1",
        title: `${childName}のふりかえりActionを見送る`,
        description: `選択中の「${childName}」が参加するCycleの候補を、理由付きで見送りとして監査記録します。`,
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["cycle_id", "action_id", "expected_version", "reason", "idempotency_key"],
          properties: {
            cycle_id: { type: "string", format: "uuid" },
            action_id: { type: "string", format: "uuid" },
            expected_version: { type: "integer", minimum: 1 },
            reason: { type: "string", minLength: 1, maxLength: 500 },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: true },
        execute: async (input, { signal }) => {
          const cycleId = requireWebMcpString(input, "cycle_id", 128);
          const actionId = requireWebMcpString(input, "action_id", 128);
          const expectedVersion = requireWebMcpInteger(input, "expected_version", 1, 1_000_000);
          const reason = requireWebMcpString(input, "reason", 500);
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const cycle = requireSelectedCycle(cycleId);
          const action = cycle.review_actions.find(
            (candidate) =>
              candidate.id === actionId &&
              candidate.status === "proposed" &&
              (candidate.child_id === childId || candidate.child_id === null),
          );
          if (action === undefined)
            throw new Error("選択中の子供に適用できる未解決Actionではありません。");
          const response = await api<{ cycle: FamilyCycle; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/cycles/${cycleId}/actions/${actionId}/commands/dismiss`,
            {
              method: "POST",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({ expected_version: expectedVersion, reason }),
            },
            workspaceId,
          );
          await reload();
          return {
            schema_version: 1,
            outcome: "dismissed_with_reason",
            action_id: actionId,
            cycle_version: response.cycle.version,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_preview_routine_create_v1",
        title: `${childName}のルーティン作成を確認`,
        description: `選択中の「${childName}」について、日次・週次・月の日付・月の第何曜日のRoutineが作る日と件数をプレビューします。この操作だけではRoutineもTaskも作りません。`,
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: [
            "name",
            "child_copy",
            "schedule_kind",
            "start_local_date",
            "materialize_until_local_date",
            "idempotency_key",
          ],
          properties: {
            name: { type: "string", minLength: 1, maxLength: 160 },
            child_copy: { type: "string", minLength: 1, maxLength: 160 },
            notes: { type: "string", maxLength: 8000 },
            schedule_kind: {
              type: "string",
              enum: ["daily", "weekly", "monthly_days", "monthly_nth_weekday"],
            },
            interval: { type: "integer", minimum: 1, maximum: 52, default: 1 },
            weekdays: {
              type: "array",
              minItems: 1,
              maxItems: 7,
              uniqueItems: true,
              items: { type: "integer", minimum: 1, maximum: 7 },
            },
            month_days: {
              type: "array",
              minItems: 1,
              maxItems: 32,
              uniqueItems: true,
              items: {
                anyOf: [
                  { type: "integer", minimum: 1, maximum: 31 },
                  { type: "string", const: "last" },
                ],
              },
            },
            month_ordinal: { type: "integer", enum: [1, 2, 3, 4, 5, -1] },
            month_weekday: { type: "integer", minimum: 1, maximum: 7 },
            start_local_date: { type: "string", format: "date" },
            end_local_date: { type: "string", format: "date" },
            materialize_until_local_date: { type: "string", format: "date" },
            availability_period: {
              type: "string",
              enum: ["anytime", "morning", "after_school", "evening", "custom"],
              default: "anytime",
            },
            available_from_local_time: {
              type: "string",
              pattern: "^(?:[01][0-9]|2[0-3]):[0-5][0-9]$",
            },
            available_until_local_time: {
              type: "string",
              pattern: "^(?:[01][0-9]|2[0-3]):[0-5][0-9]$",
            },
            child_reminder: { type: "boolean", default: false },
            guardian_digest: { type: "boolean", default: false },
            default_duration_minutes: { type: "integer", minimum: 0, maximum: 1440 },
            due_offset_days: { type: "integer", minimum: 0, maximum: 30, default: 0 },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: true },
        execute: async (input, { signal }) => {
          const name = requireWebMcpString(input, "name", 160);
          const childCopy = requireWebMcpString(input, "child_copy", 160);
          const schedule: RoutineSchedule = requireWebMcpRoutineSchedule(input);
          const startLocalDate = requireWebMcpDate(input, "start_local_date");
          const materializeUntilLocalDate = requireWebMcpDate(
            input,
            "materialize_until_local_date",
          );
          const endLocalDate =
            input.end_local_date === undefined ? null : requireWebMcpDate(input, "end_local_date");
          const notes = input.notes === undefined ? "" : requireWebMcpString(input, "notes", 8000);
          const duration =
            input.default_duration_minutes === undefined
              ? null
              : requireWebMcpInteger(input, "default_duration_minutes", 0, 1440);
          const dueOffset =
            input.due_offset_days === undefined
              ? 0
              : requireWebMcpInteger(input, "due_offset_days", 0, 30);
          const availabilityPeriod =
            input.availability_period === undefined
              ? "anytime"
              : requireWebMcpString(input, "availability_period", 32);
          if (
            !new Set(["anytime", "morning", "after_school", "evening", "custom"]).has(
              availabilityPeriod,
            )
          )
            throw new Error("availability_period is invalid.");
          const availableFromLocalTime =
            availabilityPeriod === "custom"
              ? requireWebMcpString(input, "available_from_local_time", 5)
              : null;
          const availableUntilLocalTime =
            availabilityPeriod === "custom"
              ? requireWebMcpString(input, "available_until_local_time", 5)
              : null;
          if (
            availableFromLocalTime !== null &&
            (!/^(?:[01]\d|2[0-3]):[0-5]\d$/u.test(availableFromLocalTime) ||
              availableUntilLocalTime === null ||
              !/^(?:[01]\d|2[0-3]):[0-5]\d$/u.test(availableUntilLocalTime) ||
              availableUntilLocalTime <= availableFromLocalTime)
          )
            throw new Error("Custom availability requires an increasing HH:MM range.");
          if (
            (input.child_reminder !== undefined && typeof input.child_reminder !== "boolean") ||
            (input.guardian_digest !== undefined && typeof input.guardian_digest !== "boolean")
          )
            throw new Error("Reminder controls must be boolean.");
          const childReminder = input.child_reminder === true;
          const guardianDigest = input.guardian_digest === true;
          if ((childReminder || guardianDigest) && availabilityPeriod !== "custom")
            throw new Error("Routine reminders require an explicit custom time range.");
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<{
            preview: RoutineCreatePreview;
            request_id: string;
          }>(
            `/api/v1/workspaces/${workspaceId}/routines/previews`,
            {
              method: "POST",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({
                name,
                child_copy: childCopy,
                notes,
                area_id: null,
                project_id: null,
                term_id: null,
                child_ids: [childId],
                color: selectedChild.theme_color,
                schedule,
                timezone: session.workspace.timezone,
                start_local_date: startLocalDate,
                end_local_date: endLocalDate,
                availability: {
                  period: availabilityPeriod,
                  from_local_time: availableFromLocalTime,
                  until_local_time: availableUntilLocalTime,
                },
                due_offset_days: dueOffset,
                default_duration_minutes: duration,
                verification_policy: "none",
                evidence_policy: { required: "none" },
                notification_policy: {
                  child_reminder: childReminder,
                  guardian_digest: guardianDigest,
                },
                exception_dates: [],
                pause_ranges: [],
                materialize_from_local_date: startLocalDate,
                materialize_until_local_date: materializeUntilLocalDate,
              }),
            },
            workspaceId,
          );
          return {
            schema_version: 1,
            outcome: "preview_only",
            preview: response.preview,
            instruction: "生成日と件数を親へ提示し、明示確認後にapply toolを呼んでください。",
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_apply_routine_create_v1",
        title: "確認済みのルーティンを作成",
        description:
          "直前のpreviewの全生成日と件数を親へ提示して明示確認を得た後だけ呼びます。confirmation=create_routineが必要です。",
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["preview_id", "expected_affected_count", "confirmation", "idempotency_key"],
          properties: {
            preview_id: { type: "string", format: "uuid" },
            expected_affected_count: { type: "integer", minimum: 0, maximum: 500 },
            confirmation: { type: "string", const: "create_routine" },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: true },
        execute: async (input, { signal }) => {
          const previewId = requireWebMcpString(input, "preview_id", 64);
          const expectedAffectedCount = requireWebMcpInteger(
            input,
            "expected_affected_count",
            0,
            500,
          );
          if (input.confirmation !== "create_routine") {
            throw new Error("confirmation must be create_routine.");
          }
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<{
            routine: Routine;
            materialized_count: number;
            request_id: string;
          }>(
            `/api/v1/workspaces/${workspaceId}/routines/apply`,
            {
              method: "POST",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({
                preview_id: previewId,
                expected_affected_count: expectedAffectedCount,
                confirmation: "create_routine",
              }),
            },
            workspaceId,
          );
          await Promise.all([reload(), reloadTasks(), reloadPlanTasks()]);
          return {
            schema_version: 1,
            outcome: "created",
            routine_id: response.routine.id,
            materialized_count: response.materialized_count,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_create_task_v1",
        title: `${childName}にタスクを追加`,
        description: `現在画面で選択中の子供「${childName}」に1件のタスクを追加します。やる日が未定ならplanned_local_date=nullでBacklogへ置きます。任意の時間帯とToday先頭固定も同じCore APIへ保存します。`,
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: [
            "title",
            "child_copy",
            "planned_local_date",
            "estimated_minutes",
            "idempotency_key",
          ],
          properties: {
            title: { type: "string", minLength: 1, maxLength: 240 },
            child_copy: { type: "string", minLength: 1, maxLength: 160 },
            planned_local_date: { type: ["string", "null"], format: "date" },
            due_local_date: { type: "string", format: "date" },
            available_from_local_time: {
              type: ["string", "null"],
              pattern: "^(?:[01][0-9]|2[0-3]):[0-5][0-9]$",
            },
            available_until_local_time: {
              type: ["string", "null"],
              pattern: "^(?:[01][0-9]|2[0-3]):[0-5][0-9]$",
            },
            today_pinned: { type: "boolean", default: false },
            estimated_minutes: { type: "integer", minimum: 0, maximum: 1440 },
            idempotency_key: {
              type: "string",
              minLength: 16,
              maxLength: 128,
              description: "再試行時にも同じ値を使う一意なキー",
            },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: true },
        execute: async (input, { signal }) => {
          const title = requireWebMcpString(input, "title", 240);
          const childCopy = requireWebMcpString(input, "child_copy", 160);
          const plannedLocalDate =
            input.planned_local_date === null
              ? null
              : requireWebMcpDate(input, "planned_local_date");
          const dueLocalDate =
            input.due_local_date === undefined ? null : requireWebMcpDate(input, "due_local_date");
          if (plannedLocalDate !== null && dueLocalDate !== null && dueLocalDate < plannedLocalDate)
            throw new Error("due_local_date must not be earlier than planned_local_date.");
          const availableFromLocalTime =
            input.available_from_local_time === undefined ||
            input.available_from_local_time === null
              ? null
              : requireWebMcpString(input, "available_from_local_time", 5);
          const availableUntilLocalTime =
            input.available_until_local_time === undefined ||
            input.available_until_local_time === null
              ? null
              : requireWebMcpString(input, "available_until_local_time", 5);
          if (
            (availableFromLocalTime === null) !== (availableUntilLocalTime === null) ||
            (availableFromLocalTime !== null &&
              availableUntilLocalTime !== null &&
              (!/^(?:[01]\d|2[0-3]):[0-5]\d$/u.test(availableFromLocalTime) ||
                !/^(?:[01]\d|2[0-3]):[0-5]\d$/u.test(availableUntilLocalTime) ||
                availableUntilLocalTime <= availableFromLocalTime))
          )
            throw new Error("available time window must be an increasing from/until pair.");
          if (input.today_pinned !== undefined && typeof input.today_pinned !== "boolean")
            throw new Error("today_pinned must be a boolean.");
          const estimatedMinutes = requireWebMcpInteger(input, "estimated_minutes", 0, 1440);
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<{ item: TodayTask; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/tasks`,
            {
              method: "POST",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({
                child_id: childId,
                title,
                child_copy: childCopy,
                planned_local_date: plannedLocalDate,
                due_local_date: dueLocalDate,
                available_from_local_time: availableFromLocalTime,
                available_until_local_time: availableUntilLocalTime,
                today_pinned: input.today_pinned ?? false,
                estimated_minutes: estimatedMinutes,
                priority: 0,
                verification_policy: "none",
              }),
            },
            workspaceId,
          );
          applyScheduledTask(response.item, plannedLocalDate);
          refreshTaskSurfaces();
          return {
            schema_version: 1,
            outcome: "created",
            child: { id: childId, display_name: childName },
            task_id: response.item.task.id,
            placement: plannedLocalDate === null ? "backlog" : "scheduled",
            state: response.item.assignment.state,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_reschedule_task_v1",
        title: `${childName}のタスク日程を変更`,
        description: `選択中の「${childName}」の表示中タスク1件について、やる日、期限、任意の時間帯とToday先頭固定をversion付きで変更します。planned_local_date=nullならBacklogへ戻します。`,
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: [
            "task_id",
            "expected_version",
            "planned_local_date",
            "due_local_date",
            "idempotency_key",
          ],
          properties: {
            task_id: { type: "string", format: "uuid" },
            expected_version: { type: "integer", minimum: 1 },
            planned_local_date: { type: ["string", "null"], format: "date" },
            due_local_date: { type: ["string", "null"], format: "date" },
            available_from_local_time: {
              type: ["string", "null"],
              pattern: "^(?:[01][0-9]|2[0-3]):[0-5][0-9]$",
            },
            available_until_local_time: {
              type: ["string", "null"],
              pattern: "^(?:[01][0-9]|2[0-3]):[0-5][0-9]$",
            },
            today_pinned: { type: "boolean" },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: false },
        execute: async (input, { signal }) => {
          const taskId = requireWebMcpString(input, "task_id", 128);
          const item = requireVisibleTask(taskId);
          const expectedVersion = requireWebMcpInteger(input, "expected_version", 1, 1_000_000);
          if (expectedVersion !== item.task.version)
            throw new Error("タスクが別の端末で変更されています。再読込してください。");
          const plannedLocalDate =
            input.planned_local_date === null
              ? null
              : requireWebMcpDate(input, "planned_local_date");
          const dueLocalDate =
            input.due_local_date === null ? null : requireWebMcpDate(input, "due_local_date");
          if (plannedLocalDate !== null && dueLocalDate !== null && dueLocalDate < plannedLocalDate)
            throw new Error("due_local_date must not be earlier than planned_local_date.");
          const availableFromLocalTime =
            input.available_from_local_time === undefined
              ? item.task.available_from_local_time
              : input.available_from_local_time === null
                ? null
                : requireWebMcpString(input, "available_from_local_time", 5);
          const availableUntilLocalTime =
            input.available_until_local_time === undefined
              ? item.task.available_until_local_time
              : input.available_until_local_time === null
                ? null
                : requireWebMcpString(input, "available_until_local_time", 5);
          if (
            (availableFromLocalTime === null) !== (availableUntilLocalTime === null) ||
            (availableFromLocalTime !== null &&
              availableUntilLocalTime !== null &&
              (!/^(?:[01]\d|2[0-3]):[0-5]\d$/u.test(availableFromLocalTime) ||
                !/^(?:[01]\d|2[0-3]):[0-5]\d$/u.test(availableUntilLocalTime) ||
                availableUntilLocalTime <= availableFromLocalTime))
          )
            throw new Error("available time window must be an increasing from/until pair.");
          if (input.today_pinned !== undefined && typeof input.today_pinned !== "boolean")
            throw new Error("today_pinned must be a boolean.");
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<{ item: TodayTask; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/tasks/${taskId}/schedule`,
            {
              method: "PATCH",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({
                assignment_id: item.assignment.id,
                expected_version: expectedVersion,
                planned_local_date: plannedLocalDate,
                due_local_date: dueLocalDate,
                available_from_local_time: availableFromLocalTime,
                available_until_local_time: availableUntilLocalTime,
                today_pinned: input.today_pinned ?? item.task.today_pinned,
              }),
            },
            workspaceId,
          );
          applyScheduledTask(response.item, plannedLocalDate);
          refreshTaskSurfaces();
          return {
            schema_version: 1,
            outcome: "updated",
            task_id: response.item.task.id,
            placement: plannedLocalDate === null ? "backlog" : "scheduled",
            planned_local_date: plannedLocalDate,
            due_local_date: dueLocalDate,
            available_from_local_time: availableFromLocalTime,
            available_until_local_time: availableUntilLocalTime,
            today_pinned: input.today_pinned ?? item.task.today_pinned,
            version: response.item.task.version,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_reopen_task_v1",
        title: `${childName}の誤完了を取り消す`,
        description: `選択中の「${childName}」の表示中タスク1件を、完了から進行中へ明示的に戻します。確認済みタスクは戻さず、以前の完了履歴も削除しません。`,
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["task_id", "expected_version", "idempotency_key"],
          properties: {
            task_id: { type: "string", format: "uuid" },
            expected_version: { type: "integer", minimum: 1 },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: false },
        execute: async (input, { signal }) => {
          const taskId = requireWebMcpString(input, "task_id", 128);
          const item = requireVisibleTask(taskId);
          const expectedVersion = requireWebMcpInteger(input, "expected_version", 1, 1_000_000);
          if (item.assignment.state !== "done")
            throw new Error("未確認の完了タスクだけを進行中へ戻せます。");
          if (expectedVersion !== item.assignment.version)
            throw new Error("タスクが別の端末で変更されています。再読込してください。");
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<TaskCommandResponse & { request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/tasks/${taskId}/commands/reopen`,
            {
              method: "POST",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({
                assignment_id: item.assignment.id,
                expected_version: expectedVersion,
              }),
            },
            workspaceId,
          );
          applyScheduledTask(response.item, response.item.task.planned_local_date);
          refreshTaskSurfaces();
          void reloadNotifications();
          return {
            schema_version: 1,
            outcome: "reopened",
            task_id: response.item.task.id,
            state: response.item.assignment.state,
            version: response.item.assignment.version,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_change_task_plan_state_v1",
        title: `${childName}のタスク予定を見直す`,
        description: `選択中の「${childName}」の表示中タスク1件を、あとに回す・今回はやらない・取り消す・予定へ戻す、のいずれかへ明示変更します。削除はしません。defer/skip/cancelの理由は任意で、入力した場合だけ履歴へ残します。`,
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["task_id", "expected_version", "command", "idempotency_key"],
          properties: {
            task_id: { type: "string", format: "uuid" },
            expected_version: { type: "integer", minimum: 1 },
            command: { type: "string", enum: ["defer", "skip", "cancel", "restore"] },
            note: { type: "string", minLength: 1, maxLength: 1000 },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: false },
        execute: async (input, { signal }) => {
          const taskId = requireWebMcpString(input, "task_id", 128);
          const item = requireVisibleTask(taskId);
          const expectedVersion = requireWebMcpInteger(input, "expected_version", 1, 1_000_000);
          if (expectedVersion !== item.assignment.version)
            throw new Error("タスクが別の端末で変更されています。再読込してください。");
          const command = requireWebMcpString(input, "command", 16);
          if (
            command !== "defer" &&
            command !== "skip" &&
            command !== "cancel" &&
            command !== "restore"
          )
            throw new Error("command must be defer, skip, cancel, or restore.");
          const note =
            input.note === undefined ? undefined : requireWebMcpString(input, "note", 1000);
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<TaskCommandResponse & { request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/tasks/${taskId}/commands/${command}`,
            {
              method: "POST",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({
                assignment_id: item.assignment.id,
                expected_version: expectedVersion,
                ...(note === undefined ? {} : { note }),
              }),
            },
            workspaceId,
          );
          applyScheduledTask(response.item, response.item.task.planned_local_date);
          refreshTaskSurfaces();
          void reloadNotifications();
          return {
            schema_version: 1,
            outcome: "updated",
            task_id: response.item.task.id,
            command: response.command,
            state: response.item.assignment.state,
            version: response.item.assignment.version,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_reorder_today_tasks_v1",
        title: `${childName}の今日の順番を変更`,
        description: `選択中の「${childName}」について、表示中のToday全件をassignment version付きで原子的に並べ替えます。欠落・重複・他端末変更・親の先頭固定・時間帯・依存順違反は全件拒否します。`,
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["items", "idempotency_key"],
          properties: {
            items: {
              type: "array",
              minItems: 2,
              maxItems: 50,
              items: {
                type: "object",
                additionalProperties: false,
                required: ["task_id", "expected_version"],
                properties: {
                  task_id: { type: "string", format: "uuid" },
                  expected_version: { type: "integer", minimum: 1 },
                },
              },
            },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: false },
        execute: async (input, { signal }) => {
          const ordered = requireTodayOrderItems(input, tasks);
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          if (signal.aborted) throw signal.reason;
          const response = await enqueueTodayTaskReorder(
            session,
            childId,
            workspaceLocalDate,
            ordered,
            `webmcp:${idempotencyKey}`,
          );
          if (response !== null) setTasks(response.items);
          return {
            schema_version: 1,
            outcome: response === null ? "queued_offline" : "reordered",
            child: { id: childId, display_name: childName },
            ordered_task_ids: ordered.map((item) => item.task.id),
            versions:
              response?.items.map((item) => ({
                task_id: item.task.id,
                version: item.assignment.version,
              })) ??
              ordered.map((item) => ({
                task_id: item.task.id,
                version: item.assignment.version,
              })),
            request_id: response?.request_id ?? null,
          };
        },
      },
      {
        name: "oyako_list_notification_preferences_v1",
        title: "家族の通知設定を読む",
        description:
          "現在ログインしている親が管理できる、親・子供・ルーティン・曜日・時間帯・通知方法ごとのルールをCore APIから読みます。",
        inputSchema: { type: "object", additionalProperties: false },
        annotations: { readOnlyHint: true, untrustedContentHint: false },
        execute: async (_input, { signal }) => {
          const response = await api<{
            items: NotificationPreference[];
            request_id: string;
          }>(`/api/v1/workspaces/${workspaceId}/notification-preferences`, { signal }, workspaceId);
          return {
            schema_version: 1,
            items: response.items,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_save_notification_preference_v1",
        title: "家族の通知設定を保存",
        description:
          "通知先、対象の子供・ルーティン、曜日、時間帯、通知方法、その都度またはダイジェストをすべて明示し、楽観versionと冪等キー付きでCore APIへ保存します。",
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["preference", "idempotency_key"],
          properties: {
            preference: {
              type: "object",
              additionalProperties: false,
              required: [
                "expected_version",
                "recipient",
                "related_child_id",
                "routine_id",
                "channel",
                "event_types",
                "quiet_hours_behavior",
                "batching_mode",
                "enabled",
                "active_weekdays",
                "active_from_local_time",
                "active_until_local_time",
                "digest_local_time",
              ],
              properties: {
                expected_version: { type: ["integer", "null"], minimum: 1 },
                recipient: {
                  oneOf: [
                    {
                      type: "object",
                      additionalProperties: false,
                      required: ["type"],
                      properties: { type: { const: "membership" } },
                    },
                    {
                      type: "object",
                      additionalProperties: false,
                      required: ["type", "child_id"],
                      properties: {
                        type: { const: "child" },
                        child_id: { type: "string", format: "uuid" },
                      },
                    },
                  ],
                },
                related_child_id: { type: ["string", "null"], format: "uuid" },
                routine_id: { type: ["string", "null"], format: "uuid" },
                channel: { type: "string", enum: ["in_app", "web_push"] },
                event_types: {
                  type: "array",
                  minItems: 1,
                  maxItems: 8,
                  uniqueItems: true,
                  items: {
                    type: "string",
                    enum: [...new Set([...guardianNotificationEvents, ...childNotificationEvents])],
                  },
                },
                quiet_hours_behavior: {
                  type: "string",
                  enum: ["defer", "digest", "allow"],
                },
                batching_mode: { type: "string", enum: ["immediate", "digest"] },
                enabled: { type: "boolean" },
                active_weekdays: {
                  type: "array",
                  minItems: 1,
                  maxItems: 7,
                  uniqueItems: true,
                  items: { type: "integer", minimum: 0, maximum: 6 },
                },
                active_from_local_time: {
                  type: ["string", "null"],
                  pattern: "^(?:[01][0-9]|2[0-3]):[0-5][0-9]$",
                },
                active_until_local_time: {
                  type: ["string", "null"],
                  pattern: "^(?:[01][0-9]|2[0-3]):[0-5][0-9]$",
                },
                digest_local_time: {
                  type: ["string", "null"],
                  pattern: "^(?:[01][0-9]|2[0-3]):[0-5][0-9]$",
                },
              },
            },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: false },
        execute: async (input, { signal }) => {
          const preference = parseWebMcpNotificationPreference(input.preference);
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<{
            preference: NotificationPreference;
            request_id: string;
          }>(
            `/api/v1/workspaces/${workspaceId}/notification-preferences`,
            {
              method: "PUT",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify(preference),
            },
            workspaceId,
          );
          await reloadNotificationPreferences();
          return {
            schema_version: 1,
            outcome: "saved",
            preference: response.preference,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_list_notifications_v1",
        title: "親に届いた通知を読む",
        description:
          "現在ログインしている親本人の通知をCore APIから読みます。通知本文をタスク完了や子供の評価として解釈しません。",
        inputSchema: {
          type: "object",
          additionalProperties: false,
          properties: {
            unread_only: { type: "boolean", default: false },
            limit: { type: "integer", minimum: 1, maximum: 100, default: 50 },
            cursor: { type: "string", minLength: 1, maxLength: 4096 },
          },
        },
        annotations: { readOnlyHint: true, untrustedContentHint: true },
        execute: async (input, { signal }) => {
          const unreadOnly = input.unread_only === undefined ? false : input.unread_only;
          if (typeof unreadOnly !== "boolean") throw new Error("unread_only must be boolean.");
          const limit =
            input.limit === undefined ? 50 : requireWebMcpInteger(input, "limit", 1, 100);
          const cursor =
            input.cursor === undefined ? undefined : requireWebMcpString(input, "cursor", 4096);
          const query = new URLSearchParams({
            unread_only: String(unreadOnly),
            limit: String(limit),
          });
          if (cursor !== undefined) query.set("cursor", cursor);
          const response = await api<{
            items: ParentNotification[];
            next_cursor: string | null;
            request_id: string;
          }>(
            `/api/v1/workspaces/${workspaceId}/notifications?${query.toString()}`,
            { signal },
            workspaceId,
          );
          return {
            schema_version: 1,
            items: response.items,
            next_cursor: response.next_cursor,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_set_notification_state_v1",
        title: "通知を既読・未読・あとでにする",
        description:
          "画面に表示中の親本人の通知をversion付きで更新します。snoozeでは5分後から30日後までの時刻を明示します。",
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["notification_id", "command", "expected_version", "idempotency_key"],
          properties: {
            notification_id: { type: "string", format: "uuid" },
            command: { type: "string", enum: ["read", "unread", "snooze"] },
            expected_version: { type: "integer", minimum: 1 },
            snoozed_until: { type: "integer", minimum: 1 },
            idempotency_key: { type: "string", minLength: 16, maxLength: 128 },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: false },
        execute: async (input, { signal }) => {
          const notificationId = requireWebMcpString(input, "notification_id", 128);
          const command = requireWebMcpString(input, "command", 20);
          if (command !== "read" && command !== "unread" && command !== "snooze")
            throw new Error("command must be read, unread, or snooze.");
          const expectedVersion = requireWebMcpInteger(input, "expected_version", 1, 1_000_000);
          const current = notifications.find((item) => item.id === notificationId);
          if (current === undefined)
            throw new Error("画面に表示されていない通知です。再読込してください。");
          if (current.version !== expectedVersion)
            throw new Error("通知が別の操作で変更されています。再読込してください。");
          const snoozedUntil =
            command === "snooze"
              ? requireWebMcpInteger(input, "snoozed_until", 1, Number.MAX_SAFE_INTEGER)
              : undefined;
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<{
            notification: ParentNotification;
            request_id: string;
          }>(
            `/api/v1/workspaces/${workspaceId}/notifications/${notificationId}/commands/${command}`,
            {
              method: "POST",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({
                expected_version: expectedVersion,
                ...(command === "snooze" ? { snoozed_until: snoozedUntil } : {}),
              }),
            },
            workspaceId,
          );
          await reloadNotifications();
          return {
            schema_version: 1,
            outcome: command,
            notification: response.notification,
            request_id: response.request_id,
          };
        },
      },
      {
        name: "oyako_capture_inbox_item_v1",
        title: "未整理の内容をInboxへ保存",
        description:
          "子供や日付を推測せず、親があとで確認するInbox項目として保存します。子供のTodayには表示しません。",
        inputSchema: {
          type: "object",
          additionalProperties: false,
          required: ["title", "idempotency_key"],
          properties: {
            title: { type: "string", minLength: 1, maxLength: 240 },
            notes: { type: "string", maxLength: 8000 },
            idempotency_key: {
              type: "string",
              minLength: 16,
              maxLength: 128,
              description: "再試行時にも同じ値を使う一意なキー",
            },
          },
        },
        annotations: { readOnlyHint: false, untrustedContentHint: true },
        execute: async (input, { signal }) => {
          const title = requireWebMcpString(input, "title", 240);
          const notesValue = input.notes;
          if (notesValue !== undefined && typeof notesValue !== "string") {
            throw new Error("notes must be a string.");
          }
          const notes = notesValue?.trim() ?? "";
          if (notes.length > 8000) throw new Error("notes must be at most 8000 characters.");
          const idempotencyKey = requireWebMcpIdempotencyKey(input);
          const response = await api<{ item: InboxItem; request_id: string }>(
            `/api/v1/workspaces/${workspaceId}/inbox-items`,
            {
              method: "POST",
              signal,
              headers: { "Idempotency-Key": `webmcp:${idempotencyKey}` },
              body: JSON.stringify({ title, notes }),
            },
            workspaceId,
          );
          await reloadInbox();
          return {
            schema_version: 1,
            outcome: "captured_for_parent_review",
            inbox_item_id: response.item.id,
            status: response.item.status,
            request_id: response.request_id,
          };
        },
      },
    ];
    const registration = registerWebMcpTools(
      selectParentWebMcpTools(tools, {
        view: activeView,
        isGuardian,
        capabilities: new Set(session.actor.membership_scopes.map((scope) => scope.capability)),
        notificationCenterOpen,
      }),
    );
    return registration.dispose;
  }, [
    activeView,
    applyScheduledTask,
    backlogTasks,
    carryoverTasks,
    cycles,
    familyLoading,
    isGuardian,
    notificationCenterOpen,
    notifications,
    planTasks,
    refreshTaskSurfaces,
    reload,
    reloadBacklogTasks,
    reloadCarryoverTasks,
    reloadInbox,
    reloadPlanTasks,
    reloadNotifications,
    reloadNotificationPreferences,
    reloadRoutineReconciliations,
    reloadTasks,
    routines,
    selectedChild,
    session,
    tasks,
    workspaceCalendars,
    workspaceId,
    workspaceLocalDate,
  ]);

  useEffect(() => {
    if (!isGuardian || activeView !== "search") return;
    const tools = createWebMcpSearchHistoryTools(workspaceId, (path, signal) =>
      api<unknown>(path, { signal }, workspaceId),
    );
    const registration = registerWebMcpTools(tools);
    return registration.dispose;
  }, [activeView, isGuardian, workspaceId]);

  const parentColumns: { lane: TodayLaneId; title: string; states: TaskState[] }[] = [
    {
      lane: "todo",
      title: "やる予定",
      states: ["planned", "ready", "deferred", "needs_changes"],
    },
    { lane: "doing", title: "進行中", states: ["in_progress", "blocked"] },
    { lane: "done", title: "完了", states: ["done", "verified", "skipped", "cancelled"] },
  ];
  const selectedParentTodayTask =
    tasks.find((item) => item.task.id === selectedParentTodayTaskId) ?? null;
  const selectedParentTodayColumn =
    selectedParentTodayTask === null
      ? null
      : (parentColumns.find((column) =>
          column.states.includes(selectedParentTodayTask.assignment.state),
        ) ?? null);
  const selectedParentTodayColumnTasks =
    selectedParentTodayColumn === null
      ? []
      : tasks.filter((item) => selectedParentTodayColumn.states.includes(item.assignment.state));
  const activeCount = tasks.filter((item) =>
    ["in_progress", "blocked"].includes(item.assignment.state),
  ).length;
  const doneCount = tasks.filter((item) =>
    ["done", "verified"].includes(item.assignment.state),
  ).length;
  const unreadNotificationCount = notifications.filter(
    (notification) => notification.read_at === null,
  ).length;
  const planWeekDays = planningWeek(planAnchorDate);
  const planMonthDays = planningMonth(planAnchorDate);
  const visiblePlanDays = planDisplayMode === "week" ? planWeekDays : planMonthDays;
  const planMinutes = planTasks.reduce(
    (total, item) => total + (item.task.estimated_minutes ?? 0),
    0,
  );
  const viewCopy = {
    today: {
      eyebrow: workspaceLocalDate,
      title: "今日のタスク",
      description: "子供ごとの進み具合を見ながら、必要なときだけ声をかけられます。",
    },
    dashboard: {
      eyebrow: "振り返りと見通し",
      title: "ダッシュボード",
      description: "順位ではなく、負荷・自己開始・助けを求められたことを会話につなげます。",
    },
    inbox: {
      eyebrow: "未整理",
      title: "Inbox",
      description: "日付や子供がまだ決まっていないことを失わず、あとから計画に移します。",
    },
    plan: {
      eyebrow: "計画",
      title: "今週の計画",
      description: "未来の予定と期限を見ながら、無理のない一週間を組み立てます。",
    },
    routines: {
      eyebrow: "繰り返し",
      title: "ルーティン",
      description: "曜日や間隔、休む日を決め、毎日の計画へ先回りしてつなげます。",
    },
    projects: {
      eyebrow: "計画",
      title: "プロジェクト",
      description: "目標と期間、取り組む子供をまとめ、毎日のタスクにつなげます。",
    },
    organization: {
      eyebrow: "整理",
      title: "領域と期間",
      description: "家族の取り組みを分類し、学期や長期休みの区切りを計画に結び付けます。",
    },
    search: {
      eyebrow: "見つける・確かめる",
      title: "検索と履歴",
      description: "予定の内容、家族で行った変更、セキュリティ記録を混ぜずに確認します。",
    },
    review: {
      eyebrow: "親子の会話",
      title: "家族サイクル",
      description: "一緒に決めた約束を見返し、できたことと次に変えることを残します。",
    },
    settings: {
      eyebrow: "設定",
      title: "家族と設定",
      description: "子供プロフィール、端末、アカウントの安全を管理します。",
    },
  }[activeView];

  return (
    <main className="parent-app-shell">
      <aside className="parent-sidebar" aria-label="親メニュー">
        <div className="parent-brand">
          <span className="brand-mark" aria-hidden="true">
            O
          </span>
          <div>
            <strong>Oyako Flow</strong>
            <small>{session.workspace.name}</small>
          </div>
        </div>
        {isGuardian ? (
          <button
            className="parent-notification-launcher"
            aria-label={`通知${unreadNotificationCount > 0 ? `、未読${String(unreadNotificationCount)}件` : ""}`}
            onClick={() => setNotificationCenterOpen(true)}
          >
            <span aria-hidden="true">♢</span>
            <span>通知</span>
            {unreadNotificationCount > 0 ? (
              <strong aria-hidden="true">{unreadNotificationCount}</strong>
            ) : null}
          </button>
        ) : null}
        <div className="parent-sidebar-scroll">
          {activeChildren.length === 0 ? null : (
            <div className="parent-global-child-switcher" role="group" aria-label="表示する子供">
              <label>
                <span>表示する子供</span>
                <select
                  value={selectedChildId ?? ""}
                  onChange={(event) => selectChild(event.currentTarget.value)}
                >
                  {activeChildren.map((child) => (
                    <option key={child.id} value={child.id}>
                      {child.display_name}
                    </option>
                  ))}
                </select>
              </label>
              {isGuardian ? (
                <button
                  className="child-add-shortcut"
                  onClick={() => {
                    navigateParent("settings");
                    openChildCreator();
                  }}
                >
                  <span aria-hidden="true">＋</span>
                  <span>子供を追加</span>
                </button>
              ) : null}
            </div>
          )}
          <nav className="parent-nav">
            {canReadTasks ? (
              <button
                aria-current={activeView === "today" ? "page" : undefined}
                onClick={() => navigateParent("today")}
              >
                <span aria-hidden="true">☀</span>
                今日
              </button>
            ) : null}
            {canReadInsights ? (
              <button
                aria-current={activeView === "dashboard" ? "page" : undefined}
                onClick={() => navigateParent("dashboard")}
              >
                <span aria-hidden="true">◫</span>
                集計
              </button>
            ) : null}
            {isGuardian ? (
              <button
                aria-current={activeView === "inbox" ? "page" : undefined}
                onClick={() => navigateParent("inbox")}
              >
                <span aria-hidden="true">⌁</span>
                Inbox
              </button>
            ) : null}
            {canReadTasks ? (
              <button
                aria-current={activeView === "plan" ? "page" : undefined}
                onClick={() => navigateParent("plan")}
              >
                <span aria-hidden="true">▤</span>
                計画
              </button>
            ) : null}
            {canReadRoutines ? (
              <button
                className="parent-nav-secondary"
                aria-current={activeView === "routines" ? "page" : undefined}
                onClick={() => navigateParent("routines")}
              >
                <span aria-hidden="true">↻</span>
                ルーティン
              </button>
            ) : null}
            {isGuardian ? (
              <>
                <button
                  className="parent-nav-secondary"
                  aria-current={activeView === "projects" ? "page" : undefined}
                  onClick={() => navigateParent("projects")}
                >
                  <span aria-hidden="true">▦</span>
                  プロジェクト
                </button>
                <button
                  className="parent-nav-secondary"
                  aria-current={activeView === "organization" ? "page" : undefined}
                  onClick={() => navigateParent("organization")}
                >
                  <span aria-hidden="true">◇</span>
                  領域と期間
                </button>
              </>
            ) : null}
            {canReadReviews ? (
              <button
                className="parent-nav-secondary"
                aria-current={activeView === "review" ? "page" : undefined}
                onClick={() => navigateParent("review")}
              >
                <span aria-hidden="true">◎</span>
                ふりかえり
              </button>
            ) : null}
            {isGuardian ? (
              <>
                <button
                  className="parent-nav-secondary"
                  aria-current={activeView === "search" ? "page" : undefined}
                  onClick={() => navigateParent("search")}
                >
                  <span aria-hidden="true">⌕</span>
                  検索と履歴
                </button>
                <button
                  className="parent-nav-secondary"
                  aria-current={activeView === "settings" ? "page" : undefined}
                  onClick={() => navigateParent("settings")}
                >
                  <span aria-hidden="true">⌂</span>
                  設定
                </button>
              </>
            ) : null}
            {isGuardian || canReadRoutines || canReadReviews ? (
              <details className="parent-more-nav">
                <summary
                  aria-current={
                    activeView === "routines" ||
                    activeView === "projects" ||
                    activeView === "organization" ||
                    activeView === "review" ||
                    activeView === "search" ||
                    activeView === "settings"
                      ? "page"
                      : undefined
                  }
                >
                  <span aria-hidden="true">•••</span>
                  その他
                </summary>
                <div>
                  {canReadRoutines ? (
                    <button
                      aria-current={activeView === "routines" ? "page" : undefined}
                      onClick={(event) => {
                        navigateParent("routines");
                        event.currentTarget.closest("details")?.removeAttribute("open");
                      }}
                    >
                      ルーティン
                    </button>
                  ) : null}
                  {isGuardian ? (
                    <button
                      aria-current={activeView === "projects" ? "page" : undefined}
                      onClick={(event) => {
                        navigateParent("projects");
                        event.currentTarget.closest("details")?.removeAttribute("open");
                      }}
                    >
                      プロジェクト
                    </button>
                  ) : null}
                  {isGuardian ? (
                    <button
                      aria-current={activeView === "organization" ? "page" : undefined}
                      onClick={(event) => {
                        navigateParent("organization");
                        event.currentTarget.closest("details")?.removeAttribute("open");
                      }}
                    >
                      領域と期間
                    </button>
                  ) : null}
                  {canReadReviews ? (
                    <button
                      aria-current={activeView === "review" ? "page" : undefined}
                      onClick={(event) => {
                        navigateParent("review");
                        event.currentTarget.closest("details")?.removeAttribute("open");
                      }}
                    >
                      ふりかえり
                    </button>
                  ) : null}
                  {isGuardian ? (
                    <button
                      aria-current={activeView === "search" ? "page" : undefined}
                      onClick={(event) => {
                        navigateParent("search");
                        event.currentTarget.closest("details")?.removeAttribute("open");
                      }}
                    >
                      検索と履歴
                    </button>
                  ) : null}
                  {isGuardian ? (
                    <button
                      aria-current={activeView === "settings" ? "page" : undefined}
                      onClick={(event) => {
                        navigateParent("settings");
                        event.currentTarget.closest("details")?.removeAttribute("open");
                      }}
                    >
                      設定
                    </button>
                  ) : null}
                </div>
              </details>
            ) : null}
          </nav>
        </div>
        <button className="sidebar-logout" onClick={onLogout}>
          ログアウト
        </button>
      </aside>

      <section className="parent-main">
        <div className="mobile-global-context">
          {activeChildren.length === 0 ? null : (
            <label className="mobile-child-context">
              <span>表示する子供</span>
              <select
                value={selectedChildId ?? ""}
                onChange={(event) => selectChild(event.currentTarget.value)}
              >
                {activeChildren.map((child) => (
                  <option key={child.id} value={child.id}>
                    {child.display_name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="mobile-session-actions">
            <button className="mobile-logout" onClick={onLogout}>
              ログアウト
            </button>
            {isGuardian ? (
              <button
                className="mobile-notification-launcher"
                aria-label={`通知${unreadNotificationCount > 0 ? `、未読${String(unreadNotificationCount)}件` : ""}`}
                onClick={() => setNotificationCenterOpen(true)}
              >
                通知
                {unreadNotificationCount > 0 ? <strong>{unreadNotificationCount}</strong> : null}
              </button>
            ) : null}
          </div>
        </div>
        <header className="parent-page-header">
          <div>
            <p className="eyebrow">{viewCopy.eyebrow}</p>
            <h1>{viewCopy.title}</h1>
            <p className="lede">{viewCopy.description}</p>
          </div>
          {activeView === "today" ? (
            <div className="parent-header-actions">
              {selectedChild === null ? null : (
                <>
                  <a
                    className="button-link header-link"
                    href={childPreviewHref(selectedChild.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    子供画面を開く <span aria-hidden="true">↗</span>
                  </a>
                  {canWriteTasks ? (
                    <button
                      className="primary add-task-button"
                      onClick={() => openTaskCreator({ context: "today" })}
                    >
                      ＋ 今日に追加
                    </button>
                  ) : null}
                </>
              )}
            </div>
          ) : null}
          {activeView === "inbox" ? (
            <button className="primary add-task-button" onClick={openInboxCreator}>
              ＋ Inboxに追加
            </button>
          ) : null}
          {activeView === "plan" && children.length > 0 && canWriteTasks ? (
            <button
              className="primary add-task-button"
              onClick={() => openTaskCreator({ context: "plan", localDate: planAnchorDate })}
            >
              ＋ タスクを作成
            </button>
          ) : null}
          {activeView === "projects" && children.length > 0 ? (
            <button className="header-link" onClick={openProjectCreator}>
              ＋ プロジェクトを作成
            </button>
          ) : null}
          {activeView === "routines" && children.length > 0 && canWriteRoutines ? (
            <button className="header-link" onClick={openRoutineCreator}>
              ＋ ルーティンを作成
            </button>
          ) : null}
          {activeView === "organization" ? (
            <div className="parent-header-actions">
              <button className="header-link" onClick={() => openAreaEditor("create")}>
                ＋ 領域を追加
              </button>
              <button className="header-link" onClick={() => openTermEditor("create")}>
                ＋ 期間を追加
              </button>
            </div>
          ) : null}
          {activeView === "review" && children.length > 0 && isGuardian ? (
            <button className="header-link" onClick={() => openCyclePlanEditor("create")}>
              ＋ 次のサイクルを作成
            </button>
          ) : null}
          {activeView === "settings" ? (
            <button className="header-link" onClick={openChildCreator}>
              ＋ 子供を追加
            </button>
          ) : null}
        </header>

        {error === null ? null : (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        {notice === null || selectedParentTodayTaskId !== null ? null : (
          <FloatingNotice message={notice} onDismiss={dismissNotice} />
        )}

        {activeView === "today" ? (
          <div className="parent-view">
            {familyLoading ? (
              <section className="panel loading-panel" aria-busy="true">
                <p className="status" role="status">
                  家族の情報を読み込んでいます…
                </p>
              </section>
            ) : children.length === 0 ? (
              <section className="panel empty-onboarding">
                <h2>{isGuardian ? "まず子供を追加しましょう" : "表示できる子供がいません"}</h2>
                <p>
                  {isGuardian
                    ? "プロフィールを作ると、その子の今日のタスクを計画できます。"
                    : "管理者に、子供を指定した支援範囲を確認してもらってください。"}
                </p>
                {isGuardian ? (
                  <button
                    className="primary"
                    onClick={() => {
                      navigateParent("settings");
                      openChildCreator();
                    }}
                  >
                    子供を追加
                  </button>
                ) : null}
              </section>
            ) : tasksLoading ? (
              <section className="panel loading-panel" aria-busy="true">
                <p className="status" role="status">
                  {selectedChild?.display_name ?? "子供"}の今日を読み込んでいます…
                </p>
              </section>
            ) : (
              <>
                <CarryoverSection
                  items={carryoverTasks}
                  loading={carryoverTasksLoading}
                  busyTaskId={dragMoveBusyTaskId}
                  canWrite={canWriteTasks}
                  onMoveToday={(item) => void moveCarryoverTaskToToday(item)}
                  onChooseDate={(item) => openTaskScheduleEditor(item)}
                  onReview={openTaskPlanDecision}
                />
                <section className="task-summary" aria-label="今日の集計">
                  <div>
                    <strong>{tasks.length}</strong>
                    <span>今日のタスク</span>
                  </div>
                  <div>
                    <strong>{activeCount}</strong>
                    <span>進行中</span>
                  </div>
                  <div>
                    <strong>{doneCount}</strong>
                    <span>完了</span>
                  </div>
                </section>

                <section
                  className="parent-kanban"
                  aria-label={`${selectedChild?.display_name ?? "子供"}の今日`}
                >
                  {parentColumns.map((column) => {
                    const columnTasks = tasks.filter((item) =>
                      column.states.includes(item.assignment.state),
                    );
                    return (
                      <article
                        className={`parent-column today-drop-lane${parentTodayDropTarget?.lane === column.lane && parentTodayDropTarget.taskId === null ? " today-drop-lane-active" : ""}`}
                        key={column.title}
                        data-today-drop-lane={column.lane}
                      >
                        <header>
                          <h2>{column.title}</h2>
                          <span>{columnTasks.length}</span>
                        </header>
                        {columnTasks.length === 0 ? (
                          <p className="column-empty">タスクはありません</p>
                        ) : (
                          <ul className="task-list">
                            {columnTasks.map((item) => {
                              const canDrag = canDirectDragParentTodayTask(item);
                              const dropClass =
                                parentTodayDropTarget?.taskId !== item.task.id
                                  ? ""
                                  : parentTodayDropTarget.placement === "before"
                                    ? "parent-today-task-drop-before"
                                    : "parent-today-task-drop-after";
                              return (
                                <li
                                  className={`task-card parent-task-card parent-today-task-card${canDrag ? " parent-today-task-card-draggable" : ""}${dropClass === "" ? "" : ` ${dropClass}`}`}
                                  key={item.task.id}
                                  data-today-drag-task-id={item.task.id}
                                  onPointerDown={
                                    canDrag
                                      ? (event) => beginParentTodayPointerDrag(event, item)
                                      : undefined
                                  }
                                  onPointerMove={canDrag ? moveParentTodayPointerDrag : undefined}
                                  onPointerUp={
                                    canDrag
                                      ? (event) => finishParentTodayPointerDrag(event)
                                      : undefined
                                  }
                                  onPointerCancel={
                                    canDrag
                                      ? (event) => finishParentTodayPointerDrag(event, true)
                                      : undefined
                                  }
                                  onLostPointerCapture={
                                    canDrag
                                      ? (event) => finishParentTodayPointerDrag(event, true)
                                      : undefined
                                  }
                                >
                                  <button
                                    type="button"
                                    className="parent-today-task-card-open"
                                    aria-label={`${item.task.title}を開く${canDrag ? "。カードをドラッグして順番や状態を変更" : ""}`}
                                    aria-haspopup="dialog"
                                    draggable={false}
                                    onClick={() => openParentTodayTaskFromCard(item)}
                                  />
                                  <div className="task-card-heading">
                                    <TaskIcon id={taskIcon(item)} />
                                    <div>
                                      <strong>{item.task.title}</strong>
                                      <small>{item.task.child_copy}</small>
                                      {item.project === null ? null : (
                                        <span className="project-label">
                                          <i
                                            style={{ background: item.project.color }}
                                            aria-hidden="true"
                                          />
                                          {item.project.name}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="task-meta">
                                    {item.task.estimated_minutes === null ? null : (
                                      <span>{item.task.estimated_minutes}分</span>
                                    )}
                                    {item.task.today_pinned ? (
                                      <span className="task-constraint-pill">📌 先頭に固定</span>
                                    ) : null}
                                    {item.task.available_from_local_time === null ||
                                    item.task.available_until_local_time === null ? null : (
                                      <span className="task-constraint-pill">
                                        {item.task.available_from_local_time}–
                                        {item.task.available_until_local_time}
                                      </span>
                                    )}
                                    {item.help === null ? null : (
                                      <span className="help-pill">要確認</span>
                                    )}
                                    {item.assignment.state === "done" &&
                                    item.task.verification_policy === "guardian_required" ? (
                                      <span className="help-pill">親の確認待ち</span>
                                    ) : null}
                                    {item.task.evidence_policy.required === "none" ? null : (
                                      <span className="task-constraint-pill">
                                        {item.task.evidence_policy.required === "photo"
                                          ? "写真必須"
                                          : item.task.evidence_policy.required === "note"
                                            ? "メモ必須"
                                            : "写真またはメモ必須"}
                                      </span>
                                    )}
                                    {item.assignment.state === "needs_changes" ? (
                                      <span className="task-state-pill">見直し中</span>
                                    ) : item.assignment.state === "deferred" ? (
                                      <span className="task-state-pill">あとに回した</span>
                                    ) : item.assignment.state === "skipped" ? (
                                      <span className="task-state-pill">今回は見送り</span>
                                    ) : item.assignment.state === "cancelled" ? (
                                      <span className="task-state-pill">取り消し</span>
                                    ) : item.assignment.state === "verified" ? (
                                      <span className="task-state-pill task-state-success">
                                        確認済み
                                      </span>
                                    ) : null}
                                  </div>
                                  <span className="parent-today-task-card-hint" aria-hidden="true">
                                    詳細を開く →
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </article>
                    );
                  })}
                </section>
                <dialog
                  ref={parentTodayTaskDialogRef}
                  className="app-dialog parent-today-task-dialog"
                  aria-labelledby="parent-today-task-title"
                  onClose={() => setSelectedParentTodayTaskId(null)}
                  onCancel={(event) => {
                    if (taskCommandBusyId === selectedParentTodayTaskId) event.preventDefault();
                  }}
                >
                  {selectedParentTodayTask === null || selectedParentTodayColumn === null ? null : (
                    <>
                      <header className="app-dialog-header">
                        <div>
                          <p className="eyebrow">{selectedParentTodayColumn.title}</p>
                          <h2 id="parent-today-task-title">{selectedParentTodayTask.task.title}</h2>
                        </div>
                        <button
                          type="button"
                          className="dialog-close"
                          aria-label="閉じる"
                          disabled={taskCommandBusyId === selectedParentTodayTask.task.id}
                          onClick={() => setSelectedParentTodayTaskId(null)}
                        >
                          ×
                        </button>
                      </header>
                      <div className="app-dialog-body parent-today-task-detail-body">
                        <p className="dialog-description">
                          {selectedParentTodayTask.task.child_copy} ・{" "}
                          {selectedParentTodayTask.task.estimated_minutes ?? 0}分
                        </p>
                        {notice === null ? null : (
                          <FloatingNotice message={notice} onDismiss={dismissNotice} />
                        )}
                        {error === null ? null : (
                          <p className="error" role="alert">
                            {error}
                          </p>
                        )}
                        {canReadEvidence ? (
                          <TaskEvidencePanel
                            session={session}
                            item={selectedParentTodayTask}
                            childFacing={false}
                            onReadyChange={handleEvidenceReady}
                          />
                        ) : null}
                        <div className="task-actions parent-today-task-actions">
                          {selectedParentTodayColumnTasks.length > 1 && canWriteTasks ? (
                            <div className="task-order-actions" aria-label="今日の順番">
                              <button
                                type="button"
                                disabled={
                                  taskCommandBusyId !== null ||
                                  selectedParentTodayColumnTasks[0]?.task.id ===
                                    selectedParentTodayTask.task.id
                                }
                                onClick={() =>
                                  void reorderParentTask(selectedParentTodayTask, "earlier")
                                }
                              >
                                上へ
                              </button>
                              <button
                                type="button"
                                disabled={
                                  taskCommandBusyId !== null ||
                                  selectedParentTodayColumnTasks.at(-1)?.task.id ===
                                    selectedParentTodayTask.task.id
                                }
                                onClick={() =>
                                  void reorderParentTask(selectedParentTodayTask, "later")
                                }
                              >
                                下へ
                              </button>
                            </div>
                          ) : null}
                          {!canWriteTasks ||
                          ["done", "verified", "cancelled"].includes(
                            selectedParentTodayTask.task.status,
                          ) ? null : (
                            <button
                              disabled={taskCommandBusyId !== null}
                              onClick={() => {
                                parentTodayTaskDialogRef.current?.close();
                                setSelectedParentTodayTaskId(null);
                                openTaskScheduleEditor(selectedParentTodayTask);
                              }}
                            >
                              日程変更
                            </button>
                          )}
                          {canProxyTasks && selectedParentTodayColumn.lane === "todo" ? (
                            <button
                              disabled={taskCommandBusyId !== null}
                              onClick={() =>
                                void parentTaskCommand(selectedParentTodayTask, "start")
                              }
                            >
                              {taskCommandBusyId === selectedParentTodayTask.task.id
                                ? "更新中…"
                                : "開始"}
                            </button>
                          ) : null}
                          {canProxyTasks && selectedParentTodayColumn.lane === "doing" ? (
                            selectedParentTodayTask.assignment.state === "in_progress" ? (
                              <button
                                disabled={taskCommandBusyId !== null}
                                onClick={() =>
                                  void parentTaskCommand(selectedParentTodayTask, "restore")
                                }
                              >
                                {taskCommandBusyId === selectedParentTodayTask.task.id
                                  ? "更新中…"
                                  : "やる予定に戻す"}
                              </button>
                            ) : null
                          ) : null}
                          {canProxyTasks && selectedParentTodayColumn.lane === "doing" ? (
                            <button
                              className="primary"
                              disabled={
                                taskCommandBusyId !== null ||
                                (selectedParentTodayTask.task.evidence_policy.required !== "none" &&
                                  evidenceReadyByTask[selectedParentTodayTask.task.id] !== true)
                              }
                              onClick={() =>
                                void parentTaskCommand(selectedParentTodayTask, "complete")
                              }
                            >
                              {taskCommandBusyId === selectedParentTodayTask.task.id
                                ? "更新中…"
                                : "完了"}
                            </button>
                          ) : null}
                          {canProxyTasks && selectedParentTodayTask.assignment.state === "done" ? (
                            <button
                              disabled={taskCommandBusyId !== null}
                              onClick={() =>
                                void parentTaskCommand(selectedParentTodayTask, "reopen")
                              }
                            >
                              {taskCommandBusyId === selectedParentTodayTask.task.id
                                ? "更新中…"
                                : "進行中に戻す"}
                            </button>
                          ) : null}
                          {canVerifyEvidence &&
                          selectedParentTodayTask.assignment.state === "done" &&
                          selectedParentTodayTask.task.verification_policy ===
                            "guardian_required" ? (
                            <>
                              <button
                                disabled={taskCommandBusyId !== null}
                                onClick={() => {
                                  parentTodayTaskDialogRef.current?.close();
                                  setSelectedParentTodayTaskId(null);
                                  setTaskReturnError(null);
                                  setReturningTask(selectedParentTodayTask);
                                }}
                              >
                                直してほしいこと
                              </button>
                              <button
                                className="primary"
                                disabled={taskCommandBusyId !== null}
                                onClick={() =>
                                  void parentTaskCommand(selectedParentTodayTask, "verify")
                                }
                              >
                                {taskCommandBusyId === selectedParentTodayTask.task.id
                                  ? "確認中…"
                                  : "確認済みにする"}
                              </button>
                            </>
                          ) : null}
                          {!canWriteTasks ||
                          taskPlanCommandsFor(selectedParentTodayTask.assignment.state).length ===
                            0 ? null : (
                            <button
                              disabled={taskCommandBusyId !== null}
                              onClick={() => {
                                parentTodayTaskDialogRef.current?.close();
                                setSelectedParentTodayTaskId(null);
                                openTaskPlanDecision(selectedParentTodayTask);
                              }}
                            >
                              {["deferred", "skipped", "cancelled"].includes(
                                selectedParentTodayTask.assignment.state,
                              )
                                ? "予定に戻す・見直す"
                                : "予定を見直す"}
                            </button>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </dialog>
              </>
            )}
          </div>
        ) : null}
        {activeView === "dashboard" ? (
          <ParentDashboard session={session} selectedChild={selectedChild} />
        ) : null}

        {activeView === "search" ? (
          <ParentSearchHistory selectedChild={selectedChild} request={searchHistoryRequest} />
        ) : null}

        {activeView === "inbox" ? (
          <div className="parent-view inbox-view">
            {inboxLoading ? (
              <section className="panel loading-panel" aria-busy="true">
                <p className="status" role="status">
                  Inboxを読み込んでいます…
                </p>
              </section>
            ) : inboxItems.length === 0 ? (
              <section className="panel empty-onboarding">
                <h2>未整理の項目はありません</h2>
                <p>
                  学校から届いたことや、あとで予定を決めたいことを、子供や日付を決めずに保存できます。
                </p>
                <button className="primary" onClick={openInboxCreator}>
                  Inboxに追加
                </button>
              </section>
            ) : (
              <>
                <section className="inbox-summary" aria-label="Inboxの集計">
                  <div>
                    <span>未整理</span>
                    <strong>{inboxItems.length}件</strong>
                  </div>
                  <div>
                    <span>外部から</span>
                    <strong>
                      {inboxItems.filter((item) => item.source_type !== "manual").length}件
                    </strong>
                  </div>
                  <p>
                    計画するまでは子供のTodayへ表示されません。内容を確認してから、やる日を決めます。
                  </p>
                </section>
                <section className="inbox-list" aria-label="未整理の項目">
                  {inboxItems.map((item) => (
                    <article className="panel inbox-card" key={item.id}>
                      <header>
                        <div>
                          <span className={`inbox-source inbox-source-${item.source_type}`}>
                            {item.source_type === "manual"
                              ? "親が追加"
                              : (item.source_label ??
                                (item.source_type === "mcp" ? "Remote MCP" : "外部API"))}
                          </span>
                          <time dateTime={new Date(item.created_at).toISOString()}>
                            {new Intl.DateTimeFormat("ja-JP", {
                              month: "numeric",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            }).format(new Date(item.created_at))}
                          </time>
                        </div>
                        <h2>{item.title}</h2>
                      </header>
                      {item.notes.length === 0 ? null : <p>{item.notes}</p>}
                      <div className="inbox-card-actions">
                        <button onClick={() => openInboxCancellation(item)}>取り消す</button>
                        <button
                          className="primary"
                          disabled={selectedChild === null}
                          onClick={() => openInboxPlanner(item)}
                        >
                          {selectedChild === null
                            ? "子供を追加して計画"
                            : `${selectedChild.display_name}の計画へ`}
                        </button>
                      </div>
                    </article>
                  ))}
                </section>
              </>
            )}
          </div>
        ) : null}

        {activeView === "plan" ? (
          <div className="parent-view plan-view">
            {familyLoading ? (
              <section className="panel loading-panel" aria-busy="true">
                <p className="status" role="status">
                  家族の情報を読み込んでいます…
                </p>
              </section>
            ) : children.length === 0 ? (
              <section className="panel empty-onboarding">
                <h2>計画する子供を追加しましょう</h2>
                <p>プロフィールを作成すると、一週間の予定を組み立てられます。</p>
                <button
                  className="primary"
                  onClick={() => {
                    navigateParent("settings");
                    openChildCreator();
                  }}
                >
                  子供を追加
                </button>
              </section>
            ) : (
              <>
                <CarryoverSection
                  items={carryoverTasks}
                  loading={carryoverTasksLoading}
                  busyTaskId={dragMoveBusyTaskId}
                  canWrite={canWriteTasks}
                  onMoveToday={(item) => void moveCarryoverTaskToToday(item)}
                  onChooseDate={(item) => openTaskScheduleEditor(item)}
                  onReview={openTaskPlanDecision}
                />
                <section className="plan-view-controls" aria-label="計画の表示方法">
                  <div className="segmented-control" aria-label="期間表示">
                    <button
                      aria-pressed={planDisplayMode === "week"}
                      className={planDisplayMode === "week" ? "active" : ""}
                      onClick={() => setPlanDisplayMode("week")}
                    >
                      週
                    </button>
                    <button
                      aria-pressed={planDisplayMode === "month"}
                      className={planDisplayMode === "month" ? "active" : ""}
                      onClick={() => setPlanDisplayMode("month")}
                    >
                      月
                    </button>
                  </div>
                  <div className="plan-view-actions">
                    <button
                      className={backlogOpen ? "active" : ""}
                      aria-controls="plan-backlog-panel"
                      aria-expanded={backlogOpen}
                      aria-label={backlogOpen ? "Backlogを閉じる" : "Backlogを開く"}
                      onClick={() => setBacklogOpen((current) => !current)}
                    >
                      Backlog {backlogTasksLoading ? "" : `${String(backlogTasks.length)}件`}{" "}
                      <span aria-hidden="true">{backlogOpen ? "▴" : "▾"}</span>
                    </button>
                    {canWriteTasks ? (
                      <button
                        className={bulkPlanningMode ? "active" : ""}
                        aria-pressed={bulkPlanningMode}
                        onClick={toggleBulkPlanningMode}
                      >
                        {bulkPlanningMode ? "選択をやめる" : "まとめて調整"}
                      </button>
                    ) : null}
                    <button
                      className={`planning-together-toggle${planningTogether ? " active" : ""}`}
                      aria-pressed={planningTogether}
                      onClick={() => setPlanningTogether((current) => !current)}
                    >
                      いっしょにきめる
                    </button>
                  </div>
                </section>
                <section
                  className="plan-toolbar"
                  aria-label={planDisplayMode === "week" ? "表示する週" : "表示する月"}
                >
                  <button
                    aria-label={planDisplayMode === "week" ? "前の週" : "前の月"}
                    onClick={() =>
                      setPlanAnchorDate((current) =>
                        planDisplayMode === "week"
                          ? addLocalDays(current, -7)
                          : addLocalMonths(current, -1),
                      )
                    }
                  >
                    ← 前へ
                  </button>
                  <div>
                    <span>{selectedChild?.display_name ?? "子供"}の予定</span>
                    <strong>
                      {planDisplayMode === "week"
                        ? planningWeekLabel(planAnchorDate)
                        : planningMonthLabel(planAnchorDate)}
                    </strong>
                  </div>
                  <button onClick={() => setPlanAnchorDate(workspaceLocalDate)}>今日へ</button>
                  <button
                    aria-label={planDisplayMode === "week" ? "次の週" : "次の月"}
                    onClick={() =>
                      setPlanAnchorDate((current) =>
                        planDisplayMode === "week"
                          ? addLocalDays(current, 7)
                          : addLocalMonths(current, 1),
                      )
                    }
                  >
                    次へ →
                  </button>
                </section>

                {planningTogether ? (
                  <section className="planning-together-banner" role="status">
                    <strong>いっしょにきめるモード</strong>
                    <span>
                      「いつやる？」「どのくらいかかる？」を子供と話しながら、カードの日程変更で決めます。
                    </span>
                  </section>
                ) : null}

                {bulkPlanningMode ? (
                  <section className="bulk-planning-bar" aria-label="まとめて日程変更">
                    <div>
                      <strong>{bulkSelectedTaskIds.length}件を選択中</strong>
                      <span>2〜20件を選び、変更前に全タスクの差分を確認します。</span>
                    </div>
                    <button
                      className="primary"
                      disabled={bulkSelectedTaskIds.length < 2}
                      onClick={openBulkPlanningDialog}
                    >
                      やる日をまとめて変更
                    </button>
                  </section>
                ) : null}
                {bulkPlanningError === null || bulkPlanningDialogOpen ? null : (
                  <p className="error" role="alert">
                    {bulkPlanningError}
                  </p>
                )}
                {planInteractionError === null ? null : (
                  <p className="error" role="alert">
                    {planInteractionError}
                  </p>
                )}

                {planTasksLoading || backlogTasksLoading ? (
                  <section className="panel loading-panel" aria-busy="true">
                    <p className="status" role="status">
                      計画とBacklogを読み込んでいます…
                    </p>
                  </section>
                ) : (
                  <>
                    <section className="plan-summary" aria-label="表示期間の負荷">
                      <div>
                        <span>予定</span>
                        <strong>{planTasks.length}件</strong>
                      </div>
                      <div>
                        <span>見積もり</span>
                        <strong>{planMinutes}分</strong>
                      </div>
                      <div>
                        <span>日付未定</span>
                        <strong>{backlogTasks.length}件</strong>
                      </div>
                      <p>日ごとの件数と分数を見比べ、期限とは別に「やる日」を調整します。</p>
                    </section>
                    {canWriteTasks ? (
                      <p className="plan-drag-help">
                        カード全体をドラッグして、同じ日の順番変更や別の日への移動ができます。短く押すと編集画面が開きます。キーボードでは上下で同じ日の順番、左右で前後の日、Shift＋Enterで日程を選べます。
                      </p>
                    ) : null}
                    <div
                      className={`plan-workspace${backlogOpen ? " plan-workspace-backlog-open" : ""}`}
                    >
                      <section
                        className={planDisplayMode === "week" ? "plan-week" : "plan-month"}
                        aria-label={
                          planDisplayMode === "week" ? "一週間のタスク" : "一か月のタスク"
                        }
                      >
                        {planDisplayMode === "month" ? (
                          <div className="plan-month-weekdays" aria-hidden="true">
                            {["月", "火", "水", "木", "金", "土", "日"].map((weekday) => (
                              <span key={weekday}>{weekday}</span>
                            ))}
                          </div>
                        ) : null}
                        {visiblePlanDays.map((day, index) => {
                          const dayTasks = planTasks.filter(
                            (item) => item.schedule_local_date === day.localDate,
                          );
                          const minutes = dayTasks.reduce(
                            (total, item) => total + (item.task.estimated_minutes ?? 0),
                            0,
                          );
                          const isToday = day.localDate === workspaceLocalDate;
                          return (
                            <article
                              className={`plan-day${isToday ? " plan-day-today" : ""}${planDropTarget?.localDate === day.localDate ? " plan-day-drop-target" : ""}`}
                              key={day.localDate}
                              data-plan-drop-date={day.localDate}
                              style={
                                planDisplayMode === "month" && index === 0
                                  ? { gridColumnStart: planMonthDays[0]?.monthColumn }
                                  : undefined
                              }
                            >
                              <header>
                                <div>
                                  <span>{day.weekday}</span>
                                  <strong>{day.shortLabel}</strong>
                                  {planDropTarget?.localDate === day.localDate ? (
                                    <small className="plan-drop-label">ここで並べ替え</small>
                                  ) : isToday ? (
                                    <small>今日</small>
                                  ) : null}
                                </div>
                                {canWriteTasks ? (
                                  <button
                                    aria-label={`${day.shortLabel}にタスクを追加`}
                                    onClick={() =>
                                      openTaskCreator({ context: "plan", localDate: day.localDate })
                                    }
                                  >
                                    ＋
                                  </button>
                                ) : null}
                              </header>
                              <p className="plan-day-load">
                                {dayTasks.length}件 · {minutes}分
                              </p>
                              {dayTasks.length === 0 ? (
                                <p className="plan-day-empty">予定なし</p>
                              ) : (
                                <ul>
                                  {dayTasks.map((item) => (
                                    <li
                                      className={`${canEditPlanTask(item) ? "plan-task-card-editable" : ""}${canMovePlanTask(item) ? " plan-task-card-draggable" : ""}${planDropTarget?.taskId === item.task.id && planDropTarget.placement === "before" ? " plan-task-drop-before" : ""}${planDropTarget?.taskId === item.task.id && planDropTarget.placement === "after" ? " plan-task-drop-after" : ""}`.trim()}
                                      key={item.task.id}
                                      data-plan-task-id={item.task.id}
                                      data-plan-task-date={day.localDate}
                                      onPointerDown={
                                        canMovePlanTask(item)
                                          ? (event) => beginPlanPointerDrag(event, item)
                                          : undefined
                                      }
                                      onPointerMove={
                                        canMovePlanTask(item) ? movePlanPointerDrag : undefined
                                      }
                                      onPointerUp={
                                        canMovePlanTask(item)
                                          ? (event) => finishPlanPointerDrag(event)
                                          : undefined
                                      }
                                      onPointerCancel={
                                        canMovePlanTask(item)
                                          ? (event) => finishPlanPointerDrag(event, true)
                                          : undefined
                                      }
                                      onLostPointerCapture={
                                        canMovePlanTask(item)
                                          ? (event) => finishPlanPointerDrag(event, true)
                                          : undefined
                                      }
                                    >
                                      {canEditPlanTask(item) || canMovePlanTask(item) ? (
                                        <button
                                          type="button"
                                          className="plan-task-edit-hitarea"
                                          aria-label={`${planningTogether ? item.task.child_copy : item.task.title}${canEditPlanTask(item) ? "を編集" : "の日程を変更"}${canMovePlanTask(item) ? "。カードをドラッグして同じ日の順番変更または別の日へ移動" : ""}`}
                                          aria-keyshortcuts={
                                            canMovePlanTask(item)
                                              ? "ArrowUp ArrowDown ArrowLeft ArrowRight Shift+Enter"
                                              : undefined
                                          }
                                          onKeyDown={(event) => handlePlanTaskKeyDown(event, item)}
                                          onClick={() => openPlanTaskFromCard(item)}
                                        />
                                      ) : null}
                                      {bulkPlanningMode ? (
                                        <label className="bulk-task-selector">
                                          <input
                                            type="checkbox"
                                            checked={bulkSelectedTaskIds.includes(item.task.id)}
                                            disabled={
                                              ["done", "verified", "cancelled"].includes(
                                                item.task.status,
                                              ) ||
                                              ["done", "verified", "cancelled"].includes(
                                                item.assignment.state,
                                              )
                                            }
                                            onChange={() => toggleBulkTask(item.task.id)}
                                          />
                                          <span>このタスクを選ぶ</span>
                                        </label>
                                      ) : null}
                                      <div className="plan-task-title-row">
                                        {planningTogether ||
                                        !canWriteTasks ||
                                        item.task.status === "done" ||
                                        item.task.status === "verified" ||
                                        item.task.status === "cancelled" ? (
                                          <strong className="plan-task-title-label">
                                            {planningTogether
                                              ? item.task.child_copy
                                              : item.task.title}
                                          </strong>
                                        ) : (
                                          <strong className="plan-task-title-label">
                                            {item.task.title}
                                          </strong>
                                        )}
                                      </div>
                                      {planningTogether ? null : (
                                        <span>{item.task.child_copy}</span>
                                      )}
                                      {planningTogether || item.project === null ? null : (
                                        <span className="project-label">
                                          <i
                                            style={{ background: item.project.color }}
                                            aria-hidden="true"
                                          />
                                          {item.project.name}
                                        </span>
                                      )}
                                      <div>
                                        <small>{item.task.estimated_minutes ?? 0}分</small>
                                        {planningTogether ||
                                        item.task.due_local_date === null ? null : (
                                          <small>期限 {item.task.due_local_date}</small>
                                        )}
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </article>
                          );
                        })}
                      </section>
                      <aside
                        id="plan-backlog-panel"
                        className="plan-backlog"
                        aria-labelledby="backlog-title"
                        hidden={!backlogOpen}
                      >
                        <header>
                          <div>
                            <span>日付を決める前</span>
                            <h2 id="backlog-title">Backlog</h2>
                          </div>
                          {canWriteTasks ? (
                            <button
                              aria-label="Backlogにタスクを追加"
                              onClick={() =>
                                openTaskCreator({ context: "plan", placement: "backlog" })
                              }
                            >
                              ＋
                            </button>
                          ) : null}
                        </header>
                        <p className="plan-backlog-help">
                          日付がまだ決まらないタスクを置き、話し合ってから予定に入れます。
                        </p>
                        {backlogTasks.length === 0 ? (
                          <div className="plan-backlog-empty">
                            <strong>未予定のタスクはありません</strong>
                            <span>必要なら＋から日付を決めずに追加できます。</span>
                          </div>
                        ) : (
                          <ul>
                            {backlogTasks.map((item) => (
                              <li
                                className={`${canEditPlanTask(item) ? "plan-task-card-editable" : ""}${canMovePlanTask(item) ? " plan-task-card-draggable" : ""}`.trim()}
                                key={item.task.id}
                                onPointerDown={
                                  canMovePlanTask(item)
                                    ? (event) => beginPlanPointerDrag(event, item)
                                    : undefined
                                }
                                onPointerMove={
                                  canMovePlanTask(item) ? movePlanPointerDrag : undefined
                                }
                                onPointerUp={
                                  canMovePlanTask(item)
                                    ? (event) => finishPlanPointerDrag(event)
                                    : undefined
                                }
                                onPointerCancel={
                                  canMovePlanTask(item)
                                    ? (event) => finishPlanPointerDrag(event, true)
                                    : undefined
                                }
                                onLostPointerCapture={
                                  canMovePlanTask(item)
                                    ? (event) => finishPlanPointerDrag(event, true)
                                    : undefined
                                }
                              >
                                {canEditPlanTask(item) || canMovePlanTask(item) ? (
                                  <button
                                    type="button"
                                    className="plan-task-edit-hitarea"
                                    aria-label={`${planningTogether ? item.task.child_copy : item.task.title}${canEditPlanTask(item) ? "を編集" : "の日程を変更"}${canMovePlanTask(item) ? "。カードをドラッグして予定へ移動" : ""}`}
                                    aria-keyshortcuts={
                                      canMovePlanTask(item) ? "Shift+Enter" : undefined
                                    }
                                    onKeyDown={(event) => handlePlanTaskKeyDown(event, item)}
                                    onClick={() => openPlanTaskFromCard(item)}
                                  />
                                ) : null}
                                <div className="plan-task-title-row">
                                  {planningTogether ||
                                  !canWriteTasks ||
                                  item.task.status === "done" ||
                                  item.task.status === "verified" ||
                                  item.task.status === "cancelled" ? (
                                    <strong className="plan-task-title-label">
                                      {planningTogether ? item.task.child_copy : item.task.title}
                                    </strong>
                                  ) : (
                                    <strong className="plan-task-title-label">
                                      {item.task.title}
                                    </strong>
                                  )}
                                </div>
                                {planningTogether ? null : <span>{item.task.child_copy}</span>}
                                <div>
                                  <small>{item.task.estimated_minutes ?? 0}分</small>
                                  {planningTogether || item.task.due_local_date === null ? null : (
                                    <small>期限 {item.task.due_local_date}</small>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </aside>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        ) : null}

        {activeView === "routines" ? (
          <div className="routines-layout">
            {isGuardian ? (
              <>
                <section className="panel routine-template-panel" aria-labelledby="template-title">
                  <div className="routine-calendar-heading">
                    <div>
                      <p className="eyebrow">よく使う繰り返し</p>
                      <h2 id="template-title">ルーティンのテンプレート</h2>
                      <p>
                        曜日・時間帯・ことばを再利用します。子供、開始日、プロジェクト、お休みは使うときに決めます。
                      </p>
                    </div>
                    <button
                      type="button"
                      className="primary"
                      onClick={() => openRoutineTemplateEditor("create")}
                    >
                      ＋ テンプレートを作成
                    </button>
                  </div>
                  {activeRoutineTemplates.length === 0 ? (
                    <div className="routine-template-empty">
                      <p>まだテンプレートはありません。</p>
                      <span>作成済みルーティンの「テンプレートに保存」からも登録できます。</span>
                    </div>
                  ) : (
                    <ul className="routine-template-list">
                      {activeRoutineTemplates.map((template) => (
                        <li key={template.id}>
                          <span className="routine-color" style={{ background: template.color }} />
                          <div>
                            <strong>{template.name}</strong>
                            <span>
                              {routineScheduleLabel(template.schedule)}・{template.child_copy}
                              {template.default_duration_minutes === null
                                ? ""
                                : `・${String(template.default_duration_minutes)}分`}
                            </span>
                          </div>
                          <div className="routine-calendar-actions">
                            <button
                              type="button"
                              className="primary"
                              disabled={selectedChildId === null}
                              onClick={() => openRoutineCreatorFromTemplate(template)}
                            >
                              {selectedChild?.display_name ?? "この子"}に使う
                            </button>
                            <button
                              type="button"
                              onClick={() => openRoutineTemplateEditor(template)}
                            >
                              編集
                            </button>
                            <button
                              type="button"
                              disabled={routineTemplateBusy}
                              onClick={() => void changeRoutineTemplateArchive(template)}
                            >
                              アーカイブ
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {archivedRoutineTemplates.length === 0 ? null : (
                    <details className="routine-calendar-archive">
                      <summary>アーカイブ済みテンプレート</summary>
                      <ul className="routine-template-list">
                        {archivedRoutineTemplates.map((template) => (
                          <li key={template.id}>
                            <span
                              className="routine-color"
                              style={{ background: template.color }}
                            />
                            <div>
                              <strong>{template.name}</strong>
                              <span>{routineScheduleLabel(template.schedule)}</span>
                            </div>
                            <button
                              type="button"
                              disabled={routineTemplateBusy}
                              onClick={() => void changeRoutineTemplateArchive(template)}
                            >
                              復元
                            </button>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </section>
                <section className="panel routine-calendar-panel" aria-labelledby="calendar-title">
                  <div className="routine-calendar-heading">
                    <div>
                      <p className="eyebrow">予定を自動で休む</p>
                      <h2 id="calendar-title">お休みカレンダー</h2>
                      <p>
                        学校の休み、旅行、体調不良などを登録し、選んだルーティンの新規Task生成を止めます。
                      </p>
                    </div>
                    <button
                      className="primary"
                      type="button"
                      disabled={selectedChildId === null}
                      onClick={() => openCalendarEditor("create")}
                    >
                      ＋ お休みを登録
                    </button>
                  </div>
                  {selectedChildCalendars.length === 0 ? (
                    <p className="routine-calendar-empty">
                      {selectedChild?.display_name ?? "選択中の子供"}
                      のお休みカレンダーはまだありません。
                    </p>
                  ) : (
                    <ul className="routine-calendar-list">
                      {selectedChildCalendars.map((calendar) => (
                        <li key={calendar.id}>
                          <div>
                            <strong>{calendar.name}</strong>
                            <span>
                              {calendar.periods
                                .map(
                                  (period) =>
                                    `${period.name} ${period.start_local_date}〜${period.end_local_date}`,
                                )
                                .join(" / ")}
                            </span>
                            <small>
                              対象:{" "}
                              {calendar.child_ids
                                .map(
                                  (childId) =>
                                    activeChildren.find((child) => child.id === childId)
                                      ?.display_name,
                                )
                                .filter((name): name is string => name !== undefined)
                                .join("・")}
                            </small>
                          </div>
                          <div className="routine-calendar-actions">
                            <button type="button" onClick={() => openCalendarEditor(calendar)}>
                              編集
                            </button>
                            <button
                              type="button"
                              disabled={calendarBusyId !== null}
                              onClick={() => void runCalendarArchiveCommand(calendar, "archive")}
                            >
                              {calendarBusyId === calendar.id ? "変更中…" : "アーカイブ"}
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {archivedCalendars.some((calendar) =>
                    calendar.child_ids.includes(selectedChildId ?? ""),
                  ) ? (
                    <details className="routine-calendar-archive">
                      <summary>アーカイブ済み</summary>
                      <ul>
                        {archivedCalendars
                          .filter((calendar) => calendar.child_ids.includes(selectedChildId ?? ""))
                          .map((calendar) => (
                            <li key={calendar.id}>
                              <span>{calendar.name}</span>
                              <button
                                type="button"
                                disabled={calendarBusyId !== null}
                                onClick={() => void runCalendarArchiveCommand(calendar, "restore")}
                              >
                                復元
                              </button>
                            </li>
                          ))}
                      </ul>
                    </details>
                  ) : null}
                </section>
                <section
                  className="panel routine-reconciliation-panel"
                  aria-labelledby="routine-check-title"
                >
                  <div className="routine-calendar-heading">
                    <div>
                      <p className="eyebrow">自動生成の安全確認</p>
                      <h2 id="routine-check-title">生成漏れを点検</h2>
                      <p>
                        今後14日分を再計算し、まだ作られていないTaskだけを同じ一意キーで補います。既存Taskは上書きしません。
                      </p>
                    </div>
                    <button
                      className="primary"
                      type="button"
                      disabled={routineReconciliationBusy || routines.length === 0}
                      onClick={() => void runRoutineReconciliation()}
                    >
                      {routineReconciliationBusy ? "点検中…" : "今すぐ点検・修復"}
                    </button>
                  </div>
                  {routineReconciliations.length === 0 ? (
                    <p className="routine-calendar-empty">点検履歴はまだありません。</p>
                  ) : (
                    <ol className="routine-reconciliation-list">
                      {routineReconciliations.slice(0, 5).map((run) => (
                        <li key={run.id}>
                          <div>
                            <strong>
                              {run.status === "queued"
                                ? "受付済み"
                                : run.status === "running"
                                  ? "点検中"
                                  : run.status === "completed"
                                    ? "点検完了"
                                    : "点検失敗"}
                            </strong>
                            <span>
                              {run.from_local_date}〜{run.until_local_date}
                            </span>
                          </div>
                          <span>
                            {run.status === "completed"
                              ? `漏れ ${String(run.missing_count)}件 / 修復 ${String(run.repaired_count)}件`
                              : run.status === "failed"
                                ? "再試行できます"
                                : "バックグラウンドで処理中"}
                          </span>
                        </li>
                      ))}
                    </ol>
                  )}
                </section>
              </>
            ) : null}
            <section className="routine-list-section" aria-labelledby="routine-list-title">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">{selectedChild?.display_name ?? "子供"}</p>
                  <h2 id="routine-list-title">続ける予定</h2>
                </div>
                <span className="count-badge">{selectedChildRoutines.length}</span>
              </div>
              {routinesLoading ? (
                <div className="panel loading-panel" aria-busy="true">
                  読み込んでいます…
                </div>
              ) : children.length === 0 ? (
                <section className="panel empty-onboarding">
                  <h3>{isGuardian ? "先に子供を追加しましょう" : "表示できる子供がいません"}</h3>
                  <p>
                    {isGuardian
                      ? "ルーティンは、選んだ子供の計画へ繰り返しTaskを作ります。"
                      : "管理者に、子供を指定した支援範囲を確認してもらってください。"}
                  </p>
                  {isGuardian ? (
                    <button className="primary" onClick={() => navigateParent("settings")}>
                      子供を追加
                    </button>
                  ) : null}
                </section>
              ) : selectedChildRoutines.length === 0 ? (
                <section className="panel empty-onboarding">
                  <h3>続ける予定はまだありません</h3>
                  <p>音読、持ち物の準備、習い事の練習などを曜日や間隔で計画できます。</p>
                  {canWriteRoutines ? (
                    <button className="primary" onClick={openRoutineCreator}>
                      最初のルーティンを作成
                    </button>
                  ) : null}
                </section>
              ) : (
                <div className="routine-card-grid">
                  {selectedChildRoutines.map((routine) => (
                    <article className="panel routine-card" key={routine.id}>
                      <div className="routine-card-heading">
                        <span className="routine-color" style={{ background: routine.color }} />
                        <div className="routine-card-title">
                          <p className="routine-schedule">
                            <span aria-hidden="true">↻</span>
                            {routineScheduleLabel(routine.current_version.schedule)}
                          </p>
                          <h3>{routine.name}</h3>
                          <p className="routine-child-copy">
                            <span>子供の画面</span>
                            <strong>{routine.child_copy}</strong>
                          </p>
                        </div>
                        <span className={`status-chip status-${routine.status}`}>
                          {routine.status === "active" ? "実行中" : "一時停止"}
                        </span>
                      </div>
                      {routine.notes.length === 0 ? null : <p>{routine.notes}</p>}
                      <dl className="routine-facts">
                        <div>
                          <dt>期間</dt>
                          <dd>
                            {routine.current_version.start_local_date} →{" "}
                            {routine.current_version.end_local_date ?? "継続"}
                          </dd>
                        </div>
                        <div>
                          <dt>目安</dt>
                          <dd>
                            {routine.current_version.default_duration_minutes === null
                              ? "未設定"
                              : `${String(routine.current_version.default_duration_minutes)}分`}
                          </dd>
                        </div>
                        <div>
                          <dt>確認</dt>
                          <dd>
                            {routine.current_version.verification_policy === "none"
                              ? "子供の完了で終了"
                              : routine.current_version.verification_policy === "guardian_optional"
                                ? "親の確認は任意"
                                : routine.current_version.verification_policy ===
                                    "guardian_required"
                                  ? "親の確認が必要"
                                  : "証跡が必要"}
                          </dd>
                        </div>
                        <div>
                          <dt>参加</dt>
                          <dd>
                            {routine.participants
                              .map((participant) => participant.display_name)
                              .join("・")}
                          </dd>
                        </div>
                        <div className="routine-fact-wide">
                          <dt>お休み</dt>
                          <dd>
                            {routine.current_version.calendar_ids.length === 0
                              ? "個別設定のみ"
                              : routine.current_version.calendar_ids
                                  .map(
                                    (calendarId) =>
                                      workspaceCalendars.find(
                                        (calendar) => calendar.id === calendarId,
                                      )?.name,
                                  )
                                  .filter((name): name is string => name !== undefined)
                                  .join("・") || "アーカイブ済み"}
                          </dd>
                        </div>
                      </dl>
                      <section
                        className="routine-upcoming"
                        aria-label={`${routine.name}の次回予定`}
                      >
                        <strong>
                          <span aria-hidden="true">◷</span>
                          次に計画済み
                        </strong>
                        {routine.upcoming.length === 0 ? (
                          <p>次回分を生成中です。</p>
                        ) : (
                          <ol>
                            {routine.upcoming.slice(0, 4).map((occurrence) => (
                              <li key={occurrence.id}>
                                <time dateTime={occurrence.planned_local_date}>
                                  {occurrence.planned_local_date}
                                </time>
                                <span>
                                  {routine.participants.find(
                                    (participant) => participant.child_id === occurrence.child_id,
                                  )?.display_name ?? "子供"}
                                </span>
                              </li>
                            ))}
                          </ol>
                        )}
                      </section>
                      {routine.status === "paused" ? (
                        <p className="routine-status-note">
                          新しいTaskの生成を停止中です。すでに計画したTaskはそのまま残ります。
                        </p>
                      ) : null}
                      <div className="routine-card-actions">
                        {canWriteRoutines ? (
                          <>
                            <button
                              type="button"
                              className="primary routine-edit-action"
                              onClick={() => openRoutineEditor(routine)}
                            >
                              <span aria-hidden="true">✎</span>
                              <span>設定を編集</span>
                            </button>
                            <button
                              type="button"
                              className={`routine-status-action${
                                routine.status === "paused" ? " routine-resume-action" : ""
                              }`}
                              disabled={routineStatusBusyId !== null}
                              onClick={() => void changeRoutineStatus(routine)}
                            >
                              <span aria-hidden="true">
                                {routine.status === "active" ? "Ⅱ" : "▶"}
                              </span>
                              <span>
                                {routineStatusBusyId === routine.id
                                  ? "変更中…"
                                  : routine.status === "active"
                                    ? "生成を一時停止"
                                    : "生成を再開"}
                              </span>
                            </button>
                          </>
                        ) : null}
                        {isGuardian ? (
                          <button
                            type="button"
                            onClick={() => openRoutineTemplateEditor("create", routine)}
                          >
                            <span aria-hidden="true">▣</span>
                            <span>テンプレートに保存</span>
                          </button>
                        ) : null}
                        <button type="button" onClick={() => void openRoutineHistory(routine)}>
                          <span aria-hidden="true">↺</span>
                          <span>バージョン履歴</span>
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <dialog
              ref={routineTemplateDialogRef}
              className="app-dialog routine-template-dialog"
              aria-labelledby="routine-template-dialog-title"
              onClose={() => {
                setEditingRoutineTemplate(null);
                setRoutineTemplateSource(null);
                setRoutineTemplateError(null);
              }}
              onCancel={(event) => {
                if (routineTemplateBusy) event.preventDefault();
              }}
            >
              <header className="app-dialog-header">
                <div>
                  <p className="eyebrow">繰り返しを再利用</p>
                  <h2 id="routine-template-dialog-title">
                    {routineTemplateRecord === null ? "テンプレートを作成" : "テンプレートを編集"}
                  </h2>
                </div>
                <button
                  type="button"
                  className="dialog-close"
                  aria-label="閉じる"
                  disabled={routineTemplateBusy}
                  onClick={closeRoutineTemplateEditor}
                >
                  ×
                </button>
              </header>
              <div className="app-dialog-body">
                <p className="dialog-description">
                  子供や開始日には結び付けません。選択中の子供へ使うとき、通常の生成プレビューで確認します。
                </p>
                {routineTemplateError === null ? null : (
                  <p className="error" role="alert">
                    {routineTemplateError}
                  </p>
                )}
                <form
                  key={routineTemplateRecord?.id ?? routineTemplateSource?.id ?? "new-template"}
                  className="routine-create-form"
                  onSubmit={(event) => void saveRoutineTemplate(event)}
                >
                  <label>
                    親向けの名前
                    <input
                      name="name"
                      required
                      maxLength={160}
                      placeholder="平日の音読"
                      defaultValue={
                        routineTemplateRecord?.name ?? routineTemplateSource?.name ?? ""
                      }
                      autoFocus
                    />
                  </label>
                  <label>
                    子供に見せることば
                    <input
                      name="child_copy"
                      required
                      maxLength={160}
                      placeholder="おんどく"
                      defaultValue={
                        routineTemplateRecord?.child_copy ?? routineTemplateSource?.child_copy ?? ""
                      }
                    />
                  </label>
                  <label className="form-span">
                    目的・メモ
                    <textarea
                      name="notes"
                      maxLength={8000}
                      rows={2}
                      defaultValue={
                        routineTemplateRecord?.notes ?? routineTemplateSource?.notes ?? ""
                      }
                    />
                  </label>
                  <label>
                    繰り返し
                    <select
                      value={routineTemplateScheduleKind}
                      onChange={(event) =>
                        setRoutineTemplateScheduleKind(
                          event.currentTarget.value as RoutineSchedule["kind"],
                        )
                      }
                    >
                      <option value="daily">日ごと</option>
                      <option value="weekly">曜日ごと</option>
                      <option value="monthly_days">毎月の日付</option>
                      <option value="monthly_nth_weekday">毎月の第○曜日</option>
                    </select>
                  </label>
                  {routineTemplateScheduleKind === "daily" ? (
                    <label>
                      何日おき
                      <input
                        name="interval_days"
                        type="number"
                        min={1}
                        max={365}
                        required
                        defaultValue={
                          routineTemplateFormSchedule?.kind === "daily"
                            ? routineTemplateFormSchedule.interval_days
                            : 1
                        }
                      />
                    </label>
                  ) : routineTemplateScheduleKind === "weekly" ? (
                    <>
                      <label>
                        何週おき
                        <input
                          name="interval_weeks"
                          type="number"
                          min={1}
                          max={52}
                          required
                          defaultValue={
                            routineTemplateFormSchedule?.kind === "weekly"
                              ? routineTemplateFormSchedule.interval_weeks
                              : 1
                          }
                        />
                      </label>
                      <fieldset className="weekday-picker form-span">
                        <legend>曜日</legend>
                        {["月", "火", "水", "木", "金", "土", "日"].map((label, index) => (
                          <label key={label}>
                            <input
                              type="checkbox"
                              name="weekdays"
                              value={index + 1}
                              defaultChecked={
                                routineTemplateFormSchedule?.kind === "weekly" &&
                                routineTemplateFormSchedule.weekdays.includes(index + 1)
                              }
                            />
                            {label}
                          </label>
                        ))}
                      </fieldset>
                    </>
                  ) : routineTemplateScheduleKind === "monthly_days" ? (
                    <label>
                      毎月の日
                      <input
                        name="month_days"
                        required
                        placeholder="1, 15, 末日"
                        defaultValue={
                          routineTemplateFormSchedule?.kind === "monthly_days"
                            ? routineTemplateFormSchedule.days
                                .map((day) => (day === "last" ? "末日" : String(day)))
                                .join(", ")
                            : ""
                        }
                      />
                    </label>
                  ) : (
                    <div className="routine-inline-fields">
                      <label>
                        週
                        <select
                          name="month_ordinal"
                          defaultValue={
                            routineTemplateFormSchedule?.kind === "monthly_nth_weekday"
                              ? String(routineTemplateFormSchedule.ordinal)
                              : "1"
                          }
                        >
                          <option value="1">第1</option>
                          <option value="2">第2</option>
                          <option value="3">第3</option>
                          <option value="4">第4</option>
                          <option value="5">第5</option>
                          <option value="-1">最終</option>
                        </select>
                      </label>
                      <label>
                        曜日
                        <select
                          name="month_weekday"
                          defaultValue={
                            routineTemplateFormSchedule?.kind === "monthly_nth_weekday"
                              ? String(routineTemplateFormSchedule.weekday)
                              : "1"
                          }
                        >
                          {["月", "火", "水", "木", "金", "土", "日"].map((label, index) => (
                            <option key={label} value={index + 1}>
                              {label}曜
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  )}
                  <label>
                    取り組む時間帯
                    <select
                      value={routineTemplateAvailability}
                      onChange={(event) =>
                        setRoutineTemplateAvailability(
                          event.currentTarget.value as RoutineVersion["availability"]["period"],
                        )
                      }
                    >
                      <option value="anytime">いつでも</option>
                      <option value="morning">朝</option>
                      <option value="after_school">放課後</option>
                      <option value="evening">夜</option>
                      <option value="custom">時間を指定</option>
                    </select>
                  </label>
                  {routineTemplateAvailability === "custom" ? (
                    <div className="routine-inline-fields">
                      <label>
                        開始
                        <input
                          name="available_from_local_time"
                          type="time"
                          required
                          defaultValue={routineTemplateFormAvailability?.from_local_time ?? ""}
                        />
                      </label>
                      <label>
                        終了
                        <input
                          name="available_until_local_time"
                          type="time"
                          required
                          defaultValue={routineTemplateFormAvailability?.until_local_time ?? ""}
                        />
                      </label>
                    </div>
                  ) : (
                    <div />
                  )}
                  <fieldset className="routine-notifications form-span">
                    <legend>通知</legend>
                    <label>
                      <input
                        type="checkbox"
                        name="child_reminder"
                        disabled={routineTemplateAvailability !== "custom"}
                        defaultChecked={
                          routineTemplateRecord?.notification_policy.child_reminder ??
                          routineTemplateSource?.current_version.notification_policy
                            .child_reminder ??
                          false
                        }
                      />
                      <span>
                        <strong>開始時刻に子供へ知らせる</strong>
                        <small>子供画面の「きょう」に、やさしい開始案内を出します</small>
                      </span>
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        name="guardian_digest"
                        disabled={routineTemplateAvailability !== "custom"}
                        defaultChecked={
                          routineTemplateRecord?.notification_policy.guardian_digest ??
                          routineTemplateSource?.current_version.notification_policy
                            .guardian_digest ??
                          false
                        }
                      />
                      <span>
                        <strong>終了時刻に未完了なら親へ知らせる</strong>
                        <small>急かす通知ではなく、予定を一緒に見直す案内です</small>
                      </span>
                    </label>
                    {routineTemplateAvailability === "custom" ? null : (
                      <small>通知を使うときは「時間を指定」を選んでください。</small>
                    )}
                  </fieldset>
                  <label>
                    目安（分）
                    <input
                      name="default_duration_minutes"
                      type="number"
                      min={0}
                      max={1440}
                      defaultValue={
                        routineTemplateRecord?.default_duration_minutes ??
                        routineTemplateSource?.current_version.default_duration_minutes ??
                        10
                      }
                    />
                  </label>
                  <label>
                    期限
                    <select
                      name="due_offset_days"
                      defaultValue={String(
                        routineTemplateRecord?.due_offset_days ??
                          routineTemplateSource?.current_version.due_offset_days ??
                          0,
                      )}
                    >
                      <option value="0">やる日まで</option>
                      <option value="1">翌日まで</option>
                      <option value="2">2日後まで</option>
                      <option value="7">1週間後まで</option>
                    </select>
                  </label>
                  <label>
                    色
                    <input
                      name="color"
                      type="color"
                      defaultValue={
                        routineTemplateRecord?.color ?? routineTemplateSource?.color ?? "#4f8f72"
                      }
                    />
                  </label>
                  <div className="dialog-actions form-span">
                    <button
                      type="button"
                      disabled={routineTemplateBusy}
                      onClick={closeRoutineTemplateEditor}
                    >
                      キャンセル
                    </button>
                    <button className="primary" disabled={routineTemplateBusy}>
                      {routineTemplateBusy ? "保存中…" : "テンプレートを保存"}
                    </button>
                  </div>
                </form>
              </div>
            </dialog>

            <dialog
              ref={calendarDialogRef}
              className="app-dialog organization-dialog calendar-dialog"
              aria-labelledby="calendar-dialog-title"
              onClose={() => {
                setEditingCalendar(null);
                setCalendarPeriodDrafts([]);
                setCalendarDialogError(null);
              }}
              onCancel={(event) => {
                if (calendarBusyId !== null) event.preventDefault();
              }}
            >
              <header className="app-dialog-header">
                <div>
                  <p className="eyebrow">ルーティンの生成を休む日</p>
                  <h2 id="calendar-dialog-title">
                    {editingCalendar === "create"
                      ? "お休みカレンダーを登録"
                      : "お休みカレンダーを編集"}
                  </h2>
                </div>
                <button
                  type="button"
                  className="dialog-close"
                  aria-label="閉じる"
                  disabled={calendarBusyId !== null}
                  onClick={closeCalendarEditor}
                >
                  ×
                </button>
              </header>
              <div className="app-dialog-body">
                {calendarDialogError === null ? null : (
                  <p className="error" role="alert">
                    {calendarDialogError}
                  </p>
                )}
                <form
                  className="calendar-form"
                  onSubmit={(event) => void saveWorkspaceCalendar(event)}
                >
                  <label>
                    カレンダー名
                    <input
                      name="name"
                      required
                      maxLength={160}
                      placeholder="2026年 夏休み"
                      defaultValue={
                        editingCalendar === "create" ? "" : (editingCalendar?.name ?? "")
                      }
                      autoFocus
                    />
                  </label>
                  <label>
                    種類
                    <select
                      name="kind"
                      defaultValue={
                        editingCalendar === "create"
                          ? "school_holiday"
                          : (editingCalendar?.kind ?? "school_holiday")
                      }
                    >
                      <option value="school_holiday">学校の休み</option>
                      <option value="public_holiday">祝日</option>
                      <option value="travel">旅行・外出</option>
                      <option value="sick">体調に合わせて休む</option>
                      <option value="custom">その他</option>
                    </select>
                  </label>
                  {editingCalendar === "create" ? null : (
                    <fieldset className="calendar-children form-span">
                      <legend>このカレンダーを使う子供</legend>
                      <div>
                        {activeChildren.map((child) => (
                          <label key={child.id}>
                            <input
                              type="checkbox"
                              name="calendar_child_ids"
                              value={child.id}
                              defaultChecked={
                                editingCalendar?.child_ids.includes(child.id) ?? false
                              }
                            />
                            <span
                              className="avatar"
                              style={{ background: child.theme_color }}
                              aria-hidden="true"
                            />
                            {child.display_name}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  )}
                  <fieldset className="calendar-periods form-span">
                    <legend>お休み期間</legend>
                    <p>期間ごとに、親子で分かる名前と開始・終了日を設定します。</p>
                    <div className="calendar-period-list">
                      {calendarPeriodDrafts.map((period, index) => (
                        <section key={period.key} className="calendar-period-row">
                          <label>
                            期間の名前
                            <input
                              required
                              maxLength={120}
                              placeholder="夏休み"
                              value={period.name}
                              onChange={(event) =>
                                updateCalendarPeriod(period.key, {
                                  name: event.currentTarget.value,
                                })
                              }
                            />
                          </label>
                          <label>
                            開始日
                            <input
                              required
                              type="date"
                              value={period.start_local_date}
                              onChange={(event) =>
                                updateCalendarPeriod(period.key, {
                                  start_local_date: event.currentTarget.value,
                                })
                              }
                            />
                          </label>
                          <label>
                            終了日
                            <input
                              required
                              type="date"
                              value={period.end_local_date}
                              onChange={(event) =>
                                updateCalendarPeriod(period.key, {
                                  end_local_date: event.currentTarget.value,
                                })
                              }
                            />
                          </label>
                          <button
                            type="button"
                            disabled={calendarPeriodDrafts.length === 1}
                            aria-label={`${String(index + 1)}件目の期間を削除`}
                            onClick={() => removeCalendarPeriod(period.key)}
                          >
                            削除
                          </button>
                        </section>
                      ))}
                    </div>
                    <button
                      type="button"
                      disabled={calendarPeriodDrafts.length >= 64}
                      onClick={addCalendarPeriod}
                    >
                      ＋ 期間を追加
                    </button>
                  </fieldset>
                  <p className="routine-window-note form-span">
                    カレンダーを保存しただけでは、すでに計画済みのTaskは消えません。ルーティン編集でこのカレンダーを選び、差分プレビューを確認すると今後の予定へ反映できます。
                  </p>
                  <div className="dialog-actions form-span">
                    <button
                      type="button"
                      disabled={calendarBusyId !== null}
                      onClick={closeCalendarEditor}
                    >
                      キャンセル
                    </button>
                    <button className="primary" disabled={calendarBusyId !== null}>
                      {calendarBusyId !== null ? "保存中…" : "保存"}
                    </button>
                  </div>
                </form>
              </div>
            </dialog>

            <dialog
              ref={routineDialogRef}
              className="app-dialog routine-dialog"
              aria-labelledby="routine-create-title"
              onClose={() => {
                setRoutineCreateOpen(false);
                setRoutinePreview(null);
                setRoutineDraft(null);
                setRoutineEditing(null);
                setRoutineTemplatePreset(null);
              }}
              onCancel={(event) => {
                if (routineBusy !== null) event.preventDefault();
              }}
            >
              <header className="app-dialog-header">
                <div>
                  <p className="eyebrow">繰り返しを計画</p>
                  <h2 id="routine-create-title">
                    {routinePreview === null
                      ? routineEditing === null
                        ? "ルーティンを作成"
                        : "ルーティンを編集"
                      : routineEditing === null
                        ? "作成内容を確認"
                        : "変更内容を確認"}
                  </h2>
                </div>
                <button
                  type="button"
                  className="dialog-close"
                  aria-label="閉じる"
                  disabled={routineBusy !== null}
                  onClick={closeRoutineCreator}
                >
                  ×
                </button>
              </header>
              <div className="app-dialog-body">
                {routineDialogError === null ? null : (
                  <p className="error" role="alert">
                    {routineDialogError}
                  </p>
                )}
                {routinePreview === null ? (
                  <form
                    key={routineEditing?.id ?? routineTemplatePreset?.id ?? "new"}
                    className="routine-create-form"
                    onSubmit={(event) => void previewRoutineCreate(event)}
                  >
                    <label>
                      親向けの名前
                      <input
                        name="name"
                        required
                        maxLength={160}
                        placeholder="平日の音読"
                        defaultValue={routineEditing?.name ?? routineTemplatePreset?.name ?? ""}
                        autoFocus
                      />
                    </label>
                    <label>
                      子供に見せることば
                      <input
                        name="child_copy"
                        required
                        maxLength={160}
                        placeholder="おんどく"
                        defaultValue={
                          routineEditing?.child_copy ?? routineTemplatePreset?.child_copy ?? ""
                        }
                      />
                    </label>
                    <label className="form-span">
                      目的・メモ
                      <textarea
                        name="notes"
                        maxLength={8000}
                        rows={2}
                        placeholder="いつ、どのくらい取り組むか"
                        defaultValue={routineEditing?.notes ?? routineTemplatePreset?.notes ?? ""}
                      />
                    </label>
                    {routineEditing === null ? null : (
                      <fieldset className="routine-extra-children form-span">
                        <legend>取り組む子供</legend>
                        <div>
                          {activeChildren.map((child) => (
                            <label key={child.id}>
                              <input
                                type="checkbox"
                                name="routine_child_ids"
                                value={child.id}
                                defaultChecked={routineEditing.participants.some(
                                  (participant) => participant.child_id === child.id,
                                )}
                              />
                              <span
                                className="avatar"
                                style={{ background: child.theme_color }}
                                aria-hidden="true"
                              />
                              {child.display_name}
                            </label>
                          ))}
                        </div>
                      </fieldset>
                    )}
                    <label>
                      繰り返し
                      <select
                        name="schedule_kind"
                        value={routineScheduleKind}
                        onChange={(event) =>
                          setRoutineScheduleKind(
                            event.currentTarget.value as RoutineSchedule["kind"],
                          )
                        }
                      >
                        <option value="daily">日ごと</option>
                        <option value="weekly">曜日ごと</option>
                        <option value="monthly_days">毎月の日付</option>
                        <option value="monthly_nth_weekday">毎月の第○曜日</option>
                      </select>
                    </label>
                    {routineScheduleKind === "daily" ? (
                      <label>
                        何日おき
                        <input
                          name="interval_days"
                          type="number"
                          min={1}
                          max={365}
                          defaultValue={
                            routineFormSchedule?.kind === "daily"
                              ? routineFormSchedule.interval_days
                              : 1
                          }
                          required
                        />
                      </label>
                    ) : routineScheduleKind === "weekly" ? (
                      <>
                        <label>
                          何週おき
                          <input
                            name="interval_weeks"
                            type="number"
                            min={1}
                            max={52}
                            defaultValue={
                              routineFormSchedule?.kind === "weekly"
                                ? routineFormSchedule.interval_weeks
                                : 1
                            }
                            required
                          />
                        </label>
                        <fieldset className="weekday-picker form-span">
                          <legend>曜日</legend>
                          {["月", "火", "水", "木", "金", "土", "日"].map((label, index) => (
                            <label key={label}>
                              <input
                                type="checkbox"
                                name="weekdays"
                                value={index + 1}
                                defaultChecked={
                                  routineFormSchedule?.kind === "weekly" &&
                                  routineFormSchedule.weekdays.includes(index + 1)
                                }
                              />
                              {label}
                            </label>
                          ))}
                        </fieldset>
                      </>
                    ) : routineScheduleKind === "monthly_days" ? (
                      <label>
                        毎月の日
                        <input
                          name="month_days"
                          required
                          placeholder="1, 15, 末日"
                          defaultValue={
                            routineFormSchedule?.kind === "monthly_days"
                              ? routineFormSchedule.days
                                  .map((day) => (day === "last" ? "末日" : String(day)))
                                  .join(", ")
                              : ""
                          }
                        />
                      </label>
                    ) : (
                      <div className="routine-inline-fields">
                        <label>
                          週
                          <select
                            name="month_ordinal"
                            defaultValue={
                              routineFormSchedule?.kind === "monthly_nth_weekday"
                                ? String(routineFormSchedule.ordinal)
                                : "1"
                            }
                          >
                            <option value="1">第1</option>
                            <option value="2">第2</option>
                            <option value="3">第3</option>
                            <option value="4">第4</option>
                            <option value="5">第5</option>
                            <option value="-1">最終</option>
                          </select>
                        </label>
                        <label>
                          曜日
                          <select
                            name="month_weekday"
                            defaultValue={
                              routineFormSchedule?.kind === "monthly_nth_weekday"
                                ? String(routineFormSchedule.weekday)
                                : "1"
                            }
                          >
                            {["月", "火", "水", "木", "金", "土", "日"].map((label, index) => (
                              <option key={label} value={index + 1}>
                                {label}曜
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    )}
                    <label>
                      開始日
                      <input
                        name="start_local_date"
                        type="date"
                        required
                        defaultValue={
                          routineEditing?.current_version.start_local_date ?? workspaceLocalDate
                        }
                      />
                    </label>
                    <label>
                      終了日（任意）
                      <input
                        name="end_local_date"
                        type="date"
                        defaultValue={routineEditing?.current_version.end_local_date ?? ""}
                      />
                    </label>
                    <label>
                      取り組む時間帯
                      <select
                        name="availability_period"
                        value={routineAvailability}
                        onChange={(event) =>
                          setRoutineAvailability(
                            event.currentTarget
                              .value as RoutineCreateDraft["availability"]["period"],
                          )
                        }
                      >
                        <option value="anytime">いつでも</option>
                        <option value="morning">朝</option>
                        <option value="after_school">放課後</option>
                        <option value="evening">夜</option>
                        <option value="custom">時間を指定</option>
                      </select>
                    </label>
                    {routineAvailability === "custom" ? (
                      <div className="routine-inline-fields">
                        <label>
                          開始
                          <input
                            name="available_from_local_time"
                            type="time"
                            required
                            defaultValue={routineFormAvailability?.from_local_time ?? ""}
                          />
                        </label>
                        <label>
                          終了
                          <input
                            name="available_until_local_time"
                            type="time"
                            required
                            defaultValue={routineFormAvailability?.until_local_time ?? ""}
                          />
                        </label>
                      </div>
                    ) : (
                      <div />
                    )}
                    <fieldset className="routine-notifications form-span">
                      <legend>通知</legend>
                      <label>
                        <input
                          type="checkbox"
                          name="child_reminder"
                          disabled={routineAvailability !== "custom"}
                          defaultChecked={
                            routineEditing?.current_version.notification_policy.child_reminder ??
                            routineTemplatePreset?.notification_policy.child_reminder ??
                            false
                          }
                        />
                        <span>
                          <strong>開始時刻に子供へ知らせる</strong>
                          <small>子供画面の「きょう」に、やさしい開始案内を出します</small>
                        </span>
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          name="guardian_digest"
                          disabled={routineAvailability !== "custom"}
                          defaultChecked={
                            routineEditing?.current_version.notification_policy.guardian_digest ??
                            routineTemplatePreset?.notification_policy.guardian_digest ??
                            false
                          }
                        />
                        <span>
                          <strong>終了時刻に未完了なら親へ知らせる</strong>
                          <small>急かす通知ではなく、予定を一緒に見直す案内です</small>
                        </span>
                      </label>
                      {routineAvailability === "custom" ? null : (
                        <small>通知を使うときは「時間を指定」を選んでください。</small>
                      )}
                    </fieldset>
                    <label>
                      目安（分）
                      <input
                        name="default_duration_minutes"
                        type="number"
                        min={0}
                        max={1440}
                        defaultValue={
                          routineEditing?.current_version.default_duration_minutes ??
                          routineTemplatePreset?.default_duration_minutes ??
                          10
                        }
                      />
                    </label>
                    <label>
                      期限
                      <select
                        name="due_offset_days"
                        defaultValue={String(
                          routineEditing?.current_version.due_offset_days ??
                            routineTemplatePreset?.due_offset_days ??
                            0,
                        )}
                      >
                        <option value="0">やる日まで</option>
                        <option value="1">翌日まで</option>
                        <option value="2">2日後まで</option>
                        <option value="7">1週間後まで</option>
                      </select>
                    </label>
                    <label>
                      プロジェクト（任意）
                      <select name="project_id" defaultValue={routineEditing?.project_id ?? ""}>
                        <option value="">なし</option>
                        {selectedChildProjects.map((project) => (
                          <option key={project.id} value={project.id}>
                            {project.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      領域（任意）
                      <select name="area_id" defaultValue={routineEditing?.area_id ?? ""}>
                        <option value="">なし</option>
                        {activeAreas.map((area) => (
                          <option key={area.id} value={area.id}>
                            {area.icon === null ? "" : `${area.icon} `}
                            {area.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      期間（任意）
                      <select name="term_id" defaultValue={routineEditing?.term_id ?? ""}>
                        <option value="">なし</option>
                        {activeTerms.map((term) => (
                          <option key={term.id} value={term.id}>
                            {term.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <fieldset className="routine-calendars form-span">
                      <legend>お休みカレンダー（任意）</legend>
                      {routineCalendarOptions.length === 0 ? (
                        <p>利用できるカレンダーはありません。画面上部から先に登録できます。</p>
                      ) : (
                        <div>
                          {routineCalendarOptions.map((calendar) => (
                            <label key={calendar.id}>
                              <input
                                type="checkbox"
                                name="routine_calendar_ids"
                                value={calendar.id}
                                defaultChecked={
                                  routineEditing?.current_version.calendar_ids.includes(
                                    calendar.id,
                                  ) ?? false
                                }
                              />
                              <span>
                                <strong>{calendar.name}</strong>
                                <small>
                                  {calendar.periods
                                    .map(
                                      (period) =>
                                        `${period.start_local_date}〜${period.end_local_date}`,
                                    )
                                    .join(" / ")}
                                </small>
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                      <small>
                        選んだカレンダーの日は新しいTaskを作りません。編集時は確認画面で取消対象も表示します。
                      </small>
                    </fieldset>
                    <label>
                      色
                      <input
                        name="color"
                        type="color"
                        defaultValue={
                          routineEditing?.color ?? routineTemplatePreset?.color ?? "#4f8f72"
                        }
                      />
                    </label>
                    <label className="form-span">
                      お休み日（任意）
                      <input
                        name="exception_dates"
                        placeholder="2026-09-21, 2026-09-22"
                        defaultValue={
                          routineEditing?.current_version.exception_dates.join(", ") ?? ""
                        }
                      />
                      <small>この日だけTaskを作りません。</small>
                    </label>
                    <fieldset className="routine-pause-range form-span">
                      <legend>一時停止する期間（任意）</legend>
                      {(routineEditing?.current_version.pause_ranges.length
                        ? routineEditing.current_version.pause_ranges
                        : [{ start_local_date: "", end_local_date: "" }]
                      ).map((range, index) => (
                        <div
                          className="routine-inline-fields"
                          key={`${range.start_local_date}:${String(index)}`}
                        >
                          <label>
                            {index === 0 ? "開始" : `開始 ${String(index + 1)}`}
                            <input
                              name="pause_start_local_dates"
                              type="date"
                              defaultValue={range.start_local_date}
                            />
                          </label>
                          <label>
                            {index === 0 ? "終了" : `終了 ${String(index + 1)}`}
                            <input
                              name="pause_end_local_dates"
                              type="date"
                              defaultValue={range.end_local_date}
                            />
                          </label>
                        </div>
                      ))}
                    </fieldset>
                    <p className="routine-window-note form-span">
                      {routineEditing === null
                        ? "作成前に、開始日から最大6週間分の生成日と件数を確認します。"
                        : "変更前に、今後6週間の追加・変更・取消と、変更せず保護するTaskを確認します。"}
                    </p>
                    <div className="dialog-actions form-span">
                      <button
                        type="button"
                        disabled={routineBusy !== null}
                        onClick={closeRoutineCreator}
                      >
                        キャンセル
                      </button>
                      <button className="primary" disabled={routineBusy !== null}>
                        {routineBusy === "preview" ? "確認内容を作成中…" : "生成日を確認"}
                      </button>
                    </div>
                  </form>
                ) : (
                  <section className="routine-preview-panel">
                    <div className="routine-preview-summary">
                      <span className="routine-color" style={{ background: routineDraft?.color }} />
                      <div>
                        <h3>{routineDraft?.name}</h3>
                        <p>
                          {routineDraft === null ? "" : routineScheduleLabel(routineDraft.schedule)}
                        </p>
                      </div>
                    </div>
                    <dl>
                      <div>
                        <dt>追加先</dt>
                        <dd>
                          {routineDraft?.child_ids
                            .map(
                              (childId) =>
                                activeChildren.find((child) => child.id === childId)?.display_name,
                            )
                            .filter((name): name is string => name !== undefined)
                            .join("・")}
                        </dd>
                      </div>
                      <div>
                        <dt>{"items" in routinePreview ? "変更するTask" : "生成するTask"}</dt>
                        <dd>
                          <strong>{routinePreview.affected_count}件</strong>
                        </dd>
                      </div>
                      {"items" in routinePreview ? (
                        <div>
                          <dt>そのまま保護</dt>
                          <dd>
                            <strong>{routinePreview.protected_count}件</strong>
                          </dd>
                        </div>
                      ) : null}
                      <div>
                        <dt>確認期限</dt>
                        <dd>
                          {new Intl.DateTimeFormat("ja-JP", {
                            dateStyle: "medium",
                            timeStyle: "short",
                          }).format(new Date(routinePreview.expires_at))}
                        </dd>
                      </div>
                    </dl>
                    {"items" in routinePreview ? (
                      <div className="routine-preview-occurrences">
                        <h4>Taskごとの変更</h4>
                        <ol>
                          {routinePreview.items.map((item) => (
                            <li
                              key={`${item.child_id}:${item.planned_local_date}:${item.task_id ?? "new"}`}
                            >
                              <time dateTime={item.planned_local_date}>
                                {item.planned_local_date}
                              </time>
                              <span>
                                {activeChildren.find((child) => child.id === item.child_id)
                                  ?.display_name ?? "子供"}
                                ・
                                {item.action === "create"
                                  ? "追加"
                                  : item.action === "update"
                                    ? "変更"
                                    : item.action === "cancel"
                                      ? "取消"
                                      : item.protected_reason === "started_or_finished"
                                        ? "開始・完了済みのため保護"
                                        : item.protected_reason === "individually_changed"
                                          ? "個別変更済みのため保護"
                                          : "取消済みのため保護"}
                              </span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    ) : (
                      <div className="routine-preview-occurrences">
                        <h4>生成される日</h4>
                        <ol>
                          {routinePreview.occurrences.map((occurrence) => (
                            <li key={`${occurrence.child_id}:${occurrence.planned_local_date}`}>
                              <time dateTime={occurrence.planned_local_date}>
                                {occurrence.planned_local_date}
                              </time>
                              <span>
                                {
                                  activeChildren.find((child) => child.id === occurrence.child_id)
                                    ?.display_name
                                }
                              </span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                    <p className="dialog-description">
                      {"items" in routinePreview
                        ? `確定するとバージョン${String(routinePreview.next_version_number)}を作成し、上記の今後のTaskだけを反映します。開始・完了済みや個別変更済みのTaskは上書きしません。`
                        : "確定すると、ルーティンと上記Taskを同時に作成します。プレビューだけでは保存されません。"}
                    </p>
                    <div className="dialog-actions">
                      <button
                        type="button"
                        disabled={routineBusy !== null}
                        onClick={() => {
                          setRoutinePreview(null);
                          setRoutineDraft(null);
                        }}
                      >
                        内容を戻して編集
                      </button>
                      <button
                        className="primary"
                        disabled={routineBusy !== null}
                        onClick={() => void applyRoutineCreate()}
                      >
                        {routineBusy === "apply"
                          ? routineEditing === null
                            ? "作成しています…"
                            : "変更しています…"
                          : routineEditing === null
                            ? `${String(routinePreview.affected_count)}件を確認して作成`
                            : `${String(routinePreview.affected_count)}件を確認して変更`}
                      </button>
                    </div>
                  </section>
                )}
              </div>
            </dialog>

            <dialog
              ref={routineHistoryDialogRef}
              className="app-dialog routine-history-dialog"
              aria-labelledby="routine-history-title"
              onClose={() => {
                setRoutineHistory(null);
                setRoutineHistoryItems([]);
                setRoutineHistoryNextCursor(null);
                setRoutineHistoryComparison(null);
                setRoutineHistoryError(null);
              }}
              onCancel={(event) => {
                if (routineHistoryLoading || routineHistoryComparisonLoading)
                  event.preventDefault();
              }}
            >
              <header className="app-dialog-header">
                <div>
                  <p className="eyebrow">変更の記録</p>
                  <h2 id="routine-history-title">
                    {routineHistory === null ? "ルーティン履歴" : `${routineHistory.name}の履歴`}
                  </h2>
                </div>
                <button
                  type="button"
                  className="dialog-close"
                  aria-label="閉じる"
                  disabled={routineHistoryLoading || routineHistoryComparisonLoading}
                  onClick={closeRoutineHistory}
                >
                  ×
                </button>
              </header>
              <div className="app-dialog-body">
                <p className="dialog-description">
                  各バージョンは後から書き換えません。現在使っているバージョンが先頭です。
                </p>
                {routineHistoryError === null ? null : (
                  <p className="error" role="alert">
                    {routineHistoryError}
                  </p>
                )}
                {routineHistoryLoading ? (
                  <div className="loading-panel" aria-busy="true">
                    履歴を読み込んでいます…
                  </div>
                ) : routineHistoryItems.length === 0 ? (
                  <p>保存済みのバージョンはありません。</p>
                ) : (
                  <>
                    {routineHistoryComparison === null ? null : (
                      <section className="routine-version-comparison" aria-live="polite">
                        <div className="routine-history-heading">
                          <div>
                            <p className="eyebrow">差分</p>
                            <h3>
                              バージョン {routineHistoryComparison.from_version.version_number} →{" "}
                              {routineHistoryComparison.to_version.version_number}
                            </h3>
                          </div>
                          <button type="button" onClick={() => setRoutineHistoryComparison(null)}>
                            比較を閉じる
                          </button>
                        </div>
                        {routineHistoryComparison.changed_fields.length === 0 ? (
                          <p>設定内容に差はありません。</p>
                        ) : (
                          <table>
                            <thead>
                              <tr>
                                <th scope="col">項目</th>
                                <th scope="col">以前</th>
                                <th scope="col">現在</th>
                              </tr>
                            </thead>
                            <tbody>
                              {routineHistoryComparison.changed_fields.map((field) => (
                                <tr key={field}>
                                  <th scope="row">{routineComparisonLabels[field]}</th>
                                  <td>
                                    {routineComparisonValue(
                                      field,
                                      routineHistoryComparison.from_version,
                                    )}
                                  </td>
                                  <td>
                                    {routineComparisonValue(
                                      field,
                                      routineHistoryComparison.to_version,
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </section>
                    )}
                    <ol className="routine-history-list">
                      {routineHistoryItems.map((version) => {
                        const isCurrent =
                          version.version_number === routineHistory?.current_version.version_number;
                        return (
                          <li key={version.id}>
                            <div className="routine-history-heading">
                              <strong>バージョン {version.version_number}</strong>
                              {isCurrent ? (
                                <span className="status-chip status-active">現在</span>
                              ) : (
                                <button
                                  type="button"
                                  disabled={routineHistoryComparisonLoading}
                                  onClick={() => void compareRoutineVersion(version)}
                                >
                                  現在と比較
                                </button>
                              )}
                            </div>
                            <p>{routineScheduleLabel(version.schedule)}</p>
                            <dl>
                              <div>
                                <dt>有効になった日時</dt>
                                <dd>
                                  {new Intl.DateTimeFormat("ja-JP", {
                                    dateStyle: "medium",
                                    timeStyle: "short",
                                  }).format(new Date(version.effective_at))}
                                </dd>
                              </div>
                              <div>
                                <dt>期間</dt>
                                <dd>
                                  {version.start_local_date} → {version.end_local_date ?? "継続"}
                                </dd>
                              </div>
                              <div>
                                <dt>例外</dt>
                                <dd>
                                  お休み {version.exception_dates.length}日・停止期間{" "}
                                  {version.pause_ranges.length}件
                                </dd>
                              </div>
                            </dl>
                          </li>
                        );
                      })}
                    </ol>
                    {routineHistoryNextCursor === null ? null : (
                      <button
                        type="button"
                        className="routine-history-more"
                        disabled={routineHistoryLoadingMore}
                        onClick={() => void loadMoreRoutineHistory()}
                      >
                        {routineHistoryLoadingMore ? "読み込み中…" : "さらに古い履歴を読み込む"}
                      </button>
                    )}
                  </>
                )}
                <div className="dialog-actions">
                  <button
                    type="button"
                    disabled={routineHistoryLoading || routineHistoryComparisonLoading}
                    onClick={closeRoutineHistory}
                  >
                    閉じる
                  </button>
                </div>
              </div>
            </dialog>
          </div>
        ) : null}

        {activeView === "projects" ? (
          <div className="projects-layout">
            <section className="project-list-section" aria-labelledby="project-list-title">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">{selectedChild?.display_name ?? "子供"}</p>
                  <h2 id="project-list-title">参加しているプロジェクト</h2>
                </div>
                <span className="count-badge">{selectedChildProjects.length}</span>
              </div>
              {familyLoading ? (
                <div className="panel loading-panel" aria-busy="true">
                  読み込んでいます…
                </div>
              ) : children.length === 0 ? (
                <section className="panel empty-onboarding">
                  <h3>先に子供を追加しましょう</h3>
                  <p>プロジェクトには、取り組む子供を1人以上設定します。</p>
                  <button className="primary" onClick={() => navigateParent("settings")}>
                    子供を追加
                  </button>
                </section>
              ) : selectedChildProjects.length === 0 ? (
                <section className="panel empty-onboarding">
                  <h3>参加中のプロジェクトはありません</h3>
                  <p>学校の宿題、発表会の練習、長く続ける目標などをまとめられます。</p>
                  <button className="primary" onClick={openProjectCreator}>
                    最初のプロジェクトを作成
                  </button>
                </section>
              ) : (
                <div className="project-card-grid">
                  {selectedChildProjects.map((project) => (
                    <article className="panel project-card" key={project.id}>
                      <div className="project-card-accent" style={{ background: project.color }} />
                      <div className="project-card-heading">
                        <div>
                          <span className={`status-chip status-${project.status}`}>
                            {project.status === "active"
                              ? "進行中"
                              : project.status === "planned"
                                ? "計画中"
                                : project.status}
                          </span>
                          <h3>{project.name}</h3>
                          <p>{project.child_copy}</p>
                        </div>
                      </div>
                      {project.description.length === 0 ? null : (
                        <p className="project-description">{project.description}</p>
                      )}
                      <dl className="project-facts">
                        {project.area_id === null && project.term_id === null ? null : (
                          <div>
                            <dt>整理</dt>
                            <dd>
                              {areas.find((area) => area.id === project.area_id)?.name ?? ""}
                              {project.area_id !== null && project.term_id !== null ? " · " : ""}
                              {terms.find((term) => term.id === project.term_id)?.name ?? ""}
                            </dd>
                          </div>
                        )}
                        <div>
                          <dt>期間</dt>
                          <dd>
                            {project.start_local_date ?? "未設定"} →{" "}
                            {project.target_local_date ?? "未設定"}
                          </dd>
                        </div>
                        <div>
                          <dt>参加</dt>
                          <dd>
                            {project.participants
                              .map((participant) => participant.display_name)
                              .join("・")}
                          </dd>
                        </div>
                      </dl>
                      <section
                        className="milestone-section"
                        aria-label={`${project.name}のマイルストーン`}
                      >
                        <div className="milestone-heading">
                          <strong>マイルストーン</strong>
                          <div>
                            <span>
                              {
                                project.milestones.filter(
                                  (milestone) => milestone.status === "completed",
                                ).length
                              }
                              /{project.milestones.length}
                            </span>
                            <button onClick={() => openMilestoneCreator(project)}>＋ 追加</button>
                          </div>
                        </div>
                        {project.milestones.length === 0 ? (
                          <p className="empty-state">区切りとなる目標はまだありません。</p>
                        ) : (
                          <ul className="milestone-list">
                            {project.milestones.map((milestone) => (
                              <li key={milestone.id}>
                                <span
                                  className={`milestone-state milestone-${milestone.status}`}
                                  aria-hidden="true"
                                >
                                  {milestone.status === "completed"
                                    ? "✓"
                                    : milestone.status === "in_progress"
                                      ? "●"
                                      : "○"}
                                </span>
                                <div>
                                  <strong>{milestone.title}</strong>
                                  {milestone.due_local_date === null ? null : (
                                    <small>目標 {milestone.due_local_date}</small>
                                  )}
                                </div>
                                {milestone.status === "planned" ? (
                                  <button
                                    disabled={milestoneBusyId !== null}
                                    onClick={() =>
                                      void runMilestoneCommand(project.id, milestone, "start")
                                    }
                                  >
                                    始める
                                  </button>
                                ) : milestone.status === "in_progress" ? (
                                  <button
                                    className="primary"
                                    disabled={milestoneBusyId !== null}
                                    onClick={() =>
                                      void runMilestoneCommand(project.id, milestone, "complete")
                                    }
                                  >
                                    完了
                                  </button>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        )}
                      </section>
                      <div className="project-card-actions">
                        <button
                          className="primary"
                          onClick={() =>
                            openTaskCreator({
                              context: "project",
                              localDate: planAnchorDate,
                              projectId: project.id,
                            })
                          }
                        >
                          ＋ タスクを追加
                        </button>
                        <button onClick={() => openProjectEditor(project)}>編集</button>
                        <button
                          className="danger"
                          disabled={updatingProjectId !== null}
                          onClick={() => void runProjectArchiveCommand(project, "archive")}
                        >
                          {updatingProjectId === project.id ? "処理中…" : "アーカイブ"}
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
              {archivedChildProjects.length === 0 ? null : (
                <details className="archived-resources project-archive-list">
                  <summary>アーカイブ済み（{archivedChildProjects.length}）</summary>
                  <ul className="resource-list">
                    {archivedChildProjects.map((project) => (
                      <li key={project.id}>
                        <span
                          className="project-list-color"
                          style={{ background: project.color }}
                          aria-hidden="true"
                        />
                        <div className="resource-copy">
                          <strong>{project.name}</strong>
                          <small>
                            {project.participants
                              .map((participant) => participant.display_name)
                              .join("・")}
                            {project.archived_at === null
                              ? ""
                              : ` · ${new Intl.DateTimeFormat("ja-JP", { dateStyle: "medium" }).format(new Date(project.archived_at))}にアーカイブ`}
                          </small>
                        </div>
                        <button
                          disabled={updatingProjectId !== null}
                          onClick={() => void runProjectArchiveCommand(project, "restore")}
                        >
                          {updatingProjectId === project.id ? "復元中…" : "復元"}
                        </button>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </section>

            {children.length === 0 ? null : (
              <dialog
                ref={projectDialogRef}
                className="app-dialog project-dialog"
                aria-labelledby="project-create-title"
                aria-describedby="project-create-description"
                onClose={() => setProjectCreateOpen(false)}
                onCancel={(event) => {
                  if (creatingProject) event.preventDefault();
                }}
              >
                <header className="app-dialog-header">
                  <div>
                    <p className="eyebrow">新しい取り組み</p>
                    <h2 id="project-create-title">プロジェクトを作成</h2>
                  </div>
                  <button
                    type="button"
                    className="dialog-close"
                    aria-label="閉じる"
                    disabled={creatingProject}
                    onClick={closeProjectCreator}
                  >
                    ×
                  </button>
                </header>
                <div className="app-dialog-body">
                  <p id="project-create-description" className="dialog-description">
                    目標と期間を決めます。あとから変更できます。
                  </p>
                  {error === null ? null : (
                    <p className="error" role="alert">
                      {error}
                    </p>
                  )}
                  <form
                    className="project-create-form"
                    onSubmit={(event) => void createProject(event)}
                  >
                    <label>
                      親向けの名前
                      <input
                        name="name"
                        required
                        maxLength={160}
                        placeholder="夏休みの自由研究"
                        autoFocus
                      />
                    </label>
                    <label>
                      子供に見せることば
                      <input
                        name="child_copy"
                        required
                        maxLength={160}
                        placeholder="じゆうけんきゅう"
                      />
                    </label>
                    <label className="form-span">
                      目標・メモ
                      <textarea
                        name="description"
                        maxLength={4000}
                        rows={3}
                        placeholder="何をできるようにするか、親子で決めたこと"
                      />
                    </label>
                    <label>
                      領域
                      <select name="area_id" defaultValue="">
                        <option value="">未設定</option>
                        {activeAreas.map((area) => (
                          <option key={area.id} value={area.id}>
                            {area.icon === null ? "" : `${area.icon} `}
                            {area.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      期間
                      <select name="term_id" defaultValue="">
                        <option value="">未設定</option>
                        {activeTerms.map((term) => (
                          <option key={term.id} value={term.id}>
                            {term.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      開始日
                      <input
                        name="start_local_date"
                        type="date"
                        defaultValue={workspaceLocalDate}
                      />
                    </label>
                    <label>
                      目標日
                      <input name="target_local_date" type="date" />
                    </label>
                    <label>
                      状態
                      <select name="status" defaultValue="active">
                        <option value="active">進行中</option>
                        <option value="planned">計画中</option>
                      </select>
                    </label>
                    <label>
                      色<input name="color" type="color" defaultValue="#4f8f72" />
                    </label>
                    <div className="dialog-actions form-span">
                      <button
                        type="button"
                        disabled={creatingProject}
                        onClick={closeProjectCreator}
                      >
                        キャンセル
                      </button>
                      <button className="primary" disabled={creatingProject}>
                        {creatingProject ? "作成しています…" : "プロジェクトを作成"}
                      </button>
                    </div>
                  </form>
                </div>
              </dialog>
            )}

            <dialog
              ref={projectEditDialogRef}
              className="app-dialog project-dialog"
              aria-labelledby="project-edit-title"
              aria-describedby="project-edit-description"
              onClose={() => setEditingProject(null)}
              onCancel={(event) => {
                if (updatingProjectId !== null) event.preventDefault();
              }}
            >
              <header className="app-dialog-header">
                <div>
                  <p className="eyebrow">取り組みを整える</p>
                  <h2 id="project-edit-title">プロジェクトを編集</h2>
                </div>
                <button
                  type="button"
                  className="dialog-close"
                  aria-label="閉じる"
                  disabled={updatingProjectId !== null}
                  onClick={closeProjectEditor}
                >
                  ×
                </button>
              </header>
              <div className="app-dialog-body">
                <p id="project-edit-description" className="dialog-description">
                  目標、期間、参加する子供、進行状態をまとめて変更します。
                </p>
                {error === null ? null : (
                  <p className="error" role="alert">
                    {error}
                  </p>
                )}
                {editingProject === null ? null : (
                  <form
                    className="project-create-form"
                    key={`${editingProject.id}:${String(editingProject.version)}`}
                    onSubmit={(event) => void updateProject(event, editingProject)}
                  >
                    <label>
                      親向けの名前
                      <input
                        name="name"
                        required
                        maxLength={160}
                        defaultValue={editingProject.name}
                        autoFocus
                      />
                    </label>
                    <label>
                      子供に見せることば
                      <input
                        name="child_copy"
                        required
                        maxLength={160}
                        defaultValue={editingProject.child_copy}
                      />
                    </label>
                    <label className="form-span">
                      目標・メモ
                      <textarea
                        name="description"
                        maxLength={4000}
                        rows={4}
                        defaultValue={editingProject.description}
                      />
                    </label>
                    <fieldset className="participant-picker form-span">
                      <legend>取り組む子供</legend>
                      {children.map((child) => (
                        <label key={child.id}>
                          <input
                            type="checkbox"
                            name="child_ids"
                            value={child.id}
                            defaultChecked={editingProject.participants.some(
                              (participant) => participant.child_id === child.id,
                            )}
                          />
                          <span
                            className="avatar"
                            style={{ background: child.theme_color }}
                            aria-hidden="true"
                          />
                          {child.display_name}
                        </label>
                      ))}
                    </fieldset>
                    <label>
                      領域
                      <select name="area_id" defaultValue={editingProject.area_id ?? ""}>
                        <option value="">未設定</option>
                        {activeAreas.map((area) => (
                          <option key={area.id} value={area.id}>
                            {area.icon === null ? "" : `${area.icon} `}
                            {area.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      期間
                      <select name="term_id" defaultValue={editingProject.term_id ?? ""}>
                        <option value="">未設定</option>
                        {activeTerms.map((term) => (
                          <option key={term.id} value={term.id}>
                            {term.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      開始日
                      <input
                        name="start_local_date"
                        type="date"
                        defaultValue={editingProject.start_local_date ?? ""}
                      />
                    </label>
                    <label>
                      目標日
                      <input
                        name="target_local_date"
                        type="date"
                        defaultValue={editingProject.target_local_date ?? ""}
                      />
                    </label>
                    <label>
                      状態
                      <select name="status" defaultValue={editingProject.status}>
                        <option value="planned">計画中</option>
                        <option value="active">進行中</option>
                        <option value="paused">一時停止</option>
                        <option value="completed">完了</option>
                        <option value="cancelled">中止</option>
                      </select>
                    </label>
                    <label>
                      色
                      <input name="color" type="color" defaultValue={editingProject.color} />
                    </label>
                    <div className="dialog-actions form-span">
                      <button
                        type="button"
                        disabled={updatingProjectId !== null}
                        onClick={closeProjectEditor}
                      >
                        キャンセル
                      </button>
                      <button className="primary" disabled={updatingProjectId !== null}>
                        {updatingProjectId === editingProject.id ? "保存しています…" : "変更を保存"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </dialog>

            <dialog
              ref={milestoneDialogRef}
              className="app-dialog compact-dialog"
              aria-labelledby="milestone-create-title"
              aria-describedby="milestone-create-description"
              onClose={() => setMilestoneProject(null)}
              onCancel={(event) => {
                if (milestoneBusyId !== null) event.preventDefault();
              }}
            >
              <header className="app-dialog-header">
                <div>
                  <p className="eyebrow">次の区切り</p>
                  <h2 id="milestone-create-title">マイルストーンを追加</h2>
                </div>
                <button
                  type="button"
                  className="dialog-close"
                  aria-label="閉じる"
                  disabled={milestoneBusyId !== null}
                  onClick={closeMilestoneCreator}
                >
                  ×
                </button>
              </header>
              <div className="app-dialog-body">
                <p id="milestone-create-description" className="dialog-description">
                  {milestoneProject?.name ?? "プロジェクト"}
                  の途中で確認する、小さな目標を追加します。
                </p>
                {error === null ? null : (
                  <p className="error" role="alert">
                    {error}
                  </p>
                )}
                {milestoneProject === null ? null : (
                  <form onSubmit={(event) => void createMilestone(event, milestoneProject.id)}>
                    <label>
                      目標
                      <input
                        name="title"
                        required
                        maxLength={240}
                        placeholder="テーマを決める"
                        autoFocus
                      />
                    </label>
                    <label>
                      目標日（任意）
                      <input name="due_local_date" type="date" />
                    </label>
                    <div className="dialog-actions">
                      <button
                        type="button"
                        disabled={milestoneBusyId !== null}
                        onClick={closeMilestoneCreator}
                      >
                        キャンセル
                      </button>
                      <button className="primary" disabled={milestoneBusyId !== null}>
                        {milestoneBusyId === milestoneProject.id ? "追加しています…" : "追加"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </dialog>
          </div>
        ) : null}

        {children.length === 0 ? null : (
          <dialog
            ref={taskCreateDialogRef}
            className="app-dialog task-create-dialog"
            aria-labelledby="task-create-title"
            aria-describedby="task-create-description"
            onClose={() => setTaskCreateOpen(false)}
            onCancel={(event) => {
              if (creatingTask) event.preventDefault();
            }}
          >
            <header className="app-dialog-header">
              <div>
                <p className="eyebrow">
                  {taskCreateContext === "today" ? "今日の実行" : "予定を組み立てる"}
                </p>
                <h2 id="task-create-title">
                  {taskCreateContext === "today" ? "今日にタスクを追加" : "タスクを作成"}
                </h2>
              </div>
              <button
                type="button"
                className="dialog-close"
                aria-label="閉じる"
                disabled={creatingTask}
                onClick={closeTaskCreator}
              >
                ×
              </button>
            </header>
            <div className="app-dialog-body">
              <p id="task-create-description" className="dialog-description">
                {taskCreateContext === "today"
                  ? "今日すぐに取り組む1件を追加します。未来の予定は計画画面から作成できます。"
                  : "やる日が決まっていれば予定へ、まだならBacklogへ置きます。期限は別に管理できます。"}
              </p>
              {taskDialogError === null ? null : (
                <p className="error" role="alert">
                  {taskDialogError}
                </p>
              )}
              <form
                ref={taskCreateFormRef}
                className="task-create-form"
                onSubmit={(event) => void createTask(event)}
              >
                {taskCreateContext === "today" ? null : (
                  <fieldset className="task-placement-control form-span">
                    <legend>計画への置き方</legend>
                    <label>
                      <input
                        type="radio"
                        name="task_placement"
                        value="scheduled"
                        checked={taskCreatePlacement === "scheduled"}
                        onChange={() => setTaskCreatePlacement("scheduled")}
                      />
                      <span>
                        <strong>やる日を決める</strong>
                        <small>週・月のカレンダーに表示</small>
                      </span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="task_placement"
                        value="backlog"
                        checked={taskCreatePlacement === "backlog"}
                        onChange={() => setTaskCreatePlacement("backlog")}
                      />
                      <span>
                        <strong>Backlogに置く</strong>
                        <small>日付はあとで親子で決める</small>
                      </span>
                    </label>
                  </fieldset>
                )}
                <label>
                  親向けタイトル
                  <input
                    name="title"
                    required
                    maxLength={240}
                    placeholder="算数プリント"
                    autoFocus
                  />
                </label>
                <label>
                  子供に見せることば
                  <input
                    name="child_copy"
                    required
                    maxLength={160}
                    placeholder="さんすう プリント"
                  />
                </label>
                <label>
                  やる日
                  <input
                    name="planned_local_date"
                    type="date"
                    required={taskCreatePlacement === "scheduled"}
                    disabled={taskCreatePlacement === "backlog"}
                    value={taskCreateDate}
                    readOnly={taskCreateContext === "today"}
                    onChange={(event) => setTaskCreateDate(event.currentTarget.value)}
                  />
                  <small>
                    {taskCreateContext === "today"
                      ? "今日に固定されています"
                      : taskCreatePlacement === "backlog"
                        ? "Backlogでは、やる日を決めません"
                        : "この日に取り組む予定として表示します"}
                  </small>
                </label>
                <label>
                  期限（任意）
                  <input
                    name="due_local_date"
                    type="date"
                    min={taskCreatePlacement === "scheduled" ? taskCreateDate : undefined}
                  />
                  <small>やる日と期限は別の意味です</small>
                </label>
                <fieldset className="task-constraint-control form-span">
                  <legend>取り組む時間と順番（任意）</legend>
                  <div className="task-time-window-fields">
                    <label>
                      開始できる時刻
                      <input name="available_from_local_time" type="time" />
                    </label>
                    <label>
                      ここまでに始める
                      <input name="available_until_local_time" type="time" />
                    </label>
                  </div>
                  <label className="checkbox-row">
                    <input name="today_pinned" type="checkbox" />
                    <span>
                      <strong>Todayの先頭グループに固定</strong>
                      <small>子供が通常タスクより後ろへ移せないようにします</small>
                    </span>
                  </label>
                  <small>時間帯を設定したタスクは、開始時刻の早い順を守ります。</small>
                </fieldset>
                <label>
                  プロジェクト（任意）
                  <select
                    name="project_id"
                    value={draftProjectId}
                    onChange={(event) => setDraftProjectId(event.currentTarget.value)}
                  >
                    <option value="">なし（単独のタスク）</option>
                    {selectedChildProjects
                      .filter(
                        (project) => project.status === "active" || project.status === "planned",
                      )
                      .map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                  </select>
                </label>
                <label>
                  めやす（分）
                  <input
                    name="estimated_minutes"
                    type="number"
                    min={0}
                    max={1440}
                    defaultValue={15}
                    required
                  />
                </label>
                <label>
                  完了のしかた
                  <select name="completion_policy" defaultValue="none">
                    <option value="none">子供が完了したら終了</option>
                    <option value="guardian_required">親が確認して終了</option>
                    <option value="evidence_photo">写真を残して終了</option>
                    <option value="evidence_note">メモを残して終了</option>
                    <option value="evidence_photo_or_note">写真またはメモを残して終了</option>
                  </select>
                  <small>
                    写真は端末内で縮小し、位置情報などを除いて非公開保存します。「親が確認」は完了後に通知と確認ボタンを表示します。
                  </small>
                </label>
                <div className="dialog-actions form-span">
                  <button type="button" disabled={creatingTask} onClick={closeTaskCreator}>
                    キャンセル
                  </button>
                  <button className="primary" disabled={creatingTask}>
                    {creatingTask
                      ? "作成しています…"
                      : taskCreateContext === "today"
                        ? "今日に追加"
                        : taskCreatePlacement === "backlog"
                          ? "Backlogに追加"
                          : "予定に追加"}
                  </button>
                </div>
              </form>
            </div>
          </dialog>
        )}

        <dialog
          ref={taskBulkScheduleDialogRef}
          className="app-dialog task-bulk-schedule-dialog"
          aria-labelledby="task-bulk-schedule-title"
          aria-describedby="task-bulk-schedule-description"
          onClose={() => {
            setBulkPlanningDialogOpen(false);
            setBulkPreview(null);
          }}
          onCancel={(event) => {
            if (bulkPlanningBusy !== null) event.preventDefault();
          }}
        >
          <header className="app-dialog-header">
            <div>
              <p className="eyebrow">負荷を調整</p>
              <h2 id="task-bulk-schedule-title">
                {bulkPreview === null ? "やる日をまとめて選ぶ" : "変更内容を確認"}
              </h2>
            </div>
            <button
              type="button"
              className="dialog-close"
              aria-label="閉じる"
              disabled={bulkPlanningBusy !== null}
              onClick={closeBulkPlanningDialog}
            >
              ×
            </button>
          </header>
          <div className="app-dialog-body">
            <p id="task-bulk-schedule-description" className="dialog-description">
              {bulkPreview === null
                ? "選択したタスクはまだ変わりません。まず移動先の日付から差分を作ります。"
                : "次の全変更を原子的に適用します。1件でも別端末で変わっていれば、全件を適用しません。"}
            </p>
            {bulkPlanningError === null ? null : (
              <p className="error" role="alert">
                {bulkPlanningError}
              </p>
            )}
            {bulkPreview === null ? (
              <form
                className="bulk-planning-form"
                onSubmit={(event) => void previewBulkPlanning(event)}
              >
                <div className="bulk-selected-summary">
                  <strong>{bulkSelectedTaskIds.length}件</strong>
                  <ul>
                    {bulkSelectedTaskIds.map((taskId) => {
                      const item = planTasks.find((candidate) => candidate.task.id === taskId);
                      return item === undefined ? null : (
                        <li key={taskId}>
                          <span>{item.task.title}</span>
                          <small>{item.schedule_local_date}</small>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <label>
                  新しいやる日
                  <input
                    name="planned_local_date"
                    type="date"
                    defaultValue={workspaceLocalDate}
                    required
                    autoFocus
                  />
                  <small>期限は変えません。期限より後になるタスクは事前に拒否します。</small>
                </label>
                <div className="dialog-actions">
                  <button type="button" onClick={closeBulkPlanningDialog}>
                    キャンセル
                  </button>
                  <button className="primary" disabled={bulkPlanningBusy !== null}>
                    {bulkPlanningBusy === "preview" ? "差分を作成中…" : "変更内容を確認"}
                  </button>
                </div>
              </form>
            ) : (
              <section className="bulk-preview-panel">
                <div className="bulk-preview-count">
                  <span>変更するタスク</span>
                  <strong>{bulkPreview.affected_count}件</strong>
                  <small>
                    確認期限 {new Date(bulkPreview.expires_at).toLocaleTimeString("ja-JP")}
                  </small>
                </div>
                <ul>
                  {bulkPreview.changes.map((change) => (
                    <li key={change.task_id}>
                      <strong>{change.task_title}</strong>
                      <span>
                        {change.previous_planned_local_date ?? "日付未定"} →{" "}
                        {change.planned_local_date}
                      </span>
                      <small>期限 {change.due_local_date ?? "なし"}（変更しません）</small>
                    </li>
                  ))}
                </ul>
                <div className="dialog-actions">
                  <button
                    type="button"
                    disabled={bulkPlanningBusy !== null}
                    onClick={() => {
                      setBulkPreview(null);
                      setBulkPlanningError(null);
                      setBulkPreviewIdempotencyKey(crypto.randomUUID());
                    }}
                  >
                    日付を選び直す
                  </button>
                  <button
                    type="button"
                    className="primary"
                    disabled={bulkPlanningBusy !== null}
                    onClick={() => void applyBulkPlanning()}
                  >
                    {bulkPlanningBusy === "apply"
                      ? "全件を変更中…"
                      : `${String(bulkPreview.affected_count)}件を確定`}
                  </button>
                </div>
              </section>
            )}
          </div>
        </dialog>

        {editingTask === null ? null : (
          <dialog
            ref={taskEditDialogRef}
            className="app-dialog compact-dialog"
            aria-labelledby="task-edit-title"
            aria-describedby="task-edit-description"
            onClose={() => setEditingTask(null)}
            onCancel={(event) => {
              if (updatingTask) event.preventDefault();
            }}
          >
            <header className="app-dialog-header">
              <div>
                <p className="eyebrow">計画の内容</p>
                <h2 id="task-edit-title">タスクを編集</h2>
              </div>
              <button
                type="button"
                className="dialog-close"
                aria-label="閉じる"
                disabled={updatingTask}
                onClick={closeTaskEditor}
              >
                ×
              </button>
            </header>
            <div className="app-dialog-body">
              <p id="task-edit-description" className="dialog-description">
                親向けの名前、子供に見せることば、見積もり時間を変更します。
              </p>
              {taskEditError === null ? null : (
                <p className="error" role="alert">
                  {taskEditError}
                </p>
              )}
              <form
                className="task-edit-form"
                key={`${editingTask.task.id}:${String(editingTask.task.version)}`}
                onSubmit={(event) => void updateTask(event)}
              >
                <label>
                  親向けタイトル
                  <input
                    name="title"
                    required
                    maxLength={240}
                    defaultValue={editingTask.task.title}
                    autoFocus
                  />
                </label>
                <label>
                  子供に見せることば
                  <input
                    name="child_copy"
                    required
                    maxLength={160}
                    defaultValue={editingTask.task.child_copy}
                  />
                </label>
                <label>
                  めやす（分）
                  <input
                    name="estimated_minutes"
                    type="number"
                    min={0}
                    max={1440}
                    required
                    defaultValue={editingTask.task.estimated_minutes ?? 0}
                  />
                </label>
                <div className="dialog-actions">
                  {["deferred", "skipped", "cancelled"].includes(editingTask.assignment.state) ? (
                    <button
                      type="button"
                      disabled={updatingTask}
                      onClick={() => {
                        const item = editingTask;
                        taskEditDialogRef.current?.close();
                        setEditingTask(null);
                        openTaskPlanDecision(item);
                      }}
                    >
                      予定に戻す
                    </button>
                  ) : null}
                  {editingTask.task.planned_local_date === null ? null : (
                    <button
                      type="button"
                      disabled={updatingTask}
                      onClick={() => void moveEditingTaskToBacklog()}
                    >
                      {updatingTask ? "戻しています…" : "Backlogに戻す"}
                    </button>
                  )}
                  <button type="button" disabled={updatingTask} onClick={closeTaskEditor}>
                    キャンセル
                  </button>
                  <button className="primary" disabled={updatingTask}>
                    {updatingTask ? "保存しています…" : "変更を保存"}
                  </button>
                </div>
              </form>
            </div>
          </dialog>
        )}

        {editingScheduleTask === null ? null : (
          <dialog
            ref={taskScheduleDialogRef}
            className="app-dialog compact-dialog"
            aria-labelledby="task-schedule-title"
            aria-describedby="task-schedule-description"
            onClose={() => setEditingScheduleTask(null)}
            onCancel={(event) => {
              if (reschedulingTask) event.preventDefault();
            }}
          >
            <header className="app-dialog-header">
              <div>
                <p className="eyebrow">計画を調整</p>
                <h2 id="task-schedule-title">タスクの日程変更</h2>
              </div>
              <button
                type="button"
                className="dialog-close"
                aria-label="閉じる"
                disabled={reschedulingTask}
                onClick={closeTaskScheduleEditor}
              >
                ×
              </button>
            </header>
            <div className="app-dialog-body">
              <p id="task-schedule-description" className="dialog-description">
                「やる日」は計画に表示する日です。「期限」は終わらせたい最終日として別に管理します。
              </p>
              <div className="schedule-task-summary">
                <strong>{editingScheduleTask.task.title}</strong>
                <span>{editingScheduleTask.task.child_copy}</span>
              </div>
              {scheduleDialogError === null ? null : (
                <p className="error" role="alert">
                  {scheduleDialogError}
                </p>
              )}
              <form className="schedule-edit-form" onSubmit={(event) => void rescheduleTask(event)}>
                <fieldset className="task-placement-control form-span">
                  <legend>計画への置き方</legend>
                  <label>
                    <input
                      type="radio"
                      name="schedule_placement"
                      value="scheduled"
                      checked={schedulePlacement === "scheduled"}
                      onChange={() => setSchedulePlacement("scheduled")}
                    />
                    <span>
                      <strong>やる日を決める</strong>
                      <small>カレンダーに表示</small>
                    </span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="schedule_placement"
                      value="backlog"
                      checked={schedulePlacement === "backlog"}
                      onChange={() => setSchedulePlacement("backlog")}
                    />
                    <span>
                      <strong>Backlogに戻す</strong>
                      <small>日付を決めずに保留</small>
                    </span>
                  </label>
                </fieldset>
                <label>
                  やる日
                  <input
                    name="planned_local_date"
                    type="date"
                    required={schedulePlacement === "scheduled"}
                    disabled={schedulePlacement === "backlog"}
                    defaultValue={editingScheduleTask.task.planned_local_date ?? workspaceLocalDate}
                    autoFocus
                  />
                </label>
                <label>
                  期限（任意）
                  <input
                    name="due_local_date"
                    type="date"
                    defaultValue={editingScheduleTask.task.due_local_date ?? ""}
                  />
                </label>
                <fieldset className="task-constraint-control form-span">
                  <legend>取り組む時間と順番</legend>
                  <div className="task-time-window-fields">
                    <label>
                      開始できる時刻
                      <input
                        name="available_from_local_time"
                        type="time"
                        defaultValue={editingScheduleTask.task.available_from_local_time ?? ""}
                      />
                    </label>
                    <label>
                      ここまでに始める
                      <input
                        name="available_until_local_time"
                        type="time"
                        defaultValue={editingScheduleTask.task.available_until_local_time ?? ""}
                      />
                    </label>
                  </div>
                  <label className="checkbox-row">
                    <input
                      name="today_pinned"
                      type="checkbox"
                      defaultChecked={editingScheduleTask.task.today_pinned}
                    />
                    <span>
                      <strong>Todayの先頭グループに固定</strong>
                      <small>子供が通常タスクより後ろへ移せないようにします</small>
                    </span>
                  </label>
                </fieldset>
                <div className="dialog-actions form-span">
                  <button
                    type="button"
                    disabled={reschedulingTask}
                    onClick={closeTaskScheduleEditor}
                  >
                    キャンセル
                  </button>
                  <button className="primary" disabled={reschedulingTask}>
                    {reschedulingTask
                      ? "変更しています…"
                      : schedulePlacement === "backlog"
                        ? "Backlogへ移す"
                        : "予定を更新"}
                  </button>
                </div>
              </form>
            </div>
          </dialog>
        )}

        {returningTask === null ? null : (
          <dialog
            ref={taskReturnDialogRef}
            className="app-dialog compact-dialog"
            aria-labelledby="task-return-title"
            aria-describedby="task-return-description"
            onClose={() => {
              setReturningTask(null);
              setTaskReturnError(null);
            }}
            onCancel={(event) => {
              if (taskCommandBusyId !== null) event.preventDefault();
            }}
          >
            <header className="app-dialog-header">
              <div>
                <p className="eyebrow">いっしょに仕上げる</p>
                <h2 id="task-return-title">直してほしいことを伝える</h2>
              </div>
              <button
                type="button"
                className="dialog-close"
                aria-label="閉じる"
                disabled={taskCommandBusyId !== null}
                onClick={() => setReturningTask(null)}
              >
                ×
              </button>
            </header>
            <div className="app-dialog-body">
              <p id="task-return-description" className="dialog-description">
                できていない点を責めるのではなく、次に何をすればよいかを短く具体的に伝えます。
              </p>
              <div className="schedule-task-summary">
                <strong>{returningTask.task.title}</strong>
                <span>{returningTask.task.child_copy}</span>
              </div>
              {taskReturnError === null ? null : (
                <p className="error" role="alert">
                  {taskReturnError}
                </p>
              )}
              <form onSubmit={(event) => void returnTaskForChanges(event)}>
                <label>
                  子供に伝えること
                  <textarea
                    name="note"
                    required
                    maxLength={1000}
                    rows={4}
                    placeholder="例：さいごの もんだいを もういちど みてみよう"
                    autoFocus
                  />
                </label>
                <div className="dialog-actions">
                  <button
                    type="button"
                    disabled={taskCommandBusyId !== null}
                    onClick={() => setReturningTask(null)}
                  >
                    キャンセル
                  </button>
                  <button className="primary" disabled={taskCommandBusyId !== null}>
                    {taskCommandBusyId === returningTask.task.id
                      ? "伝えています…"
                      : "子供の画面へ戻す"}
                  </button>
                </div>
              </form>
            </div>
          </dialog>
        )}

        {taskPlanDecision === null ? null : (
          <dialog
            ref={taskPlanDecisionDialogRef}
            className="app-dialog compact-dialog"
            aria-labelledby="task-plan-decision-title"
            aria-describedby="task-plan-decision-description"
            onClose={() => {
              setTaskPlanDecision(null);
              setTaskPlanDecisionError(null);
            }}
            onCancel={(event) => {
              if (taskCommandBusyId !== null) event.preventDefault();
            }}
          >
            <header className="app-dialog-header">
              <div>
                <p className="eyebrow">予定の見直し</p>
                <h2 id="task-plan-decision-title">このタスクをどうしますか？</h2>
              </div>
              <button
                type="button"
                className="dialog-close"
                aria-label="閉じる"
                disabled={taskCommandBusyId !== null}
                onClick={() => setTaskPlanDecision(null)}
              >
                ×
              </button>
            </header>
            <div className="app-dialog-body">
              <p id="task-plan-decision-description" className="dialog-description">
                消してしまわず、家族で決めた変更として履歴を残します。
              </p>
              <div className="schedule-task-summary">
                <strong>{taskPlanDecision.item.task.title}</strong>
                <span>{taskPlanDecision.item.task.child_copy}</span>
              </div>
              {taskPlanDecisionError === null ? null : (
                <p className="error" role="alert">
                  {taskPlanDecisionError}
                </p>
              )}
              <form onSubmit={(event) => void submitTaskPlanDecision(event)}>
                <fieldset className="task-plan-options">
                  <legend>変更する内容</legend>
                  {taskPlanCommandsFor(taskPlanDecision.item.assignment.state).map((command) => (
                    <label key={command}>
                      <input
                        type="radio"
                        name="command"
                        value={command}
                        checked={taskPlanDecision.command === command}
                        onChange={() => {
                          setTaskPlanDecisionError(null);
                          setTaskPlanDecision((current) =>
                            current === null ? null : { ...current, command },
                          );
                        }}
                      />
                      <span>
                        <strong>{taskPlanCommandCopy[command].label}</strong>
                        <small>{taskPlanCommandCopy[command].description}</small>
                      </span>
                    </label>
                  ))}
                </fieldset>
                {taskPlanDecision.command === "restore" ? null : (
                  <label>
                    理由（任意）
                    <textarea
                      name="note"
                      maxLength={1000}
                      rows={3}
                      placeholder="例：今日は予定が変わったため"
                      autoFocus
                    />
                  </label>
                )}
                <div className="dialog-actions">
                  <button
                    type="button"
                    disabled={taskCommandBusyId !== null}
                    onClick={() => setTaskPlanDecision(null)}
                  >
                    キャンセル
                  </button>
                  <button
                    className={taskPlanDecision.command === "cancel" ? "danger" : "primary"}
                    disabled={taskCommandBusyId !== null}
                  >
                    {taskCommandBusyId === taskPlanDecision.item.task.id
                      ? "更新しています…"
                      : taskPlanCommandCopy[taskPlanDecision.command].submit}
                  </button>
                </div>
              </form>
            </div>
          </dialog>
        )}

        <dialog
          ref={inboxCreateDialogRef}
          className="app-dialog compact-dialog"
          aria-labelledby="inbox-create-title"
          aria-describedby="inbox-create-description"
          onClose={() => setInboxCreateOpen(false)}
          onCancel={(event) => {
            if (creatingInboxItem) event.preventDefault();
          }}
        >
          <header className="app-dialog-header">
            <div>
              <p className="eyebrow">未整理のまま保存</p>
              <h2 id="inbox-create-title">Inboxに追加</h2>
            </div>
            <button
              type="button"
              className="dialog-close"
              aria-label="閉じる"
              disabled={creatingInboxItem}
              onClick={closeInboxCreator}
            >
              ×
            </button>
          </header>
          <div className="app-dialog-body">
            <p id="inbox-create-description" className="dialog-description">
              子供、やる日、プロジェクトはまだ決めなくて大丈夫です。なくさないために内容だけ保存します。
            </p>
            {inboxDialogError === null ? null : (
              <p className="error" role="alert">
                {inboxDialogError}
              </p>
            )}
            <form className="inbox-capture-form" onSubmit={(event) => void createInboxItem(event)}>
              <label>
                内容
                <input
                  name="title"
                  required
                  maxLength={240}
                  placeholder="学校から自由研究のお知らせ"
                  autoFocus
                />
              </label>
              <label>
                メモ（任意）
                <textarea
                  name="notes"
                  maxLength={8000}
                  rows={5}
                  placeholder="提出日や、あとで確認したいこと"
                />
              </label>
              <div className="dialog-actions">
                <button type="button" disabled={creatingInboxItem} onClick={closeInboxCreator}>
                  キャンセル
                </button>
                <button className="primary" disabled={creatingInboxItem}>
                  {creatingInboxItem ? "保存しています…" : "Inboxに保存"}
                </button>
              </div>
            </form>
          </div>
        </dialog>

        {planningInboxItem === null || selectedChild === null ? null : (
          <dialog
            ref={inboxPlanDialogRef}
            className="app-dialog task-create-dialog"
            aria-labelledby="inbox-plan-title"
            aria-describedby="inbox-plan-description"
            onClose={() => setPlanningInboxItem(null)}
            onCancel={(event) => {
              if (planningInboxBusy) event.preventDefault();
            }}
          >
            <header className="app-dialog-header">
              <div>
                <p className="eyebrow">Inboxから計画へ</p>
                <h2 id="inbox-plan-title">やる日を決める</h2>
              </div>
              <button
                type="button"
                className="dialog-close"
                aria-label="閉じる"
                disabled={planningInboxBusy}
                onClick={closeInboxPlanner}
              >
                ×
              </button>
            </header>
            <div className="app-dialog-body">
              <p id="inbox-plan-description" className="dialog-description">
                内容を確認し、子供に見せることばと、やる日を決めてから計画へ移します。
              </p>
              {inboxPlanError === null ? null : (
                <p className="error" role="alert">
                  {inboxPlanError}
                </p>
              )}
              <form className="task-create-form" onSubmit={(event) => void planInboxItem(event)}>
                <label>
                  親向けタイトル
                  <input
                    name="title"
                    required
                    maxLength={240}
                    defaultValue={planningInboxItem.title}
                    autoFocus
                  />
                </label>
                <label>
                  子供に見せることば
                  <input
                    name="child_copy"
                    required
                    maxLength={160}
                    placeholder="じゆうけんきゅうを きめる"
                  />
                </label>
                <label>
                  やる日
                  <input
                    name="planned_local_date"
                    type="date"
                    required
                    defaultValue={workspaceLocalDate}
                  />
                </label>
                <label>
                  期限（任意）
                  <input name="due_local_date" type="date" />
                </label>
                <label>
                  プロジェクト（任意）
                  <select name="project_id" defaultValue="">
                    <option value="">なし（単独のタスク）</option>
                    {selectedChildProjects
                      .filter(
                        (project) => project.status === "active" || project.status === "planned",
                      )
                      .map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                  </select>
                </label>
                <label>
                  めやす（分）
                  <input
                    name="estimated_minutes"
                    type="number"
                    min={0}
                    max={1440}
                    defaultValue={15}
                    required
                  />
                </label>
                {planningInboxItem.notes.length === 0 ? null : (
                  <div className="inbox-original-note form-span">
                    <span>保存したメモ</span>
                    <p>{planningInboxItem.notes}</p>
                  </div>
                )}
                <div className="dialog-actions form-span">
                  <button type="button" disabled={planningInboxBusy} onClick={closeInboxPlanner}>
                    キャンセル
                  </button>
                  <button className="primary" disabled={planningInboxBusy}>
                    {planningInboxBusy ? "計画しています…" : "計画へ移す"}
                  </button>
                </div>
              </form>
            </div>
          </dialog>
        )}

        {cancellingInboxItem === null ? null : (
          <dialog
            ref={inboxCancelDialogRef}
            className="app-dialog compact-dialog"
            aria-labelledby="inbox-cancel-title"
            aria-describedby="inbox-cancel-description"
            onClose={() => setCancellingInboxItem(null)}
            onCancel={(event) => {
              if (cancellingInboxBusy) event.preventDefault();
            }}
          >
            <header className="app-dialog-header">
              <div>
                <p className="eyebrow">Inboxを整理</p>
                <h2 id="inbox-cancel-title">この項目を取り消しますか？</h2>
              </div>
              <button
                type="button"
                className="dialog-close"
                aria-label="閉じる"
                disabled={cancellingInboxBusy}
                onClick={closeInboxCancellation}
              >
                ×
              </button>
            </header>
            <div className="app-dialog-body">
              <p id="inbox-cancel-description" className="dialog-description">
                「{cancellingInboxItem.title}」を未整理一覧から外します。タスクは作成されません。
              </p>
              <div className="dialog-actions">
                <button disabled={cancellingInboxBusy} onClick={closeInboxCancellation}>
                  戻る
                </button>
                <button
                  className="danger"
                  disabled={cancellingInboxBusy}
                  onClick={() => void cancelInboxItem()}
                >
                  {cancellingInboxBusy ? "取り消しています…" : "Inboxから取り消す"}
                </button>
              </div>
            </div>
          </dialog>
        )}

        {activeView === "review" ? (
          <div className="cycle-page">
            <section className="cycle-intro panel">
              <div>
                <p className="eyebrow">家庭向けの軽い週次サイクル</p>
                <h2>決める・取り組む・話すをひと続きに</h2>
                <p>
                  タスクをサイクル内へ閉じ込めず、親子で合意した約束と振り返りだけを残します。今日のタスクは、プロジェクトや領域をまたいでこれまでどおり表示されます。
                </p>
              </div>
              <ol className="cycle-steps" aria-label="家族サイクルの流れ">
                <li>
                  <span>1</span>
                  <strong>いっしょにきめる</strong>
                </li>
                <li>
                  <span>2</span>
                  <strong>まいにちやる</strong>
                </li>
                <li>
                  <span>3</span>
                  <strong>できたをみる</strong>
                </li>
                <li>
                  <span>4</span>
                  <strong>ふりかえる</strong>
                </li>
              </ol>
            </section>

            {cyclesLoading ? (
              <section className="panel loading-panel" aria-busy="true">
                <p className="status" role="status">
                  家族サイクルを読み込んでいます…
                </p>
              </section>
            ) : selectedChild === null ? (
              <section className="panel empty-onboarding">
                <h2>
                  {isGuardian ? "一緒に計画する子供を追加してください" : "表示できる子供がいません"}
                </h2>
                {isGuardian ? (
                  <button className="primary" onClick={() => navigateParent("settings")}>
                    家族設定へ
                  </button>
                ) : (
                  <p>家族の管理者に、子供と「ふりかえり閲覧」の範囲を確認してください。</p>
                )}
              </section>
            ) : selectedChildCycles.length === 0 ? (
              <section className="panel empty-onboarding">
                <h2>
                  {isGuardian
                    ? `${selectedChild.display_name}と話す最初のサイクル`
                    : `${selectedChild.display_name}のサイクルはまだありません`}
                </h2>
                {isGuardian ? (
                  <>
                    <p>来週の行事や負荷、やりたいことを確認する下書きから始めます。</p>
                    <button className="primary" onClick={() => openCyclePlanEditor("create")}>
                      最初のサイクルを作成
                    </button>
                  </>
                ) : (
                  <p>サイクルが作成されると、ここで内容を確認できます。</p>
                )}
              </section>
            ) : (
              <section
                className="cycle-list"
                aria-label={`${selectedChild.display_name}の家族サイクル`}
              >
                {selectedChildCycles.map((cycle) => {
                  const stateCopy = {
                    draft: "下書き",
                    planned: "計画済み",
                    active: "実行中",
                    reviewing: "ふりかえり中",
                    closed: "完了",
                  }[cycle.state];
                  const childNames = cycle.participants
                    .filter((participant) => participant.participant_type === "child")
                    .map(
                      (participant) =>
                        children.find((child) => child.id === participant.participant_id)
                          ?.display_name ?? "子供",
                    );
                  return (
                    <article className={`cycle-card cycle-${cycle.state}`} key={cycle.id}>
                      <header>
                        <div>
                          <span className="cycle-state">{stateCopy}</span>
                          <h2>
                            {cycle.start_local_date.replaceAll("-", "/")} —{" "}
                            {cycle.end_local_date.replaceAll("-", "/")}
                          </h2>
                          <p>{childNames.join("・")}と一緒に進めるサイクル</p>
                        </div>
                        <span className="version-badge">v{cycle.version}</span>
                      </header>
                      {cycle.plan_summary === "" ? null : (
                        <p className="cycle-summary">{cycle.plan_summary}</p>
                      )}
                      <section className="cycle-commitments" aria-label="今週の約束">
                        <div className="section-heading compact">
                          <h3>一緒に決めたこと</h3>
                          <span className="count-badge">{cycle.commitments.length}</span>
                        </div>
                        {cycle.commitments.length === 0 ? (
                          <p className="muted">
                            まだ約束はありません。予定や負荷を見ながら話して決められます。
                          </p>
                        ) : (
                          <ul>
                            {cycle.commitments.map((commitment) => (
                              <li key={commitment.id}>
                                <span aria-hidden="true">
                                  {commitment.status === "agreed" ? "✓" : "○"}
                                </span>
                                <div>
                                  <strong>{commitment.title}</strong>
                                  <small>{commitment.child_copy}</small>
                                </div>
                                <span>
                                  {commitment.status === "agreed"
                                    ? "合意"
                                    : commitment.status === "changed"
                                      ? "変更"
                                      : "候補"}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </section>
                      {cycle.review_notes.length === 0 && cycle.review_summary === "" ? null : (
                        <section className="cycle-review-preview">
                          <h3>話したこと</h3>
                          {cycle.review_summary === "" ? null : <p>{cycle.review_summary}</p>}
                          <div className="review-note-grid">
                            {(["good", "helped", "difficult", "next_change"] as const).map(
                              (category) => {
                                const categoryNotes = cycle.review_notes.filter(
                                  (note) => note.category === category,
                                );
                                if (categoryNotes.length === 0) return null;
                                const label = {
                                  good: "よかったこと",
                                  helped: "たすけてもらえたこと",
                                  difficult: "むずかしかったこと",
                                  next_change: "つぎにかえること",
                                }[category];
                                return (
                                  <div key={category}>
                                    <strong>{label}</strong>
                                    {categoryNotes.map((note) => (
                                      <p key={note.id}>{note.note}</p>
                                    ))}
                                  </div>
                                );
                              },
                            )}
                          </div>
                          {cycle.review_actions.length === 0 ? null : (
                            <div className="cycle-actions-preview">
                              <strong>次に変えること</strong>
                              {cycleActionError === null ||
                              (cycleActionIntent?.cycle_id !== cycle.id &&
                                cycleActionDismissal?.cycle_id !== cycle.id) ? null : (
                                <p className="error" role="alert">
                                  {cycleActionError}
                                </p>
                              )}
                              <ul>
                                {cycle.review_actions.map((action) => {
                                  const actionLabel = {
                                    create_task: "新しいタスク",
                                    reschedule_task: "タスクの日程変更",
                                    update_routine: "ルーティン変更",
                                    other: "話し合いメモ",
                                  }[action.kind];
                                  const pendingLink =
                                    cycleActionIntent?.action_id === action.id &&
                                    cycleActionIntent.downstream_request_id !== null;
                                  return (
                                    <li
                                      className={`cycle-action-item ${action.status}`}
                                      key={action.id}
                                    >
                                      <div>
                                        <span className="cycle-action-status">
                                          {action.status === "applied"
                                            ? "反映済み"
                                            : action.status === "dismissed"
                                              ? "見送り"
                                              : pendingLink
                                                ? "リンク未完了"
                                                : "候補"}
                                        </span>
                                        <strong>{action.summary}</strong>
                                        <small>{actionLabel}</small>
                                        {action.command_id === null ? null : (
                                          <small>監査ID: {action.command_id}</small>
                                        )}
                                      </div>
                                      {action.status !== "proposed" || !isGuardian ? null : (
                                        <div className="cycle-action-controls">
                                          {pendingLink ? (
                                            <button
                                              type="button"
                                              className="primary"
                                              disabled={cycleActionBusyId !== null}
                                              onClick={() => void retryCycleActionLink()}
                                            >
                                              監査リンクを再試行
                                            </button>
                                          ) : action.kind === "other" ? null : (
                                            <button
                                              type="button"
                                              className="primary"
                                              disabled={cycleActionBusyId !== null}
                                              onClick={() => startCycleAction(cycle, action)}
                                            >
                                              {action.kind === "create_task"
                                                ? "タスク作成へ"
                                                : action.kind === "reschedule_task"
                                                  ? "日程変更へ"
                                                  : "ルーティン編集へ"}
                                            </button>
                                          )}
                                          <button
                                            type="button"
                                            disabled={cycleActionBusyId !== null || pendingLink}
                                            onClick={() => {
                                              setCycleActionError(null);
                                              setCycleActionDismissal({
                                                cycle_id: cycle.id,
                                                action_id: action.id,
                                                expected_version: cycle.version,
                                                summary: action.summary,
                                                idempotency_key: crypto.randomUUID(),
                                              });
                                            }}
                                          >
                                            見送る
                                          </button>
                                        </div>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                        </section>
                      )}
                      {isGuardian ? (
                        <footer className="cycle-card-actions">
                          {cycle.state === "draft" ? (
                            <>
                              <button onClick={() => openCyclePlanEditor(cycle)}>計画を編集</button>
                              <button
                                className="primary"
                                disabled={cycleBusyId !== null}
                                onClick={() => void transitionCycle(cycle, "plan")}
                              >
                                計画を確定
                              </button>
                            </>
                          ) : null}
                          {cycle.state === "planned" ? (
                            <>
                              <button onClick={() => openCyclePlanEditor(cycle)}>計画を編集</button>
                              <button
                                disabled={cycleBusyId !== null}
                                onClick={() => void transitionCycle(cycle, "return_to_draft")}
                              >
                                下書きへ戻す
                              </button>
                              <button
                                className="primary"
                                disabled={cycleBusyId !== null}
                                onClick={() => void transitionCycle(cycle, "activate")}
                              >
                                取り組みを始める
                              </button>
                            </>
                          ) : null}
                          {cycle.state === "active" ? (
                            <button
                              className="primary"
                              disabled={cycleBusyId !== null}
                              onClick={() => void transitionCycle(cycle, "start_review")}
                            >
                              ふりかえりを始める
                            </button>
                          ) : null}
                          {cycle.state === "reviewing" ? (
                            <>
                              <button onClick={() => openCycleReviewEditor(cycle)}>
                                ふりかえりを記録
                              </button>
                              <button
                                disabled={cycleBusyId !== null}
                                onClick={() => void transitionCycle(cycle, "return_to_active")}
                              >
                                実行中へ戻す
                              </button>
                              <button
                                className="primary"
                                disabled={cycleBusyId !== null}
                                onClick={() => void transitionCycle(cycle, "close")}
                              >
                                このサイクルを閉じる
                              </button>
                            </>
                          ) : null}
                          {cycle.state === "closed" ? (
                            <button
                              disabled={cycleBusyId !== null}
                              onClick={() => void transitionCycle(cycle, "reopen_review")}
                            >
                              ふりかえりを再開
                            </button>
                          ) : null}
                        </footer>
                      ) : null}
                    </article>
                  );
                })}
              </section>
            )}

            <dialog
              ref={cyclePlanDialogRef}
              className="app-dialog cycle-dialog"
              aria-labelledby="cycle-plan-title"
              onCancel={(event) => {
                if (cycleBusyId !== null) event.preventDefault();
                else closeCyclePlanEditor();
              }}
              onClose={() => {
                if (cyclePlanEditor !== null && cycleBusyId === null) closeCyclePlanEditor();
              }}
            >
              {!isGuardian || cyclePlanEditor === null ? null : (
                <>
                  <header className="app-dialog-header">
                    <div>
                      <p className="eyebrow">いっしょにきめる</p>
                      <h2 id="cycle-plan-title">
                        {cyclePlanEditor === "create"
                          ? "家族サイクルを作成"
                          : "サイクルの計画を編集"}
                      </h2>
                    </div>
                    <button
                      type="button"
                      className="dialog-close"
                      aria-label="閉じる"
                      disabled={cycleBusyId !== null}
                      onClick={closeCyclePlanEditor}
                    >
                      ×
                    </button>
                  </header>
                  <div className="app-dialog-body">
                    <p className="dialog-description">
                      行事、負荷、やりたいことを親子で確認し、合意したことだけを保存します。
                    </p>
                    {cycleDialogError === null ? null : (
                      <p className="error" role="alert">
                        {cycleDialogError}
                      </p>
                    )}
                    <form className="cycle-form" onSubmit={(event) => void saveCyclePlan(event)}>
                      <div className="form-grid two-columns">
                        <label>
                          開始日
                          <input
                            name="start_local_date"
                            type="date"
                            required
                            autoFocus
                            defaultValue={
                              cyclePlanEditor === "create"
                                ? startOfPlanningWeek(addLocalDays(workspaceLocalDate, 7))
                                : cyclePlanEditor.start_local_date
                            }
                          />
                        </label>
                        <label>
                          終了日
                          <input
                            name="end_local_date"
                            type="date"
                            required
                            defaultValue={
                              cyclePlanEditor === "create"
                                ? addLocalDays(
                                    startOfPlanningWeek(addLocalDays(workspaceLocalDate, 7)),
                                    (workspaceSettings?.default_cycle_days ?? 7) - 1,
                                  )
                                : cyclePlanEditor.end_local_date
                            }
                          />
                        </label>
                      </div>
                      <label>
                        今回いっしょに確認すること
                        <textarea
                          name="plan_summary"
                          maxLength={8000}
                          rows={3}
                          defaultValue={
                            cyclePlanEditor === "create" ? "" : cyclePlanEditor.plan_summary
                          }
                          placeholder="学校行事、負荷が高そうな日、本人がやりたいこと"
                        />
                      </label>
                      {cyclePlanEditor === "create" ? null : (
                        <fieldset className="cycle-participants">
                          <legend>一緒に計画する子供</legend>
                          {activeChildren.map((child) => (
                            <label key={child.id}>
                              <input
                                name={`cycle-child-${child.id}`}
                                type="checkbox"
                                defaultChecked={cyclePlanEditor.participants.some(
                                  (participant) =>
                                    participant.participant_type === "child" &&
                                    participant.participant_id === child.id,
                                )}
                              />
                              <span
                                className="avatar"
                                style={{ background: child.theme_color }}
                                aria-hidden="true"
                              />
                              {child.display_name}
                            </label>
                          ))}
                        </fieldset>
                      )}
                      <section className="cycle-commitment-editor">
                        <div className="section-heading compact">
                          <div>
                            <h3>一緒に決めたこと</h3>
                            <p>候補のままでも、合意済みにしても保存できます。</p>
                          </div>
                          <button type="button" onClick={addCycleCommitment}>
                            ＋ 約束を追加
                          </button>
                        </div>
                        {cyclePlanCommitments.length === 0 ? (
                          <p className="muted">約束はまだありません。</p>
                        ) : (
                          cyclePlanCommitments.map((commitment) => (
                            <div className="cycle-commitment-row" key={commitment.key}>
                              <label>
                                親向けの名前
                                <input
                                  value={commitment.title}
                                  maxLength={240}
                                  onChange={(event) =>
                                    updateCycleCommitment(commitment.key, {
                                      title: event.currentTarget.value,
                                    })
                                  }
                                />
                              </label>
                              <label>
                                子供に見せることば
                                <input
                                  value={commitment.child_copy}
                                  maxLength={160}
                                  onChange={(event) =>
                                    updateCycleCommitment(commitment.key, {
                                      child_copy: event.currentTarget.value,
                                    })
                                  }
                                />
                              </label>
                              <label>
                                対象
                                <select
                                  value={commitment.child_id ?? ""}
                                  onChange={(event) =>
                                    updateCycleCommitment(commitment.key, {
                                      child_id:
                                        event.currentTarget.value === ""
                                          ? null
                                          : event.currentTarget.value,
                                    })
                                  }
                                >
                                  <option value="">家族全体</option>
                                  {activeChildren.map((child) => (
                                    <option key={child.id} value={child.id}>
                                      {child.display_name}
                                    </option>
                                  ))}
                                </select>
                              </label>
                              <label>
                                合意
                                <select
                                  value={commitment.status}
                                  onChange={(event) =>
                                    updateCycleCommitment(commitment.key, {
                                      status: event.currentTarget
                                        .value as CycleCommitmentDraft["status"],
                                    })
                                  }
                                >
                                  <option value="proposed">候補</option>
                                  <option value="agreed">合意</option>
                                  <option value="changed">変更した</option>
                                </select>
                              </label>
                              <button
                                type="button"
                                className="danger quiet"
                                onClick={() =>
                                  setCyclePlanCommitments((current) =>
                                    current.filter((item) => item.key !== commitment.key),
                                  )
                                }
                              >
                                外す
                              </button>
                            </div>
                          ))
                        )}
                      </section>
                      <div className="dialog-actions">
                        <button
                          type="button"
                          disabled={cycleBusyId !== null}
                          onClick={closeCyclePlanEditor}
                        >
                          キャンセル
                        </button>
                        <button className="primary" disabled={cycleBusyId !== null}>
                          {cycleBusyId === null ? "計画を保存" : "保存しています…"}
                        </button>
                      </div>
                    </form>
                  </div>
                </>
              )}
            </dialog>

            <dialog
              ref={cycleReviewDialogRef}
              className="app-dialog cycle-dialog review-dialog"
              aria-labelledby="cycle-review-title"
              onCancel={(event) => {
                if (cycleBusyId !== null) event.preventDefault();
                else closeCycleReviewEditor();
              }}
              onClose={() => {
                if (cycleReviewEditor !== null && cycleBusyId === null) closeCycleReviewEditor();
              }}
            >
              {!isGuardian || cycleReviewEditor === null ? null : (
                <>
                  <header className="app-dialog-header">
                    <div>
                      <p className="eyebrow">親子の会話</p>
                      <h2 id="cycle-review-title">ふりかえりを記録</h2>
                    </div>
                    <button
                      type="button"
                      className="dialog-close"
                      aria-label="閉じる"
                      disabled={cycleBusyId !== null}
                      onClick={closeCycleReviewEditor}
                    >
                      ×
                    </button>
                  </header>
                  <div className="app-dialog-body">
                    <p className="dialog-description">
                      完了数の採点ではなく、できたこと、助けを求められたこと、難しかったこと、次に変えることを順番に話します。1行を1件として保存します。
                    </p>
                    {cycleDialogError === null ? null : (
                      <p className="error" role="alert">
                        {cycleDialogError}
                      </p>
                    )}
                    <form
                      className="cycle-form review-form"
                      onSubmit={(event) => void saveCycleReview(event)}
                    >
                      <label>
                        会話の短いまとめ
                        <textarea
                          name="review_summary"
                          rows={3}
                          maxLength={8000}
                          defaultValue={cycleReviewEditor.review_summary}
                        />
                      </label>
                      {(
                        [
                          ["good", "よかったこと"],
                          ["helped", "たすけてもらえたこと"],
                          ["difficult", "むずかしかったこと"],
                          ["next_change", "つぎにかえること"],
                        ] as const
                      ).map(([category, label]) => (
                        <label key={category}>
                          {label}
                          <textarea
                            name={category}
                            rows={3}
                            maxLength={8000}
                            defaultValue={cycleReviewEditor.review_notes
                              .filter(
                                (note) =>
                                  note.category === category &&
                                  (note.child_id === selectedChildId || note.child_id === null),
                              )
                              .map((note) => note.note)
                              .join("\n")}
                          />
                        </label>
                      ))}
                      <section className="cycle-review-action-editor">
                        <div className="section-heading compact">
                          <div>
                            <h3>次のサイクルで変えること</h3>
                            <p>
                              候補の種類と対象を決めます。保存だけではTaskやRoutineを変更しません。
                            </p>
                          </div>
                          <button type="button" onClick={addCycleReviewAction}>
                            ＋ Actionを追加
                          </button>
                        </div>
                        {cycleReviewActions.length === 0 ? (
                          <p className="muted">次に変える候補はまだありません。</p>
                        ) : (
                          cycleReviewActions.map((action) => (
                            <div className="cycle-review-action-row" key={action.key}>
                              <label>
                                種類
                                <select
                                  value={action.kind}
                                  onChange={(event) =>
                                    updateCycleReviewAction(action.key, {
                                      kind: event.currentTarget.value as ReviewActionKind,
                                    })
                                  }
                                >
                                  <option value="create_task">新しいタスクを作る</option>
                                  <option value="reschedule_task">タスクの日程を変える</option>
                                  <option value="update_routine">ルーティンを変える</option>
                                  <option value="other">話し合いメモとして残す</option>
                                </select>
                              </label>
                              <label className="cycle-review-action-summary">
                                何を変える？
                                <input
                                  value={action.summary}
                                  maxLength={2000}
                                  placeholder="例：算数は夕食前に10分だけ取り組む"
                                  onChange={(event) =>
                                    updateCycleReviewAction(action.key, {
                                      summary: event.currentTarget.value,
                                    })
                                  }
                                />
                              </label>
                              {action.kind === "reschedule_task" ? (
                                <label>
                                  対象タスク
                                  <select
                                    value={action.target_id ?? ""}
                                    onChange={(event) =>
                                      updateCycleReviewAction(action.key, {
                                        target_id: event.currentTarget.value || null,
                                      })
                                    }
                                  >
                                    <option value="">選択してください</option>
                                    {[...tasks, ...planTasks]
                                      .filter(
                                        (item, index, all) =>
                                          item.assignment.child_id === selectedChildId &&
                                          all.findIndex(
                                            (candidate) => candidate.task.id === item.task.id,
                                          ) === index,
                                      )
                                      .map((item) => (
                                        <option key={item.task.id} value={item.task.id}>
                                          {item.task.title}
                                        </option>
                                      ))}
                                  </select>
                                </label>
                              ) : action.kind === "update_routine" ? (
                                <label>
                                  対象ルーティン
                                  <select
                                    value={action.target_id ?? ""}
                                    onChange={(event) =>
                                      updateCycleReviewAction(action.key, {
                                        target_id: event.currentTarget.value || null,
                                      })
                                    }
                                  >
                                    <option value="">選択してください</option>
                                    {selectedChildRoutines.map((routine) => (
                                      <option key={routine.id} value={routine.id}>
                                        {routine.name}
                                      </option>
                                    ))}
                                  </select>
                                </label>
                              ) : null}
                              <button
                                type="button"
                                className="danger quiet"
                                onClick={() =>
                                  setCycleReviewActions((current) =>
                                    current.filter((candidate) => candidate.key !== action.key),
                                  )
                                }
                              >
                                外す
                              </button>
                            </div>
                          ))
                        )}
                      </section>
                      <div className="dialog-actions">
                        <button
                          type="button"
                          disabled={cycleBusyId !== null}
                          onClick={closeCycleReviewEditor}
                        >
                          キャンセル
                        </button>
                        <button className="primary" disabled={cycleBusyId !== null}>
                          {cycleBusyId === null ? "ふりかえりを保存" : "保存しています…"}
                        </button>
                      </div>
                    </form>
                  </div>
                </>
              )}
            </dialog>
            <dialog
              ref={cycleActionDismissDialogRef}
              className="app-dialog compact-dialog"
              aria-labelledby="cycle-action-dismiss-title"
              onCancel={(event) => {
                if (cycleActionBusyId !== null) event.preventDefault();
                else setCycleActionDismissal(null);
              }}
              onClose={() => {
                if (cycleActionBusyId === null) setCycleActionDismissal(null);
              }}
            >
              {!isGuardian || cycleActionDismissal === null ? null : (
                <>
                  <header className="app-dialog-header">
                    <div>
                      <p className="eyebrow">ふりかえりの履歴</p>
                      <h2 id="cycle-action-dismiss-title">このActionを見送る</h2>
                    </div>
                    <button
                      type="button"
                      className="dialog-close"
                      aria-label="閉じる"
                      disabled={cycleActionBusyId !== null}
                      onClick={() => setCycleActionDismissal(null)}
                    >
                      ×
                    </button>
                  </header>
                  <div className="app-dialog-body">
                    <p className="dialog-description">{cycleActionDismissal.summary}</p>
                    {cycleActionError === null ? null : (
                      <p className="error" role="alert">
                        {cycleActionError}
                      </p>
                    )}
                    <form onSubmit={(event) => void dismissCycleAction(event)}>
                      <label>
                        見送る理由
                        <textarea
                          name="reason"
                          rows={4}
                          maxLength={500}
                          required
                          autoFocus
                          placeholder="例：本人と話して、今のやり方をもう1週間続けることにした"
                        />
                      </label>
                      <div className="dialog-actions">
                        <button
                          type="button"
                          disabled={cycleActionBusyId !== null}
                          onClick={() => setCycleActionDismissal(null)}
                        >
                          戻る
                        </button>
                        <button className="primary" disabled={cycleActionBusyId !== null}>
                          {cycleActionBusyId === null ? "理由を記録して見送る" : "記録しています…"}
                        </button>
                      </div>
                    </form>
                  </div>
                </>
              )}
            </dialog>
          </div>
        ) : null}

        {activeView === "organization" ? (
          <div className="organization-layout">
            <section className="panel organization-panel" aria-labelledby="area-list-title">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">続いていく分類</p>
                  <h2 id="area-list-title">領域</h2>
                </div>
                <span className="count-badge">{activeAreas.length}</span>
              </div>
              <p className="section-description">
                学習・暮らし・習い事など、プロジェクトやルーティンを横断する分類です。子供画面で切り替える必要はありません。
              </p>
              {familyLoading ? (
                <div className="loading-panel" aria-busy="true">
                  読み込んでいます…
                </div>
              ) : activeAreas.length === 0 ? (
                <div className="organization-empty">
                  <strong>領域はまだありません</strong>
                  <p>まず1つ作ると、計画が増えても見失いにくくなります。</p>
                  <button className="primary" onClick={() => openAreaEditor("create")}>
                    最初の領域を追加
                  </button>
                </div>
              ) : (
                <ol className="organization-list area-list">
                  {activeAreas.map((area, index) => (
                    <li key={area.id}>
                      <span className="organization-color" style={{ background: area.color }}>
                        {area.icon ?? "•"}
                      </span>
                      <div className="organization-copy">
                        <strong>{area.name}</strong>
                        <small>子供には「{area.child_copy}」</small>
                      </div>
                      <div className="organization-order" aria-label={`${area.name}の順番`}>
                        <button
                          aria-label={`${area.name}を上へ`}
                          disabled={index === 0 || organizationBusyId !== null}
                          onClick={() => void moveArea(area.id, -1)}
                        >
                          ↑
                        </button>
                        <button
                          aria-label={`${area.name}を下へ`}
                          disabled={index === activeAreas.length - 1 || organizationBusyId !== null}
                          onClick={() => void moveArea(area.id, 1)}
                        >
                          ↓
                        </button>
                      </div>
                      <div className="row-actions">
                        <button onClick={() => openAreaEditor(area)}>編集</button>
                        <button
                          className="danger"
                          disabled={organizationBusyId !== null}
                          onClick={() => void runAreaArchiveCommand(area, "archive")}
                        >
                          アーカイブ
                        </button>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
              {archivedAreas.length === 0 ? null : (
                <details className="archived-resources">
                  <summary>アーカイブ済み（{archivedAreas.length}）</summary>
                  <ul className="organization-list">
                    {archivedAreas.map((area) => (
                      <li key={area.id}>
                        <span className="organization-color" style={{ background: area.color }}>
                          {area.icon ?? "•"}
                        </span>
                        <div className="organization-copy">
                          <strong>{area.name}</strong>
                        </div>
                        <button
                          disabled={organizationBusyId !== null}
                          onClick={() => void runAreaArchiveCommand(area, "restore")}
                        >
                          復元
                        </button>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </section>

            <section className="panel organization-panel" aria-labelledby="term-list-title">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">計画の区切り</p>
                  <h2 id="term-list-title">期間</h2>
                </div>
                <span className="count-badge">{activeTerms.length}</span>
              </div>
              <p className="section-description">
                学期・長期休み・年度などの期間です。日々の表示を分断せず、計画の絞り込みに使います。
              </p>
              {familyLoading ? (
                <div className="loading-panel" aria-busy="true">
                  読み込んでいます…
                </div>
              ) : activeTerms.length === 0 ? (
                <div className="organization-empty">
                  <strong>期間はまだありません</strong>
                  <p>学期や休みの開始日と終了日を登録できます。</p>
                  <button className="primary" onClick={() => openTermEditor("create")}>
                    最初の期間を追加
                  </button>
                </div>
              ) : (
                <ul className="organization-list term-list">
                  {activeTerms.map((term) => {
                    const phase =
                      workspaceLocalDate < term.start_local_date
                        ? "今後"
                        : workspaceLocalDate > term.end_local_date
                          ? "終了"
                          : "現在";
                    return (
                      <li key={term.id}>
                        <span className={`term-phase term-phase-${phase}`}>{phase}</span>
                        <div className="organization-copy">
                          <strong>{term.name}</strong>
                          <small>
                            {term.school_year_label === null ? "" : `${term.school_year_label} · `}
                            {term.start_local_date} → {term.end_local_date}
                          </small>
                        </div>
                        <div className="row-actions">
                          <button onClick={() => openTermEditor(term)}>編集</button>
                          <button
                            className="danger"
                            disabled={organizationBusyId !== null}
                            onClick={() => void runTermArchiveCommand(term, "archive")}
                          >
                            アーカイブ
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              {archivedTerms.length === 0 ? null : (
                <details className="archived-resources">
                  <summary>アーカイブ済み（{archivedTerms.length}）</summary>
                  <ul className="organization-list">
                    {archivedTerms.map((term) => (
                      <li key={term.id}>
                        <div className="organization-copy">
                          <strong>{term.name}</strong>
                          <small>
                            {term.start_local_date} → {term.end_local_date}
                          </small>
                        </div>
                        <button
                          disabled={organizationBusyId !== null}
                          onClick={() => void runTermArchiveCommand(term, "restore")}
                        >
                          復元
                        </button>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </section>

            <dialog
              ref={areaDialogRef}
              className="app-dialog organization-dialog"
              aria-labelledby="area-dialog-title"
              onClose={() => setEditingArea(null)}
              onCancel={(event) => {
                if (organizationBusyId !== null) event.preventDefault();
              }}
            >
              <header className="app-dialog-header">
                <div>
                  <p className="eyebrow">整理</p>
                  <h2 id="area-dialog-title">
                    {editingArea === "create" ? "領域を追加" : "領域を編集"}
                  </h2>
                </div>
                <button className="dialog-close" aria-label="閉じる" onClick={closeAreaEditor}>
                  ×
                </button>
              </header>
              <div className="app-dialog-body">
                <p className="dialog-description">
                  親向けの名前と、子供に見せる短いことばを分けて設定します。
                </p>
                {organizationDialogError === null ? null : (
                  <p className="error" role="alert">
                    {organizationDialogError}
                  </p>
                )}
                <form
                  key={editingArea === "create" ? "area:create" : editingArea?.id}
                  className="organization-form"
                  onSubmit={(event) => void saveArea(event)}
                >
                  <label>
                    親向けの名前
                    <input
                      name="name"
                      required
                      maxLength={120}
                      autoFocus
                      defaultValue={editingArea === "create" ? "" : editingArea?.name}
                      placeholder="学習"
                    />
                  </label>
                  <label>
                    子供に見せることば
                    <input
                      name="child_copy"
                      required
                      maxLength={120}
                      defaultValue={editingArea === "create" ? "" : editingArea?.child_copy}
                      placeholder="べんきょう"
                    />
                  </label>
                  <label>
                    アイコン
                    <input
                      name="icon"
                      maxLength={80}
                      defaultValue={editingArea === "create" ? "" : (editingArea?.icon ?? "")}
                      placeholder="📚"
                    />
                  </label>
                  <label>
                    色
                    <input
                      name="color"
                      type="color"
                      defaultValue={editingArea === "create" ? "#4f8f72" : editingArea?.color}
                    />
                  </label>
                  <div className="dialog-actions form-span">
                    <button
                      type="button"
                      disabled={organizationBusyId !== null}
                      onClick={closeAreaEditor}
                    >
                      キャンセル
                    </button>
                    <button className="primary" disabled={organizationBusyId !== null}>
                      {organizationBusyId === null ? "保存" : "保存しています…"}
                    </button>
                  </div>
                </form>
              </div>
            </dialog>

            <dialog
              ref={termDialogRef}
              className="app-dialog organization-dialog"
              aria-labelledby="term-dialog-title"
              onClose={() => setEditingTerm(null)}
              onCancel={(event) => {
                if (organizationBusyId !== null) event.preventDefault();
              }}
            >
              <header className="app-dialog-header">
                <div>
                  <p className="eyebrow">計画の区切り</p>
                  <h2 id="term-dialog-title">
                    {editingTerm === "create" ? "期間を追加" : "期間を編集"}
                  </h2>
                </div>
                <button className="dialog-close" aria-label="閉じる" onClick={closeTermEditor}>
                  ×
                </button>
              </header>
              <div className="app-dialog-body">
                <p className="dialog-description">学期や長期休みの開始日と終了日を登録します。</p>
                {organizationDialogError === null ? null : (
                  <p className="error" role="alert">
                    {organizationDialogError}
                  </p>
                )}
                <form
                  key={editingTerm === "create" ? "term:create" : editingTerm?.id}
                  className="organization-form"
                  onSubmit={(event) => void saveTerm(event)}
                >
                  <label className="form-span">
                    期間の名前
                    <input
                      name="name"
                      required
                      maxLength={120}
                      autoFocus
                      defaultValue={editingTerm === "create" ? "" : editingTerm?.name}
                      placeholder="1学期"
                    />
                  </label>
                  <label className="form-span">
                    年度などの補足
                    <input
                      name="school_year_label"
                      maxLength={80}
                      defaultValue={
                        editingTerm === "create" ? "" : (editingTerm?.school_year_label ?? "")
                      }
                      placeholder="2026年度"
                    />
                  </label>
                  <label>
                    開始日
                    <input
                      name="start_local_date"
                      type="date"
                      required
                      defaultValue={
                        editingTerm === "create"
                          ? workspaceLocalDate
                          : editingTerm?.start_local_date
                      }
                    />
                  </label>
                  <label>
                    終了日
                    <input
                      name="end_local_date"
                      type="date"
                      required
                      min={
                        editingTerm === "create"
                          ? workspaceLocalDate
                          : editingTerm?.start_local_date
                      }
                      defaultValue={
                        editingTerm === "create" ? workspaceLocalDate : editingTerm?.end_local_date
                      }
                    />
                  </label>
                  <div className="dialog-actions form-span">
                    <button
                      type="button"
                      disabled={organizationBusyId !== null}
                      onClick={closeTermEditor}
                    >
                      キャンセル
                    </button>
                    <button className="primary" disabled={organizationBusyId !== null}>
                      {organizationBusyId === null ? "保存" : "保存しています…"}
                    </button>
                  </div>
                </form>
              </div>
            </dialog>
          </div>
        ) : null}

        {activeView === "settings" && familyLoading ? (
          <section className="panel loading-panel" aria-busy="true">
            <p className="status" role="status">
              家族と端末を読み込んでいます…
            </p>
          </section>
        ) : null}

        {activeView === "settings" && !familyLoading ? (
          <div className="family-settings-grid">
            {workspaceSettings === null ? null : (
              <section className="panel settings-wide">
                <div className="section-heading">
                  <div>
                    <p className="eyebrow">家族の基本設定</p>
                    <h2>ワークスペース</h2>
                  </div>
                </div>
                <form
                  key={`${workspaceSettings.id}:${String(workspaceSettings.version)}`}
                  className="settings-form"
                  onSubmit={(event) => void updateWorkspace(event)}
                >
                  <label>
                    家族の名前
                    <input
                      name="name"
                      required
                      maxLength={120}
                      defaultValue={workspaceSettings.name}
                    />
                  </label>
                  <label>
                    タイムゾーン
                    <input
                      name="timezone"
                      required
                      maxLength={80}
                      defaultValue={workspaceSettings.timezone}
                      spellCheck={false}
                    />
                  </label>
                  <label>
                    週の開始
                    <select name="week_starts_on" defaultValue={workspaceSettings.week_starts_on}>
                      <option value={1}>月曜日</option>
                      <option value={0}>日曜日</option>
                    </select>
                  </label>
                  <label>
                    計画サイクル（日）
                    <input
                      name="default_cycle_days"
                      type="number"
                      min={1}
                      max={31}
                      defaultValue={workspaceSettings.default_cycle_days}
                    />
                  </label>
                  <label>
                    言語・地域
                    <select name="locale" defaultValue={workspaceSettings.locale}>
                      <option value="ja-JP">日本語（日本）</option>
                      <option value="en-US">English (United States)</option>
                    </select>
                  </label>
                  <fieldset className="quiet-hours-fieldset">
                    <legend>通知しない時間</legend>
                    <label className="checkbox-row">
                      <input
                        name="quiet_hours_enabled"
                        type="checkbox"
                        defaultChecked={workspaceSettings.quiet_hours.enabled}
                      />
                      有効にする
                    </label>
                    <label>
                      開始
                      <input
                        name="quiet_hours_start"
                        type="time"
                        defaultValue={workspaceSettings.quiet_hours.start}
                      />
                    </label>
                    <label>
                      終了
                      <input
                        name="quiet_hours_end"
                        type="time"
                        defaultValue={workspaceSettings.quiet_hours.end}
                      />
                    </label>
                  </fieldset>
                  <div className="settings-form-actions form-span">
                    <button className="primary" disabled={familyMutationBusy !== null}>
                      {familyMutationBusy === "workspace" ? "保存しています…" : "設定を保存"}
                    </button>
                  </div>
                </form>
              </section>
            )}

            <section className="panel settings-wide">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">プロフィール</p>
                  <h2>子供</h2>
                </div>
                <div className="section-heading-actions">
                  <span className="count-badge">{activeChildren.length}</span>
                  <button className="primary" onClick={openChildCreator}>
                    子供を追加
                  </button>
                </div>
              </div>
              {activeChildren.length === 0 ? (
                <div className="family-empty-state">
                  <div>
                    <h3>子供プロフィールはまだありません</h3>
                    <p>表示名や読みやすさを設定すると、今日のタスクを計画できます。</p>
                  </div>
                  <button className="primary" onClick={openChildCreator}>
                    最初の子供を追加
                  </button>
                </div>
              ) : (
                <ul className="resource-list">
                  {activeChildren.map((child) => (
                    <li key={child.id}>
                      <span
                        className="avatar"
                        style={{ background: child.theme_color }}
                        aria-hidden="true"
                      />
                      <div className="resource-copy">
                        <strong>{child.display_name}</strong>
                        <small>
                          {child.reading_level === "emergent"
                            ? "はじめて"
                            : child.reading_level === "beginner"
                              ? "やさしい"
                              : "じぶんで読める"}
                          {child.display_density === "compact" ? " · コンパクト" : " · ゆったり"}
                        </small>
                      </div>
                      <div className="row-actions">
                        <a
                          className="button-link"
                          href={childPreviewHref(child.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          子供画面 <span aria-hidden="true">↗</span>
                        </a>
                        <button
                          onClick={() => {
                            selectChild(child.id);
                            navigateParent("today");
                          }}
                        >
                          今日を見る
                        </button>
                        <button
                          disabled={enrollmentBusyChildId !== null}
                          onClick={() => openEnrollmentCreator(child)}
                        >
                          端末を登録
                        </button>
                        <button onClick={() => setEditingChild(child)}>編集</button>
                        <button
                          className="danger"
                          disabled={familyMutationBusy !== null}
                          onClick={() => void setChildArchived(child, true)}
                        >
                          アーカイブ
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {archivedChildren.length === 0 ? null : (
                <details className="archived-resources">
                  <summary>アーカイブ済み（{archivedChildren.length}）</summary>
                  <ul className="resource-list">
                    {archivedChildren.map((child) => (
                      <li key={child.id}>
                        <span
                          className="avatar"
                          style={{ background: child.theme_color }}
                          aria-hidden="true"
                        />
                        <div className="resource-copy">
                          <strong>{child.display_name}</strong>
                          <small>タスクと記録は保持されています</small>
                        </div>
                        <button
                          disabled={familyMutationBusy !== null}
                          onClick={() => void setChildArchived(child, false)}
                        >
                          元に戻す
                        </button>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </section>

            {isGuardian ? (
              <DailySummarySettings
                key={workspaceId}
                workspaceId={workspaceId}
                pushControl={<WebPushControl session={session} />}
              />
            ) : null}

            <section className="panel settings-wide notification-preference-panel">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">声かけの設計</p>
                  <h2>通知</h2>
                </div>
                <div className="section-heading-actions">
                  <span className="count-badge">{notificationPreferences.length}</span>
                  <button className="primary" onClick={() => openNotificationPreference()}>
                    通知ルールを追加
                  </button>
                </div>
              </div>
              <p className="panel-intro">
                親・子供・ルーティンごとに、通知する曜日と時間帯を設定できます。親向けの予定見直しは、複数件を指定時刻のダイジェストにまとめられます。
              </p>
              {notificationPreferences.length === 0 ? (
                <div className="family-empty-state">
                  <div>
                    <h3>個別の通知ルールはありません</h3>
                    <p>現在はルーティンの設定と、上の「通知しない時間」に従って即時通知します。</p>
                  </div>
                  <button className="primary" onClick={() => openNotificationPreference()}>
                    最初の通知ルールを作る
                  </button>
                </div>
              ) : (
                <ul className="resource-list notification-preference-list">
                  {notificationPreferences.map((preference) => {
                    const recipientChild = children.find(
                      (child) => child.id === preference.child_id,
                    );
                    const relatedChild = children.find(
                      (child) => child.id === preference.related_child_id,
                    );
                    const routine = routines.find((item) => item.id === preference.routine_id);
                    return (
                      <li key={preference.id}>
                        <div className="resource-copy">
                          <strong>
                            {preference.recipient_type === "child"
                              ? `${recipientChild?.display_name ?? "子供"}へのやさしい声かけ`
                              : relatedChild === undefined
                                ? "親への通知"
                                : `${relatedChild.display_name}について親へ通知`}
                          </strong>
                          <small>
                            {preference.channel === "apns"
                              ? "iPhone・iPad通知"
                              : preference.channel === "web_push"
                                ? "Web Push"
                                : "アプリ内"}
                            {preference.batching_mode === "digest"
                              ? ` · ${preference.digest_local_time ?? "--:--"}にまとめる`
                              : " · その都度"}
                            {preference.enabled ? " · 有効" : " · 停止中"}
                          </small>
                          <small>
                            {preference.event_types.map(notificationEventLabel).join("、")}
                          </small>
                          <small>
                            {routine === undefined ? "すべてのルーティン" : routine.name}
                            {` · 週${String(preference.active_weekdays.length)}日`}
                            {preference.active_from_local_time === null
                              ? " · 終日"
                              : ` · ${preference.active_from_local_time}〜${preference.active_until_local_time ?? ""}`}
                          </small>
                        </div>
                        <button onClick={() => openNotificationPreference(preference)}>編集</button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <section className="panel settings-wide">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">エージェント連携</p>
                  <h2>Remote MCP</h2>
                </div>
                <span className="count-badge">
                  {
                    agentGrants.filter(
                      (grant) =>
                        grant.revoked_at === null && Date.parse(grant.expires_at) > familyViewNow,
                    ).length
                  }
                </span>
              </div>
              {agentGrants.length === 0 ? (
                <p className="empty-state">
                  許可済みのRemote
                  MCP連携はありません。MCPクライアントから接続を始めると、ここで権限を確認して許可できます。
                </p>
              ) : (
                <ul className="resource-list">
                  {agentGrants.map((grant) => {
                    const active =
                      grant.revoked_at === null && Date.parse(grant.expires_at) > familyViewNow;
                    return (
                      <li key={grant.id}>
                        <div className="resource-copy">
                          <strong>{grant.client_name}</strong>
                          <small>
                            {active
                              ? "利用中"
                              : grant.revoked_at === null
                                ? "期限切れ"
                                : "解除済み"}
                            {` · ${grant.scopes.map((scope) => mcpScopeLabels[scope].title).join("、")}`}
                          </small>
                          <small>
                            {grant.child_scope === "all"
                              ? "対象 家族全体"
                              : `対象 ${String(grant.child_ids.length)}人`}{" "}
                            · 有効期限
                            {new Date(grant.expires_at).toLocaleString("ja-JP")}
                          </small>
                        </div>
                        {active ? (
                          <button
                            className="danger"
                            disabled={familyMutationBusy !== null}
                            onClick={() => void revokeAgentGrant(grant)}
                          >
                            {familyMutationBusy === `grant:${grant.id}` ? "解除中…" : "連携を解除"}
                          </button>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              )}
              <p className="panel-footnote">
                Agentはここで許可したWorkspace・子供・操作だけをCore
                API経由で利用します。端末管理や削除はRemote MCPへ公開しません。
              </p>
            </section>

            <section className="panel settings-wide">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">家族のアクセス</p>
                  <h2>保護者とサポーター</h2>
                </div>
                <div className="section-heading-actions">
                  <span className="count-badge">{memberships.length}</span>
                  <button className="primary" onClick={openMembershipInvitation}>
                    招待する
                  </button>
                </div>
              </div>
              <ul className="resource-list">
                {memberships.map((membership) => {
                  const isCurrent = membership.account_id === session.actor.id;
                  const canManage =
                    !isCurrent &&
                    membership.role !== "owner" &&
                    (session.actor.membership_role === "owner" ||
                      (session.actor.membership_role === "guardian" &&
                        membership.role === "supporter"));
                  return (
                    <li key={membership.id}>
                      <div className="resource-copy">
                        <strong>
                          {membership.display_name}
                          {isCurrent ? "（あなた）" : ""}
                        </strong>
                        <small>
                          {membership.role === "owner"
                            ? "オーナー"
                            : membership.role === "guardian"
                              ? "保護者"
                              : "サポーター"}
                          {membership.status === "active"
                            ? " · 利用中"
                            : membership.status === "suspended"
                              ? " · 一時停止"
                              : ` · ${membership.status}`}
                        </small>
                      </div>
                      <div className="row-actions">
                        {membership.scopes.length === 0 ? (
                          <span className="scope-chip">家族全体</span>
                        ) : (
                          <span className="scope-chip">権限 {membership.scopes.length}件</span>
                        )}
                        {canManage ? (
                          <button onClick={() => openMembershipEditor(membership)}>管理</button>
                        ) : null}
                        {isCurrent && membership.role !== "owner" ? (
                          <button
                            className="danger"
                            onClick={() => {
                              setMembershipEditError(null);
                              setLeaveWorkspaceOpen(true);
                            }}
                          >
                            退出
                          </button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
              {membershipInvitations.length === 0 ? null : (
                <div className="settings-subsection">
                  <h3>招待リンク</h3>
                  <ul className="resource-list">
                    {membershipInvitations.map((invitation) => (
                      <li key={invitation.id}>
                        <div className="resource-copy">
                          <strong>
                            {invitation.role === "guardian" ? "保護者" : "サポーター"}への招待
                          </strong>
                          <small>
                            {invitation.status === "active"
                              ? `有効期限 ${new Date(invitation.expires_at).toLocaleString("ja-JP")}`
                              : invitation.status === "accepted"
                                ? "参加済み"
                                : invitation.status === "revoked"
                                  ? "取り消し済み"
                                  : "期限切れ"}
                          </small>
                          {invitation.scopes.length === 0 ? null : (
                            <small>対象別の操作権限 {invitation.scopes.length}件</small>
                          )}
                        </div>
                        {invitation.status === "active" ? (
                          <button
                            className="danger"
                            disabled={familyMutationBusy !== null}
                            onClick={() => void revokeMembershipInvitationFromSettings(invitation)}
                          >
                            {familyMutationBusy === `membership-invite:${invitation.id}`
                              ? "取消中…"
                              : "取り消す"}
                          </button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="panel-footnote">
                招待リンクは一度だけ利用できます。サポーターには、選んだ子供・Area・操作だけが許可されます。
              </p>
            </section>

            <section className="panel settings-wide">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">ログインの安全</p>
                  <h2>パスキー</h2>
                </div>
                <span className="count-badge">{passkeys.length}</span>
              </div>
              <ul className="resource-list">
                {passkeys.map((credential) => (
                  <li key={credential.id}>
                    <div className="resource-copy">
                      <strong>{credential.device_label}</strong>
                      <small>
                        {credential.last_used_at === null
                          ? "まだ使用されていません"
                          : `最終使用 ${new Date(credential.last_used_at).toLocaleString("ja-JP")}`}
                        {credential.backed_up ? " · 同期済み" : ""}
                      </small>
                    </div>
                    <div className="row-actions">
                      <button onClick={() => setEditingPasskey(credential)}>名前を変更</button>
                      <button
                        className="danger"
                        disabled={familyMutationBusy !== null}
                        onClick={() => void revokePasskey(credential)}
                      >
                        削除
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>

            <section className="panel settings-wide">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">アクセス管理</p>
                  <h2>登録済み端末</h2>
                </div>
                <span className="count-badge">{devices.length}</span>
              </div>
              {devices.length === 0 ? (
                <p className="empty-state">登録済み端末はありません。</p>
              ) : (
                <ul className="resource-list device-list">
                  {devices.map((device) => (
                    <li key={device.id}>
                      <div className="resource-copy">
                        <strong>{device.label}</strong>
                        <small>
                          {device.actor_type === "child" ? "子供" : "保護者"}
                          {device.revoked_at === null ? " · 有効" : " · 失効済み"}
                        </small>
                      </div>
                      <div className="row-actions">
                        <button onClick={() => setEditingDevice(device)}>名前を変更</button>
                        {device.revoked_at === null && device.id !== session.actor.device_id ? (
                          <button
                            className="danger"
                            disabled={revokingDeviceId !== null}
                            onClick={() => void revokeDevice(device.id)}
                          >
                            {revokingDeviceId === device.id ? "失効中…" : "失効"}
                          </button>
                        ) : null}
                        {device.id === session.actor.device_id ? (
                          <span className="scope-chip">現在の端末</span>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="panel settings-wide export-panel" aria-labelledby="export-title">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">データ管理</p>
                  <h2 id="export-title">データの書き出し</h2>
                </div>
                <div className="section-heading-actions">
                  <button
                    disabled={selectedChild === null || exportBusy !== null}
                    onClick={() => openWorkspaceExport("child")}
                  >
                    {selectedChild === null
                      ? "子供を選択してください"
                      : selectedChild.display_name + "の記録"}
                  </button>
                  <button
                    className="primary"
                    disabled={exportBusy !== null}
                    onClick={() => openWorkspaceExport("workspace")}
                  >
                    家族全体を書き出す
                  </button>
                </div>
              </div>
              <p className="panel-intro">
                JSON・CSVと、選択した場合は非公開の写真を、1つのZIPにまとめます。ファイルはprivate
                R2に24時間だけ保存され、期限後は自動削除されます。
              </p>
              {workspaceExports.length === 0 ? (
                <div className="family-empty-state">
                  <div>
                    <h3>書き出しファイルはありません</h3>
                    <p>家族全体、または選択中の子供のポータブル記録を作成できます。</p>
                  </div>
                </div>
              ) : (
                <ul className="resource-list export-list">
                  {workspaceExports.map((item) => {
                    const child = children.find((candidate) => candidate.id === item.child_id);
                    const errorMessage = workspaceExportErrorLabel(item.error_code);
                    return (
                      <li key={item.id}>
                        <div className="resource-copy">
                          <strong>
                            {item.scope === "workspace"
                              ? "家族全体の記録"
                              : (child?.display_name ?? "子供") + "の記録"}
                          </strong>
                          <small>
                            {workspaceExportStatusLabel(item.status)} {" · "}
                            {new Date(item.requested_at).toLocaleString("ja-JP")}
                          </small>
                          {item.status === "completed" ? (
                            <small>
                              {item.archive_bytes === null
                                ? ""
                                : formatByteSize(item.archive_bytes)}
                              {" · データ "}
                              {Object.values(item.table_counts).reduce(
                                (sum, count) => sum + count,
                                0,
                              )}
                              件
                              {item.media_entry_count > 0
                                ? " · 写真 " + String(item.media_entry_count) + "件"
                                : ""}
                              {" · "}
                              {new Date(item.expires_at).toLocaleString("ja-JP")}まで
                            </small>
                          ) : null}
                          {errorMessage === null ? null : (
                            <small className="error" role="alert">
                              {errorMessage}
                            </small>
                          )}
                        </div>
                        <div className="row-actions">
                          {["queued", "running", "deleting"].includes(item.status) ? (
                            <span className="scope-chip" role="status">
                              {workspaceExportStatusLabel(item.status)}…
                            </span>
                          ) : null}
                          {item.status === "completed" ? (
                            <button
                              className="primary"
                              disabled={exportBusy !== null}
                              onClick={() => void downloadWorkspaceExport(item)}
                            >
                              {exportBusy === item.id ? "準備中…" : "ダウンロード"}
                            </button>
                          ) : null}
                          {item.status === "completed" || item.status === "failed" ? (
                            <button
                              className="danger"
                              disabled={exportBusy !== null}
                              onClick={() => {
                                setExportError(null);
                                setExportDeleteTarget(item);
                              }}
                            >
                              今すぐ削除
                            </button>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              <p className="panel-footnote">
                パスキー、セッション、回復コード、登録リンク、OAuthトークン、通知端末キーなどの認証情報は含めません。作成・ダウンロード・削除には直近の親認証が必要です。
              </p>
            </section>

            {location.hostname.includes("oyako-flow-web-staging") ? (
              <section className="panel settings-wide" aria-labelledby="staging-proof-title">
                <p className="eyebrow">開発環境のみ</p>
                <h2 id="staging-proof-title">staging結合診断</h2>
                <p className="empty-state">
                  認証済みの実データ経路で、Cloudflareの各bindingを往復確認します。
                </p>
                <button
                  className="secondary"
                  disabled={stagingProofBusy}
                  onClick={() => void runStagingProof()}
                >
                  {stagingProofBusy ? "診断しています…" : "実bindingを診断"}
                </button>
                {stagingProof === null ? null : (
                  <pre className="proof-output" role="status">
                    {stagingProof}
                  </pre>
                )}
              </section>
            ) : null}
          </div>
        ) : null}

        <dialog
          ref={exportDialogRef}
          className="app-dialog compact-dialog export-dialog"
          aria-labelledby="export-dialog-title"
          onClose={() => setExportDialogOpen(false)}
          onCancel={(event) => {
            if (exportBusy === "create") event.preventDefault();
          }}
        >
          <header className="app-dialog-header">
            <div>
              <p className="eyebrow">データ管理</p>
              <h2 id="export-dialog-title">書き出す内容を確認</h2>
            </div>
            <button
              type="button"
              className="dialog-close"
              aria-label="閉じる"
              disabled={exportBusy === "create"}
              onClick={closeWorkspaceExport}
            >
              ×
            </button>
          </header>
          <div className="app-dialog-body">
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void createWorkspaceExport();
              }}
            >
              <fieldset>
                <legend>対象</legend>
                <label className="checkbox-row">
                  <input
                    type="radio"
                    name="export_scope"
                    value="workspace"
                    checked={exportScope === "workspace"}
                    onChange={() => setExportScope("workspace")}
                  />
                  <span>
                    <strong>家族全体</strong>
                    <small>家族設定、計画、タスク、ルーティン、記録、履歴を含みます。</small>
                  </span>
                </label>
                <label className="checkbox-row">
                  <input
                    type="radio"
                    name="export_scope"
                    value="child"
                    checked={exportScope === "child"}
                    disabled={selectedChild === null}
                    onChange={() => setExportScope("child")}
                  />
                  <span>
                    <strong>
                      {selectedChild === null
                        ? "選択中の子供はいません"
                        : selectedChild.display_name + "のポータブル記録"}
                    </strong>
                    <small>この子供に関係する計画、タスク、記録だけを書き出します。</small>
                  </span>
                </label>
              </fieldset>
              <label className="checkbox-row export-media-choice">
                <input
                  type="checkbox"
                  checked={exportIncludeMedia}
                  onChange={(event) => setExportIncludeMedia(event.currentTarget.checked)}
                />
                <span>
                  <strong>保存済みの写真も含める</strong>
                  <small>写真はメタデータ除去済みのprivate R2オブジェクトから読み込みます。</small>
                </span>
              </label>
              <p className="security-note">
                認証情報や秘密鍵は含まれません。完成したZIPは24時間後に自動削除されます。作成中にデータが変わった場合は、安全のため失敗として終了します。
              </p>
              {exportError === null ? null : (
                <p className="error" role="alert">
                  {exportError}
                </p>
              )}
              <div className="dialog-actions">
                <button
                  type="button"
                  disabled={exportBusy === "create"}
                  onClick={closeWorkspaceExport}
                >
                  キャンセル
                </button>
                <button className="primary" disabled={exportBusy !== null}>
                  {exportBusy === "create" ? "書き出しを開始しています…" : "この内容で書き出す"}
                </button>
              </div>
            </form>
          </div>
        </dialog>

        <dialog
          ref={exportDeleteDialogRef}
          className="app-dialog compact-dialog export-dialog"
          aria-labelledby="export-delete-title"
          onClose={() => setExportDeleteTarget(null)}
          onCancel={(event) => {
            if (exportBusy !== null) event.preventDefault();
          }}
        >
          <header className="app-dialog-header">
            <div>
              <p className="eyebrow">データ管理</p>
              <h2 id="export-delete-title">書き出しファイルを削除</h2>
            </div>
            <button
              type="button"
              className="dialog-close"
              aria-label="閉じる"
              disabled={exportBusy !== null}
              onClick={() => setExportDeleteTarget(null)}
            >
              ×
            </button>
          </header>
          <div className="app-dialog-body">
            <p className="dialog-description">
              private
              R2に保存されたこのZIPを、24時間の期限を待たずに削除します。元の家族データや写真は削除されません。
            </p>
            {exportDeleteTarget === null ? null : (
              <dl className="compact-details">
                <div>
                  <dt>対象</dt>
                  <dd>
                    {exportDeleteTarget.scope === "workspace"
                      ? "家族全体"
                      : (children.find((child) => child.id === exportDeleteTarget.child_id)
                          ?.display_name ?? "子供")}
                  </dd>
                </div>
                <div>
                  <dt>作成日時</dt>
                  <dd>{new Date(exportDeleteTarget.requested_at).toLocaleString("ja-JP")}</dd>
                </div>
              </dl>
            )}
            {exportError === null ? null : (
              <p className="error" role="alert">
                {exportError}
              </p>
            )}
            <div className="dialog-actions">
              <button disabled={exportBusy !== null} onClick={() => setExportDeleteTarget(null)}>
                残しておく
              </button>
              <button
                className="danger"
                disabled={exportBusy !== null}
                onClick={() => void deleteWorkspaceExport()}
              >
                {exportBusy === exportDeleteTarget?.id ? "削除を開始しています…" : "ファイルを削除"}
              </button>
            </div>
          </div>
        </dialog>

        <dialog
          ref={membershipInviteDialogRef}
          className="app-dialog compact-dialog membership-invite-dialog"
          aria-labelledby="membership-invite-title"
          onClose={() => setMembershipInviteOpen(false)}
          onCancel={(event) => {
            if (familyMutationBusy === "membership-invite") event.preventDefault();
          }}
        >
          <header className="app-dialog-header">
            <div>
              <p className="eyebrow">家族のアクセス</p>
              <h2 id="membership-invite-title">
                {membershipInviteUrl === null ? "家族を招待" : "招待リンクができました"}
              </h2>
            </div>
            <button
              type="button"
              className="dialog-close"
              aria-label="閉じる"
              disabled={familyMutationBusy === "membership-invite"}
              onClick={closeMembershipInvitation}
            >
              ×
            </button>
          </header>
          <div className="app-dialog-body">
            {membershipInviteUrl === null ? (
              <form onSubmit={(event) => void createMembershipInvitationFromForm(event)}>
                <label>
                  参加する役割
                  <select
                    value={membershipInviteRole}
                    onChange={(event) => {
                      const role = event.currentTarget.value as "guardian" | "supporter";
                      setMembershipInviteRole(role);
                      setMembershipInviteError(null);
                    }}
                  >
                    <option value="guardian">保護者 — 家族の計画と確認</option>
                    <option value="supporter">サポーター — 選んだ範囲だけ</option>
                  </select>
                </label>
                {membershipInviteRole === "supporter" ? (
                  <>
                    <fieldset>
                      <legend>対象の子供</legend>
                      <div className="consent-child-grid">
                        {activeChildren.map((child) => (
                          <label key={child.id} className="consent-child">
                            <input
                              type="checkbox"
                              checked={membershipInviteChildIds.includes(child.id)}
                              onChange={(event) =>
                                setMembershipInviteChildIds((current) =>
                                  event.currentTarget.checked
                                    ? [...new Set([...current, child.id])]
                                    : current.filter((id) => id !== child.id),
                                )
                              }
                            />
                            <span
                              className="avatar"
                              style={{ background: child.theme_color }}
                              aria-hidden="true"
                            />
                            <strong>{child.display_name}</strong>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    <label>
                      Areaでさらに限定（任意）
                      <select
                        value={membershipInviteAreaId}
                        onChange={(event) => setMembershipInviteAreaId(event.currentTarget.value)}
                      >
                        <option value="">すべてのArea</option>
                        {areas
                          .filter((area) => area.archived_at === null)
                          .map((area) => (
                            <option key={area.id} value={area.id}>
                              {area.name}
                            </option>
                          ))}
                      </select>
                    </label>
                    <fieldset>
                      <legend>許可する操作</legend>
                      <div className="invitation-capability-grid">
                        {(Object.keys(supporterCapabilityCopy) as SupporterCapability[]).map(
                          (capability) => (
                            <label key={capability} className="checkbox-row">
                              <input
                                type="checkbox"
                                checked={membershipInviteCapabilities.includes(capability)}
                                onChange={(event) =>
                                  setMembershipInviteCapabilities((current) =>
                                    event.currentTarget.checked
                                      ? [...new Set([...current, capability])]
                                      : current.filter((item) => item !== capability),
                                  )
                                }
                              />
                              {supporterCapabilityCopy[capability]}
                            </label>
                          ),
                        )}
                      </div>
                    </fieldset>
                  </>
                ) : (
                  <p className="security-note">
                    保護者は、子供・計画・証跡・集計を家族全体で扱えます。メンバー変更などの重要操作には制限があります。
                  </p>
                )}
                <label>
                  リンクの有効期間
                  <select name="expires_in_hours" defaultValue="72">
                    <option value="24">24時間</option>
                    <option value="72">3日</option>
                    <option value="168">7日</option>
                  </select>
                </label>
                {membershipInviteError === null ? null : (
                  <p className="error" role="alert">
                    {membershipInviteError}
                  </p>
                )}
                <div className="dialog-actions">
                  <button type="button" onClick={closeMembershipInvitation}>
                    キャンセル
                  </button>
                  <button className="primary" disabled={familyMutationBusy !== null}>
                    {familyMutationBusy === "membership-invite"
                      ? "作成しています…"
                      : "一度限りのリンクを作る"}
                  </button>
                </div>
              </form>
            ) : (
              <div className="invitation-result">
                <p className="dialog-description">
                  このリンクを参加する人へ安全な方法で渡してください。リンクは参加後に無効になります。
                </p>
                <label>
                  招待リンク
                  <input
                    value={membershipInviteUrl}
                    readOnly
                    autoFocus
                    onFocus={(event) => event.currentTarget.select()}
                  />
                </label>
                {membershipInviteError === null ? null : (
                  <p className="error" role="alert">
                    {membershipInviteError}
                  </p>
                )}
                <div className="dialog-actions">
                  <button onClick={closeMembershipInvitation}>閉じる</button>
                  <button className="primary" onClick={() => void copyMembershipInvitation()}>
                    リンクをコピー
                  </button>
                </div>
              </div>
            )}
          </div>
        </dialog>

        <dialog
          ref={membershipEditDialogRef}
          className="app-dialog compact-dialog membership-invite-dialog"
          aria-labelledby="membership-edit-title"
          onClose={() => setEditingMembership(null)}
          onCancel={(event) => {
            if (familyMutationBusy?.startsWith("membership:") === true) event.preventDefault();
          }}
        >
          <header className="app-dialog-header">
            <div>
              <p className="eyebrow">家族のアクセス</p>
              <h2 id="membership-edit-title">
                {editingMembership === null
                  ? "アクセスを管理"
                  : `${editingMembership.display_name}さんのアクセス`}
              </h2>
            </div>
            <button
              type="button"
              className="dialog-close"
              aria-label="閉じる"
              disabled={familyMutationBusy?.startsWith("membership:") === true}
              onClick={closeMembershipEditor}
            >
              ×
            </button>
          </header>
          {editingMembership === null ? null : (
            <div className="app-dialog-body">
              <form onSubmit={(event) => void saveMembership(event)}>
                <div className="dialog-field-grid">
                  <label>
                    役割
                    <select
                      value={membershipEditRole}
                      disabled={session.actor.membership_role !== "owner"}
                      onChange={(event) => {
                        setMembershipEditRole(
                          event.currentTarget.value as "guardian" | "supporter",
                        );
                        setMembershipEditError(null);
                      }}
                    >
                      <option value="guardian">保護者 — 家族全体</option>
                      <option value="supporter">サポーター — 指定範囲だけ</option>
                    </select>
                  </label>
                  <label>
                    利用状態
                    <select
                      value={membershipEditStatus}
                      onChange={(event) =>
                        setMembershipEditStatus(event.currentTarget.value as "active" | "suspended")
                      }
                    >
                      <option value="active">利用中</option>
                      <option value="suspended">一時停止</option>
                    </select>
                  </label>
                </div>

                {membershipEditRole === "supporter" ? (
                  <fieldset>
                    <legend>許可する範囲</legend>
                    <p className="dialog-description">
                      1行ごとに、対象の子供・Area・操作を指定します。子供とAreaの両方を選ぶと、その組み合わせだけに限定されます。
                    </p>
                    <div className="membership-scope-editor">
                      {membershipScopeDrafts.map((scope, index) => (
                        <div
                          className="membership-scope-row"
                          key={`${scope.child_id ?? "any"}:${scope.area_id ?? "any"}:${scope.capability}:${String(index)}`}
                        >
                          <label>
                            子供
                            <select
                              value={scope.child_id ?? ""}
                              onChange={(event) =>
                                setMembershipScopeDrafts((current) =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? { ...item, child_id: event.currentTarget.value || null }
                                      : item,
                                  ),
                                )
                              }
                            >
                              <option value="">限定しない</option>
                              {activeChildren.map((child) => (
                                <option key={child.id} value={child.id}>
                                  {child.display_name}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            Area
                            <select
                              value={scope.area_id ?? ""}
                              onChange={(event) =>
                                setMembershipScopeDrafts((current) =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? { ...item, area_id: event.currentTarget.value || null }
                                      : item,
                                  ),
                                )
                              }
                            >
                              <option value="">限定しない</option>
                              {areas
                                .filter((area) => area.archived_at === null)
                                .map((area) => (
                                  <option key={area.id} value={area.id}>
                                    {area.name}
                                  </option>
                                ))}
                            </select>
                          </label>
                          <label>
                            操作
                            <select
                              value={scope.capability}
                              onChange={(event) =>
                                setMembershipScopeDrafts((current) =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? {
                                          ...item,
                                          capability: event.currentTarget
                                            .value as SupporterCapability,
                                        }
                                      : item,
                                  ),
                                )
                              }
                            >
                              {(Object.keys(supporterCapabilityCopy) as SupporterCapability[]).map(
                                (capability) => (
                                  <option key={capability} value={capability}>
                                    {supporterCapabilityCopy[capability]}
                                  </option>
                                ),
                              )}
                            </select>
                          </label>
                          <button
                            type="button"
                            className="danger text-action"
                            onClick={() =>
                              setMembershipScopeDrafts((current) =>
                                current.filter((_, itemIndex) => itemIndex !== index),
                              )
                            }
                          >
                            削除
                          </button>
                        </div>
                      ))}
                    </div>
                    <button type="button" onClick={addMembershipScopeDraft}>
                      ＋ 許可範囲を追加
                    </button>
                  </fieldset>
                ) : (
                  <p className="security-note">
                    保護者は家族全体の計画・確認を行えます。オーナー移譲とWorkspace削除はできません。
                  </p>
                )}

                {membershipEditError === null ? null : (
                  <p className="error" role="alert">
                    {membershipEditError}
                  </p>
                )}
                <div className="dialog-actions">
                  <button type="button" onClick={closeMembershipEditor}>
                    キャンセル
                  </button>
                  <button className="primary" disabled={familyMutationBusy !== null}>
                    {familyMutationBusy === `membership:${editingMembership.id}`
                      ? "保存しています…"
                      : "アクセスを保存"}
                  </button>
                </div>
              </form>

              {session.actor.membership_role === "owner" &&
              editingMembership.role === "guardian" &&
              editingMembership.status === "active" ? (
                <section className="dialog-danger-zone" aria-labelledby="ownership-transfer-title">
                  <h3 id="ownership-transfer-title">オーナーを移譲</h3>
                  <p>
                    移譲後、あなたは保護者になります。Workspaceには常に1人以上のオーナーが残ります。
                  </p>
                  <button
                    type="button"
                    className="danger"
                    disabled={familyMutationBusy !== null}
                    onClick={() => void transferOwnershipToEditedMembership()}
                  >
                    {familyMutationBusy === `membership:transfer:${editingMembership.id}`
                      ? "移譲しています…"
                      : `${editingMembership.display_name}さんをオーナーにする`}
                  </button>
                </section>
              ) : null}
            </div>
          )}
        </dialog>

        <dialog
          ref={leaveWorkspaceDialogRef}
          className="app-dialog compact-dialog"
          aria-labelledby="leave-workspace-title"
          onClose={() => setLeaveWorkspaceOpen(false)}
          onCancel={(event) => {
            if (familyMutationBusy === "membership:leave") event.preventDefault();
          }}
        >
          <header className="app-dialog-header">
            <div>
              <p className="eyebrow">家族のアクセス</p>
              <h2 id="leave-workspace-title">このWorkspaceから退出</h2>
            </div>
            <button
              type="button"
              className="dialog-close"
              aria-label="閉じる"
              disabled={familyMutationBusy === "membership:leave"}
              onClick={() => setLeaveWorkspaceOpen(false)}
            >
              ×
            </button>
          </header>
          <div className="app-dialog-body">
            <p>
              退出すると、このWorkspaceのあなたの端末とセッションはすべて失効し、家族のデータを開けなくなります。
            </p>
            {membershipEditError === null ? null : (
              <p className="error" role="alert">
                {membershipEditError}
              </p>
            )}
            <div className="dialog-actions">
              <button type="button" onClick={() => setLeaveWorkspaceOpen(false)}>
                戻る
              </button>
              <button
                type="button"
                className="danger"
                disabled={familyMutationBusy !== null}
                onClick={() => void leaveWorkspace()}
              >
                {familyMutationBusy === "membership:leave" ? "退出しています…" : "退出する"}
              </button>
            </div>
          </div>
        </dialog>

        <dialog
          ref={childEditDialogRef}
          className="app-dialog compact-dialog"
          aria-labelledby="child-edit-title"
          onClose={() => setEditingChild(null)}
          onCancel={(event) => {
            if (familyMutationBusy !== null) event.preventDefault();
          }}
        >
          <header className="app-dialog-header">
            <div>
              <p className="eyebrow">プロフィール</p>
              <h2 id="child-edit-title">子供の設定を編集</h2>
            </div>
            <button
              type="button"
              className="dialog-close"
              aria-label="閉じる"
              disabled={familyMutationBusy !== null}
              onClick={() => setEditingChild(null)}
            >
              ×
            </button>
          </header>
          <div className="app-dialog-body">
            {editingChild === null ? null : (
              <form
                key={`${editingChild.id}:${String(editingChild.version)}`}
                className="child-create-form"
                onSubmit={(event) => void updateChild(event, editingChild)}
              >
                <label className="form-span">
                  表示名
                  <input
                    name="display_name"
                    required
                    maxLength={80}
                    defaultValue={editingChild.display_name}
                    autoFocus
                  />
                </label>
                <label>
                  読みやすさ
                  <select name="reading_level" defaultValue={editingChild.reading_level}>
                    <option value="emergent">はじめて</option>
                    <option value="beginner">やさしい</option>
                    <option value="independent">じぶんで読める</option>
                  </select>
                </label>
                <label>
                  画面の密度
                  <select name="display_density" defaultValue={editingChild.display_density}>
                    <option value="comfortable">ゆったり</option>
                    <option value="compact">コンパクト</option>
                  </select>
                </label>
                <label className="form-span child-color-field">
                  テーマ色
                  <input name="theme_color" type="color" defaultValue={editingChild.theme_color} />
                </label>
                <div className="dialog-actions form-span">
                  <button
                    type="button"
                    disabled={familyMutationBusy !== null}
                    onClick={() => setEditingChild(null)}
                  >
                    キャンセル
                  </button>
                  <button className="primary" disabled={familyMutationBusy !== null}>
                    {familyMutationBusy === `child:${editingChild.id}`
                      ? "保存しています…"
                      : "変更を保存"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </dialog>

        <dialog
          ref={deviceEditDialogRef}
          className="app-dialog compact-dialog"
          aria-labelledby="device-edit-title"
          onClose={() => setEditingDevice(null)}
          onCancel={(event) => {
            if (familyMutationBusy !== null) event.preventDefault();
          }}
        >
          <header className="app-dialog-header">
            <div>
              <p className="eyebrow">端末管理</p>
              <h2 id="device-edit-title">端末名を変更</h2>
            </div>
            <button
              type="button"
              className="dialog-close"
              aria-label="閉じる"
              disabled={familyMutationBusy !== null}
              onClick={() => setEditingDevice(null)}
            >
              ×
            </button>
          </header>
          <div className="app-dialog-body">
            {editingDevice === null ? null : (
              <form onSubmit={(event) => void updateDevice(event, editingDevice)}>
                <label>
                  端末名
                  <input
                    name="label"
                    required
                    maxLength={120}
                    defaultValue={editingDevice.label}
                    autoFocus
                  />
                </label>
                <div className="dialog-actions">
                  <button type="button" onClick={() => setEditingDevice(null)}>
                    キャンセル
                  </button>
                  <button className="primary" disabled={familyMutationBusy !== null}>
                    保存
                  </button>
                </div>
              </form>
            )}
          </div>
        </dialog>

        <dialog
          ref={passkeyEditDialogRef}
          className="app-dialog compact-dialog"
          aria-labelledby="passkey-edit-title"
          onClose={() => setEditingPasskey(null)}
          onCancel={(event) => {
            if (familyMutationBusy !== null) event.preventDefault();
          }}
        >
          <header className="app-dialog-header">
            <div>
              <p className="eyebrow">ログインの安全</p>
              <h2 id="passkey-edit-title">パスキー名を変更</h2>
            </div>
            <button
              type="button"
              className="dialog-close"
              aria-label="閉じる"
              disabled={familyMutationBusy !== null}
              onClick={() => setEditingPasskey(null)}
            >
              ×
            </button>
          </header>
          <div className="app-dialog-body">
            {editingPasskey === null ? null : (
              <form onSubmit={(event) => void updatePasskey(event, editingPasskey)}>
                <label>
                  パスキー名
                  <input
                    name="device_label"
                    required
                    maxLength={120}
                    defaultValue={editingPasskey.device_label}
                    autoFocus
                  />
                </label>
                <div className="dialog-actions">
                  <button type="button" onClick={() => setEditingPasskey(null)}>
                    キャンセル
                  </button>
                  <button className="primary" disabled={familyMutationBusy !== null}>
                    保存
                  </button>
                </div>
              </form>
            )}
          </div>
        </dialog>

        <dialog
          ref={childDialogRef}
          className="app-dialog child-dialog"
          aria-labelledby="child-dialog-title"
          aria-describedby={childDialogStep === "closed" ? undefined : "child-dialog-description"}
          onClose={() => setChildDialogStep("closed")}
          onCancel={(event) => {
            if (creatingChild || enrollmentBusyChildId !== null) event.preventDefault();
          }}
        >
          <header className="app-dialog-header">
            <div>
              <p className="eyebrow">
                {childDialogStep === "create"
                  ? "プロフィール"
                  : childDialogStep === "created"
                    ? "追加しました"
                    : "子供端末"}
              </p>
              <h2 id="child-dialog-title">
                {childDialogStep === "create"
                  ? "子供を追加"
                  : childDialogStep === "created"
                    ? `${childDialogChild?.display_name ?? "子供"}を追加しました`
                    : childDialogStep === "enroll"
                      ? `${childDialogChild?.display_name ?? "子供"}の端末を登録`
                      : "登録の準備ができました"}
              </h2>
            </div>
            <button
              type="button"
              className="dialog-close"
              aria-label="閉じる"
              disabled={creatingChild || enrollmentBusyChildId !== null}
              onClick={closeChildDialog}
            >
              ×
            </button>
          </header>
          <div className="app-dialog-body">
            {childDialogStep === "create" ? (
              <>
                <p id="child-dialog-description" className="dialog-description">
                  家族の中で使うプロフィールです。本名でなく、いつもの呼び名でも構いません。
                </p>
                {error === null ? null : (
                  <p className="error" role="alert">
                    {error}
                  </p>
                )}
                <form
                  ref={childCreateFormRef}
                  className="child-create-form"
                  onSubmit={(event) => void createChild(event)}
                >
                  <label className="form-span">
                    表示名
                    <input
                      name="display_name"
                      required
                      maxLength={80}
                      placeholder="例：とまと"
                      autoFocus
                    />
                    <small>親画面と子供画面に表示されます。</small>
                  </label>
                  <label>
                    読みやすさ
                    <select name="reading_level" defaultValue="beginner">
                      <option value="emergent">はじめて</option>
                      <option value="beginner">やさしい</option>
                      <option value="independent">じぶんで読める</option>
                    </select>
                  </label>
                  <label>
                    画面の密度
                    <select name="display_density" defaultValue="comfortable">
                      <option value="comfortable">ゆったり</option>
                      <option value="compact">コンパクト</option>
                    </select>
                  </label>
                  <label className="child-color-field form-span">
                    テーマ色
                    <input name="theme_color" type="color" defaultValue="#4f8f72" />
                  </label>
                  <div className="dialog-actions form-span">
                    <button type="button" disabled={creatingChild} onClick={closeChildDialog}>
                      キャンセル
                    </button>
                    <button className="primary" disabled={creatingChild}>
                      {creatingChild ? "追加しています…" : "子供を追加"}
                    </button>
                  </div>
                </form>
              </>
            ) : null}

            {childDialogStep === "created" && childDialogChild !== null ? (
              <div className="child-created-step">
                <p id="child-dialog-description" className="dialog-description">
                  プロフィールを作成し、表示する子供を
                  <strong>{childDialogChild.display_name}</strong>
                  に切り替えました。次にすることを選べます。
                </p>
                <div className="next-step-grid">
                  <button
                    className="next-step-card primary-step"
                    onClick={() => {
                      const childId = childDialogChild.id;
                      closeChildDialog();
                      selectChild(childId);
                      navigateParent("plan");
                    }}
                  >
                    <strong>一週間の計画を開く</strong>
                    <span>今日だけでなく、これからやることを整理します</span>
                  </button>
                  <button
                    className="next-step-card"
                    onClick={() => {
                      setError(null);
                      setEnrollmentResult(null);
                      setChildDialogStep("enroll");
                    }}
                  >
                    <strong>子供の端末を登録</strong>
                    <span>別のiPadやブラウザで使えるようにします</span>
                  </button>
                </div>
                <div className="dialog-actions">
                  <button onClick={closeChildDialog}>あとで</button>
                </div>
              </div>
            ) : null}

            {childDialogStep === "enroll" && childDialogChild !== null ? (
              <>
                <p id="child-dialog-description" className="dialog-description">
                  登録する端末の名前と、その端末でできる操作を確認してください。
                </p>
                {error === null ? null : (
                  <p className="error" role="alert">
                    {error}
                  </p>
                )}
                <form
                  key={childDialogChild.id}
                  className="enrollment-form"
                  onSubmit={(event) => void issueEnrollment(event)}
                >
                  <label>
                    端末の名前
                    <input
                      name="device_label"
                      required
                      maxLength={120}
                      defaultValue={`${childDialogChild.display_name}の端末`}
                      autoFocus
                    />
                    <small>あとで端末一覧から見分けるための名前です。</small>
                  </label>
                  <fieldset className="capability-picker">
                    <legend>この端末でできること</legend>
                    {childDeviceCapabilities.map((capability) => (
                      <label key={capability.value}>
                        <input
                          type="checkbox"
                          name="capabilities"
                          value={capability.value}
                          defaultChecked
                        />
                        <span>{capability.label}</span>
                      </label>
                    ))}
                  </fieldset>
                  <p className="security-note">
                    登録リンクは10分間・1回だけ有効です。子供のパスワードやメールアドレスは作りません。
                  </p>
                  <div className="dialog-actions">
                    <button type="button" onClick={closeChildDialog}>
                      キャンセル
                    </button>
                    <button className="primary" disabled={enrollmentBusyChildId !== null}>
                      {enrollmentBusyChildId === null ? "登録用QRを作る" : "作成しています…"}
                    </button>
                  </div>
                </form>
              </>
            ) : null}

            {childDialogStep === "enrollment-ready" &&
            childDialogChild !== null &&
            enrollmentResult !== null ? (
              <div className="enrollment-ready-step">
                <p id="child-dialog-description" className="dialog-description">
                  <strong>{childDialogChild.display_name}</strong>
                  の端末でQRコードを読み取るか、登録リンクを開いてください。
                </p>
                <div className="enrollment-share-grid">
                  <div className="qr-frame">
                    {enrollmentQrCode === null ? (
                      <span className="qr-loading" role="status">
                        QRコードを作成しています…
                      </span>
                    ) : (
                      <img
                        src={enrollmentQrCode}
                        alt={`${childDialogChild.display_name}の端末登録QRコード`}
                      />
                    )}
                  </div>
                  <div className="enrollment-link-panel">
                    <strong>登録リンク</strong>
                    <input
                      aria-label="子供端末の登録リンク"
                      readOnly
                      value={enrollmentResult.url}
                      onFocus={(event) => event.currentTarget.select()}
                    />
                    <button className="primary" onClick={() => void copyEnrollmentLink()}>
                      リンクをコピー
                    </button>
                    {copyNotice === null ? null : (
                      <p className="copy-notice" role="status">
                        {copyNotice}
                      </p>
                    )}
                  </div>
                </div>
                <p className="security-note">
                  有効期限は
                  {new Date(enrollmentResult.expiresAt).toLocaleTimeString("ja-JP", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  までです。この親端末では開かず、登録する子供端末で使ってください。
                </p>
                <div className="dialog-actions">
                  <button className="primary" onClick={closeChildDialog}>
                    完了
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </dialog>
        <dialog
          ref={notificationPreferenceDialogRef}
          className="app-dialog notification-preference-dialog"
          aria-labelledby="notification-preference-title"
          onClose={() => setNotificationPreferenceDraft(null)}
          onCancel={(event) => {
            if (notificationPreferenceBusy) event.preventDefault();
          }}
        >
          <header className="app-dialog-header">
            <div>
              <p className="eyebrow">声かけの設計</p>
              <h2 id="notification-preference-title">
                {notificationPreferenceDraft?.current === null
                  ? "通知ルールを追加"
                  : "通知ルールを編集"}
              </h2>
            </div>
            <button
              type="button"
              className="dialog-close"
              aria-label="通知設定を閉じる"
              disabled={notificationPreferenceBusy}
              onClick={() => setNotificationPreferenceDraft(null)}
            >
              ×
            </button>
          </header>
          <div className="app-dialog-body">
            {notificationPreferenceDraft === null ? null : (
              <form
                className="notification-preference-form"
                onSubmit={(event) => void saveNotificationPreference(event)}
              >
                <fieldset disabled={notificationPreferenceBusy}>
                  <legend>通知先と対象</legend>
                  <div className="settings-form notification-target-grid">
                    <label>
                      通知先
                      <select
                        value={notificationPreferenceDraft.recipient_type}
                        disabled={notificationPreferenceDraft.current !== null}
                        onChange={(event) => {
                          const recipientType = event.currentTarget.value as "membership" | "child";
                          setNotificationPreferenceDraft((current) =>
                            current === null
                              ? null
                              : {
                                  ...current,
                                  recipient_type: recipientType,
                                  child_id:
                                    recipientType === "child"
                                      ? (activeChildren[0]?.id ?? null)
                                      : null,
                                  related_child_id: null,
                                  event_types:
                                    recipientType === "child"
                                      ? [...childNotificationEvents]
                                      : [...guardianNotificationEvents],
                                  batching_mode: "immediate",
                                  quiet_hours_behavior: "defer",
                                },
                          );
                        }}
                      >
                        <option value="membership">親</option>
                        <option value="child" disabled={activeChildren.length === 0}>
                          子供
                        </option>
                      </select>
                    </label>
                    {notificationPreferenceDraft.recipient_type === "child" ? (
                      <label>
                        声をかける子供
                        <select
                          required
                          value={notificationPreferenceDraft.child_id ?? ""}
                          disabled={notificationPreferenceDraft.current !== null}
                          onChange={(event) =>
                            setNotificationPreferenceDraft((current) =>
                              current === null
                                ? null
                                : { ...current, child_id: event.currentTarget.value },
                            )
                          }
                        >
                          {activeChildren.map((child) => (
                            <option key={child.id} value={child.id}>
                              {child.display_name}
                            </option>
                          ))}
                        </select>
                      </label>
                    ) : (
                      <label>
                        対象の子供
                        <select
                          value={notificationPreferenceDraft.related_child_id ?? ""}
                          disabled={notificationPreferenceDraft.current !== null}
                          onChange={(event) =>
                            setNotificationPreferenceDraft((current) =>
                              current === null
                                ? null
                                : {
                                    ...current,
                                    related_child_id: event.currentTarget.value || null,
                                  },
                            )
                          }
                        >
                          <option value="">家族全体</option>
                          {activeChildren.map((child) => (
                            <option key={child.id} value={child.id}>
                              {child.display_name}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    <label>
                      ルーティン
                      <select
                        value={notificationPreferenceDraft.routine_id ?? ""}
                        disabled={notificationPreferenceDraft.current !== null}
                        onChange={(event) =>
                          setNotificationPreferenceDraft((current) =>
                            current === null
                              ? null
                              : { ...current, routine_id: event.currentTarget.value || null },
                          )
                        }
                      >
                        <option value="">すべてのルーティン</option>
                        {routines.map((routine) => (
                          <option key={routine.id} value={routine.id}>
                            {routine.name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      通知方法
                      <select
                        value={notificationPreferenceDraft.channel}
                        disabled={notificationPreferenceDraft.current !== null}
                        onChange={(event) =>
                          setNotificationPreferenceDraft((current) =>
                            current === null
                              ? null
                              : {
                                  ...current,
                                  channel: event.currentTarget.value as "in_app" | "web_push",
                                },
                          )
                        }
                      >
                        <option value="in_app">アプリ内</option>
                        <option value="web_push">Web Push</option>
                        <option value="apns">iPhone・iPad通知</option>
                      </select>
                    </label>
                  </div>
                </fieldset>

                <fieldset disabled={notificationPreferenceBusy}>
                  <legend>通知する出来事</legend>
                  <div className="notification-event-grid">
                    {(notificationPreferenceDraft.recipient_type === "child"
                      ? childNotificationEvents
                      : guardianNotificationEvents
                    ).map((eventType) => (
                      <label key={eventType} className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={notificationPreferenceDraft.event_types.includes(eventType)}
                          onChange={(event) =>
                            setNotificationPreferenceDraft((current) =>
                              current === null
                                ? null
                                : {
                                    ...current,
                                    event_types: event.currentTarget.checked
                                      ? [...new Set([...current.event_types, eventType])]
                                      : current.event_types.filter((item) => item !== eventType),
                                  },
                            )
                          }
                        />
                        {notificationEventLabel(eventType)}
                      </label>
                    ))}
                  </div>
                </fieldset>

                <fieldset disabled={notificationPreferenceBusy}>
                  <legend>曜日と時間</legend>
                  <div className="weekday-picker" aria-label="通知する曜日">
                    {["日", "月", "火", "水", "木", "金", "土"].map((label, weekday) => (
                      <label key={label}>
                        <input
                          type="checkbox"
                          checked={notificationPreferenceDraft.active_weekdays.includes(weekday)}
                          onChange={(event) =>
                            setNotificationPreferenceDraft((current) =>
                              current === null
                                ? null
                                : {
                                    ...current,
                                    active_weekdays: event.currentTarget.checked
                                      ? [...new Set([...current.active_weekdays, weekday])].sort()
                                      : current.active_weekdays.filter((item) => item !== weekday),
                                  },
                            )
                          }
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                  <div className="settings-form notification-time-grid">
                    <label>
                      開始（任意）
                      <input
                        type="time"
                        value={notificationPreferenceDraft.active_from_local_time}
                        onChange={(event) =>
                          setNotificationPreferenceDraft((current) =>
                            current === null
                              ? null
                              : { ...current, active_from_local_time: event.currentTarget.value },
                          )
                        }
                      />
                    </label>
                    <label>
                      終了（任意）
                      <input
                        type="time"
                        value={notificationPreferenceDraft.active_until_local_time}
                        onChange={(event) =>
                          setNotificationPreferenceDraft((current) =>
                            current === null
                              ? null
                              : { ...current, active_until_local_time: event.currentTarget.value },
                          )
                        }
                      />
                    </label>
                  </div>
                </fieldset>

                <fieldset disabled={notificationPreferenceBusy}>
                  <legend>届け方</legend>
                  <div className="settings-form notification-delivery-grid">
                    {notificationPreferenceDraft.recipient_type === "membership" ? (
                      <label>
                        まとめ方
                        <select
                          value={notificationPreferenceDraft.batching_mode}
                          onChange={(event) =>
                            setNotificationPreferenceDraft((current) =>
                              current === null
                                ? null
                                : {
                                    ...current,
                                    batching_mode: event.currentTarget.value as
                                      "immediate" | "digest",
                                  },
                            )
                          }
                        >
                          <option value="immediate">その都度</option>
                          <option value="digest">指定時刻にまとめる</option>
                        </select>
                      </label>
                    ) : null}
                    {notificationPreferenceDraft.recipient_type === "membership" &&
                    notificationPreferenceDraft.batching_mode === "digest" ? (
                      <label>
                        ダイジェスト時刻
                        <input
                          type="time"
                          required
                          value={notificationPreferenceDraft.digest_local_time}
                          onChange={(event) =>
                            setNotificationPreferenceDraft((current) =>
                              current === null
                                ? null
                                : { ...current, digest_local_time: event.currentTarget.value },
                            )
                          }
                        />
                      </label>
                    ) : null}
                    <label>
                      通知しない時間に重なったら
                      <select
                        value={notificationPreferenceDraft.quiet_hours_behavior}
                        onChange={(event) =>
                          setNotificationPreferenceDraft((current) =>
                            current === null
                              ? null
                              : {
                                  ...current,
                                  quiet_hours_behavior: event.currentTarget.value as
                                    "defer" | "digest" | "allow",
                                },
                          )
                        }
                      >
                        <option value="defer">終了後まで待つ</option>
                        <option value="allow">この通知は届ける</option>
                        {notificationPreferenceDraft.recipient_type === "membership" ? (
                          <option value="digest">ダイジェスト時刻まで待つ</option>
                        ) : null}
                      </select>
                    </label>
                    <label className="checkbox-row notification-enabled-row">
                      <input
                        type="checkbox"
                        checked={notificationPreferenceDraft.enabled}
                        onChange={(event) =>
                          setNotificationPreferenceDraft((current) =>
                            current === null
                              ? null
                              : { ...current, enabled: event.currentTarget.checked },
                          )
                        }
                      />
                      このルールを有効にする
                    </label>
                  </div>
                </fieldset>
                {notificationPreferenceError === null ? null : (
                  <p className="error" role="alert">
                    {notificationPreferenceError}
                  </p>
                )}
                <div className="dialog-actions">
                  <button
                    type="button"
                    disabled={notificationPreferenceBusy}
                    onClick={() => setNotificationPreferenceDraft(null)}
                  >
                    キャンセル
                  </button>
                  <button
                    className="primary"
                    disabled={
                      notificationPreferenceBusy ||
                      notificationPreferenceDraft.active_weekdays.length === 0 ||
                      (notificationPreferenceDraft.recipient_type === "child" &&
                        notificationPreferenceDraft.child_id === null)
                    }
                  >
                    {notificationPreferenceBusy ? "保存しています…" : "通知ルールを保存"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </dialog>
        <dialog
          ref={notificationDialogRef}
          className="app-dialog notification-center-dialog"
          aria-labelledby="notification-center-title"
          onClose={() => setNotificationCenterOpen(false)}
          onCancel={(event) => {
            if (notificationBusyId !== null) event.preventDefault();
          }}
        >
          <header className="app-dialog-header">
            <div>
              <p className="eyebrow">家族の動き</p>
              <h2 id="notification-center-title">通知</h2>
            </div>
            <button
              type="button"
              className="dialog-close"
              aria-label="通知を閉じる"
              disabled={notificationBusyId !== null}
              onClick={() => setNotificationCenterOpen(false)}
            >
              ×
            </button>
          </header>
          <div className="app-dialog-body notification-center-body">
            <div className="notification-center-summary">
              <div>
                <strong>{unreadNotificationCount}</strong>
                <span>未読</span>
              </div>
              <button
                type="button"
                disabled={notificationsLoading}
                onClick={() => void reloadNotifications()}
              >
                {notificationsLoading ? "更新中…" : "最新に更新"}
              </button>
            </div>
            <WebPushControl session={session} />
            {notificationsLoading && notifications.length === 0 ? (
              <p className="status" role="status">
                通知を読み込んでいます…
              </p>
            ) : notifications.length === 0 ? (
              <div className="notification-empty">
                <strong>新しい通知はありません</strong>
                <p>生成点検や子供からの連絡があると、ここに届きます。</p>
              </div>
            ) : (
              <ol className="notification-list">
                {notifications.map((notification) => {
                  const copy = parentNotificationCopy(notification);
                  const relatedChild = children.find(
                    (child) => child.id === notification.related_child_id,
                  );
                  return (
                    <li
                      key={notification.id}
                      className={notification.read_at === null ? "notification-unread" : undefined}
                    >
                      <div className="notification-copy">
                        <div>
                          {notification.read_at === null ? (
                            <span className="notification-unread-mark">未読</span>
                          ) : null}
                          <time dateTime={new Date(notification.created_at).toISOString()}>
                            {new Date(notification.created_at).toLocaleString("ja-JP", {
                              month: "numeric",
                              day: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </time>
                        </div>
                        <strong>{copy.title}</strong>
                        <p>{copy.description}</p>
                        {relatedChild === undefined ? null : (
                          <small>{relatedChild.display_name}</small>
                        )}
                      </div>
                      <div className="notification-actions">
                        <button
                          type="button"
                          disabled={notificationBusyId !== null}
                          onClick={() => void openNotificationDestination(notification)}
                        >
                          関連画面を開く
                        </button>
                        <button
                          type="button"
                          disabled={notificationBusyId !== null}
                          onClick={() =>
                            void commandNotification(
                              notification,
                              notification.read_at === null ? "read" : "unread",
                            )
                          }
                        >
                          {notification.read_at === null ? "既読にする" : "未読に戻す"}
                        </button>
                        <button
                          type="button"
                          disabled={notificationBusyId !== null}
                          onClick={() =>
                            void commandNotification(
                              notification,
                              "snooze",
                              Date.now() + 60 * 60 * 1_000,
                            )
                          }
                        >
                          1時間後に見る
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </dialog>
      </section>
    </main>
  );
}

function McpConsentPanel({
  session,
  authorizationRequestId,
  onLogout,
}: {
  session: BrowserSession;
  authorizationRequestId: string;
  onLogout: () => void;
}) {
  const workspaceId = session.workspace.id;
  const [request, setRequest] = useState<McpAuthorizationRequest | null>(null);
  const [children, setChildren] = useState<ChildProfile[]>([]);
  const [approvedScopes, setApprovedScopes] = useState<McpScope[]>([]);
  const [childScope, setChildScope] = useState<"selected" | "all">("selected");
  const [selectedChildIds, setSelectedChildIds] = useState<string[]>([]);
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [busy, setBusy] = useState<"approve" | "deny" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      api<{ authorization_request: McpAuthorizationRequest }>(
        `/api/v1/workspaces/${workspaceId}/mcp/authorization-requests/${authorizationRequestId}`,
        undefined,
        workspaceId,
      ),
      api<{ items: ChildProfile[] }>(
        `/api/v1/workspaces/${workspaceId}/children?limit=100`,
        undefined,
        workspaceId,
      ),
    ])
      .then(([authorization, childPage]) => {
        if (cancelled) return;
        const activeChildren = childPage.items.filter((child) => child.archived_at === null);
        setRequest(authorization.authorization_request);
        setApprovedScopes(authorization.authorization_request.requested_scopes);
        setChildren(activeChildren);
        setSelectedChildIds(activeChildren.map((child) => child.id));
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        setError(caught instanceof Error ? caught.message : "連携内容を読み込めませんでした。");
      });
    return () => {
      cancelled = true;
    };
  }, [authorizationRequestId, workspaceId]);

  async function finish(decision: "approve" | "deny") {
    if (busy !== null) return;
    setBusy(decision);
    setError(null);
    try {
      const result = await api<{ redirect_uri: string }>(
        `/api/v1/workspaces/${workspaceId}/mcp/authorization-requests/${authorizationRequestId}/${decision}`,
        {
          method: "POST",
          body: JSON.stringify(
            decision === "approve"
              ? {
                  approved_scopes: approvedScopes,
                  child_scope: childScope,
                  child_ids: childScope === "all" ? [] : selectedChildIds,
                  expires_in_days: expiresInDays,
                }
              : {},
          ),
        },
        workspaceId,
      );
      location.assign(result.redirect_uri);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.code === "RECENT_AUTH_REQUIRED") {
        setError(
          "安全のため、連携の許可には直前のパスキーログインが必要です。一度ログアウトし、もう一度ログインしてからこの連携URLを開いてください。",
        );
      } else {
        setError(caught instanceof Error ? caught.message : "連携を確定できませんでした。");
      }
      setBusy(null);
    }
  }

  const needsChildren = approvedScopes.some((scope) =>
    [
      "tasks:read",
      "tasks:write",
      "routines:read",
      "routines:write",
      "reviews:read",
      "reviews:write",
      "insights:read",
    ].includes(scope),
  );
  const needsFamilyScope = approvedScopes.some(
    (scope) => scope === "notifications:read" || scope === "notifications:write",
  );
  const canApprove =
    request !== null &&
    approvedScopes.length > 0 &&
    (!needsFamilyScope || childScope === "all") &&
    (!needsChildren ||
      childScope === "all" ||
      children.length === 0 ||
      selectedChildIds.length > 0);

  return (
    <main className="auth-shell">
      <section className="auth-card mcp-consent-card" aria-labelledby="mcp-consent-title">
        <p className="eyebrow">Remote MCP連携</p>
        <h1 id="mcp-consent-title">エージェントに許可する内容</h1>
        {request === null && error === null ? (
          <p className="status" role="status">
            連携内容を確認しています…
          </p>
        ) : null}
        {request === null ? null : (
          <>
            <p className="lede">
              <strong>{request.client_name}</strong> が「{session.workspace.name}
              」へのアクセスを求めています。 必要な権限と子供だけを選んでください。
            </p>
            <section className="consent-section" aria-labelledby="mcp-scope-title">
              <h2 id="mcp-scope-title">許可する操作</h2>
              <div className="consent-option-list">
                {request.requested_scopes.map((scope) => {
                  const copy = mcpScopeLabels[scope];
                  return (
                    <label key={scope} className="consent-option">
                      <input
                        type="checkbox"
                        checked={approvedScopes.includes(scope)}
                        onChange={(event) =>
                          setApprovedScopes((current) =>
                            event.currentTarget.checked
                              ? [...new Set([...current, scope])]
                              : current.filter((item) => item !== scope),
                          )
                        }
                      />
                      <span>
                        <strong>{copy.title}</strong>
                        <small>{copy.description}</small>
                      </span>
                    </label>
                  );
                })}
              </div>
            </section>
            {children.length === 0 && !needsFamilyScope ? null : (
              <section className="consent-section" aria-labelledby="mcp-child-title">
                <h2 id="mcp-child-title">対象の子供</h2>
                <div className="consent-scope-mode" role="radiogroup" aria-label="対象範囲">
                  <label>
                    <input
                      type="radio"
                      name="child_scope"
                      checked={childScope === "selected"}
                      onChange={() => setChildScope("selected")}
                    />
                    選んだ子供だけ
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="child_scope"
                      checked={childScope === "all"}
                      onChange={() => setChildScope("all")}
                    />
                    家族全体（今後追加する子供を含む）
                  </label>
                </div>
                {needsFamilyScope && childScope !== "all" ? (
                  <p className="security-note" role="status">
                    通知は複数の子供にまたがるため、「家族全体」を明示して選んでください。
                  </p>
                ) : null}
                <div className="consent-child-grid" aria-disabled={childScope === "all"}>
                  {children.map((child) => (
                    <label key={child.id} className="consent-child">
                      <input
                        type="checkbox"
                        disabled={childScope === "all"}
                        checked={selectedChildIds.includes(child.id)}
                        onChange={(event) =>
                          setSelectedChildIds((current) =>
                            event.currentTarget.checked
                              ? [...new Set([...current, child.id])]
                              : current.filter((id) => id !== child.id),
                          )
                        }
                      />
                      <span
                        className="avatar"
                        style={{ background: child.theme_color }}
                        aria-hidden="true"
                      />
                      <strong>{child.display_name}</strong>
                    </label>
                  ))}
                </div>
              </section>
            )}
            <label className="consent-expiry">
              許可の有効期間
              <select
                value={expiresInDays}
                onChange={(event) => setExpiresInDays(Number(event.currentTarget.value))}
              >
                <option value={1}>1日</option>
                <option value={7}>7日</option>
                <option value={30}>30日</option>
                <option value={90}>90日</option>
              </select>
            </label>
            <p className="security-note">
              写真の中身、パスキー、回復コード、端末失効、メンバー変更、削除はこの許可に含まれません。許可は設定画面からいつでも取り消せます。
            </p>
          </>
        )}
        {error === null ? null : (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <div className="consent-actions">
          <button disabled={busy !== null || request === null} onClick={() => void finish("deny")}>
            {busy === "deny" ? "拒否しています…" : "許可しない"}
          </button>
          <button
            className="primary"
            disabled={busy !== null || !canApprove}
            onClick={() => void finish("approve")}
          >
            {busy === "approve" ? "許可しています…" : "選んだ内容を許可"}
          </button>
        </div>
        <button className="text-action" onClick={onLogout}>
          別の保護者でログイン
        </button>
      </section>
    </main>
  );
}

export function App() {
  const nativeAuthorizationRequestId = new URLSearchParams(location.search).get("native_authorize");
  const enrollmentToken = new URLSearchParams(location.search).get("enroll");
  const membershipInvitationToken = new URLSearchParams(location.search).get("invite");
  const mcpAuthorizationRequestId = new URLSearchParams(location.search).get("mcp_authorize");
  const storedWorkspaceId =
    enrollmentToken === null && membershipInvitationToken === null
      ? localStorage.getItem(WORKSPACE_KEY)
      : null;
  const [checking, setChecking] = useState(storedWorkspaceId !== null);
  const [session, setSession] = useState<BrowserSession | null>(null);
  const [recoveredSession, setRecoveredSession] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [childPreviewId, setChildPreviewId] = useState(readChildPreviewId);
  const [childSessionError, setChildSessionError] = useState<string | null>(null);
  const sessionWorkspaceId = session?.workspace.id;
  const sessionChildId = session?.actor.bound_child_id;
  const sessionDeviceId = session?.actor.device_id;
  const isChildSession = session?.actor.type === "child";

  useEffect(() => {
    if (!isChildSession || !sessionWorkspaceId || !sessionChildId || !sessionDeviceId) {
      configureChildSession(null);
      return;
    }
    configureChildSession({
      workspaceId: sessionWorkspaceId,
      childId: sessionChildId,
      deviceId: sessionDeviceId,
      onRenew: (next) => {
        setSession({ actor: next.actor, workspace: next.workspace, csrf_token: next.csrf_token });
        setChildSessionError(null);
      },
    });
    const renew = () => {
      void ensureChildSession(sessionWorkspaceId).catch((error: unknown) => {
        setChildSessionError(
          error instanceof Error ? error.message : "つうしんを かくにんしてね。",
        );
      });
    };
    renew();
    const timer = window.setInterval(renew, 30_000);
    window.addEventListener("online", renew);
    window.addEventListener("focus", renew);
    return () => {
      configureChildSession(null);
      window.clearInterval(timer);
      window.removeEventListener("online", renew);
      window.removeEventListener("focus", renew);
    };
  }, [isChildSession, sessionWorkspaceId, sessionChildId, sessionDeviceId]);

  useEffect(() => {
    const syncPreviewRoute = () => setChildPreviewId(readChildPreviewId());
    window.addEventListener("hashchange", syncPreviewRoute);
    return () => window.removeEventListener("hashchange", syncPreviewRoute);
  }, []);

  const acceptSession = useCallback((next: BrowserSession, recovered = false) => {
    setRecoveredSession(recovered);
    if (recovered) sessionStorage.setItem("oyako-recovered-session", next.actor.session_id);
    else sessionStorage.removeItem("oyako-recovered-session");
    localStorage.setItem(WORKSPACE_KEY, next.workspace.id);
    setSession(next);
    setRecoveryCodes(next.recovery_codes ?? null);
  }, []);

  const acceptInvitationSession = useCallback(
    (next: BrowserSession) => {
      const query = new URLSearchParams(location.search);
      query.delete("invite");
      window.history.replaceState(
        null,
        "",
        query.has("native_authorize") ? `/?${query.toString()}` : "/#parent/settings",
      );
      acceptSession(next);
    },
    [acceptSession],
  );

  useEffect(() => {
    if (
      enrollmentToken !== null ||
      membershipInvitationToken !== null ||
      storedWorkspaceId === null
    )
      return;
    void api<{ actor: SessionActor; workspace: WorkspaceSummary }>(
      "/api/v1/me",
      undefined,
      storedWorkspaceId,
    )
      .then((me) => {
        setSession({
          actor: me.actor,
          workspace: me.workspace,
          csrf_token: readCookie("oyako_csrf") ?? "",
        });
      })
      .catch(() => {
        localStorage.removeItem(WORKSPACE_KEY);
      })
      .finally(() => {
        setChecking(false);
      });
  }, [enrollmentToken, membershipInvitationToken, storedWorkspaceId]);

  async function logout() {
    if (session !== null) {
      await api(
        "/api/v1/auth/logout",
        { method: "POST", body: JSON.stringify({ workspace_id: session.workspace.id }) },
        session.workspace.id,
      ).catch(() => undefined);
    }
    localStorage.removeItem(WORKSPACE_KEY);
    setSession(null);
  }

  if (new URLSearchParams(location.search).has("account_cleanup"))
    return <UnjoinedAccountDeletion />;
  if (enrollmentToken !== null)
    return <EnrollmentPanel token={enrollmentToken} onSession={acceptSession} />;
  if (membershipInvitationToken !== null)
    return (
      <MembershipInvitationPanel
        token={membershipInvitationToken}
        onSession={acceptInvitationSession}
      />
    );
  if (checking)
    return (
      <main className="auth-shell">
        <p className="status" role="status">
          セッションを確認しています…
        </p>
      </main>
    );
  if (recoveryCodes !== null)
    return <RecoveryCodes codes={recoveryCodes} onDone={() => setRecoveryCodes(null)} />;
  if (session === null) return <AuthPanel onSession={acceptSession} />;
  if (session.actor.type === "child")
    return (
      <>
        {childSessionError === null ? null : (
          <p className="error" role="alert">
            {childSessionError}
          </p>
        )}
        <ChildHome session={session} onLogout={() => void logout()} />
      </>
    );
  if (nativeAuthorizationRequestId !== null)
    return (
      <NativeConsent
        requestId={nativeAuthorizationRequestId}
        requiresPasskey={
          recoveredSession ||
          sessionStorage.getItem("oyako-recovered-session") === session.actor.session_id ||
          new URLSearchParams(location.search).get("native_action") === "add_passkey"
        }
        workspaceId={session.workspace.id}
        csrf={session.csrf_token}
        onLogout={() => void logout()}
      />
    );
  if (mcpAuthorizationRequestId !== null)
    return (
      <McpConsentPanel
        session={session}
        authorizationRequestId={mcpAuthorizationRequestId}
        onLogout={() => void logout()}
      />
    );
  if (childPreviewId !== null)
    return <ParentChildPreview session={session} childId={childPreviewId} />;
  return (
    <ParentHome
      session={session}
      onLogout={() => void logout()}
      onWorkspaceUpdated={(workspace) =>
        setSession((current) => (current === null ? current : { ...current, workspace }))
      }
    />
  );
}
